// src/types.ts
import "react-i18next";

// src/provider.tsx
import { useEffect } from "react";
import { I18nextProvider } from "react-i18next";

// src/config.ts
import i18n from "i18next";
import ICU from "i18next-icu";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// src/locales/en/common.json
var common_default = {
  actions: {
    save: "Save",
    cancel: "Cancel",
    confirm: "Confirm",
    delete: "Delete",
    edit: "Edit",
    back: "Back",
    next: "Next",
    previous: "Previous",
    submit: "Submit",
    done: "Done",
    skipForNow: "Skip for now",
    close: "Close",
    search: "Search",
    clear: "Clear",
    apply: "Apply",
    upload: "Upload",
    download: "Download",
    review: "Review",
    view: "View",
    viewTicket: "View Ticket",
    viewTeamMembers: "View Team Members",
    viewDetails: "View Details",
    add: "Add",
    viewAll: "VIEW ALL",
    tryAgain: "Try Again",
    login: "LOGIN",
    backToWebsite: "BACK TO WEBSITE",
    optional: "(optional)",
    closeAlert: "Close alert"
  },
  labels: {
    required: "Required",
    optional: "Optional",
    loading: "Loading...",
    noResults: "No results",
    all: "All",
    none: "None",
    yes: "Yes",
    no: "No",
    searchAndSelect: "Search and select an option",
    notAvailable: "N/A"
  },
  dateInfo: {
    startedOn: "Started on:",
    completedOn: "Completed on:",
    expectedBy: "Expected by:",
    estimatedSLA: "Estimated SLA:",
    oneBusinessDay: "1 business day",
    twoBusinessDays: "2 business days",
    nBusinessDays: "{estimatedSLA} business days"
  },
  multiSelect: {
    selectOptions: "Select options",
    selectedCount: "{count} selected",
    clearAllAriaLabel: "Clear all selections",
    selectionInfo: "{current} of {max} selected"
  },
  dropdown: {
    selectOption: "Select an option"
  },
  mobileInput: {
    placeholder: "Enter mobile number"
  },
  companyLogo: {
    alt: "Company Logo"
  },
  header: {
    companyImageAlt: "Company logo",
    welcomeText: "Hi,"
  },
  fileUpload: {
    clickToUpload: "Click to upload",
    dragAndDrop: "or drag and drop",
    tapToUpload: "Tap to upload",
    supportedFormats: "PDF, PNG or JPG only (max. 5 MB)",
    singleFileOnly: "Only one file can be uploaded at a time."
  },
  errors: {
    sessionExpiredTitle: "Session Expired",
    errorTitle: "Error",
    generic: "Something went wrong. Please try again.",
    somethingWentWrong: "Something went wrong.",
    somethingWentWrongDescription: "We couldn't complete the action due to a system error. Please try again in a few moments.",
    unableToComplete: "Something went wrong, we were unable to complete the action. Please refresh page and try again.",
    unexpectedError: "We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.",
    tryAgain: "Try Again",
    reloadPage: "Reload Page",
    refreshPage: "Refresh page",
    errorDetails: "Error Details (Development Only)",
    network: "Network error. Please check your connection.",
    notFound: "The requested resource was not found.",
    unauthorized: "You are not authorized to perform this action.",
    tasksLoadError: "Failed to load tasks: {error}",
    serverError: "Server error. Please try again later.",
    genericError: "An unexpected error occurred. Please try again.",
    failedToLoadCountries: "Failed to load countries",
    failedToLoadGccCountries: "Failed to load GCC countries",
    failedToLoad: "Failed to load data",
    validation: {
      required: "This field is required.",
      email: "Please enter a valid email address.",
      minLength: "Must be at least {min, number} {min, plural, one {character} other {characters}}.",
      maxLength: "Must be no more than {max, number} {max, plural, one {character} other {characters}}.",
      pattern: "The value entered is not valid."
    },
    fileUpload: {
      fileRequired: "A file is required for upload",
      fileTooLarge: "File size exceeds the maximum allowed size of {maxMb}MB",
      invalidFileType: "File type not allowed",
      invalidFilename: "A valid filename is required",
      serviceNotConfigured: "File upload service is not configured",
      fileTooLargeServer: "File size exceeds the server limit",
      unsupportedMediaType: "File type not supported",
      uploadFailed: "Failed to upload file. Please try again.",
      fileIdRequiredForDeletion: "File ID is required for deletion",
      fileIdRequired: "File ID is required",
      accessDenied: "Access denied to this file",
      fileNotFound: "File not found",
      deleteFailed: "Failed to delete file",
      metadataFailed: "Failed to retrieve file information"
    }
  },
  pagination: {
    showing: "Showing {from, number}\u2013{to, number} of {total, number} {total, plural, one {result} other {results}}",
    itemsPerPage: "Items per page",
    page: "Page {page, number} of {total, number}",
    ariaLabel: "Pagination Navigation",
    previousPage: "Go to previous page",
    nextPage: "Go to next page",
    goToPage: "Go to page {page, number}"
  },
  feedback: {
    saved: "Changes saved.",
    deleted: "Successfully deleted.",
    copied: "Copied to clipboard."
  },
  search: {
    placeholder: "Search",
    resultsAriaLabel: "Search results",
    searching: "Searching...",
    noResults: "No results found",
    adjustSearchTerms: "Try adjusting your search terms",
    inCategory: "in {category}",
    clearAriaLabel: "Clear search"
  },
  floatingMenu: {
    open: "Open Menu",
    edit: "Edit",
    duplicate: "Duplicate",
    archive: "Archive",
    delete: "Delete"
  },
  modalHeader: {
    close: "Close modal"
  },
  sidePanel: {
    collapse: "Collapse panel",
    expand: "Expand panel",
    close: "Close panel"
  },
  companyLogoUpload: {
    supportingText: "PNG, JPG, SVG, GIF, WEBP (max. {max})",
    editAriaLabel: "Edit company logo",
    fileTooLarge: "File exceeds the {max} limit. Please choose a smaller file.",
    fileTypeInvalid: "File type not supported. Please upload PNG, JPG, SVG, GIF, or WEBP.",
    networkError: "No internet connection. Please check your network and try again."
  },
  errorPage: {
    notFoundTitle: "A 404 error has occurred.",
    notFoundDescription: "Please try go back to the Home screen.",
    errorTitle: "Something went wrong",
    errorDescription: "An unexpected error occurred. Please reload the page or return to the home page. If the problem persists, contact support.",
    goToHome: "Go to Home",
    goBack: "Go Back",
    reloadPage: "Reload Page",
    goHome: "Go to Home",
    backToHome: "Back to Home"
  },
  services: {
    emptyHeading: "You don't have any services yet.",
    failedToLoad: "Failed to load services: {error}"
  },
  appInfo: {
    title: "App Info",
    backToHome: "Back to Home"
  },
  layout: {
    information: "Information"
  },
  logos: {
    tasama: "Tasama Logo",
    tasamaWithTagline: "Tasama Logo with tag line"
  },
  fileUploadItem: {
    statusUploading: "Uploading...",
    statusComplete: "Complete",
    statusFailed: "Failed",
    removeAriaLabel: "Remove file"
  },
  status: {
    notStarted: "Not started",
    moreInfoRequired: "More info required",
    incomplete: "Incomplete",
    waitingYourConfirmation: "Waiting your confirmation",
    inProgress: "In progress",
    requested: "Requested",
    completed: "Completed",
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    onHold: "On hold",
    confirmCompletion: "Confirm completion",
    rejected: "Rejected",
    awaitingCustomer: "Awaiting customer",
    awaitingVendor: "Awaiting vendor",
    awaitingCandidate: "Awaiting candidate",
    closedIncomplete: "Closed incomplete",
    new: "New",
    ticketOpen: "Open",
    awaitingCustomerTicket: "Awaiting Customer",
    resolved: "Resolved",
    onHoldTicket: "On Hold",
    awaitingThirdPartyInfo: "Awaiting 3rd party info",
    closedTicket: "Closed",
    canceledTicket: "Canceled"
  },
  tasks: {
    noTasks: "You don't have any tasks yet."
  },
  breadcrumb: {
    ariaLabel: "Breadcrumb"
  },
  accountPage: {
    tabs: {
      personalInformation: "Personal Information",
      security: "Security",
      notifications: "Notifications",
      billing: "Billing"
    },
    status: {
      securityComingSoon: "Security settings coming soon...",
      notificationsComingSoon: "Notification preferences coming soon...",
      billingComingSoon: "Billing information coming soon..."
    }
  },
  businessAdvisor: {
    headerTitle: "SAMA AI",
    headerTitleLine1: "SAMA AI",
    headerTitleLine2: "",
    headerDescription: "Powered by Tasama",
    apiError: "An error has occurred.",
    apiErrorDescription: "Please refresh the page.",
    greetingPrefix: "Hello, how can I ",
    greetingHighlight: "help you?",
    defaultSubtitle: "I'm your AI assistant here to help you understand Tasama's services, guide your journey and support your next steps.",
    initialising: "Initialising...",
    collapseModal: "Collapse Modal",
    expandModal: "Expand Modal",
    closeModal: "Close Advisor Modal",
    imageAlt: "Business AI Text Icon",
    inputPlaceholder: "Ask me anything",
    inputAriaLabel: "Ask your question",
    sendAriaLabel: "Send message",
    characterLimit: "{max, number} max character limit. {current, number}/{max, number}",
    disclaimer: "SAMA is AI and can make mistakes. Please double-check responses.",
    button: {
      askAi: "Ask SAMA AI",
      open: "Open Business AI Advisor",
      close: "Close Business AI Advisor"
    },
    errors: {
      initConversation: "Failed to initialize conversation",
      unexpected: "An unexpected error occurred",
      noSessionId: "No conversation session ID received",
      sendFailed: "Failed to send message",
      noSessionAvailable: "No conversation session available. Please initialize first.",
      emptyMessage: "Message cannot be empty",
      noContext: "No context available for message",
      fetchMessagesFailed: "Failed to fetch messages",
      refreshMessagesFailed: "Failed to refresh messages",
      loadMoreFailed: "Failed to load more messages",
      authRequired: "Authentication is required to access the advisor service",
      contextRequired: "Context information is required",
      unexpectedResponse: "Unexpected response from the advisor service",
      serviceNotFound: "Advisor service not found",
      invalidInitRequest: "Invalid session initialization request",
      unauthorized: "Unauthorized access to the advisor service",
      forbidden: "Access to the advisor service is forbidden",
      initSessionFailed: "Failed to initialize the advisor session",
      messageAndContextRequired: "Message and context are required",
      sessionIdRequired: "Conversation session ID is required",
      sessionNotFound: "Conversation session not found",
      invalidMessageRequest: "Invalid message request",
      invalidRecommendationsRequest: "Invalid recommendations request",
      recommendationsNotFound: "Recommendations not found",
      recommendationsFailed: "Failed to retrieve recommendations"
    },
    loader: {
      loading: "Loading..."
    },
    cta: {
      home: "Home",
      registerNow: "Register Now",
      resetPassword: "Reset Password",
      contactMe: "Contact me",
      restartConversation: "Restart conversation",
      supportTicket: "Support Ticket",
      serviceCatalogue: "Service Catalogue",
      notifications: "Notifications",
      myAccount: "My Account",
      myDocuments: "My Documents",
      myServices: "My Services"
    },
    aria: {
      cta: "CTA",
      suggestion: "Suggestion"
    },
    contextualPrompt: {
      delayQuestion: "I can see this is behind, why is there a delay?"
    }
  },
  chip: {
    iconAction: "Icon action"
  },
  notification: {
    defaultTitle: "Notification"
  },
  accordion: {
    ariaLabel: "Accordion"
  },
  banner: {
    defaultTitle: 'Welcome to <span class="text-brand-gold">Tasama</span>'
  },
  dateInput: {
    chooseDate: "Choose date",
    placeholder: "DD/MM/YYYY"
  },
  timeInput: {
    chooseTime: "Choose time",
    placeholder: "HH:MM AM/PM",
    done: "Done",
    hoursLabel: "Hours",
    minutesLabel: "Minutes"
  },
  dateTimeInput: {
    chooseDateTime: "Choose date and time",
    placeholder: "DD/MM/YYYY HH:MM",
    selectDateTab: "Select date",
    selectTimeTab: "Select time"
  },
  dateRangePicker: {
    chooseDateRange: "Choose date range",
    placeholder: "DD/MM/YYYY - DD/MM/YYYY",
    startDate: "Start date",
    endDate: "End date",
    clearStartDateAriaLabel: "Clear start date",
    clearEndDateAriaLabel: "Clear end date",
    mobileTitle: "Custom date range",
    presets: {
      custom: "Custom",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      last90Days: "Last 90 days",
      last1Year: "Last 1 year",
      all: "All"
    }
  },
  languageSwitcher: {
    switchTo: "Switch to Arabic",
    currentLanguage: "ENGLISH"
  },
  personForm: {
    firstName: "First name",
    lastName: "Last name",
    prefix: "Prefix",
    selectPrefix: "Select prefix",
    prefixes: {
      mr: "Mr",
      ms: "Ms",
      mrs: "Mrs",
      miss: "Miss",
      dr: "Dr"
    },
    jobTitle: "Job title",
    email: "Email",
    mobilePhone: "Mobile phone"
  },
  companyForm: {
    registeredCompanyName: "Registered company name",
    companyRegistrationNumber: "Company registration number",
    registrationStartDate: "Registration start date",
    companyWebsite: "Company website",
    enterWebsiteUrl: "Enter website url",
    industrySector: "Industry sector",
    industrySectorPlaceholder: "Choose company industry",
    countryOfRegistration: "Country of registration",
    registeredCompanyAddress: "Registered company address",
    enterAddress: "Enter the number, street name or PO Box",
    city: "City",
    enterCity: "Enter the city",
    postcode: "Postcode",
    enterPostcode: "Enter the postcode",
    countryPlaceholder: "Search and select country",
    numberOfEmployees: "Number of employees",
    numberOfEmployeesPlaceholder: "Enter the number of employees",
    annualRevenue: "Annual revenue",
    annualRevenuePlaceholder: "Enter the annual revenue",
    totalInvestmentRaised: "What is the company's total investment raised to date?",
    totalInvestmentRaisedPlaceholder: "Enter the total investment raised",
    companyNamePlaceholder: "Enter your company name",
    crNumberPlaceholder: "Enter the CR number",
    saudiArabia: "Saudi Arabia",
    employeeCountOptions: {
      range_0_5: "0 - 5",
      range_6_25: "6 - 25",
      range_26_100: "26 - 100",
      range_101_200: "101 - 200",
      range_201_1000: "201 - 1,000",
      range_gt_1000: "> 1,000"
    },
    annualRevenueOptions: {
      range_0_1M: "0 - 1M SAR",
      range_1M_5M: "1M SAR - 5M SAR",
      range_5M_20M: "5M SAR - 20M SAR",
      range_gt_20M: "> 20M SAR"
    },
    investmentRaisedOptions: {
      range_0_5M: "0 - 5M SAR",
      range_5M_20M: "5M SAR - 20M SAR",
      range_20M_50M: "20M SAR - 50M SAR",
      range_50M_100M: "50M SAR - 100M SAR",
      range_gt_100M: "> 100M SAR"
    }
  },
  onboardingNav: {
    links: {
      howItWorks: "How it works",
      whyTasama: "Why Tasama",
      resources: "Resources",
      contactUs: "Contact us"
    },
    aria: {
      mainNavigation: "Main navigation"
    },
    logo: {
      aria: {
        goToHomepage: "Go to homepage"
      },
      title: "Tasama Logo"
    }
  },
  filters: {
    title: "Filters",
    triggerLabel: "Filters",
    clearFilters: "RESET",
    apply: "APPLY"
  },
  dynamicRowsTable: {
    removeAll: "Remove all",
    requiredField: "This field is required",
    incompleteFields: "The following fields are incomplete: {fields}",
    confirmationTitle: "Confirmation",
    confirmLabel: "Remove",
    deleteRow: {
      description: "Are you sure you want to delete this row?"
    },
    removeAllConfirm: {
      description: "Are you sure you want to remove all rows?"
    }
  },
  charts: {
    noData: "No data available"
  }
};

// src/locales/en/auth.json
var auth_default = {
  customerLogin: "Customer Login",
  emailLabel: "Email",
  emailPlaceholder: "Enter your email",
  passwordLabel: "Password",
  passwordPlaceholder: "Enter your password",
  passwordHint: "Must be at least 8 characters.",
  loginButton: "Login",
  forgotPassword: "Forgot your password?",
  resetPassword: "Reset password",
  accountNotFound: "The username or password you entered is incorrect. Please check your details and try again.",
  forgotPasswordTitle: "Forgot Password",
  forgotPasswordSubtitle: "A password reset link will be sent to your registered email address.",
  resetPasswordButton: "Reset Password",
  returnTo: "Return to",
  loginLink: "Login",
  checkEmailTitle: "Please check your email",
  checkEmailDescription: "We've sent a link to your registered email address. Please check your inbox and follow the instructions.",
  didNotReceiveEmail: "Didn't receive the email?",
  tryAgain: "Try again",
  activateAccountTitle: "Activate Your Account",
  activateAccountDescription: "Set up your account to start using our services.",
  reactivateAccountTitle: "Reactivate Your Account",
  reactivateAccountDescription: "Get a new activation link and continue",
  resetPasswordTitle: "Reset Password",
  resetPasswordDescription: "You've requested a reset of your password",
  newPasswordLabel: "New Password",
  newPasswordPlaceholder: "Enter your new password",
  confirmPasswordLabel: "Confirm Password",
  confirmPasswordPlaceholder: "Confirm your new password",
  confirmPasswordHint: "Must match the password above.",
  activateAccountButton: "Activate Account",
  reactivateAccountButton: "Reactivate Your Account",
  alreadyActivated: "Already Activated?",
  resetPasswordFormDescription: "Please enter your email and create a new password to reset your account.",
  emailLinkExpired: "The email link has expired.",
  expiredLinkDescription: "Click {action} to receive a new activation link. The new link will be valid for 48 hours.",
  reactivateAction: "Reactivate Account",
  resetAction: "Reset Password",
  tooManyRequests: "Too many requests. Please try again later.",
  tooManyPasswordResets: "Too many password reset attempts. Please try again in 15 minutes.",
  anErrorOccurred: "An error occurred. Please try again.",
  emailNotFound: "Email address not found",
  failedToSendResetLink: "Failed to send reset link",
  failedToSendReactivationLink: "Failed to send reactivation link",
  invalidRequest: "Invalid request. Please check your input.",
  sessionConflict: {
    title: "Are you sure you want to log out?",
    activateDescription: "Activating a new account will log you out of your current session.",
    resetPasswordDescription: "Resetting a password will log you out of your current session.",
    backToDashboard: "BACK TO DASHBOARD",
    logOut: "LOG OUT"
  },
  failedToActivateAccount: "Failed to activate account",
  failedToResetPassword: "Failed to reset password",
  validation: {
    email: {
      required: "Email address is required!",
      invalid: "Please enter a valid email address!",
      corporate: "Please use your corporate email address. Personal email domains are not allowed."
    },
    password: {
      required: "Password is required!",
      minLength: "Password must be at least 8 characters long!",
      maxLength: "Password must not exceed 20 characters",
      charset: "Password may only contain letters, numbers, and special characters (@$!%*?&)",
      lowercase: "Password must contain at least one lowercase letter",
      uppercase: "Password must contain at least one uppercase letter",
      number: "Password must contain at least one number",
      specialChar: "Password must contain at least one special character (@$!%*?&)"
    },
    resetToken: {
      required: "Reset token is required!"
    },
    confirmPassword: {
      required: "Please confirm your password!",
      mismatch: "Passwords do not match!"
    }
  },
  errors: {
    invalidCredentials: "Invalid email or password.",
    accountLocked: "Your account has been locked. Please contact support.",
    sessionExpired: "Your session has expired. Please sign in again.",
    loginFailed: "Login failed",
    fingerprintMismatch: "Security check failed. Please log in again for your safety.",
    rateLimitExceeded: "Too many requests. Please wait a moment before trying again.",
    networkError: "Network error. Please check your connection and try again.",
    authenticationError: "Authentication error: {{message}}",
    genericError: "Error: {{message}}",
    unexpectedError: "An unexpected error occurred",
    missingRefreshToken: "Refresh token is missing",
    sessionFingerprintRequired: "Session fingerprint validation required",
    sessionFingerprintInvalid: "Security validation failed. Please sign in again.",
    tokenRefreshFailed: "Failed to refresh your session. Please sign in again.",
    invalidRefreshToken: "Your session has expired. Please sign in again.",
    tokenExpired: "Your session has expired. Please sign in again.",
    invalidToken: "Invalid authentication token. Please sign in again.",
    invalidOrMissingToken: "Authentication token is missing or invalid. Please sign in again.",
    missingAuthHeader: "Authorization header is missing",
    invalidAuthHeaderFormat: "Invalid authorization header format",
    authTypeMustBeBearer: "Authorization type must be Bearer",
    tokenMissing: "Authentication token is missing",
    tokenEmpty: "Authentication token is empty",
    tokenAlgorithmNotPermitted: "Authentication token algorithm is not permitted",
    invalidTokenFormat: "Invalid authentication token format",
    signingKeyUnreachable: "Unable to verify authentication. Please try again.",
    signingKeyNotFound: "Authentication configuration error. Please contact support.",
    userGroupsNotFound: "User account configuration error. Please contact support.",
    noGroupsAssigned: "Your account has no permissions assigned. Please contact support.",
    portalAccountIdNotFound: "Account configuration error. Please contact support.",
    portalUserIdNotFound: "Account configuration error. Please contact support.",
    companyNameNotFound: "Company information not found. Please contact support.",
    token: {
      invalidPayload: "Invalid token",
      linkExpired: "This link has expired. Please request a new one.",
      invalidLink: "This link is invalid. Please request a new one.",
      invalidType: "Invalid token type"
    }
  }
};

// src/locales/en/portal.json
var portal_default = {
  userProfile: {
    errors: {
      notAuthenticated: "User is not authenticated.",
      failedToFetch: "Failed to fetch user profile."
    }
  },
  app: {
    errors: {
      rootElementNotFound: "Root element not found."
    }
  },
  backToTop: {
    label: "BACK TO TOP"
  },
  navigation: {
    home: "HOME",
    serviceCatalogue: "SERVICE CATALOGUE",
    dashboards: "DASHBOARDS",
    myServices: "MY SERVICES",
    myTasks: "MY TASKS",
    supportTickets: "SUPPORT TICKETS",
    openMenu: "Open navigation menu",
    closeMenu: "Close menu",
    superAdmin: "SUPER ADMIN",
    catalogSyncPublish: "CATALOG SYNC + PUBLISH"
  },
  myTasks: {
    title: "My tasks",
    emptyHeading: "No tasks",
    emptyText: "You haven't received any tasks yet. Once you receive a task, it will appear here.",
    loading: "Loading tasks...",
    scrollForMore: "Scroll for more...",
    errorHeading: "Something went wrong",
    errorLoading: "Error loading tasks:",
    today: "Today",
    yesterday: "Yesterday"
  },
  header: {
    myAccount: "My Account",
    logout: "Logout",
    notifications: "Notifications",
    navigateHome: "Navigate to home",
    userProfile: "User profile"
  },
  notifications: {
    title: "Notifications",
    loading: "Loading notifications...",
    empty: "No notifications",
    unreadAriaLabel: "Unread notification",
    unreadNotificationsAriaLabel: "Unread notifications",
    viewAllAriaLabel: "View all notifications",
    sortNewestFirstAria: "Sort: newest first. Click to switch to oldest first.",
    sortOldestFirstAria: "Sort: oldest first. Click to switch to newest first.",
    markAllAsReadButton: "MARK ALL AS READ",
    thirtyDayInfo: "Only notifications within 30-days will be displayed.",
    noNotificationsAvailable: "No notifications available!",
    scrollForMore: "Scroll for more...",
    errorLoading: "Error loading notifications:",
    today: "Today",
    yesterday: "Yesterday",
    sortAriaLabel: "Sort notifications"
  },
  websocketTestPanel: {
    title: "WebSocket Test Panel",
    testMessage: "Hello WebSocket!",
    notificationTitle: "Test Notification",
    notificationMessage: "This is a test notification from the dashboard.",
    connecting: "Connection status: Connecting to WebSocket server...",
    connected: "\u2705 Connection status: Successfully connected to WebSocket server",
    error: "\u274C Connection status: Error - {error}",
    disconnected: "Connection status: Disconnected from WebSocket server",
    attemptingConnect: "Attempting to connect to WebSocket...",
    disconnecting: "Disconnecting from WebSocket...",
    sendTestMessage: "Send Test Message",
    sendNotification: "Send Notification",
    connect: "Connect",
    disconnect: "Disconnect",
    clearAllNotifications: "Clear All Notifications",
    markAllAsRead: "Mark All as Read",
    subscribeToAll: "Subscribe to All",
    unsubscribeFromAll: "Unsubscribe from All",
    testResults: "Test Results",
    notifications: "Notifications",
    unreadCount: "Unread Count",
    connectionStatus: "Connection Status",
    lastMessage: "Last Message",
    stats: "Stats",
    clients: "Clients",
    testMessageLabel: "Test Message",
    notificationTitleLabel: "Notification Title",
    notificationMessageLabel: "Notification Message",
    notificationType: "Notification Type",
    notificationPriority: "Notification Priority",
    targetUserId: "Target User ID",
    userSpecific: "user-specific",
    medium: "medium",
    send: "Send",
    subscribe: "Subscribe",
    unsubscribe: "Unsubscribe",
    markAsRead: "Mark as Read",
    clearAll: "Clear All",
    testWebSocketConnection: "Test WebSocket Connection",
    testConnection: "Test Connection"
  },
  session: {
    inactivityWarning: "Inactivity Warning",
    inactivityDescription: 'You have been inactive for a while. You will be logged out in {time} due to inactivity. Click "Stay Logged In" to continue your session.',
    logout: "Logout",
    stayLoggedIn: "Stay Logged In"
  },
  home: {
    logoAlt: "Logo",
    myTasks: "My Tasks",
    tasksSubtitle: "The 3 latest tasks are shown here.",
    myServices: "My Services",
    servicesSubtitle: "The 3 latest services shown here.",
    viewAll: "VIEW ALL",
    tilesLoadError: "Failed to load home page tiles.",
    loadingServices: "Loading more services..."
  },
  status: {
    new: "New",
    closed: "Closed",
    resolved: "Resolved",
    open: "Open",
    awaitingCustomer: "Awaiting customer",
    unknown: "Unknown"
  },
  activityStatus: {
    requested: "Requested",
    inProgress: "In progress",
    awaiting: "Awaiting",
    closed: "Closed",
    closedIncomplete: "Closed incomplete"
  },
  ticketStatus: {
    new: "New",
    open: "Open",
    awaitingCustomer: "Awaiting customer",
    onHold: "On hold",
    awaiting3rdPartyInfo: "Awaiting 3rd party info",
    resolved: "Resolved",
    closed: "Closed",
    canceled: "Canceled",
    unknown: "Unknown"
  },
  ticketAge: {
    SAME_DAY: "Same day",
    TWO_DAYS: "2 days",
    TWO_TO_FIVE: "2-5 days",
    FIVE_TO_SEVEN: "5-7 days",
    ONE_TO_TWO_W: "1-2 weeks",
    TWO_TO_FOUR_W: "2-4 weeks",
    ONE_TO_TWO_M: "1-2 months",
    OVER_TWO_M: "Over 2 months"
  },
  myAccount: {
    title: "My Account",
    failedToLoad: "Failed to load account details",
    retry: "Retry",
    tabs: {
      myDetails: "My details",
      companyDetails: "Company details",
      myTeam: "My team",
      documents: "Documents"
    },
    myDetails: {
      failedToLoad: "Failed to load user details",
      loadingDetails: "Loading user details...",
      loadingForm: "Loading form...",
      edit: "EDIT",
      cancel: "CANCEL",
      save: "SAVE",
      saving: "SAVING...",
      personalInformation: "Personal information",
      activeSince: "Active since {date}"
    },
    companyDetails: {
      editButton: "EDIT",
      loadingDetails: "Loading company details...",
      failedToLoad: "Failed to load company details. Please try again.",
      logoUploadError: "Failed to upload logo. Please try again.",
      contentNotAvailable: "Content not available for companies under development.",
      changesUnderReview: "Your submitted changes are under review, updates will reflect once approved.",
      companyTypes: {
        international: "International Company",
        ksaPrivate: "KSA Private Company",
        pifEstablished: "PIF Established Company",
        pifUnderDevelopment: "PIF Under Development"
      },
      gccCountries: {
        "Saudi Arabia": "Saudi Arabia",
        "United Arab Emirates": "United Arab Emirates",
        Kuwait: "Kuwait",
        Qatar: "Qatar",
        Bahrain: "Bahrain",
        Oman: "Oman"
      },
      countries: {
        Afghanistan: "Afghanistan",
        Albania: "Albania",
        Algeria: "Algeria",
        Argentina: "Argentina",
        Australia: "Australia",
        Austria: "Austria",
        Bahrain: "Bahrain",
        Bangladesh: "Bangladesh",
        Belgium: "Belgium",
        Brazil: "Brazil",
        Canada: "Canada",
        China: "China",
        Egypt: "Egypt",
        France: "France",
        Germany: "Germany",
        India: "India",
        Indonesia: "Indonesia",
        Iran: "Iran",
        Iraq: "Iraq",
        Italy: "Italy",
        Japan: "Japan",
        Jordan: "Jordan",
        Kuwait: "Kuwait",
        Lebanon: "Lebanon",
        Malaysia: "Malaysia",
        Morocco: "Morocco",
        Netherlands: "Netherlands",
        Nigeria: "Nigeria",
        Oman: "Oman",
        Pakistan: "Pakistan",
        Qatar: "Qatar",
        "Saudi Arabia": "Saudi Arabia",
        Singapore: "Singapore",
        "South Africa": "South Africa",
        "South Korea": "South Korea",
        Spain: "Spain",
        Switzerland: "Switzerland",
        Turkey: "Turkey",
        "United Arab Emirates": "United Arab Emirates",
        "United Kingdom": "United Kingdom",
        "United States": "United States",
        Yemen: "Yemen"
      }
    },
    myTeam: {
      add: "ADD",
      cancel: "CANCEL",
      save: "SAVE",
      saving: "SAVING...",
      failedToLoad: "Failed to load team members",
      loadingMembers: "Loading team members...",
      loadingTable: "Loading table...",
      updatingMembers: "Updating...",
      loadingMore: "Loading more teams...",
      noTeamMembers: "No team members found",
      noTeamMembersDescription: "Start by adding your first team member to collaborate on projects.",
      noMatchingTeamMembers: "No team members match your search or filters",
      noMatchingTeamMembersDescription: "Try adjusting your search or filters and try again.",
      emailAlreadyExists: "Team member already exists with this email address",
      addedSuccessfully: "Team member added successfully",
      failedToUpdate: "Failed to update team member. Please try again.",
      failedToCreate: "Failed to create team member. Please try again.",
      cannotPerformAction: "Cannot perform this action. Please check the customer status.",
      failedToUpdateStatus: "Failed to update team member status. Please try again.",
      columns: {
        name: "Name",
        email: "Email",
        functions: "Functions",
        status: "Status",
        admin: "Admin"
      },
      filters: {
        searchPlaceholder: "Search by name, email or function",
        statusPlaceholder: "Filter by status",
        functionPlaceholder: "Filter by function"
      },
      permissions: {
        function: "Function",
        requester: "Requester",
        reviewer: "Reviewer",
        approver: "Approver",
        functions: "Functions",
        role: "Role",
        adminRole: "Admin",
        adminRoleDesc: "Full access to all services",
        operationTeamRole: "Operation team",
        operationTeamRoleDesc: "Select access per service",
        access: "Access"
      },
      notAvailable: "N/A",
      actions: {
        edit: "Edit",
        deactivate: "Deactivate",
        reactivate: "Reactivate"
      },
      deactivateModal: {
        deactivateTitle: "Deactivate Account",
        reactivateTitle: "Reactivate Your Account",
        deactivateDescription: "Are you sure you want to deactivate {name}'s account? They will lose access to all functions and won't be able to log in.",
        reactivateDescription: "Are you sure you want to reactivate {name}'s account? They will regain access to their assigned functions.",
        noCancel: "No, Cancel",
        yesDeactivate: "Yes, Deactivate",
        yesReactivate: "Yes, Reactivate"
      },
      cancelModal: {
        title: "Your data won't be saved",
        description: "Are you sure you want to cancel? Any usaved changes will be lost.",
        no: "No",
        yesCancel: "Yes, Cancel"
      },
      reactivate: "Reactivate",
      deactivate: "Deactivate"
    },
    keyAccountManager: {
      title: "Your key account manager",
      name: "Name:",
      phoneNumber: "Phone number:",
      email: "Email:"
    }
  },
  serviceCatalogue: {
    title: "Service Catalogue",
    bannerTitle: '<span class="text-fg-brand-gold">Our area</span> of expertise',
    bannerDescription: "Supporting organizations from inception through maturity",
    loading: "Loading service catalogue...",
    loadingServices: "Loading services...",
    failedToLoad: "Failed to load service catalogue data",
    promoText: "Choose from our service categories to get started. Each category offers detailed information and a simple way to proceed.",
    learnMore: "LEARN MORE",
    goBack: "GO BACK",
    scrollPrevious: "Scroll previous",
    scrollNext: "Scroll next",
    noServicesAvailable: "No services available for this category.",
    noServicesFound: "No services found",
    searchNoResults: 'Your search "{searchTerm}" did not match any services. Please try again.',
    allCategories: "All categories",
    requestService: "REQUEST SERVICE",
    enquireNow: "ENQUIRE NOW",
    valueToBusiness: "Value to the business",
    prerequisites: "Prerequisites",
    bannerAriaLabel: "Service Catalogue Banner",
    timeToProposalSla_label: "Time to proposal SLA:",
    timeToProposalSla_value: "{value} working days",
    serviceTierTable_serviceTier: "Service tier",
    serviceTierTable_description: "Description",
    serviceTierTable_benefits: "Benefits",
    availableWithBundles: "Available with the following bundles:",
    requestBundle: "REQUEST BUNDLE",
    viewOptions: "VIEW OPTIONS",
    serviceOptions: {
      title: "Service options",
      description: "This service can be requested individually or as part of a bundle.",
      viewDetails: "VIEW DETAILS"
    },
    badges: {
      service: "Service",
      bundle: "Bundle",
      addOn: "Add-on"
    },
    groupHeadings: {
      bundles: "Bundles",
      services: "Services",
      addOns: "Add-ons"
    },
    tierNames: {
      Advanced: "Advanced",
      Bronze: "Bronze",
      Campaign: "Campaign",
      Elite: "Elite",
      Enterprise: "Enterprise",
      Essential: "Essential",
      Gold: "Gold",
      Platinum: "Platinum",
      Foundation: "Foundation",
      Silver: "Silver",
      Standard: "Standard",
      Strategic: "Strategic"
    },
    sowBundle: {
      title: "Bundle Request Form",
      description: "Provide a few details to help Tasama prepare your bundle proposal and initiate contracting.",
      stepLabel: "Scope of work",
      validation: {
        timeframeRequired: "Please select an engagement time frame",
        tooLarge: "Maximum file size is 5 MB per document",
        invalidType: "Unsupported file type. Allowed types: PDF, PNG, JPG"
      },
      fields: {
        bundleName: "Bundle name",
        addonsLabel: "Would you like to include add-ons?",
        timeframeLabel: "When are you looking to start your first service?",
        timeframePlaceholder: "Select engagement time frame",
        requirementsLabel: "Description of your requirements (optional)",
        requirementsPlaceholder: "Add any engagement expectations, brief scope that you would like us to know. Alternatively upload a scope of work document.",
        fileSupportingText: "PDF, PNG, or JPG only (max. 5 MB)"
      }
    }
  },
  resultScreen: {
    successTitle: "Thank you",
    successDescription: "A member of our team will be in touch for next steps.",
    errorTitle: "An error has occurred.",
    errorDescription: "Please try again later.",
    proposal: {
      rejectedDescription: "Your account manager will review the reason for rejection and send you a revised proposal."
    }
  },
  serviceCard: {
    step: "Step {currentStep} of {totalSteps}:",
    ariaLabel: "{title} service card",
    serviceId: "Service ID:",
    select: "Select",
    moreDetails: "More details"
  },
  dashboards: {
    title: "Dashboards",
    lastUpdated: "Last updated: {value}",
    exportData: "Export data",
    summaryFailedToLoad: "Failed to load dashboard data. Please try again later.",
    tableFailedToLoad: "Failed to load the table. Please try again later.",
    tabs: {
      activities: "Activities",
      supportTickets: "Support tickets",
      servicesBundles: "Services & bundles"
    },
    filters: {
      l1Category: "L1 Category",
      l2Subcategory: "L2 Subcategory",
      l3Service: "L3 Service",
      topic: "Topic",
      status: "Status",
      dateRange: "Date range",
      dateRangePlaceholder: "Select date range",
      statusOptions: {
        new: "New",
        open: "Open",
        awaitingCustomer: "Awaiting customer",
        onHold: "On hold",
        awaiting3rdPartyInfo: "Awaiting 3rd party info",
        resolved: "Resolved",
        closed: "Closed",
        canceled: "Canceled",
        unknown: "Unknown"
      },
      serviceType: "Service type",
      bundles: "Bundles"
    },
    activities: {
      donut: {
        title: "Activities by status",
        totalLabel: "Total Requests"
      },
      bar: {
        title: "Active requests"
      },
      slaTrend: {
        title: "SLA trend %"
      },
      turnaroundTrend: {
        title: "Average turn-around time"
      },
      openAges: {
        title: "Open activity ages"
      },
      closedAges: {
        title: "Closed activity ages"
      },
      table: {
        sortBy: "Sort by",
        columns: {
          activity: "Activity",
          total: "Total",
          avgSlaPercent: "Avg SLA %",
          avgTurnaroundDays: "Avg turn-around",
          avgAgeDays: "Avg age"
        }
      }
    },
    supportTickets: {
      donut: {
        title: "Tickets by status",
        totalLabel: "Total Requests"
      },
      bar: {
        title: "Ticket requests"
      },
      slaTrend: {
        title: "SLA trend %"
      },
      turnaroundTrend: {
        title: "Average turn-around time"
      },
      openAges: {
        title: "Open ticket ages"
      },
      closedAges: {
        title: "Closed ticket ages"
      },
      table: {
        sortBy: "Sort by",
        columns: {
          topic: "Topic",
          total: "Total",
          avgSlaPercent: "Avg SLA %",
          avgTurnaroundDays: "Avg turn-around",
          avgAgeDays: "Avg age"
        }
      }
    },
    servicesBundles: {
      donut: {
        title: "Service requests",
        totalLabel: "Total Requests"
      },
      bar: {
        title: "Active services"
      },
      table: {
        sortBy: "Sort by",
        columns: {
          requestId: "Request ID",
          name: "Name",
          type: "Type",
          bundle: "Bundle",
          status: "Status",
          stage: "Stage",
          requestedAt: "Requested",
          activeSince: "Active since",
          lastCompletedAt: "Last completed"
        },
        type: {
          SERVICE: "Service",
          ADD_ON: "Add-On",
          BUNDLE: "Bundle"
        }
      }
    }
  },
  myServices: {
    title: "My Services",
    errorLoadingServices: "Error loading services: {error}",
    loadingMoreServices: "Loading more services...",
    emptyState: {
      requestedHeading: "No services under request",
      requestedText: "You haven't requested any services yet. Once you request a service, it will appear here.",
      activeHeading: "No active services",
      activeText: "You don't have any active services. Once a requested service is activated, it will appear here.",
      completedHeading: "No completed services",
      completedText: "Services will appear here once they're completed or expired. You'll be able to review details and access related documents anytime.",
      noResultsHeading: "No services found",
      noResultsText: "Your filters did not match any services. Please try again."
    },
    tabs_detail: {
      activities: "Activities",
      supportTickets: "Support tickets",
      documents: "Documents",
      invoices: "Invoices",
      taskBadgeSingular: "task",
      taskBadgePlural: "tasks",
      taskBadgeManyPlus: "9+ tasks"
    },
    filters: {
      l1Placeholder: "Category",
      l2Placeholder: "Subcategory",
      l3Placeholder: "Service"
    },
    bundleCard: {
      preTitle: "Bundle"
    },
    bundleDetail: {
      overviewTab: "Overview",
      servicesLabel: "Services",
      addonsLabel: "Add-ons",
      rejectionReasonLabel: "Rejection reason",
      errorLoadingBundle: "Error loading bundle: {error}"
    }
  },
  supportTickets: {
    title: "Support Tickets",
    validation: {
      required: "Please upload at least one attachment",
      mustComplete: "Please wait until all files finish uploading",
      tooLarge: "Maximum file size is 5 MB per file",
      invalidType: "Unsupported file type. Allowed types: PDF, PNG, JPG, JPEG, DOC, DOCX, TXT"
    },
    newTicket: "NEW TICKET",
    openTickets: "Open tickets",
    closedTickets: "Closed tickets",
    searchPlaceholderOpen: "Search by ticket number, function name, or subject",
    searchPlaceholderClosed: "Search by ticket number, function name, or subject",
    placeholder: "Type your response here",
    failedToLoad: "Failed to load tickets",
    unableToLoad: "Unable to load tickets",
    view: "VIEW",
    response: "Response",
    noResultsText: 'Your search "{query}" did not match any tickets. Please try again.',
    noMatchingTicketsFound: "No matching tickets found",
    noMatchingTicketsText: "Your filters did not match any tickets. Please try again.",
    noTicketsFound: "No tickets found",
    noTicketsText: "There are no support tickets to display at the moment.",
    noSupportTickets: "No support tickets",
    noSupportTicketsText: "You haven't raised any support tickets for this service. If you need assistance, you can create a ticket and track updates here.",
    unreadTicket: "Unread ticket",
    columns: {
      ticketNumber: "Ticket number",
      topic: "Topic",
      serviceSubTopic: "Service/sub-topic",
      subject: "Subject",
      status: "Status",
      creationDate: "Creation date",
      closureDate: "Closure date",
      createdBy: "Created by"
    },
    drawer: {
      title: "Please select the function this is related to.",
      topic: "Topic",
      general: "General",
      serviceSpecific: "Service Specific",
      activeServices: "Active Services",
      activeServiceLabel: "Active Service",
      activeServicePlaceholder: "Select an active service",
      requestedServices: "Requested Services",
      requestedServiceLabel: "Requested Service",
      requestedServicePlaceholder: "Select a requested service",
      itServices: "IT Services",
      oracleErpIssues: "Oracle ERP Issues",
      subTopic: "Sub-Topic",
      selectSubTopic: "Select a Sub-Topic",
      issue: "Issue",
      selectIssue: "Select an Issue",
      functionL1: "Function",
      selectFunction: "Select a Function",
      typeL2: "Type",
      selectType: "Select a Type",
      serviceL3: "Service",
      selectService: "Select a Service",
      subject: "Subject",
      subjectPlaceholder: "Enter ticket subject",
      describeNeeds: "Describe your support needs",
      describePlaceholder: "Provide detailed information about your support request",
      prefilledDescription: "I would like to inquire about the {serviceName} service",
      prefilledSubject: "{serviceName} inquiry",
      uploadSupportingText: "PDF, PNG, or JPG only (max. 5 MB)",
      somethingWentWrong: "Something went wrong",
      ticketAlreadyExists: "A ticket with the same details already exists",
      requiredSummary: {
        title: "Required before you can submit",
        schemaError: "This issue's form could not be loaded. Select the issue again to retry."
      }
    },
    priorityLabel: "{level} Priority",
    category: "Category",
    created: "Created",
    lastUpdated: "Last Updated",
    assignedTo: "Assigned To",
    closedDate: "Closed",
    success: {
      title: "Ticket submitted successfully",
      heading: "Ticket Created Successfully",
      description: "Your support request has been submitted and our team will review it shortly.",
      ticketNumber: "Ticket Number",
      topic: "Topic",
      function: "Function",
      type: "Type",
      service: "Service",
      subject: "Subject",
      created: "Created",
      descriptionLabel: "Description",
      byAuthor: "By {name}",
      attachments: "Attachments"
    },
    submissionSuccessful: "Submission Successful",
    submissionFailed: "Something went wrong",
    ticketClosed: "This ticket has been closed.",
    respond: "respond",
    responding: "Responding...",
    loadingDetails: "Loading ticket details...",
    ticketHash: "Ticket #{number}",
    loadingConversation: "Loading conversation...",
    startConversation: "Start the conversation",
    sendMessageToBegin: "Send a message to begin the conversation with our support team.",
    chatUploadSupportingText: "PDF, PNG, JPG, DOC, TXT (max. 5MB each)",
    typeResponseAriaLabel: "Type your response",
    submitResponseAriaLabel: "Submit response",
    hideUpload: "Hide upload",
    uploadFiles: "Upload files",
    status: {
      open: "Open",
      pending: "Pending",
      solved: "Solved",
      closed: "Closed",
      new: "New"
    },
    priority: {
      low: "Low",
      normal: "Normal",
      high: "High",
      urgent: "Urgent"
    },
    topic: {
      general: "General",
      service_specific: "Service Specific",
      active_services: "Active Services",
      requested_service: "Requested Service",
      it_services: "IT Services",
      oracle_erp_issues: "Oracle ERP Issues"
    },
    filters: {
      topicPlaceholder: "Filter by topic",
      servicePlaceholder: "Filter by service",
      statusPlaceholder: "Filter by status"
    }
  },
  nda: {
    downloadStep: "Download NDA",
    uploadStep: "Upload Signed NDA",
    downloadTitle: "Download NDA",
    rejectionFallbackTitle: "Updates required to your NDA",
    downloadDescription: "A copy of Tasama's NDA is available for your review and signature.",
    uploadTitle: "Upload NDA",
    uploadDescription: "Please upload the following document(s).",
    uploadError: "Upload Error",
    readyForDownload: "Ready for download",
    supportingText: "PDF, PNG, or JPG only (max. 5 MB)",
    successTitle: "Thank you.",
    successDescription: "Your account manager will be in touch soon with a service structure and proposal.",
    errorTitle: "Something went wrong.",
    errorDescription: "There was an issue processing your NDA. Please try again or contact support.",
    tile: {
      title: "Non-Disclosure Agreement"
    },
    task: {
      review: "Review NDA",
      modify: "Modify your NDA"
    },
    action: {
      sign: "Download NDA template, sign it, upload, and submit for review",
      under_review: "NDA is under legal review. Please wait for approval.",
      approved: "NDA has been approved. No action required.",
      rejected: "NDA was rejected. Please address feedback and resubmit.",
      none: "No action required."
    },
    validation: {
      required: "Please select a file to upload",
      tooLarge: "Maximum file size is 5 MB per document",
      invalidType: "Unsupported file type. Allowed types: PDF, PNG, JPG"
    }
  },
  msa: {
    initiate: {
      title: "Scope of Work",
      timeframeLabel: "When are you looking to start your first service?",
      timeframePlaceholder: "Select engagement time frame",
      descriptionPlaceholder: "Add any engagement expectations or a brief scope of work. Alternatively, upload a scope of work document.",
      additionalRequirementsLabel: "Description of your requirements",
      uploadSupportingDocumentsLabel: "Upload supporting documents (optional)",
      sidebarTitle: "Master Service Agreement",
      sidebarDescription: "Define the scope of work for your Master Service Agreement to establish clear deliverables and expectations.",
      rejectionTitle: "Updates required to your Master Service Agreement",
      validationError: "Please complete all required fields",
      submissionFailed: "Submission failed",
      fetchStatusError: "Failed to fetch MSA status",
      scopeOfWorkLabel: "Select service function you are looking for assistance in:",
      uploadSupportingText: "PDF, DOC, DOCX, PNG, JPG only (max. 5MB each)",
      timeframe: {
        this_month: "This month",
        next_month: "Next month",
        "3_months": "Within 3 months",
        "6_months": "Within 6 months",
        "12_months": "Within 12 months"
      }
    },
    reviewSidebarTitle: "Master Service Agreement",
    reviewSidebarDescription: "Complete the MSA process to establish the terms and conditions for our business relationship.",
    reviewTitle: "Review MSA",
    reviewDescription: "A copy of Tasama's Master Service Agreement is available for your review.",
    readyForDownloadReview: "Ready for download and review",
    failedToLoadDocument: "Failed to load document",
    successTitle: "Thank you.",
    successDescription: "A member of our team will be in touch regarding next steps.",
    errorTitle: "Something went wrong.",
    errorDescription: "There was an issue processing your MSA request. Please try again or contact support.",
    uploadTitle: "Upload Signed MSA",
    uploadDescription: "Please upload your copy of the signed MSA here.",
    uploadSupportingText: "PDF, DOC, DOCX only (max. 5 MB each)",
    steps: {
      reviewMSA: "Review MSA",
      signMSA: "Sign MSA",
      tasamaSignsMSA: "Tasama Signs MSA",
      msaActive: "MSA Active"
    },
    tile: {
      title: "Master Service Agreement"
    },
    task: {
      request: "Request MSA",
      sign: "Sign MSA",
      modify: "Modify your MSA"
    },
    helper: {
      draft_received: "Your MSA document is ready for review and signature.",
      pending_signature: "A signed MSA will be available soon, subject to internal approvals."
    },
    action: {
      submit_sow: "Submit Scope of Work",
      draft_pending: "Waiting for Tasama to prepare draft MSA",
      review_sign: "Review and sign the draft MSA",
      counter_signature: "Waiting for Tasama counter-signature",
      active: "MSA is active. No action required.",
      rejected: "MSA was rejected. Please address feedback and contact our support team.",
      addendum: "MSA is being superseded by a service request addendum.",
      none: "No action required."
    },
    validation: {
      scopeRequired: "Please select at least one service function",
      scopeMinItems: "At least one service function must be selected",
      timeframeRequired: "Please select an engagement timeframe",
      descriptionMaxLength: "Description must not exceed 1,000 characters",
      uploadRequired: "Please upload at least one signed MSA document",
      uploadMustComplete: "Please wait until the document finishes uploading",
      tooLarge: "Maximum file size is 5MB per document",
      invalidType: "Unsupported file type. Allowed types: PDF, DOC, DOCX"
    }
  },
  proposal: {
    reviewTitle: "Review proposal",
    reviewDescription: "Please review the proposal prices and duration. Approve or reject to proceed.",
    rejectionReasonLabel: "Reason for rejection",
    rejectionReasonPlaceholder: "Write a brief reason for rejection.",
    sidebarTitle: "Tasama proposal",
    sidebarDescription: "A tailored proposal for your review.",
    documentIdNotAvailable: "Proposal document ID is not available yet",
    downloadUrlError: "Failed to get signed download URL from API",
    fallbackFilename: "Tasama_Proposal.pdf",
    columns: {
      serviceItem: "Service item",
      frequency: "Frequency",
      unit: "Unit",
      unitPrice: "Unit price",
      total: "Total"
    },
    defaultServiceName: "Service",
    acceptQuestion: "Do you accept this proposal?"
  },
  serviceContract: {
    signTitle: "Sign contract",
    signDescription: "Please upload your copy of the signed Service Contract here."
  },
  purchaseOrder: {
    uploadTitle: "Upload PO",
    uploadDescription: "Please upload your signed purchase order to complete the financial approval process.",
    uploadPOLabel: "Upload PO",
    sidebarTitle: "Purchase order",
    sidebarDescription: "Upload a purchase order when you are ready to activate a particular service item. Your account manager will review and confirm it.",
    fileTypeError: "Only PDF, PNG, or JPG files are allowed (max. 5MB)",
    fileSizeError: "File size must not exceed 5MB",
    uploadErrorTitle: "Upload Error",
    invalidFileType: "Only PDF, PNG, or JPG files are allowed (max. 5 MB)",
    fileTooLarge: "File size must not exceed 5 MB",
    noFileSelected: "Please select a file to upload"
  },
  companyDocuments: {
    uploadTitle: "Upload Documents",
    uploadError: "Upload Error",
    descriptionInternational: "Upload your company registration certificate. For international companies, only one document is required.",
    descriptionKsaPrivate: "Upload your required documents. All three documents are required for KSA private and PIF established companies.",
    descriptionDefault: "Upload any updated documents here. Once approved, the existing version will be replaced.",
    supportingText: "PDF, PNG, or JPG only (max. 5mb)",
    lastReceived: "(last received {date})",
    invalidFileType: "Please upload only PDF, PNG, or JPG files.",
    fileTooLarge: "File size must be less than 5 MB.",
    missingDocuments: "Please upload the following required documents: {documents}",
    uploadFailedFor: "Upload failed for: {files}. Please try again.",
    someFilesFailed: "Some files failed to upload. Please try again.",
    uploadFailed: "Upload failed. Please try again."
  },
  companyEdit: {
    companyDetailsStep: "Company Details",
    uploadDocumentsStep: "Upload Documents",
    businessProfileTitle: "Business profile",
    businessProfileDescription: "Complete the rest of your business profile",
    termsAndConditions: "Terms and Conditions",
    submissionFailed: "Submission Failed",
    submissionFailedDescription: "Please resolve the above issues and try again.",
    updatesRequired: "Updates required to your company details"
  },
  rejectionModal: {
    updatesRequired: "Updates required",
    review: "Review"
  },
  confirmation: {
    thankYou: "Thank you for submitting your information."
  },
  errors: {
    failedToLoadTickets: "Failed to load tickets",
    unableToLoad: "Unable to load tickets",
    failedToFetchKam: "Failed to fetch KAM data",
    validationFailed: "Validation failed",
    noFileSelected: "No file selected for upload",
    uploadFailed: "Upload failed. Please try again.",
    submissionUnexpected: "An unexpected error occurred during submission",
    accountManagement: {
      reactivationFailed: "Failed to process your reactivation request. Please try again.",
      passwordResetFailed: "Failed to process your password reset request. Please try again.",
      forgotPasswordFailed: "Failed to process your forgot password request. Please try again.",
      linkAlreadyUsed: "This link has already been used.",
      invalidTokenTypeActivation: "Invalid activation link. Please request a new one.",
      userNotFoundByEmail: "No account found with the provided email address.",
      activationFailed: "Failed to activate your account. Please try again or contact support.",
      invalidTokenTypePasswordReset: "Invalid password reset link. Please request a new one.",
      passwordResetSubmitFailed: "Failed to reset your password. Please try again or contact support.",
      accountNotActivated: "Your account hasn\u2019t been activated yet. Please activate your account before resetting your password.",
      contactDetailsFailed: "Failed to fetch contact details."
    },
    cms: {
      contentNotFound: "Content not found",
      invalidFilePath: "Invalid file path",
      invalidFilePathCharacters: "File path contains invalid characters",
      operationFailed: "Failed to complete the operation",
      invalidContentType: "Invalid content type"
    },
    keycloakAdmin: {
      userNotFound: "User not found",
      emailOrUsernameMissing: "Email or username is required",
      emailRequired: "Email is required",
      userNotFoundByEmail: "No user found with the provided email",
      authFailed: "Authentication failed",
      initFailed: "Service initialization failed",
      resourceNotFound: "Resource not found",
      resourceConflict: "Resource already exists",
      operationFailed: "Operation failed. Please try again.",
      adminAuthRequired: "Authentication is required for admin access",
      insufficientPermissions: "Insufficient permissions",
      roleNotAuthorized: "This role is not authorized",
      tokenValidationFailed: "Token validation failed",
      accessTokenValidationFailed: "Access token validation failed",
      accessDenied: "Access denied",
      notConfigured: "Service configuration error. Please contact support.",
      serviceUnavailable: "Service is currently unavailable. Please try again later."
    },
    msa: {
      signedUploadNotAllowed: "Signed MSA can only be uploaded once the draft MSA has been received",
      signedAttachmentUploadFailed: "Failed to upload the signed MSA. Please try again.",
      signedSubmitFailed: "The file was uploaded but submitting the signed MSA failed. Please try again.",
      submitNotAllowedForStatus: "MSA cannot be submitted at the current stage",
      attachmentUploadFailed: "Failed to upload file attachments. Please try again.",
      creationFailed: "Failed to create MSA record",
      fileIdRequiredForDownload: "File ID is required for download",
      fileNotFound: "File not found",
      downloadForbidden: "Insufficient permissions to download this file",
      downloadFailed: "Failed to download file. Please try again."
    },
    myAccount: {
      authRequired: "Authentication is required",
      detailsNotFound: "Account details not found",
      invalidDetailsRequest: "Invalid account details request",
      unauthorized: "Unauthorized access to account details",
      forbidden: "Access to account details is forbidden",
      detailsFailed: "Failed to retrieve account details",
      noUpdateData: "No update data provided",
      accountDetailsNotFound: "Account details not found",
      invalidUpdateData: "Invalid update data provided",
      updateFailed: "Failed to update account details. Please try again.",
      companyDetailsNotFound: "Company details not found",
      invalidCompanyDetailsRequest: "Invalid company details request",
      companyDetailsFailed: "Failed to retrieve company details",
      companyUpdateFailed: "Failed to update company details. Please try again.",
      teamsAdminOnly: "Only administrators can view team members",
      teamMemberConflict: "A team member with this email already exists",
      teamMemberUpdateConflict: "Failed to update team member. Please try again.",
      teamMemberActivationPending: "User activation or deactivation is still pending",
      invalidTeamMemberData: "Invalid team member data provided",
      kybSubmitValidationFailed: "KYB submission validation failed",
      kybSubmitFailed: "KYB submission failed"
    },
    notifications: {
      authRequired: "Authentication is required",
      notFound: "Notifications not found",
      invalidParams: "Invalid request parameters",
      fetchFailed: "Failed to retrieve notifications",
      unreadCountNotFound: "Unread notification count not found",
      unreadCountFailed: "Failed to retrieve unread notification count",
      singleNotFound: "Notification not found",
      invalidData: "Invalid notification data provided",
      markReadFailed: "Failed to mark notification as read",
      markAllReadFailed: "Failed to mark all notifications as read"
    },
    serviceCatalogue: {
      invalidRequest: "Invalid request",
      notFound: "Service catalogue not found",
      categoryNotFound: "Category not found"
    },
    serviceRequest: {
      authRequired: "Authentication is required",
      serviceIdRequired: "Service ID is required",
      eligibilityCheckFailed: "Failed to check service eligibility",
      documentIdNotFound: "Failed to retrieve document ID",
      submitFailed: "Failed to submit service request. Please try again.",
      fetchFailed: "Failed to retrieve service requests",
      detailsFetchFailed: "Failed to fetch service request details",
      poDocumentRequired: "Purchase order document is required",
      poSubmitFailed: "Failed to submit purchase order. Please try again.",
      signedUrlNotFound: "Download URL for file not found",
      signedUrlFetchFailed: "Failed to retrieve file download URL",
      signedContractSubmitFailed: "Failed to submit signed contract. Please try again.",
      proposalReviewFailed: "Failed to submit proposal review. Please try again.",
      documentsFetchFailed: "Failed to fetch service documents"
    },
    home: {
      tasksFetchFailed: "Unable to load your tasks. Please refresh the page.",
      servicesFetchFailed: "Unable to load your services. Please refresh the page.",
      companyNotFound: "Company details could not be found. Please contact support.",
      rateLimitExceeded: "Too many requests. Please wait a moment and try again."
    }
  },
  validation: {
    myDetails: {
      firstName: {
        required: "First name is required",
        min: "First name must be at least 2 characters",
        max: "First name must be at most 255 characters",
        charset: "First name can only include letters, spaces and hyphens"
      },
      lastName: {
        required: "Last name is required",
        min: "Last name must be at least 2 characters",
        max: "Last name must be at most 255 characters",
        charset: "Last name can only include letters, spaces and hyphens"
      },
      salutation: {
        required: "Prefix is required"
      },
      jobTitle: {
        required: "Job title is required",
        max: "Job title must be at most 255 characters"
      },
      email: {
        required: "Email is required",
        invalid: "Please enter a valid email address"
      },
      country: {
        required: "Country code is required"
      },
      mobile: {
        required: "Mobile phone is required",
        invalid: "Please enter a valid mobile phone number for the selected country"
      }
    },
    companyDetails: {
      companyName: {
        required: "Company name is required",
        max: "Company name must be at most 200 characters",
        charset: "Company name can include letters, numbers, spaces and common symbols (&, -, ' , ., (, ), /, ,)"
      },
      crNumber: {
        required: "Registration number is required",
        max: "Registration number must be at most 100 characters",
        charset: "Registration number can include letters, numbers and hyphens only"
      },
      registrationStartDate: {
        required: "Registration date is required",
        format: "Please enter a valid date in DD/MM/YYYY format",
        future: "Registration date cannot be in the future"
      },
      website: {
        invalid: "Enter a valid website URL (e.g., https://example.com)"
      },
      country: {
        required: "Country of registration is required"
      },
      countryOfHeadquarter: {
        required: "Country of headquarters is required"
      },
      city: {
        charset: "City can only include letters, spaces and hyphens"
      },
      postalCode: {
        charset: "Postcode can include letters, numbers, spaces and hyphens (3-12 chars)"
      },
      industrySector: {
        required: "Industry sector is required"
      },
      address: {
        required: "Registered company address is required"
      },
      numberOfEmployees: {
        required: "Number of employees is required"
      },
      annualRevenue: {
        required: "Annual revenue is required"
      },
      totalInvestmentRaised: {
        required: "Total investment raised is required"
      },
      general: "Validation failed"
    },
    projectDetails: {
      projectCodeName: {
        required: "Project name is required",
        minLength: "Project name must be at least 3 characters",
        maxLength: "Project name must not exceed 100 characters"
      },
      projectDetails: {
        maxLength: "Project description must not exceed 1000 characters"
      },
      expectedLaunchDate: {
        invalidFuture: "Please enter a valid future date in DD/MM/YYYY format",
        invalidFormat: "Please enter a valid date in DD/MM/YYYY format",
        mustBeFuture: "Expected launch date must be in the future"
      }
    }
  },
  banner: {
    welcomeTitle: 'Welcome to <span class="text-fg-brand-gold">Tasama</span>',
    welcomeDescription: "Tasama helps businesses gain greater confidence in the Saudi market",
    softLandingTitle: 'Request <span class="text-fg-brand-gold">Tasama Business Landing in KSA</span>',
    softLandingDescription: "Welcome to Tasama, your trusted partner for a smooth and confident business landing in Saudi Arabia. We guide you through setup, compliance, and local market entry so you can establish, operate, and grow in KSA without friction.",
    msa: {
      title: 'Initiate <span class="text-fg-brand-gold">Master Service Agreement</span>',
      description: "Tell us which categories you're interested in and we'll help you set up a customized service agreement \u2014 from MSA to multi-service packages.",
      buttonText: "Request Service"
    },
    newService: {
      title: 'New Service <span class="text-fg-brand-gold">Added</span>',
      description: "Executive bootcamp program to boost your leadership capabilities and innovate for the future.",
      buttonText: "Request Service"
    }
  },
  actions: {
    retry: "Retry",
    markAllAsRead: "Mark all as read",
    backToTickets: "Back to tickets",
    createAnotherTicket: "Create another ticket",
    acceptProposal: "Yes, I accept this proposal",
    rejectProposal: "No, I need a revision"
  },
  companyForm: {
    title: "Company Details",
    countryOfHeadquarters: "Country of headquarters",
    registeredAddressHeadquarters: "Registered address of headquarters"
  },
  documents: {
    companyRegistrationCertificate: "Company registration certificate",
    vatCertificate: "Valid VAT certificate",
    nationalAddressId: "National address ID",
    lastReceived: "(last received {date})",
    serviceDocumentsInfo: "For service related documents, look under the specific services in My Services"
  },
  documentsTable: {
    documentName: "Document name",
    type: "Type",
    uploadDate: "Upload Date",
    uploadedBy: "Uploaded by",
    action: "Action",
    download: "DOWNLOAD",
    failedToLoad: "Failed to load documents. Please try again.",
    loadingDocuments: "Loading documents...",
    noDocuments: "No documents uploaded",
    noDocumentsText: "Documents related to this service will appear here once they're uploaded or shared."
  },
  serviceDetail: {
    stepOf: "Step {currentStep} of {totalSteps}",
    currentStep: "Current step",
    closeStepper: "Close stepper",
    myTasks: "My Tasks",
    tasamaActivities: "Tasama activities",
    noActionRequired: "No action required by you.",
    requestSubmitted: "Request submitted",
    requestSubmittedDescription: "Your request has been successfully submitted. We are reviewing the next steps and will be in touch soon.",
    errorLoading: "Error loading service details: {error}",
    loadingServiceDetails: "Loading service details...",
    failedToLoadActivities: "Failed to load activities. Please try again.",
    stepCountDetails: "Step count details"
  },
  addTravelDateModal: {
    title: "Add travel dates",
    description: "Please select the dates during which you will be staying in Saudi Arabia. The stay should include at least 10 working days for TASAMA to proceed with and complete the company registration activities.",
    note: "Note: Public holidays in Saudi Arabia may affect the required stay duration. During Ramadan, we recommend planning for approximately 15 working days as reduced operating hours may impact processing timelines with relevant entities.",
    weekendsLegend: "Weekends (disabled)",
    publicHolidaysLegend: "Public holidays (disabled)",
    fromLabel: "From",
    toLabel: "To",
    fromTitle: "From Date",
    toTitle: "To Date",
    validation: {
      fromRequired: "From date is required",
      toRequired: "To date is required",
      fromFuture: "From date must be today or in the future",
      toAfterFrom: "To date must be greater than From date",
      minStayDays: "Stay duration must be at least {days} days (2 weeks)"
    }
  },
  company: {
    type: {
      pif_established: "PIF Established",
      pif_under_dev: "PIF Under Development",
      ksa_private: "KSA Private",
      international: "International"
    }
  },
  onboarding: {
    action: {
      start: "Start onboarding",
      contact_details: "Submit contact details",
      company_details: "Submit company details",
      project_details: "Submit project details",
      upload_docs: "Upload required documents",
      service_needs: "Capture service needs",
      terms: "Accept terms and conditions",
      submit: "Submit for approval",
      awaiting_approval: "Awaiting approval",
      awaiting_sales: "Awaiting sales review",
      awaiting_crm: "Awaiting CRM account creation",
      awaiting_auth: "Awaiting auth setup",
      set_password: "Set your password",
      activation_pending: "Onboarding approved - account activation pending",
      complete: "Onboarding complete",
      rejected: "Application rejected",
      cancelled: "Onboarding cancelled",
      contact_support: "Contact our support team"
    }
  },
  ar: {
    task: {
      more_info_required: "Provide more information for {activityName}",
      awaiting_customer: "Provide more information for {activityName}"
    }
  },
  kyb: {
    tile: {
      title: "Company Details"
    },
    task: {
      complete_details: "Complete your company details",
      modify_details: "Modify your company details"
    },
    action: {
      upload: "Upload KYB documents and submit for review",
      under_review: "KYB is under compliance review. Please wait for approval.",
      approved: "KYB has been approved. No action required.",
      rejected: "KYB was rejected. Please address feedback and resubmit.",
      none: "No action required."
    }
  },
  common: {
    helper: {
      under_review: "Your request is being reviewed by our team.",
      updates_required: "Your submission requires updates."
    }
  },
  event: {
    source: {
      system: "System",
      customer: "Customer",
      itsm: "ITSM Service",
      snow: "ServiceNow"
    }
  },
  bundle: {
    step: {
      scope_of_work: "Scope of Work",
      proposal: "Review Tasama Proposal",
      contract: "Sign Service Contract",
      purchase_order: "Submit Purchase Order",
      activation: "Service Activation",
      error: "Error"
    },
    state: {
      submitted: "Submitted",
      proposal_route_decided: "Processing Proposal",
      proposal_received: "Proposal Received",
      proposal_accepted: "Proposal Accepted",
      proposal_rejected: "Proposal Rejected",
      contract_route_decided: "Processing Contract",
      contract_received: "Contract Received",
      contract_signed: "Contract Signed",
      contract_under_review: "Contract Under Review",
      msa_addendum_in_progress: "MSA Addendum In Progress",
      msa_addendum_submitted: "MSA Addendum Submitted",
      msa_addendum_approved: "MSA Addendum Approved",
      msa_addendum_rejected: "MSA Addendum Rejected",
      contract_approved: "Contract Approved",
      contract_rejected: "Contract Rejected",
      po_route_decided: "Processing Purchase Order",
      po_required: "PO Required",
      po_submitted: "PO Submitted",
      po_approved: "PO Approved",
      po_rejected: "PO Rejected",
      pending_activation: "Pending Activation",
      split_complete: "Split Complete",
      error: "Error"
    }
  },
  sr: {
    state: {
      pending: "Pending",
      sow_submitted: "SOW Submitted",
      proposal_received: "Proposal Received",
      proposal_accepted: "Proposal Accepted",
      proposal_rejected: "Proposal Rejected",
      contract_received: "Contract Received",
      contract_signed: "Contract Signed",
      contract_under_review: "Contract Under Review",
      contract_approved: "Contract Approved",
      contract_rejected: "Contract Rejected",
      po_required: "PO Required",
      po_submitted: "PO Submitted",
      po_approved: "PO Approved",
      po_rejected: "PO Rejected",
      msa_addendum_in_progress: "MSA Addendum In Progress",
      msa_addendum_approved: "MSA Addendum Approved",
      msa_addendum_rejected: "MSA Addendum Rejected",
      nda_pending: "NDA Pending",
      pending_activation: "Pending Activation",
      active: "Active",
      completed: "Completed",
      rejected: "Rejected"
    },
    step: {
      scope_of_work: "Scope of Work",
      review_proposal: "Review Tasama Proposal",
      sign_contract: "Sign Service Contract",
      sign_addendum: "Sign Addendum",
      review_msa: "Review MSA",
      submit_po: "Submit Purchase Order",
      pending_activation: "Pending Activation",
      active: "Service Activation"
    },
    route: {
      service_contract: "Service Contract",
      addendum: "MSA Addendum",
      covered_in_msa: "Covered in MSA"
    },
    helper: {
      sow_submitted: "Your Scope of Work has been submitted. Tasama is reviewing it.",
      proposal_received: "Tasama has sent you a proposal. Please review and respond.",
      proposal_accepted: "Your proposal has been accepted. Please await your contract.",
      proposal_rejected: "Your proposal has been rejected. Please contact your advisor.",
      contract_received: "Your service contract is ready. Please review and sign.",
      contract_signed: "Your contract has been signed and submitted for review.",
      contract_under_review: "Your contract is currently under review.",
      contract_rejected: "Your contract has been rejected. Please contact your advisor.",
      msa_addendum_in_progress: "An MSA Addendum is being prepared.",
      msa_addendum_approved: "Your signed MSA addendum is being reviewed by Tasama.",
      msa_addendum_rejected: "Your MSA Addendum has been rejected. Please contact your advisor.",
      po_required: "A Purchase Order is required to proceed.",
      po_submitted: "Your Purchase Order has been submitted.",
      po_rejected: "Your Purchase Order has been rejected.",
      nda_pending: "Your Non-Disclosure Agreement is pending signature.",
      pending_activation: "Your service is pending activation.",
      pending_activation_sales_off: "We are activating your service. This usually takes a few days. For an update, contact your Key Account Manager or raise a support ticket."
    },
    pricing: {
      frequency: {
        monthly: "Monthly",
        one_time: "One time"
      }
    },
    cta: {
      upload: "Upload",
      review: "Review",
      submit: "Submit"
    }
  },
  tbl: {
    status: {
      not_started: "Not Started",
      in_progress: "In Progress",
      more_info_required: "More Info Required",
      completed: "Completed",
      on_hold: "On Hold",
      payment_required: "Payment Required",
      bypassed: "Bypassed",
      waiting_for_tasama: "Waiting for Tasama BO",
      pending: "Pending"
    },
    stage: {
      not_started: "Not Started",
      in_progress: "In Progress",
      completed: "Completed"
    },
    overall: {
      in_progress: "In Progress",
      completed: "Completed"
    },
    payment: {
      pending: "Pending",
      invoiced: "Invoiced",
      proof_submitted: "Proof Submitted",
      confirmed: "Confirmed"
    },
    milestone: {
      service_initiation: "Service Initiation",
      doc_legal: "Doc & Legal",
      company_formation: "Company Formation",
      gm_visa: "GM Visa / Govt Platform",
      company_activated: "Company Activated"
    },
    action: {
      kickoff_required: "Your kickoff information is required",
      courier_required: "Your courier details are required",
      general_manager_required: "Your General Manager information is required",
      travel_dates_required: "Your General Manager travel dates are required",
      payment_service_initiation: "Service Initiation payment",
      payment_doc_legal: "Doc & Legal payment",
      payment_company_formation: "Company Formation payment",
      payment_gm_visa: "GM Visa / Govt Platform payment",
      payment_company_activated: "Company Activated payment"
    },
    attestation: {
      apostille: "Apostille",
      embassy: "Embassy"
    },
    gm: {
      nationality: {
        saudi: "Saudi",
        non_saudi: "Non-Saudi"
      }
    },
    doc: {
      gm_passport: "GM Passport",
      ownership_structure: "Ownership Structure",
      ubo_passport: "UBO Passport",
      courier_soft_copy: "Courier Soft Copy",
      payment_proof: "Payment Proof",
      proforma_invoice: "Proforma Invoice",
      aoa_draft: "AoA Draft",
      aoa: "Final AoA",
      cr: "Final Commercial Registration",
      misa_license: "MISA License",
      iqama: "Iqama Document",
      final_doc_package: "Final Documents Package",
      gm_visa: "GM Visa Document",
      trade_name_cert: "Trade Name Certificate",
      bank_account_certification: "Bank Account Certification",
      tax_invoice: "Tax Invoice",
      receipt: "Official Receipt",
      commercial_registration: "Commercial Registration",
      articles_of_association: "Articles of Association",
      memorandum_of_association: "Memorandum of Association",
      financial_statement: "Financial Statement",
      power_of_attorney: "Power of Attorney",
      board_resolution: "Board Resolution",
      cert_incorporation: "Certificate of Incorporation",
      cert_good_standing: "Certificate of Good Standing",
      cert_incumbency: "Certificate of Incumbency",
      authorization_letter: "Authorization Letter",
      ubo_declaration: "Beneficial Ownership Declaration",
      shareholder_register: "Shareholder Register",
      bylaws: "Bylaws",
      apostille_cert: "Apostille Certificate",
      passport_copy: "Passport Copy",
      national_id: "National ID"
    },
    attachment: {
      gm_passport: "GM Passport",
      ownership_structure: "Ownership Structure",
      ubo_passport: "UBO Passport",
      commercial_registration: "Commercial Registration",
      aoa: "Articles of Association (AoA)",
      moa: "Memorandum of Association",
      financial_statement: "Financial Statement",
      poa: "Power of Attorney (PoA)",
      board_resolution: "Board Resolution",
      cert_incorporation: "Certificate of Incorporation",
      cert_good_standing: "Certificate of Good Standing",
      cert_incumbency: "Certificate of Incumbency",
      authorization_letter: "Authorization Letter",
      ubo_declaration: "Beneficial Ownership Declaration",
      shareholder_register: "Shareholder Register",
      bylaws: "Bylaws",
      apostille_cert: "Apostille Certificate",
      passport_copy: "Passport Copy",
      national_id: "National ID",
      proof_of_payment: "Proof of Payment",
      other: "Other"
    },
    task: {
      pending: "Request Tasama Business Landing service",
      action_required: "Action required for Tasama Business Landing"
    },
    helper: {
      in_progress: "Your Soft Landing service is being set up.",
      action_required: "An action is required from your side to proceed.",
      completed: "Your company has been successfully activated in Saudi Arabia."
    }
  },
  service: {
    status: {
      requested: "Requested",
      active: "Active",
      completed: "Completed",
      rejected: "Rejected"
    }
  },
  workflow: {
    type: {
      onboarding: "Client Onboarding",
      kyb: "Know Your Business",
      nda: "Non-Disclosure Agreement",
      msa: "Master Service Agreement",
      tbl: "Tasama Business Landing",
      service_activation: "Service Activation",
      service_request: "Service Request",
      hr_request: "HR Request",
      payroll: "Payroll",
      invoice: "Invoice",
      legal_compliance: "Legal Compliance"
    },
    status: {
      in_progress: "In Progress",
      completed: "Completed",
      failed: "Failed",
      cancelled: "Cancelled",
      expired: "Expired",
      rejected: "Rejected"
    }
  },
  notification: {
    title: {
      company_status: "Company Details Status Update",
      new_document: "New Document Available",
      nda_status: "NDA Status Update",
      msa_status: "MSA Status Update",
      kyb_approved: "Company Details Approved - KYB",
      kyb_rejected: "Company Details Update Rejected - KYB",
      kyb: {
        approved: "Company Details Approved - KYB",
        rejected: "Company Details Update Rejected - KYB",
        document_available: "New Document Available"
      },
      nda: {
        document_available: "New Document Available",
        rejected: "NDA Status Update"
      },
      msa: {
        draft_ready: "New Document Available",
        document_available: "New Document Available",
        rejected: "MSA Status Update"
      },
      ticket: {
        comment: "New Comment on Support Ticket",
        status_changed: "Support Ticket status has been changed"
      },
      team: {
        member_added: "New Team Member Added",
        member_updated: "Team Member Details Updated",
        member_activated: "Team Member Activated",
        member_deactivated: "Team Member Deactivated"
      },
      sr: {
        requested: "{serviceName}: Status changed to Requested",
        proposal_ready: "{serviceName}: The 'Review Tasama proposal' task has been assigned to you.",
        proposal_accepted: "{serviceName}: Review Tasama proposal is In Progress",
        proposal_rejected: "{serviceName}: Review Tasama proposal is Rejected",
        contract_ready: "{serviceName}: The 'Sign service contract' task has been assigned to you.",
        contract_signed: "{serviceName}: Sign service contract is In Progress",
        po_required: "{serviceName}: The 'Submit purchase order' task has been assigned to you.",
        po_submitted: "{serviceName}: Submit purchase order is In Progress",
        task_rejected: "{serviceName}: The ''{rejectedTask}'' task has been assigned to you.",
        active: "{serviceName}: Status changed to Active",
        new_document: "{serviceName}: {documentName} is now available",
        invoice: "{serviceName}: Invoice is now available"
      },
      tbl: {
        requested: "Tasama Business Landing: Status change to Requested",
        kickoff_assigned: "Tasama Business Landing: Submit kickoff data has been assigned to you",
        kickoff_rejected: "Tasama Business Landing: Resubmit kickoff data has been assigned to you",
        courier_assigned: "Tasama Business Landing: Submit courier details has been assigned to you",
        courier_rejected: "Tasama Business Landing: Resubmit courier details has been assigned to you",
        travel_assigned: "Tasama Business Landing: Submit travel dates has been assigned to you",
        gm_assigned: "Tasama Business Landing: Submit General Manager information has been assigned to you",
        payment_assigned: "Tasama Business Landing: Upload proof of payment has been assigned to you",
        payment_rejected: "Tasama Business Landing: Resubmit proof of payment has been assigned to you",
        new_document: "Tasama Business Landing: {documentName} is now available",
        payment_confirmed: "Tasama Business Landing: Payment verified for {milestoneCode}",
        step_updated: "Tasama Business Landing: {stepName} status changed to {status}",
        completed: "Tasama Business Landing: Your company has been activated"
      },
      ar: {
        created: "New Activity Request Submitted",
        group_assigned: "Activity Request Team Assigned",
        member_assigned: "Activity Request Member Assigned",
        in_progress: "Activity Request In Progress",
        more_info_required: "More Information Required",
        responded: "Customer Response Received",
        confirm_completion: "Please Confirm Completion",
        rejected: "Activity Request Rejected",
        completed: "Activity Request Completed",
        on_hold: "Activity Request On Hold",
        comment_added: {
          client: "New Comment from Customer",
          tasama: "New Update from Tasama"
        },
        grace_period_expiring: "Grace Period Expiring Soon",
        auto_closed: "Activity Request Auto Closed",
        sla_approaching: "SLA Deadline Approaching",
        sla_breached: "SLA Deadline Breached"
      }
    },
    msg: {
      kyb: {
        approved: "Company details: Your changes have been approved. The updates are now reflected in your account.",
        rejected: "Company details: Your changes have been rejected, please check your email notification and resubmit.",
        document_available: "KYB: Your company documents have been verified."
      },
      nda: {
        rejected: "NDA: Your NDA has been rejected, please check your email notification and resubmit.",
        document_available: "NDA: Non-disclosure agreement is available."
      },
      msa: {
        rejected: "MSA: Your MSA has been rejected, please check your email notification and resubmit.",
        draft_ready: "MSA: Draft master service agreement is ready for review.",
        document_available: "MSA: Master service agreement contract is available."
      },
      new_document: "{requestType}: {documentName}",
      sr: {
        requested: "Your service request has been received and is now in Requested status.",
        proposal_ready: "A new Tasama proposal is ready for your review.",
        proposal_submitted: "Your proposal review has been submitted and is now In Progress.",
        proposal_rejected: "Your proposal has been rejected.",
        contract_ready: "Your service contract is ready for review and signature.",
        contract_submitted: "Your signed contract has been submitted and is under review.",
        po_ready: "Your contract has been approved. Please submit your purchase order.",
        po_submitted: "Your purchase order has been submitted and is under review.",
        submission_rejected: "Your submission was rejected. Please review the feedback and resubmit.",
        active: "Congratulations! Your service is now active.",
        new_document: "A new document has been uploaded: {documentName}",
        invoice: "A new invoice is available. Please make payment."
      },
      tbl: {
        requested: "Your Tasama Business Landing service request has been received and is now in Requested status.",
        kickoff_assigned: "A new task for Tasama Business Landing service has been assigned to you. Please submit the kickoff data.",
        kickoff_rejected: "Your kickoff submission was rejected. Reason: {rejectionReason}. Please review and resubmit.",
        courier_assigned: "A new task for Tasama Business Landing service has been assigned to you. Please submit courier details.",
        courier_rejected: "Your courier submission was rejected. Reason: {rejectionReason}. Please review and resubmit.",
        travel_assigned: "A new task for Tasama Business Landing service has been assigned to you. Please submit GM travel dates.",
        gm_assigned: "A new task for Tasama Business Landing service has been assigned to you. Please submit the General Manager information.",
        payment_assigned: "Payment installment #{installmentNumber} is due. Please upload your proof of payment.",
        new_document: "A new document has been uploaded: {documentName}",
        payment_confirmed: "Your payment has been verified and confirmed.",
        payment_rejected: "Your proof of payment for installment #{installmentNumber} was rejected. Reason: {rejectionReason}. Please review and resubmit.",
        step_updated: "Step {stepName} has been updated to {status}.",
        completed: "Congratulations! Your Tasama Business Landing service is now complete. Your company has been activated."
      },
      ticket: {
        comment: "{ticketSubject}: {commentText}",
        status_changed: "{ticketNumber}: status changed to {updatedFields}"
      },
      team: {
        member_added: "A new team member ''{contactName}'' has been added to your account",
        member_updated: "Contact details for ''{contactName}'' have been updated ({updatedFields})",
        member_activated: "New member was added to your account or had their permission updated: {contactName}",
        member_deactivated: "{contactName} access has been deactivated from your team"
      },
      ar: {
        created: "Activity request {arNumber} has been submitted and is awaiting review.",
        group_assigned: "Activity request {arNumber} has been assigned to a team.",
        member_assigned: "Activity request {arNumber} has been assigned to {ownerName}.",
        in_progress: "Activity request {arNumber} is now in progress.",
        more_info_required: "Activity request {arNumber} requires additional information from you.",
        responded: "Customer has responded to activity request {arNumber}.",
        confirm_completion: "Please confirm completion of activity request {arNumber}.",
        rejected: "Activity request {arNumber} has been rejected. Reason: {reason}",
        completed: "Activity request {arNumber} has been successfully completed.",
        on_hold: "Activity request {arNumber} is on hold. Reason: {reason}",
        comment_added: {
          client: "New comment added to activity request {arNumber}.",
          tasama: "New update on activity request {arNumber} from Tasama."
        },
        grace_period_expiring: "Grace period for activity request {arNumber} is expiring soon.",
        auto_closed: "Activity request {arNumber} has been automatically closed.",
        sla_approaching: "SLA deadline approaching for activity request {arNumber}. {remainingDays} working days remaining.",
        sla_breached: "SLA deadline breached for activity request {arNumber}."
      }
    },
    doc: {
      kyb_verified: "Your company documents have been verified",
      nda_available: "Non-disclosure agreement is available.",
      msa_available: "Master service agreement contract is available.",
      msa_draft: "Draft master service agreement is ready for review"
    }
  },
  services: {
    sow: {
      title: "Service Request Form",
      description: "Provide a few details to help Tasama prepare your service proposal and initiate contracting.",
      stepLabel: "Scope of Work",
      reviewLabel: "Review proposal",
      defaultFileName: "Tasama proposal.pdf",
      uploadPOLabel: "Upload PO",
      validation: {
        serviceTierRequired: "Please select a service tier",
        timeframeRequired: "Please select an engagement time frame",
        tooLarge: "Maximum file size is 5 MB per document",
        invalidType: "Unsupported file type. Allowed types: PDF, PNG, JPG"
      },
      fields: {
        serviceName: "Service Name",
        serviceTier: "Service tier",
        serviceTierPlaceholder: "Select your service tier",
        timeframeLabel: "When are you looking to start your first service?",
        timeframePlaceholder: "Select engagement time frame",
        timeframeOptions: {
          thisMonth: "This month",
          nextMonth: "Next month",
          within3Months: "Within 3 months",
          within6Months: "Within 6 months",
          within12Months: "Within 12 months"
        },
        descriptionLabel: "Description of your requirements",
        descriptionPlaceholder: "Add any engagement expectations or a brief scope of work. Alternatively, upload a scope of work document.",
        fileSupportingText: "PDF, PNG, JPG only (max. 5 MB each)"
      }
    },
    activityRequest: {
      title: "Request activity",
      description: "Provide details about the activity or request you want to submit. Select the specific activity type and attach any required documents.",
      stepLabel: "Request activity",
      validation: {
        titleRequired: "Please enter an activity title",
        activityRequired: "Please select an activity",
        tierRequired: "Please select a service tier",
        fieldRequired: "This field is required",
        tooLarge: "Maximum file size is 5 MB per document",
        invalidType: "Unsupported file type. Allowed types: PDF, PNG, CSV, Excel, Word, JPG",
        tooManyFiles: "Maximum of 10 files allowed",
        invalidValue: "Invalid value"
      },
      requiredSummary: {
        title: "Required before you can submit",
        schemaError: "This activity form could not be loaded. Select the activity again to retry."
      },
      fields: {
        title: "Title",
        titlePlaceholder: "Enter activity title",
        activity: "Activity",
        activityPlaceholder: "Select an activity",
        tier: "Tier",
        tierPlaceholder: "Select your service tier",
        descriptionLabel: "Description:",
        slaLabel: "Delivery time:",
        slaBusinessDays: "{count} business days",
        requiredDocuments: "Required documents",
        requiredDocumentsList: "Required documents:",
        templateForms: "Template/forms:",
        templates: "Templates",
        supportingDocuments: "Supporting documents",
        fileSupportingText: "PDF, PNG, CSV, Excel, Word, JPG only (max. 5 MB each, up to 10 files)"
      },
      successTitle: "Thank you.",
      successDescription: "You will be notified when your activity is activated.",
      multiRow: {
        addButtonLabel: "Add {label}",
        modalTitle: "Add {label}",
        editModalTitle: "Edit {label}",
        emptyStateHeading: "No {label} added yet"
      },
      tierNames: {
        AdvancedAnalytics: "Advanced Analytics",
        CampaignDelivery: "Campaign Delivery",
        ComplexAdvisory: "Complex Advisory",
        EliteOnboarding: "Elite Onboarding",
        FastTrack: "Fast Track",
        FinanceOperations: "Finance Operations",
        HROperations: "HR Operations",
        LegalReview: "Legal Review",
        ProcurementDelivery: "Procurement Delivery",
        StandardDelivery: "Standard Delivery",
        StandardOnboarding: "Standard Onboarding"
      }
    },
    serviceContract: {
      sidebar: {
        title: "Service contract",
        description: "Your service contract outlines the scope, timelines, and commercial details for the service you have requested. Please review the document carefully before signing."
      },
      steps: {
        review: "Review Contract",
        sign: "Sign Contract"
      },
      reviewScreen: {
        title: "Review contract",
        description: "A tailored service contract is ready for your review.",
        readyForDownload: "Ready for download"
      }
    }
  },
  doc: {
    type: {
      sr: {
        po: "Purchase Order",
        signed_contract: "Signed Contract",
        contract: "Contract",
        proposal: "Proposal",
        sow: "Scope of Work",
        invoice: "Invoice"
      },
      bundle: {
        sow: "Scope of Work",
        proposal: "Proposal",
        contract: "Contract",
        signed_contract: "Signed Contract",
        po: "Purchase Order",
        msa_addendum_draft: "MSA Addendum (Draft)",
        msa_addendum_signed: "MSA Addendum (Signed)"
      }
    },
    category: {
      kyb: "KYB",
      nda: "NDA",
      msa: "MSA",
      service_request: "Service Request",
      other: "Other"
    }
  },
  context: {
    my_account: "My Account",
    msa: "Master Service Agreement",
    tbl: "Tasama Business Landing"
  },
  scopeOfWork: {
    finance_accounting: "Finance & Accounting Services",
    hr: "HR Services",
    incubation: "Incubation Services",
    innovation: "Innovation Management Services",
    marketing: "Marketing & Event Services",
    digital_tech: "Digital & Technology Services",
    corporate: "Corporate Property Services",
    soft_landing: "Business Landing Services",
    company_builder: "Company Builder Services",
    legal: "Legal Services",
    pmo_as_service: "PMO-as-a-Service",
    technology: "Technology Operations",
    strategic_consulting: "Strategic Consulting Services",
    risk_management: "Risk Management",
    compliance: "Compliance & Regulatory"
  },
  task: {
    status: {
      incomplete: "Incomplete"
    }
  },
  activityRequests: {
    requestButton: "Request Activity",
    emptyState: {
      title: "Your requests will be shown here.",
      subtitle: "No activity requests yet. Submit your first request to get started.",
      noResultsTitle: "No activities found",
      noResultsSubtitle: "Your search or filters did not match any activities. Please try again."
    },
    filters: {
      searchPlaceholder: "Search for activities, titles or request numbers",
      filterByStatus: "Filter by status",
      filterByActivity: "Filter by activity",
      sortBy: "Sort by",
      sortOptions: {
        number: "Request",
        title: "Title",
        activityName: "Activity",
        lastUpdatedAt: "Date",
        ownerName: "Owner",
        status: "Status"
      },
      statuses: {
        OPEN: "Requested",
        WORK_IN_PROGRESS: "In progress",
        AWAITING_CUSTOMER: "Awaiting customer",
        AWAITING_VENDOR: "Awaiting vendor",
        AWAITING_CANDIDATE: "Awaiting candidate",
        CLOSED_COMPLETE: "Completed",
        CLOSED_INCOMPLETE: "Closed incomplete"
      }
    },
    table: {
      columns: {
        request: "Request",
        title: "Title",
        activity: "Activity",
        date: "Date",
        owner: "Owner",
        status: "Status"
      },
      viewAction: "VIEW",
      unassigned: "Unassigned"
    },
    drawer: {
      expectedCompletion: "Delivery time:",
      workingDays: "{count} working days",
      overdue: "Overdue",
      requestLabel: "Request:",
      tierLabel: "Tier:",
      activityLabel: "Activity:",
      descriptionLabel: "Description:",
      documentRequirementsLabel: "Document Requirements:",
      descriptionPlaceholder: "Activity description will be available here.",
      responseLabel: "Response",
      responsePlaceholder: "Type your response here",
      respondButton: "RESPOND",
      activityInfoAriaLabel: "Activity information",
      expandPanel: "Expand panel",
      collapsePanel: "Collapse panel",
      closePanel: "Close panel",
      slaPaused: "paused",
      uploadFiles: "Upload files",
      uploadDropzoneText: "Click to upload or drag and drop",
      uploadDropzoneInfo: "PDF, PNG, CSV, Excel, Word or JPG only (Max 5mb per file. Max 10 files)",
      submitSuccess: "Success",
      submitError: "Failed to send.",
      noActivity: "No comments or documents yet.",
      noActivitySubtitle: "Send a message to begin the conversation.",
      loadingConversation: "Loading conversation\u2026",
      confirmButton: "Confirm",
      reopenButton: "Reopen Request",
      submitReopenButton: "Submit",
      completedNotice: "This activity has been completed.",
      rejectedNotice: "This activity has been rejected and closed.",
      moreDetails: "More details",
      activityDetailsTitle: "Activity details",
      noneValue: "None.",
      yes: "Yes",
      no: "No"
    }
  },
  superAdmin: {
    catalogSync: {
      title: "Catalog Sync + Publish",
      description: "Pull the latest service catalogue from ServiceNow and publish it to the portal. This replaces the whole catalogue and clears the caches, so the portal shows the new content immediately.",
      publishButton: "Publish now",
      publishingButton: "Publishing...",
      lastPublished: "Last published {when} by {who}",
      neverPublished: "The catalogue has not been published from the portal yet.",
      inProgress: "A publish is running. This can take a few minutes.",
      steps: {
        sync: "Sync from ServiceNow",
        serviceCatalogueCache: "Refresh service catalogue",
        cmsCache: "Refresh content"
      },
      state: {
        PENDING: "Waiting",
        RUNNING: "In progress",
        SUCCEEDED: "Done",
        FAILED: "Failed",
        UNCONFIRMED: "Still running"
      },
      succeeded: "Catalogue published successfully.",
      failed: "Publish failed: {reason}",
      conflict: "A publish is already running. Wait for it to finish before starting another.",
      forbidden: "You do not have permission to publish the catalogue.",
      unconfirmed: "The sync is taking longer than the gateway allows and is still running. Do not publish again - re-check in a few minutes."
    },
    selectCompany: {
      title: "Welcome Super Admin",
      label: "Choose company",
      searchPlaceholder: "Search companies",
      selectAction: "SELECT",
      close: "CLOSE",
      loading: "Loading companies...",
      switchLabel: "Super Admin",
      switchAction: "Change company"
    }
  }
};

// src/locales/en/onboarding.json
var onboarding_default = {
  navigation: {
    steps: {
      companyType: "Company Type",
      companyDetails: "Company Information",
      yourDetails: "Personal Information",
      projectDetails: "Project Details",
      serviceNeeds: "Service Requirements",
      uploadDocuments: "Document Upload",
      confirmation: "Confirmation"
    },
    stepProgress: "Step {current, number} of {total, number}"
  },
  companyType: {
    title: "Company Type",
    description: "Please select the type of organization you represent. This information helps us connect you with the right team.",
    companyStatus: {
      title: "Company Status",
      description: "This helps us guide you through the right onboarding steps."
    },
    errorBoundary: "Something went wrong with the Company Status screen. Please try refreshing the page.",
    options: {
      international: {
        label: "International company establishing operations in Saudi Arabia",
        description: "For companies headquartered outside Saudi Arabia"
      },
      ksaPrivate: {
        label: "An Established Private Sector Company",
        description: "For established Saudi-based private companies"
      },
      pifOwned: {
        label: "PIF-Owned Company",
        description: "For Public Investment Fund owned companies"
      },
      pifEstablished: {
        label: "Our company is fully established",
        description: "We already have a CR, VAT registration, or part of the required documentation."
      },
      pifUnderDevelopment: {
        label: "We are a PIF project under development",
        description: "We do not have a registered entity yet and require Tasama's support."
      }
    },
    beforeYouBegin: {
      title: "Before You Begin",
      description: "You will need the following information and documents to complete your onboarding.",
      items: {
        companyRegistrationCertificate: "Company registration certificate",
        companyOperationDetailsHomeCountry: "Basic company details, such as size and revenue in your home market",
        otherCountriesRegistration: "Details of any other countries where your company is registered",
        companyOperationDetails: "Basic operational details about your company, such as its size and revenue"
      }
    }
  },
  companyDetails: {
    gccCountries: {
      SA: "Saudi Arabia",
      AE: "United Arab Emirates",
      KW: "Kuwait",
      QA: "Qatar",
      BH: "Bahrain",
      OM: "Oman"
    },
    title: "Tell us about your company",
    subtitle: "We use this to personalise your experience.",
    description: "Please enter details of the organisation you are representing. These information will help us get the right team to assist you in your needs.",
    fields: {
      industrySector: {
        options: {
          agriculture: "Agriculture",
          consulting: "Consulting",
          construction: "Construction",
          education: "Education",
          energy: "Energy",
          entertainment: "Entertainment",
          finance: "Finance",
          healthcare: "Healthcare",
          hospitality: "Hospitality",
          manufacturing: "Manufacturing",
          realEstate: "Real Estate",
          retail: "Retail",
          technology: "Technology",
          telecommunications: "Telecommunications",
          transportation: "Transportation",
          other: "Other"
        }
      },
      countryOfHeadquarter: {
        label: "Country of Headquarters"
      },
      addressLine1: {
        labelInternational: "Registered Address of Headquarters"
      }
    }
  },
  yourDetails: {
    title: "Tell us about yourself",
    subtitle: "We need a few details to set up your account.",
    description: "Please enter the details of the main contact person at your organisation. These details will be used by Tasama representatives to get in touch with you.",
    fields: {
      firstName: {
        placeholder: "Enter your first name"
      },
      lastName: {
        placeholder: "Enter your last name"
      },
      salutation: {
        placeholder: "Select your prefix"
      },
      jobTitle: {
        placeholder: "Enter your job title"
      },
      email: {
        placeholder: "Enter your email"
      },
      mobile: {
        placeholder: "Enter your mobile phone"
      }
    }
  },
  projectDetails: {
    title: "Project details",
    subtitle: "Help us understand your project requirements.",
    description: "Please enter details of the project you are representing. This information will help us get the right team to assist you.",
    fields: {
      projectCodeName: {
        label: "Project code name",
        placeholder: "Enter your project name"
      },
      country: {
        label: "Country of Registration"
      },
      projectDetails: {
        label: "Project details",
        placeholder: "Write a brief description of your project"
      },
      expectedLaunchDate: {
        label: "Expected launch date"
      }
    }
  },
  serviceNeeds: {
    title: "What services do you need?",
    subtitle: "Select all that apply.",
    description: "Tell us what you need so we can bring the right information when we get in touch.",
    servicesLabel: "Select any services you are looking for assistance with:",
    establishmentTimeframe: {
      label: "When are you planning to establish in KSA?",
      placeholder: "Select engagement time frame"
    },
    goalsExpectations: {
      label: "What are your goals or expectations for KSA?",
      placeholder: "Add any engagement expectations"
    },
    engagementTimeframe: {
      label: "When are you looking to start using our services?",
      placeholder: "Select an engagement time frame"
    },
    budget: {
      label: "Is there already an allocated budget for this?",
      yes: "Yes",
      no: "No"
    },
    terms: {
      text: "I accept the",
      link: "terms and conditions."
    },
    services: {
      finance_accounting: "Finance & Accounting Services",
      hr: "HR Services",
      incubation: "Incubation Services",
      innovation: "Innovation Management Services",
      marketing: "Marketing & Event Services",
      digital_tech: "Digital & Technology Services",
      corporate: "Corporate Property Services",
      soft_landing: "Business Landing Services",
      company_builder: "Company Builder Services",
      legal: "Legal Services",
      pmo_as_service: "PMO-as-a-Service",
      technology: "Technology Operations"
    },
    engagementOptions: {
      "3_months": "Within 3 months",
      "6_12_months": "6 - 12 months",
      gt_12_months: "More than 12 months",
      not_sure: "Not sure"
    },
    establishmentOptions: {
      "3_months": "Within 3 months",
      "6_12_months": "6 - 12 months",
      gt_12_months: "More than 12 months",
      not_sure: "Not sure"
    }
  },
  uploadDocuments: {
    title: "Upload your documents",
    subtitle: "Please upload the required documents to proceed.",
    validation: {
      tooLarge: "Maximum file size is 5 MB per document",
      invalidType: "Unsupported file type. Allowed types: PDF, PNG, JPG"
    },
    dropzone: "Drag and drop files here, or click to browse",
    maxSize: "Maximum file size: {size}",
    allowedTypes: "Accepted formats: {types}",
    actionText: "Click to upload",
    dragHintText: "or drag and drop",
    fields: {
      registrationCertificate: {
        label: "Company registration certificate",
        supportingText: "PDF, PNG, JPG only (max. 5 MB)"
      }
    }
  },
  confirmation: {
    title: "You're all set!",
    subtitle: "Your application has been submitted. We'll be in touch soon.",
    referenceLabel: "Reference number",
    nextStepsTitle: "What happens next?",
    followUpMessage: "A member of our team will be in touch with you within 5 working days.",
    supportPrefix: "For immediate support, you can contact our team",
    supportLinkText: "here"
  },
  errors: {
    submitFailed: "Submission failed. Please review your details and try again.",
    uploadFailed: "File upload failed. Please try again.",
    crNumberAlreadyRegistered: "This CR number is already registered. Please contact support.",
    emailAlreadyRegistered: "Email address already registered",
    projectAlreadyExists: "Project already exists",
    stepNotFound: "Current step information not available",
    failedToLoadCountries: "Failed to load countries",
    failedToLoadGccCountries: "Failed to load GCC countries",
    invalidFileSelected: "Invalid file selected.",
    fileRequired: "A file is required",
    unknownStep: "Unknown step encountered",
    validationFailed: "Validation failed. Please check your input.",
    validationUnexpectedError: "An unexpected validation error occurred",
    ndaFileRequired: "NDA file is required",
    detailsFetchFailed: "Failed to fetch your onboarding details",
    portalAccountIdMissing: "Account not found. Please ensure you are authenticated.",
    invalidDocumentType: "Invalid document type selected",
    submissionFailed: "Failed to submit your onboarding application. Please try again.",
    stepProcessingFailed: "Failed to process this step. Please try again.",
    nda: {
      submissionPending: "NDA submission is already pending review",
      validationFailed: "NDA submission validation failed",
      submissionFailed: "NDA file was uploaded but could not be submitted"
    },
    conflictError: "A conflict occurred with an existing submission",
    validation: {
      required: "This field is required",
      companyName: {
        required: "Company name is required",
        max: "Company name must be at most 200 characters",
        charset: "Company name can include letters, numbers, spaces and common symbols (&, -, ' , ., (, ), /, ,)"
      },
      registrationNumber: {
        required: "Registration number is required",
        max: "Registration number must be at most 100 characters",
        charset: "Registration number can include letters, numbers and hyphens only",
        saudiFormat: "Registration number must be exactly 10 digits",
        internationalFormat: "Registration number can include letters, numbers, hyphens, and spaces (max 50 characters)"
      },
      registrationDate: {
        required: "Registration date is required",
        format: "Please enter a valid date in DD/MM/YYYY format",
        future: "Registration date cannot be in the future"
      },
      website: {
        invalid: "Enter a valid website URL (e.g., https://example.com)"
      },
      country: {
        required: "Country of registration is required"
      },
      countryOfHeadquarter: {
        required: "Country of headquarters is required"
      },
      city: {
        charset: "City can only include letters, spaces and hyphens",
        required: "City is required"
      },
      address: {
        required: "Registered company address is required"
      },
      postcode: {
        charset: "Postcode can include letters, numbers, spaces and hyphens (3-12 chars)"
      },
      industrySector: {
        required: "Industry sector is required"
      },
      numberOfEmployees: {
        required: "Number of employees is required"
      },
      annualRevenue: {
        required: "Annual revenue is required"
      },
      totalInvestmentRaised: {
        required: "Total investment raised is required"
      },
      selectedServices: {
        required: "Please select at least one service you are looking for assistance in"
      },
      engagementTimeframe: {
        required: "Please select when you are looking to start utilising our services"
      },
      budgetAllocated: {
        required: "Please indicate if there is already an allocated budget"
      },
      acceptTerms: {
        required: "You must accept the terms and conditions to proceed"
      },
      establishmentTimeframe: {
        required: "Please select when you are planning to establish in KSA"
      },
      firstName: {
        required: "First name is required",
        min: "First name must be between 2 and 100 characters",
        max: "First name must be between 2 and 100 characters",
        charset: "First name can only include letters, spaces and hyphens"
      },
      lastName: {
        required: "Last name is required",
        min: "Last name must be between 2 and 100 characters",
        max: "Last name must be between 2 and 100 characters",
        charset: "Last name can only include letters, spaces and hyphens"
      },
      salutation: {
        required: "Prefix is required"
      },
      jobTitle: {
        required: "Job title is required",
        max: "Job title must be at most 255 characters"
      },
      email: {
        required: "Email is required",
        invalid: "Please enter a valid email address",
        business: "Only organisation email addresses are allowed",
        pif: "Please enter a valid PIF email address"
      },
      mobile: {
        required: "Mobile phone is required",
        invalid: "Please enter a valid mobile phone number for the selected country"
      },
      projectCodeName: {
        required: "Project name is required",
        min: "Project name must be at least 3 characters",
        max: "Project name must not exceed 100 characters"
      },
      projectDetails: {
        max: "Project description must not exceed 1000 characters"
      },
      expectedLaunchDate: {
        format: "Please enter a valid date in DD/MM/YYYY format",
        future: "Please enter a valid future date in DD/MM/YYYY format",
        mustBeFuture: "Expected launch date must be in the future"
      }
    }
  },
  termsAndConditions: {
    title: "Terms and Conditions",
    lastUpdated: "Last updated: {date}",
    sections: {
      acceptanceOfTerms: {
        title: "1. Acceptance of Terms",
        content: "By accessing or using this website and registering for services, you agree to comply with and be bound by these Terms and Conditions. If you do not agree to these Terms, you must refrain from using the website.",
        summary: "By using the site, you agree to our terms"
      },
      websiteUse: {
        title: "2. Website Use",
        content: "This website is intended to provide information about the services offered by TASAMA and to enable users to register and request services.\n\nUsers agree to use the website only for lawful purposes and in a manner that does not violate any applicable laws or regulations or infringe upon the rights of others.",
        guidelines: {
          permitted: "Allowed actions",
          prohibited: "Prohibited actions"
        }
      },
      userRegistration: {
        title: "3. User Registration",
        content: "To access certain services, users may be required to create an account and provide accurate and complete information.\n\nBy registering on the website, the user agrees that:",
        items: [
          "The information provided is accurate and up to date.",
          "The user is responsible for maintaining the confidentiality of login credentials.",
          "The user is responsible for all activities carried out under their account."
        ],
        additionalContent: "The Company reserves the right to suspend or terminate any account that provides inaccurate information or violates these Terms."
      },
      confidentiality: {
        title: "4. Confidentiality",
        content: "Any information shared by users through the website may be treated as confidential and used solely for the purpose of evaluating, providing, or managing the requested services.\n\nThe Company shall take reasonable measures to protect such information from unauthorized access, disclosure, or misuse, except where disclosure is required by law or regulatory authorities.",
        dataProtection: "Data protection commitment"
      },
      cookiesAndWebsiteData: {
        title: "5. Cookies and Website Data",
        content: "The website may use cookies and similar technologies to:",
        items: [
          "Improve website functionality and user experience",
          "Analyze website traffic and usage",
          "Maintain user sessions and preferences"
        ],
        additionalContent: "By continuing to use the website, users consent to the use of cookies in accordance with the TASAMA's cookie practices. Users may disable cookies through their browser settings; however, certain website features may not function properly as a result.",
        cookieSettings: "Cookie settings"
      },
      intellectualProperty: {
        title: "6. Intellectual Property",
        content: "All content on this website, including text, graphics, logos, and materials, is the property of TASAMA or its licensors and is protected by applicable intellectual property laws.\n\nUsers may not reproduce, distribute, modify, or use any website content without prior written consent from TASAMA.",
        rights: "All rights reserved"
      },
      limitationOfLiability: {
        title: "7. Limitation of Liability",
        content: 'TASAMA makes reasonable efforts to ensure that the information on this website is accurate and up to date. However, the website and its content are provided on an "as-is" basis without warranties of any kind.\n\nTo the fullest extent permitted by law, TASAMA shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of the website.',
        disclaimer: "Disclaimer"
      },
      thirdPartyLinks: {
        title: "8. Third-Party Links",
        content: "The website may contain links to third-party websites for informational purposes. TASAMA does not control or assume responsibility for the content, policies, or practices of such third-party websites.",
        warning: "External links disclaimer"
      },
      governingLaw: {
        title: "9. Governing Law",
        content: "These Terms and Conditions shall be governed by and construed in accordance with the laws of the Kingdom of Saudi Arabia. Any disputes arising from or related to the use of the website shall be subject to the jurisdiction of the competent courts in the Kingdom of Saudi Arabia.",
        jurisdiction: "Jurisdiction"
      },
      amendments: {
        title: "10. Amendments",
        content: "TASAMA reserves the right to modify or update these Terms and Conditions at any time. Updated versions will be posted on the website and will become effective upon publication.",
        notification: "Change notification"
      }
    },
    actions: {
      accept: "Accept Terms and Conditions",
      decline: "Decline",
      print: "Print Terms",
      download: "Download PDF"
    },
    warnings: {
      required: "You must accept the Terms and Conditions to proceed",
      ageRequirement: "You must be at least 18 years old to use this service"
    }
  },
  stepper: {
    journey: {
      title: "Start Your Journey",
      subtitle: "With Us Today",
      description: "Unlock your business potential in Saudi Arabia by answering a few simple questions.",
      currentStep: "Current Step",
      viewAllSteps: "View all steps",
      closeSteps: "Close steps"
    },
    login: {
      welcome: "Welcome to",
      brand: "Tasama Business Center",
      features: [
        "Fast-track your business activation in Saudi Arabia",
        "Access a wealth of business services",
        "Track and manage all your requests in one place"
      ],
      cta: {
        question: "Are you a new customer?",
        button: "Register Now"
      }
    },
    nda: {
      title: "Non-Disclosure",
      subtitle: "Agreement",
      description: "To ensure confidentiality of sensitive business intelligence and documents between your business and Tasama, a NDA is a must before we can proceed."
    }
  },
  appInfo: {
    backToOnboarding: "Back to Onboarding"
  }
};

// src/locales/en/business-landing.json
var business_landing_default = {
  kickoffDocumentationLayout: {
    sidebar: {
      title: "Kickoff Documentation",
      description: "Complete the required information for kickoff documentation"
    },
    steps: {
      generalManagerInformation: "General manager information",
      companyDetails: "Company details",
      companySetup: "Company set-up",
      businessActivities: "Business activities"
    }
  },
  companyDetails: {
    validation: {
      companyType: { required: "Company type is required" },
      sharedCapital: {
        required: "Shared capital is required",
        invalid: "Please enter a valid amount"
      },
      currency: { required: "Currency is required" },
      ownershipDocument: {
        required: "Please upload an ownership structure document",
        empty: "Company ownership structure document is required"
      },
      beneficialOwner: {
        firstName: {
          required: "Ultimate beneficial owner first name is required",
          max: "First name must be at most 255 characters",
          charset: "First name can only include letters, spaces and hyphens"
        },
        lastName: {
          required: "Ultimate beneficial owner last name is required",
          max: "Last name must be at most 255 characters",
          charset: "Last name can only include letters, spaces and hyphens"
        },
        passportCopy: {
          required: "Please upload a passport copy",
          empty: "Ultimate beneficial owner passport copy is required"
        }
      }
    },
    header: {
      title: "Company details",
      description: "Provide company ownership and legal structure details required for regulatory verification and company registration processing."
    },
    companyType: {
      label: "Company type",
      placeholder: "Select company type",
      options: {
        llc: "Limited Liability Company (LLC)",
        jsc: "Joint Stock Company (JSC)",
        sjsc: "Simplified Joint Stock Company (SJSC)"
      }
    },
    sharedCapital: {
      label: "Shared Capital in CR",
      currencyPlaceholder: "Currency",
      amountPlaceholder: "Enter amount",
      currencyOptions: {
        SAR: "SAR"
      }
    },
    ownershipDocument: {
      label: "Company ownership structure document (must include ownership percentages)",
      supportingText: "PDF, PNG, JPG, or JPEG only (max. 5mb)",
      errors: {
        invalidType: "Only PDF, PNG, JPG, or JPEG files are allowed",
        invalidSize: "File size must be less than 5MB"
      }
    },
    beneficialOwner: {
      sectionDescription: "Enter the details of the individual who will be the ultimate beneficial owner of the company in Saudi Arabia. This is required for regulatory and banking compliance.",
      firstName: {
        label: "Ultimate beneficial owner first name",
        placeholder: "Enter Ultimate beneficial owner first name"
      },
      lastName: {
        label: "Ultimate beneficial owner last name",
        placeholder: "Enter Ultimate beneficial owner last name"
      },
      passportCopy: {
        label: "Ultimate beneficial owner passport copy",
        supportingText: "PDF, PNG, JPG, or JPEG only (max. 5mb)",
        errors: {
          invalidType: "Only PDF, PNG, JPG, or JPEG files are allowed",
          invalidSize: "File size must be less than 5MB"
        }
      }
    }
  },
  generalManagerInformation: {
    validation: {
      firstName: {
        required: "First name is required",
        min: "First name must be at least 2 characters",
        max: "First name must be at most 100 characters",
        charset: "First name can only include letters, spaces and hyphens"
      },
      lastName: {
        required: "Last name is required",
        min: "Last name must be at least 2 characters",
        max: "Last name must be at most 100 characters",
        charset: "Last name can only include letters, spaces and hyphens"
      },
      email: {
        required: "Email is required",
        invalid: "Please enter a valid email address"
      },
      country: { required: "Country code is required" },
      mobile: {
        required: "Mobile phone is required",
        invalid: "Please enter a valid mobile phone number for the selected country"
      },
      homeAddress: {
        required: "Home address is required",
        max: "Home address must be at most 500 characters"
      },
      nationality: {
        required: "General manager nationality is required",
        max: "Nationality must be at most 100 characters"
      },
      passportCopy: {
        required: "General manager passport copy is required",
        size: "File size must be less than 5MB",
        type: "Only PDF, PNG, JPG, or JPEG files are allowed"
      }
    },
    header: {
      title: "General manager information",
      description: "These details help us verify the general manager's identity and support company registration, banking setup, and regulatory processing."
    },
    firstName: {
      label: "First name",
      placeholder: "Enter general manager first name"
    },
    lastName: {
      label: "Last name",
      placeholder: "Enter general manager last name"
    },
    email: {
      label: "Email",
      placeholder: "Enter general manager email"
    },
    mobile: {
      label: "Mobile phone",
      placeholder: "Enter mobile phone number"
    },
    homeAddress: {
      label: "Home address",
      placeholder: "Enter general manager home address"
    },
    nationality: {
      label: "General manager nationality as shown on the passport",
      placeholder: "Search and select nationality"
    },
    passportCopy: {
      label: "General manager passport copy",
      supportingText: "PDF, PNG, JPG, or JPEG only (max. 5mb)",
      errors: {
        invalidType: "Only PDF, PNG, JPG, or JPEG files are allowed",
        invalidSize: "File size must be less than 5MB"
      }
    }
  },
  businessActivitiesTable: {
    columns: {
      activities: "Activities",
      licenseType: "License type"
    },
    actions: {
      addActivity: "Add {activity}",
      removeActivity: "Remove {activity}"
    }
  },
  businessActivities: {
    validation: {
      required: "At least one business activity must be selected"
    },
    milestoneStepperDescription: "There are up to five major stages for Tasama Business Landing in KSA, with multiple processes under each stage.",
    description: "Please list the business activities you want to be listed under your company certificate registration for Saudi Arabia.",
    selectedActivities: "Selected activities",
    searchPlaceholder: "Search activities",
    licenseTypePlaceholder: "License type",
    activitiesCount: "{count} activities",
    noActivitiesFound: "No activities found",
    tryAdjustingFilters: "Try adjusting your search or filters.",
    noActivitiesAvailable: "No activities are available.",
    clearFilters: "Clear search and filters"
  },
  travel: {
    actions: {
      addGmDetails: "ADD GM DETAILS",
      editGmDetails: "EDIT GM DETAILS"
    }
  },
  tasamaActivities: {
    onHoldReason: "Reason: {reason}",
    tradeName: {
      approvedPrefix: "Approved trade name based on your preferences and availability:"
    },
    downloadActions: {
      misaLicense: {
        title: "MISA license issuance",
        description: "Download the MISA license issuance"
      },
      articleOfAssociation: {
        title: "Article of Association",
        description: "Download the Article of Association"
      },
      commerceRegistration: {
        title: "Commerce Registration",
        description: "Download the Commerce Registration"
      },
      generalManagerVisa: {
        title: "General Manager Visa",
        description: "Download the General Manager Visa"
      },
      commercialRegistrationUpdate: {
        title: "Commercial Registration Update",
        description: "Download the Commercial Registration Update"
      },
      bankAccountCertification: {
        title: "Bank Account Certification",
        description: "Download the Bank Account Certification"
      }
    }
  },
  projectKickoff: {
    actions: {
      addKickoffInformation: "Add Kickoff Information",
      editKickoffInformation: "Edit Kickoff Information",
      gmInfoCompleteNow: "Complete Now",
      gmInfoUpdateNow: "Update Now"
    },
    rejectionModal: {
      title: "More information required"
    }
  },
  skipGmModal: {
    title: "Skip General Manager information?",
    body: "You can provide the General Manager information later. This information will be required before TASAMA can proceed with company formation.",
    cancel: "Cancel",
    confirm: "Confirm"
  },
  docsAttestation: {
    requiredDocuments: {
      label: "Please prepare the following documents:"
    },
    actions: {
      addSubmissionDetails: "ADD SUBMISSION DETAILS",
      editSubmissionDetails: "EDIT SUBMISSION DETAILS",
      download: "DOWNLOAD"
    },
    rejectionModal: {
      title: "More information required"
    },
    powerOfAttorney: {
      title: "Power of Attorney template",
      description: "Download and sign the Power of Attorney template"
    },
    finalDocuments: {
      title: "Final documents package",
      description: "Download the Final documents package"
    }
  },
  proofOfPayment: {
    rejectionModal: {
      title: "More information required"
    },
    sidebar: {
      title: "Proof of payment",
      description: "Please download your invoice and, once payment is completed, upload proof of payment to confirm settlement and allow the service to proceed."
    },
    steps: {
      downloadInvoice: "Download Invoice",
      uploadProofOfPayment: "Upload Proof of Payment"
    },
    uploadProofOfPayment: {
      header: {
        title: "Upload Proof of Payment",
        description: "Please upload proof of payment here."
      },
      supportingText: "PDF, PNG, JPG, or JPEG only (max. 50MB)",
      errors: {
        submitFailed: "Upload failed. Please try again."
      }
    },
    header: {
      title: "Download invoice",
      description: "An invoice for the service is ready for your review."
    },
    invoice: {
      fileName: "invoice.pdf",
      description: "Service invoice"
    },
    result: {
      title: "Thank you.",
      description: "You will be notified when a tax invoice and receipt is available."
    }
  },
  documentAttestation: {
    layout: {
      sidebar: {
        title: "Document attestation / apostille requirements"
      },
      steps: {
        submissionDetails: "Add submission details"
      }
    },
    header: {
      title: "Add submission details",
      description: "Providing courier tracking details will ensure Tasama receives timely notification of your important documents."
    },
    validation: {
      courierCompany: {
        required: "Courier company is required",
        max: "Courier company name must be at most 200 characters",
        charset: "Courier company name can include letters, numbers, spaces and common symbols (&, -, ' , ., (, ), /, ,)"
      },
      trackingNumber: {
        required: "Tracking number is required",
        max: "Tracking number must be at most 100 characters",
        charset: "Tracking number can include letters, numbers, hyphens and spaces only"
      }
    },
    courierCompany: {
      label: "Courier company",
      placeholder: "Enter name of courier company"
    },
    trackingNumber: {
      label: "Tracking number",
      placeholder: "Enter tracking number"
    },
    documentUpload: {
      supportingText: "PDF, PNG, JPG, or JPEG only (max. 5MB)"
    }
  },
  companyActivated: {
    welcomeMessage: "Welcome to the Kingdom of Saudi Arabia.",
    activationMessage: "Your company has now been officially activated and is ready to operate.",
    browseServices: "Browse Services",
    title: "Company Activated",
    inProgressTitle: "Company activation in progress",
    inProgressDescription: "Your company activation is currently in progress.\n\nTo avoid delays, make sure all payments are completed and review the Invoices tab for any pending invoices. We\u2019ll notify you once it\u2019s done."
  },
  invoices: {
    emptyHeading: "No invoices yet",
    emptyText: "Invoices will appear here once they are generated.",
    columnInvoiceNumber: "Invoice number",
    columnAmount: "Amount",
    columnDate: "Date",
    columnStatus: "Status",
    payButton: "PAY",
    failedToLoad: "Failed to load invoices. Please try again."
  },
  tbl: {
    status: {
      not_started: "Not Started",
      in_progress: "In Progress",
      completed: "Completed",
      on_hold: "On Hold",
      payment_required: "Payment Required",
      bypassed: "Bypassed",
      waiting_for_tasama: "Waiting for Tasama BO",
      more_info_required: "More Info Required"
    },
    stage: {
      not_started: "Not Started",
      in_progress: "In Progress",
      completed: "Completed"
    },
    overall: {
      in_progress: "In Progress",
      completed: "Completed"
    },
    payment: {
      pending: "Pending",
      invoiced: "Invoiced",
      proof_submitted: "Proof Submitted",
      confirmed: "Confirmed",
      display: {
        paid: "Paid",
        not_paid: "Not paid",
        delayed: "Delayed"
      }
    },
    milestone: {
      service_initiation: "Service Initiation",
      doc_legal: "Doc & Legal",
      company_formation: "Company Formation",
      gm_visa: "GM Visa / Govt Platform",
      company_activated: "Company Activated"
    },
    action: {
      kickoff_required: "Your kickoff information is required",
      courier_required: "Your courier details are required",
      general_manager_required: "Your General Manager information is required",
      travel_dates_required: "Your General Manager travel dates are required",
      payment_service_initiation: "Service Initiation payment",
      payment_doc_legal: "Doc & Legal payment",
      payment_company_formation: "Company Formation payment",
      payment_gm_visa: "GM Visa / Govt Platform payment",
      payment_company_activated: "Company Activated payment"
    },
    task: {
      pending: "Pending",
      action_required: "Action Required"
    },
    helper: {
      in_progress: "In Progress",
      action_required: "Action Required",
      completed: "Completed"
    },
    attestation: {
      apostille: "Apostille",
      embassy: "Embassy"
    },
    gm: {
      nationality: {
        saudi: "Saudi",
        non_saudi: "Non-Saudi"
      }
    },
    doc: {
      gm_passport: "GM Passport",
      ownership_structure: "Ownership Structure",
      ubo_passport: "UBO Passport",
      courier_soft_copy: "Courier Soft Copy",
      payment_proof: "Payment Proof",
      proforma_invoice: "Proforma Invoice",
      aoa_draft: "AoA Draft",
      aoa: "AoA document",
      cr: "Commercial Registration",
      misa_license: "MISA License",
      iqama: "Iqama document",
      final_doc_package: "Final Documents package",
      gm_visa: "GM Visa document",
      trade_name_cert: "Trade Name Certificate",
      bank_account_certification: "Bank Account Certification",
      tax_invoice: "Tax Invoice",
      receipt: "Official Receipt"
    },
    attachment: {
      gm_passport: "GM Passport",
      ownership_structure: "Ownership Structure",
      ubo_passport: "UBO Passport",
      commercial_registration: "Commercial Registration",
      aoa: "Articles of Association (AoA)",
      moa: "Memorandum of Association",
      financial_statement: "Financial Statement",
      poa: "Power of Attorney (PoA)",
      board_resolution: "Board Resolution",
      cert_incorporation: "Certificate of Incorporation",
      cert_good_standing: "Certificate of Good Standing",
      cert_incumbency: "Certificate of Incumbency",
      authorization_letter: "Authorization Letter",
      ubo_declaration: "Beneficial Ownership Declaration",
      shareholder_register: "Shareholder Register",
      bylaws: "Bylaws",
      apostille_cert: "Apostille Certificate",
      passport_copy: "Passport Copy",
      national_id: "National ID",
      proof_of_payment: "Proof of Payment",
      other: "Other"
    }
  },
  errors: {
    kickoff: {
      passportFileRequired: "Passport file is required",
      ownershipStructureFileRequired: "Ownership structure file is required",
      invalidStepId: "Invalid step selected",
      unknownLicenseId: "Unknown license type selected",
      validationFailed: "Validation failed. Please check your input.",
      accessDenied: "Access denied",
      tblServiceNotFound: "TBL service not found",
      submissionFailed: "Kickoff submission failed. Please try again.",
      useFileEndpoint: "File upload is required for this step"
    },
    gmTravel: {
      minDateRange: "Travel return date must be at least 14 days after the departure date",
      datesLocked: "Travel dates are locked as the Iqama step has already been completed"
    },
    invoices: {
      fileRequired: "A file is required",
      invalidProofOfPayment: "Invalid proof of payment submission",
      submitFailed: "Failed to submit proof of payment. Please try again."
    },
    documentsAttestation: {
      fileRequired: "A file is required",
      softCopyRequired: "At least one soft copy file is required",
      documentTypeCountMismatch: "Number of document types must match the number of uploaded files",
      invalidCourierDetails: "Invalid courier details provided",
      submitFailed: "Failed to submit document attestation. Please try again."
    }
  },
  companySetup: {
    validation: {
      tradeName: {
        required: "Preferred registration trade name #{number} is required",
        max: "Trade name must be at most 255 characters",
        duplicate: "This name has already been entered. Please provide a unique trade name."
      },
      preferredBank: { required: "Preferred bank is required" },
      preferredBankOther: {
        required: 'Preferred bank name is required when "Other" is selected',
        max: "Bank name must be at most 255 characters"
      }
    },
    header: {
      title: "Company set-up",
      description: "Provide your preferred trade name options and banking preference to support company registration and operational setup."
    },
    tradeName: {
      label: "Preferred registration trade name #{number}",
      placeholder: "Enter preferred name"
    },
    preferredBank: {
      label: "Preferred bank",
      placeholder: "Select bank"
    },
    preferredBankOther: {
      label: "If other",
      placeholder: "Enter preferred bank"
    },
    bankOptions: {
      "jp-morgan": "JPMorgan Saudi Arabia",
      sab: "Saudi Awwal Bank (SAB)",
      snb: "Saudi National Bank (SNB)",
      enbd: "Emirates NBD Bank (ENBD)",
      other: "Other"
    }
  }
};

// src/locales/ar/common.json
var common_default2 = {
  actions: {
    save: "\u062D\u0641\u0638",
    cancel: "\u0625\u0644\u063A\u0627\u0621",
    confirm: "\u062A\u0623\u0643\u064A\u062F",
    delete: "\u062D\u0630\u0641",
    edit: "\u062A\u0639\u062F\u064A\u0644",
    back: "\u0631\u062C\u0648\u0639",
    next: "\u0627\u0644\u062A\u0627\u0644\u064A",
    previous: "\u0627\u0644\u0633\u0627\u0628\u0642",
    submit: "\u0625\u0631\u0633\u0627\u0644",
    done: "\u0625\u0646\u0647\u0627\u0621",
    skipForNow: "\u062A\u062E\u0637\u064A \u0641\u064A \u0627\u0644\u0648\u0642\u062A \u0627\u0644\u062D\u0627\u0644\u064A",
    close: "\u0625\u063A\u0644\u0627\u0642",
    search: "\u0628\u062D\u062B",
    clear: "\u0645\u0633\u062D",
    apply: "\u062A\u0637\u0628\u064A\u0642",
    upload: "\u0631\u0641\u0639",
    download: "\u062A\u0646\u0632\u064A\u0644",
    review: "\u0645\u0631\u0627\u062C\u0639\u0629",
    view: "\u0639\u0631\u0636",
    viewTicket: "\u0639\u0631\u0636 \u0627\u0644\u062A\u0630\u0643\u0631\u0629",
    viewTeamMembers: "\u0639\u0631\u0636 \u0623\u0639\u0636\u0627\u0621 \u0627\u0644\u0641\u0631\u064A\u0642",
    viewDetails: "\u0639\u0631\u0636 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644",
    add: "\u0625\u0636\u0627\u0641\u0629",
    viewAll: "\u0639\u0631\u0636 \u0627\u0644\u0643\u0644",
    tryAgain: "\u062D\u0627\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649",
    login: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644",
    backToWebsite: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0625\u0644\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    optional: "(\u0627\u062E\u062A\u064A\u0627\u0631\u064A)",
    closeAlert: "\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u062A\u0646\u0628\u064A\u0647"
  },
  labels: {
    required: "\u0645\u0637\u0644\u0648\u0628",
    optional: "\u0627\u062E\u062A\u064A\u0627\u0631\u064A",
    loading: "\u062C\u0627\u0631\u064D \u0627\u0644\u062A\u062D\u0645\u064A\u0644...",
    noResults: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0646\u062A\u0627\u0626\u062C",
    all: "\u0627\u0644\u062C\u0645\u064A\u0639",
    none: "\u0644\u0627 \u0634\u064A\u0621",
    yes: "\u0646\u0639\u0645",
    no: "\u0644\u0627",
    searchAndSelect: "\u0627\u0628\u062D\u062B \u0648\u0627\u062E\u062A\u0631 \u062E\u064A\u0627\u0631\u0627\u064B",
    notAvailable: "\u063A\u064A\u0631 \u063A\u064A\u0631 \u0645\u062A\u0627\u062D"
  },
  dateInfo: {
    startedOn: "\u0628\u062F\u0623\u062A \u0641\u064A:",
    completedOn: "\u0627\u0643\u062A\u0645\u0644 \u0641\u064A:",
    expectedBy: "\u0645\u062A\u0648\u0642\u0639 \u0628\u062D\u0644\u0648\u0644:",
    estimatedSLA: "\u0645\u062F\u0629 \u0627\u0644\u062A\u0646\u0641\u064A\u0630 \u0627\u0644\u0645\u062A\u0648\u0642\u0639\u0629:",
    oneBusinessDay: "\u064A\u0648\u0645 \u0639\u0645\u0644 \u0648\u0627\u062D\u062F",
    twoBusinessDays: "\u064A\u0648\u0645\u0627 \u0639\u0645\u0644",
    nBusinessDays: "{estimatedSLA} \u064A\u0648\u0645 \u0639\u0645\u0644"
  },
  multiSelect: {
    selectOptions: "\u0627\u062E\u062A\u0631 \u062E\u064A\u0627\u0631\u0627\u062A",
    selectedCount: "{count} \u0645\u062D\u062F\u062F",
    clearAllAriaLabel: "\u0645\u0633\u062D \u062C\u0645\u064A\u0639 \u0627\u0644\u062A\u062D\u062F\u064A\u062F\u0627\u062A",
    selectionInfo: "{current} \u0645\u0646 {max} \u0645\u062D\u062F\u062F"
  },
  dropdown: {
    selectOption: "\u0627\u062E\u062A\u0631 \u062E\u064A\u0627\u0631\u0627\u064B"
  },
  mobileInput: {
    placeholder: "\u0623\u062F\u062E\u0644 \u0631\u0642\u0645 \u0627\u0644\u0647\u0627\u062A\u0641"
  },
  companyLogo: {
    alt: "\u0634\u0639\u0627\u0631 \u0627\u0644\u0634\u0631\u0643\u0629"
  },
  header: {
    companyImageAlt: "\u0634\u0639\u0627\u0631 \u0627\u0644\u0634\u0631\u0643\u0629",
    welcomeText: "\u0623\u0647\u0644\u0627\u064B\u060C"
  },
  fileUpload: {
    clickToUpload: "\u0627\u0646\u0642\u0631 \u0644\u0644\u0631\u0641\u0639",
    dragAndDrop: "\u0623\u0648 \u0627\u0633\u062D\u0628 \u0648\u0623\u0641\u0644\u062A",
    tapToUpload: "\u0627\u0646\u0642\u0631 \u0644\u0644\u0631\u0641\u0639",
    supportedFormats: "\u0628\u0635\u064A\u063A\u0629 PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u062C\u0627\u0628\u0627\u064A\u062A)",
    singleFileOnly: "\u064A\u0645\u0643\u0646 \u0631\u0641\u0639 \u0645\u0644\u0641 \u0648\u0627\u062D\u062F \u0641\u0642\u0637 \u0641\u064A \u0643\u0644 \u0645\u0631\u0629."
  },
  errors: {
    sessionExpiredTitle: "\u0627\u0646\u062A\u0647\u062A \u0627\u0644\u062C\u0644\u0633\u0629",
    errorTitle: "\u062E\u0637\u0623",
    generic: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    somethingWentWrong: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627.",
    somethingWentWrongDescription: "\u062A\u0639\u0630\u0651\u0631 \u0625\u062A\u0645\u0627\u0645 \u0627\u0644\u0625\u062C\u0631\u0627\u0621 \u0628\u0633\u0628\u0628 \u062E\u0637\u0623 \u0641\u064A \u0627\u0644\u0646\u0638\u0627\u0645. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0628\u0639\u062F \u0644\u062D\u0638\u0627\u062A.",
    unableToComplete: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627\u060C \u062A\u0639\u0630\u0651\u0631 \u0625\u062A\u0645\u0627\u0645 \u0627\u0644\u0625\u062C\u0631\u0627\u0621. \u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0635\u0641\u062D\u0629 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    unexpectedError: "\u0648\u0627\u062C\u0647\u0646\u0627 \u062E\u0637\u0623\u064B \u063A\u064A\u0631 \u0645\u062A\u0648\u0642\u0639. \u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0635\u0641\u062D\u0629 \u0623\u0648 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645 \u0625\u0630\u0627 \u0627\u0633\u062A\u0645\u0631\u062A \u0627\u0644\u0645\u0634\u0643\u0644\u0629.",
    tryAgain: "\u062D\u0627\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649",
    reloadPage: "\u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0635\u0641\u062D\u0629",
    refreshPage: "\u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0635\u0641\u062D\u0629",
    errorDetails: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062E\u0637\u0623 (\u0644\u0644\u062A\u0637\u0648\u064A\u0631 \u0641\u0642\u0637)",
    network: "\u062E\u0637\u0623 \u0641\u064A \u0627\u0644\u0634\u0628\u0643\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0627\u062A\u0635\u0627\u0644.",
    notFound: "\u0627\u0644\u0645\u0648\u0631\u062F \u0627\u0644\u0645\u0637\u0644\u0648\u0628 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F.",
    unauthorized: "\u063A\u064A\u0631 \u0645\u0635\u0631\u062D \u0644\u0643 \u0628\u062A\u0646\u0641\u064A\u0630 \u0647\u0630\u0627 \u0627\u0644\u0625\u062C\u0631\u0627\u0621.",
    tasksLoadError: "\u0641\u0634\u0644 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0647\u0627\u0645: {error}",
    serverError: "\u062E\u0637\u0623 \u0641\u064A \u0627\u0644\u062E\u0627\u062F\u0645. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0627\u062D\u0642\u0627\u064B.",
    genericError: "\u062D\u062F\u062B \u062E\u0637\u0623 \u063A\u064A\u0631 \u0645\u062A\u0648\u0642\u0639. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    failedToLoadCountries: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062F\u0648\u0644",
    failedToLoadGccCountries: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u062F\u0648\u0644 \u0627\u0644\u062E\u0644\u064A\u062C",
    failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0628\u064A\u0627\u0646\u0627\u062A",
    validation: {
      required: "\u0647\u0630\u0627 \u0627\u0644\u062D\u0642\u0644 \u0645\u0637\u0644\u0648\u0628.",
      email: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0635\u062D\u064A\u062D.",
      minLength: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0639\u0644\u0649 {min, number} {min, plural, =0 {\u0623\u062D\u0631\u0641} one {\u062D\u0631\u0641} two {\u062D\u0631\u0641\u064A\u0646} few {\u0623\u062D\u0631\u0641} many {\u062D\u0631\u0641\u0627\u064B} other {\u062D\u0631\u0641}} \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644.",
      maxLength: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 {max, number} {max, plural, =0 {\u0623\u062D\u0631\u0641} one {\u062D\u0631\u0641} two {\u062D\u0631\u0641\u064A\u0646} few {\u0623\u062D\u0631\u0641} many {\u062D\u0631\u0641\u0627\u064B} other {\u062D\u0631\u0641}}.",
      pattern: "\u0627\u0644\u0642\u064A\u0645\u0629 \u0627\u0644\u0645\u062F\u062E\u0644\u0629 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629."
    },
    fileUpload: {
      fileRequired: "\u064A\u062C\u0628 \u0627\u062E\u062A\u064A\u0627\u0631 \u0645\u0644\u0641 \u0644\u0644\u0631\u0641\u0639",
      fileTooLarge: "\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0627\u0644\u0645\u0633\u0645\u0648\u062D \u0628\u0647 \u0648\u0647\u0648 {maxMb} \u0645\u064A\u062C\u0627\u0628\u0627\u064A\u062A",
      invalidFileType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u0633\u0645\u0648\u062D \u0628\u0647",
      invalidFilename: "\u064A\u062C\u0628 \u062A\u0648\u0641\u064A\u0631 \u0627\u0633\u0645 \u0645\u0644\u0641 \u0635\u0627\u0644\u062D",
      serviceNotConfigured: "\u062E\u062F\u0645\u0629 \u0631\u0641\u0639 \u0627\u0644\u0645\u0644\u0641\u0627\u062A \u063A\u064A\u0631 \u0645\u064F\u0639\u062F\u0651\u0629",
      fileTooLargeServer: "\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u062D\u062F \u0627\u0644\u0645\u0633\u0645\u0648\u062D \u0628\u0647 \u0641\u064A \u0627\u0644\u062E\u0627\u062F\u0645",
      unsupportedMediaType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645",
      uploadFailed: "\u0641\u0634\u0644 \u0631\u0641\u0639 \u0627\u0644\u0645\u0644\u0641. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      fileIdRequiredForDeletion: "\u0645\u0639\u0631\u0641 \u0627\u0644\u0645\u0644\u0641 \u0645\u0637\u0644\u0648\u0628 \u0644\u0644\u062D\u0630\u0641",
      fileIdRequired: "\u0645\u0639\u0631\u0641 \u0627\u0644\u0645\u0644\u0641 \u0645\u0637\u0644\u0648\u0628",
      accessDenied: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0647\u0630\u0627 \u0627\u0644\u0645\u0644\u0641",
      fileNotFound: "\u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      deleteFailed: "\u0641\u0634\u0644 \u062D\u0630\u0641 \u0627\u0644\u0645\u0644\u0641",
      metadataFailed: "\u0641\u0634\u0644 \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u0644\u0641"
    }
  },
  pagination: {
    showing: "\u0639\u0631\u0636 {from, number}\u2013{to, number} \u0645\u0646 {total, number} {total, plural, =0 {\u0646\u062A\u0627\u0626\u062C} one {\u0646\u062A\u064A\u062C\u0629} two {\u0646\u062A\u064A\u062C\u062A\u064A\u0646} few {\u0646\u062A\u0627\u0626\u062C} many {\u0646\u062A\u064A\u062C\u0629} other {\u0646\u062A\u064A\u062C\u0629}}",
    itemsPerPage: "\u0639\u0646\u0627\u0635\u0631 \u0641\u064A \u0627\u0644\u0635\u0641\u062D\u0629",
    page: "\u0627\u0644\u0635\u0641\u062D\u0629 {page, number} \u0645\u0646 {total, number}",
    ariaLabel: "\u0627\u0644\u062A\u0646\u0642\u0644 \u0628\u064A\u0646 \u0627\u0644\u0635\u0641\u062D\u0627\u062A",
    previousPage: "\u0627\u0644\u0630\u0647\u0627\u0628 \u0625\u0644\u0649 \u0627\u0644\u0635\u0641\u062D\u0629 \u0627\u0644\u0633\u0627\u0628\u0642\u0629",
    nextPage: "\u0627\u0644\u0630\u0647\u0627\u0628 \u0625\u0644\u0649 \u0627\u0644\u0635\u0641\u062D\u0629 \u0627\u0644\u062A\u0627\u0644\u064A\u0629",
    goToPage: "\u0627\u0644\u0630\u0647\u0627\u0628 \u0625\u0644\u0649 \u0627\u0644\u0635\u0641\u062D\u0629 {page, number}"
  },
  feedback: {
    saved: "\u062A\u0645 \u062D\u0641\u0638 \u0627\u0644\u062A\u063A\u064A\u064A\u0631\u0627\u062A.",
    deleted: "\u062A\u0645 \u0627\u0644\u062D\u0630\u0641 \u0628\u0646\u062C\u0627\u062D.",
    copied: "\u062A\u0645 \u0627\u0644\u0646\u0633\u062E \u0625\u0644\u0649 \u0627\u0644\u062D\u0627\u0641\u0638\u0629."
  },
  search: {
    placeholder: "\u0627\u0628\u062D\u062B...",
    resultsAriaLabel: "\u0646\u062A\u0627\u0626\u062C \u0627\u0644\u0628\u062D\u062B",
    searching: "\u062C\u0627\u0631\u064D \u0627\u0644\u0628\u062D\u062B...",
    noResults: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0646\u062A\u0627\u0626\u062C",
    adjustSearchTerms: "\u062D\u0627\u0648\u0644 \u062A\u0639\u062F\u064A\u0644 \u0645\u0635\u0637\u0644\u062D\u0627\u062A \u0627\u0644\u0628\u062D\u062B",
    inCategory: "\u0641\u064A {category}",
    clearAriaLabel: "\u0645\u0633\u062D \u0627\u0644\u0628\u062D\u062B"
  },
  floatingMenu: {
    open: "\u0641\u062A\u062D \u0627\u0644\u0642\u0627\u0626\u0645\u0629",
    edit: "\u062A\u062D\u0631\u064A\u0631",
    duplicate: "\u062A\u0643\u0631\u0627\u0631",
    archive: "\u0623\u0631\u0634\u0641\u0629",
    delete: "\u062D\u0630\u0641"
  },
  modalHeader: {
    close: "\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u0646\u0627\u0641\u0630\u0629"
  },
  sidePanel: {
    collapse: "\u0637\u064A \u0627\u0644\u0644\u0648\u062D\u0629",
    expand: "\u062A\u0648\u0633\u064A\u0639 \u0627\u0644\u0644\u0648\u062D\u0629",
    close: "\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u0644\u0648\u062D\u0629"
  },
  companyLogoUpload: {
    supportingText: "PNG\u060C JPG\u060C SVG\u060C GIF\u060C WEBP (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 {max})",
    editAriaLabel: "\u062A\u0639\u062F\u064A\u0644 \u0634\u0639\u0627\u0631 \u0627\u0644\u0634\u0631\u0643\u0629",
    fileTooLarge: "\u0627\u0644\u0645\u0644\u0641 \u064A\u062A\u062C\u0627\u0648\u0632 \u062D\u062F {max}. \u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0645\u0644\u0641 \u0623\u0635\u063A\u0631.",
    fileTypeInvalid: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645. \u064A\u0631\u062C\u0649 \u0631\u0641\u0639 PNG \u0623\u0648 JPG \u0623\u0648 SVG \u0623\u0648 GIF \u0623\u0648 WEBP.",
    networkError: "\u0644\u0627 \u064A\u0648\u062C\u062F \u0627\u062A\u0635\u0627\u0644 \u0628\u0627\u0644\u0625\u0646\u062A\u0631\u0646\u062A. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0634\u0628\u0643\u062A\u0643 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
  },
  errorPage: {
    notFoundTitle: "\u062D\u062F\u062B \u062E\u0637\u0623 404.",
    notFoundDescription: "\u064A\u0631\u062C\u0649 \u0645\u062D\u0627\u0648\u0644\u0629 \u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u0634\u0627\u0634\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629.",
    errorTitle: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627",
    errorDescription: "\u062D\u062F\u062B \u062E\u0637\u0623 \u063A\u064A\u0631 \u0645\u062A\u0648\u0642\u0639. \u064A\u0631\u062C\u0649 \u0625\u0639\u0627\u062F\u0629 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0635\u0641\u062D\u0629 \u0623\u0648 \u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u0635\u0641\u062D\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629. \u0625\u0630\u0627 \u0627\u0633\u062A\u0645\u0631\u062A \u0627\u0644\u0645\u0634\u0643\u0644\u0629\u060C \u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    goToHome: "\u0627\u0644\u0630\u0647\u0627\u0628 \u0625\u0644\u0649 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    goBack: "\u0627\u0644\u0639\u0648\u062F\u0629",
    reloadPage: "\u0625\u0639\u0627\u062F\u0629 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0635\u0641\u062D\u0629",
    goHome: "\u0627\u0644\u0630\u0647\u0627\u0628 \u0625\u0644\u0649 \u0627\u0644\u0635\u0641\u062D\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    backToHome: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629"
  },
  services: {
    emptyHeading: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0644\u062F\u064A\u0643 \u0623\u064A \u062E\u062F\u0645\u0627\u062A \u0628\u0639\u062F",
    failedToLoad: "\u0641\u0634\u0644 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0627\u062A: {error}"
  },
  appInfo: {
    title: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u062A\u0637\u0628\u064A\u0642",
    backToHome: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629"
  },
  layout: {
    information: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A"
  },
  logos: {
    tasama: "\u0634\u0639\u0627\u0631 \u062A\u0633\u0627\u0645\u0649",
    tasamaWithTagline: "\u0634\u0639\u0627\u0631 \u062A\u0633\u0627\u0645\u0649 \u0645\u0639 \u0627\u0644\u0634\u0639\u0627\u0631"
  },
  fileUploadItem: {
    statusUploading: "\u062C\u0627\u0631\u064D \u0627\u0644\u0631\u0641\u0639",
    statusComplete: "\u0645\u0643\u062A\u0645\u0644",
    statusFailed: "\u0641\u0634\u0644",
    removeAriaLabel: "\u0625\u0632\u0627\u0644\u0629 \u0627\u0644\u0645\u0644\u0641"
  },
  status: {
    notStarted: "\u0644\u0645 \u062A\u0628\u062F\u0623",
    moreInfoRequired: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0625\u0636\u0627\u0641\u064A\u0629",
    incomplete: "\u063A\u064A\u0631 \u0645\u0643\u062A\u0645\u0644\u0629",
    waitingYourConfirmation: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u062A\u0623\u0643\u064A\u062F\u0643",
    inProgress: "\u062C\u0627\u0631\u064D \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
    requested: "\u0645\u0637\u0644\u0648\u0628\u0629",
    completed: "\u0645\u0643\u062A\u0645\u0644\u0629",
    active: "\u0641\u0639\u0627\u0644\u0629",
    inactive: "\u063A\u064A\u0631 \u0646\u0634\u0637",
    pending: "\u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
    onHold: "\u0641\u064A \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
    confirmCompletion: "\u062A\u0623\u0643\u064A\u062F \u0627\u0644\u0627\u0643\u062A\u0645\u0627\u0644",
    rejected: "\u0645\u0631\u0641\u0648\u0636\u0629",
    awaitingCustomer: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0639\u0645\u064A\u0644",
    awaitingVendor: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0648\u0631\u0651\u062F",
    awaitingCandidate: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0631\u0634\u062D",
    closedIncomplete: "\u0645\u063A\u0644\u0642\u0629 (\u063A\u064A\u0631 \u0645\u0643\u062A\u0645\u0644\u0629)",
    new: "\u062C\u062F\u064A\u062F\u0629",
    ticketOpen: "\u0645\u0641\u062A\u0648\u062D\u0629",
    awaitingCustomerTicket: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0639\u0645\u064A\u0644",
    resolved: "\u062A\u0645 \u0627\u0644\u062D\u0644",
    onHoldTicket: "\u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
    awaitingThirdPartyInfo: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0645\u0646 \u0637\u0631\u0641 \u062B\u0627\u0644\u062B",
    closedTicket: "\u0645\u063A\u0644\u0642\u0629",
    canceledTicket: "\u0645\u0644\u063A\u0627\u0629"
  },
  tasks: {
    noTasks: "\u0644\u064A\u0633 \u0644\u062F\u064A\u0643 \u0623\u064A \u0645\u0647\u0627\u0645 \u0628\u0639\u062F."
  },
  breadcrumb: {
    ariaLabel: "\u0645\u0633\u0627\u0631 \u0627\u0644\u062A\u0646\u0642\u0644"
  },
  accountPage: {
    tabs: {
      personalInformation: "\u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0634\u062E\u0635\u064A\u0629",
      security: "\u0627\u0644\u0623\u0645\u0627\u0646",
      notifications: "\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A",
      billing: "\u0627\u0644\u0641\u0648\u062A\u0631\u0629"
    },
    status: {
      securityComingSoon: "\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0627\u0644\u0623\u0645\u0627\u0646 \u0642\u0627\u062F\u0645\u0629 \u0642\u0631\u064A\u0628\u064B\u0627...",
      notificationsComingSoon: "\u062A\u0641\u0636\u064A\u0644\u0627\u062A \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A \u0642\u0627\u062F\u0645\u0629 \u0642\u0631\u064A\u0628\u064B\u0627...",
      billingComingSoon: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0641\u0648\u062A\u0631\u0629 \u0642\u0627\u062F\u0645\u0629 \u0642\u0631\u064A\u0628\u064B\u0627..."
    }
  },
  businessAdvisor: {
    headerTitle: "\u0633\u0645\u0627 \u0627\u0644\u0645\u0633\u0627\u0639\u062F \u0627\u0644\u0631\u0642\u0645\u064A",
    headerTitleLine1: "\u0633\u0645\u0627",
    headerTitleLine2: "\u0627\u0644\u0645\u0633\u0627\u0639\u062F \u0627\u0644\u0631\u0642\u0645\u064A",
    headerDescription: "\u0628\u062F\u0639\u0645 \u0645\u0646 \u062A\u0633\u0627\u0645\u0649",
    apiError: "\u062D\u062F\u062B \u062E\u0637\u0623.",
    apiErrorDescription: "\u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0635\u0641\u062D\u0629.",
    greetingPrefix: "\u0645\u0631\u062D\u0628\u0627\u064B\u060C \u0643\u064A\u0641 \u064A\u0645\u0643\u0646\u0646\u064A ",
    greetingHighlight: "\u0645\u0633\u0627\u0639\u062F\u062A\u0643\u061F",
    defaultSubtitle: "\u0623\u0646\u0627 \u0645\u0633\u0627\u0639\u062F\u0643 \u0627\u0644\u0630\u0643\u064A \u0647\u0646\u0627 \u0644\u0645\u0633\u0627\u0639\u062F\u062A\u0643 \u0639\u0644\u0649 \u0641\u0647\u0645 \u062E\u062F\u0645\u0627\u062A \u062A\u0633\u0627\u0645\u0649 \u0648\u0625\u0631\u0634\u0627\u062F\u0643 \u0641\u064A \u0631\u062D\u0644\u062A\u0643 \u0648\u062F\u0639\u0645 \u062E\u0637\u0648\u0627\u062A\u0643 \u0627\u0644\u0642\u0627\u062F\u0645\u0629.",
    initialising: "\u062C\u0627\u0631\u064D \u0627\u0644\u062A\u0647\u064A\u0626\u0629...",
    collapseModal: "\u062A\u0635\u063A\u064A\u0631 \u0627\u0644\u0646\u0627\u0641\u0630\u0629",
    expandModal: "\u062A\u0648\u0633\u064A\u0639 \u0627\u0644\u0646\u0627\u0641\u0630\u0629",
    closeModal: "\u0625\u063A\u0644\u0627\u0642 \u0646\u0627\u0641\u0630\u0629 \u0627\u0644\u0645\u0633\u062A\u0634\u0627\u0631",
    imageAlt: "\u0623\u064A\u0642\u0648\u0646\u0629 \u0646\u0635 \u0627\u0644\u0630\u0643\u0627\u0621 \u0627\u0644\u0627\u0635\u0637\u0646\u0627\u0639\u064A",
    inputPlaceholder: "\u0627\u0633\u0623\u0644\u0646\u064A \u0623\u064A \u0634\u064A\u0621",
    inputAriaLabel: "\u0627\u0643\u062A\u0628 \u0633\u0624\u0627\u0644\u0643",
    sendAriaLabel: "\u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0631\u0633\u0627\u0644\u0629",
    characterLimit: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 {max, number} \u062D\u0631\u0641. {current, number}/{max, number}",
    disclaimer: "\u0633\u0645\u0627 \u0630\u0643\u0627\u0621 \u0627\u0635\u0637\u0646\u0627\u0639\u064A \u0648\u0642\u062F \u064A\u0631\u062A\u0643\u0628 \u0623\u062E\u0637\u0627\u0621. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0631\u062F\u0648\u062F.",
    button: {
      askAi: "\u0627\u0633\u0623\u0644 \u0633\u0645\u0627 \u0627\u0644\u0630\u0643\u0627\u0621 \u0627\u0644\u0627\u0635\u0637\u0646\u0627\u0639\u064A",
      open: "\u0627\u0641\u062A\u062D \u0645\u0633\u062A\u0634\u0627\u0631 \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0627\u0644\u0630\u0643\u064A",
      close: "\u0623\u063A\u0644\u0642 \u0645\u0633\u062A\u0634\u0627\u0631 \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0627\u0644\u0630\u0643\u064A"
    },
    errors: {
      initConversation: "\u0641\u0634\u0644 \u0641\u064A \u0628\u062F\u0621 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629",
      unexpected: "\u062D\u062F\u062B \u062E\u0637\u0623 \u063A\u064A\u0631 \u0645\u062A\u0648\u0642\u0639",
      noSessionId: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0633\u062A\u0642\u0628\u0627\u0644 \u0645\u0639\u0631\u0651\u0641 \u062C\u0644\u0633\u0629 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629",
      sendFailed: "\u0641\u0634\u0644 \u0641\u064A \u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0631\u0633\u0627\u0644\u0629",
      noSessionAvailable: "\u0644\u0627 \u062A\u0648\u062C\u062F \u062C\u0644\u0633\u0629 \u0645\u062D\u0627\u062F\u062B\u0629 \u0645\u062A\u0627\u062D\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0647\u064A\u0626\u0629 \u0623\u0648\u0644\u0627\u064B.",
      emptyMessage: "\u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0627\u0644\u0631\u0633\u0627\u0644\u0629 \u0641\u0627\u0631\u063A\u0629",
      noContext: "\u0644\u0627 \u064A\u0648\u062C\u062F \u0633\u064A\u0627\u0642 \u0645\u062A\u0627\u062D \u0644\u0644\u0631\u0633\u0627\u0644\u0629",
      fetchMessagesFailed: "\u0641\u0634\u0644 \u0641\u064A \u062C\u0644\u0628 \u0627\u0644\u0631\u0633\u0627\u0626\u0644",
      refreshMessagesFailed: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0631\u0633\u0627\u0626\u0644",
      loadMoreFailed: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0631\u0633\u0627\u0626\u0644",
      authRequired: "\u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u0645\u0637\u0644\u0648\u0628\u0629 \u0644\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0633\u062A\u0634\u0627\u0631",
      contextRequired: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0633\u064A\u0627\u0642 \u0645\u0637\u0644\u0648\u0628\u0629",
      unexpectedResponse: "\u0627\u0633\u062A\u062C\u0627\u0628\u0629 \u063A\u064A\u0631 \u0645\u062A\u0648\u0642\u0639\u0629 \u0645\u0646 \u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0633\u062A\u0634\u0627\u0631",
      serviceNotFound: "\u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0633\u062A\u0634\u0627\u0631 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629",
      invalidInitRequest: "\u0637\u0644\u0628 \u062A\u0647\u064A\u0626\u0629 \u0627\u0644\u062C\u0644\u0633\u0629 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      unauthorized: "\u0648\u0635\u0648\u0644 \u063A\u064A\u0631 \u0645\u0635\u0631\u062D \u0628\u0647 \u0625\u0644\u0649 \u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0633\u062A\u0634\u0627\u0631",
      forbidden: "\u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0633\u062A\u0634\u0627\u0631 \u0645\u062D\u0638\u0648\u0631",
      initSessionFailed: "\u0641\u0634\u0644 \u0641\u064A \u062A\u0647\u064A\u0626\u0629 \u062C\u0644\u0633\u0629 \u0627\u0644\u0645\u0633\u062A\u0634\u0627\u0631",
      messageAndContextRequired: "\u0627\u0644\u0631\u0633\u0627\u0644\u0629 \u0648\u0627\u0644\u0633\u064A\u0627\u0642 \u0645\u0637\u0644\u0648\u0628\u0627\u0646",
      sessionIdRequired: "\u0645\u0639\u0631\u0651\u0641 \u062C\u0644\u0633\u0629 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629 \u0645\u0637\u0644\u0648\u0628",
      sessionNotFound: "\u062C\u0644\u0633\u0629 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629",
      invalidMessageRequest: "\u0637\u0644\u0628 \u0627\u0644\u0631\u0633\u0627\u0644\u0629 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      invalidRecommendationsRequest: "\u0637\u0644\u0628 \u0627\u0644\u062A\u0648\u0635\u064A\u0627\u062A \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      recommendationsNotFound: "\u0627\u0644\u062A\u0648\u0635\u064A\u0627\u062A \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629",
      recommendationsFailed: "\u0641\u0634\u0644 \u0641\u064A \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u0627\u0644\u062A\u0648\u0635\u064A\u0627\u062A"
    },
    loader: {
      loading: "\u062C\u0627\u0631\u064D \u0627\u0644\u062A\u062D\u0645\u064A\u0644..."
    },
    cta: {
      home: "\u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      registerNow: "\u0633\u062C\u0644 \u0627\u0644\u0622\u0646",
      resetPassword: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
      contactMe: "\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u064A",
      restartConversation: "\u0625\u0639\u0627\u062F\u0629 \u0628\u062F\u0621 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629",
      supportTicket: "\u062A\u0630\u0643\u0631\u0629 \u0627\u0644\u062F\u0639\u0645",
      serviceCatalogue: "\u0643\u062A\u0627\u0644\u0648\u062C \u0627\u0644\u062E\u062F\u0645\u0627\u062A",
      notifications: "\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A",
      myAccount: "\u062D\u0633\u0627\u0628\u064A",
      myDocuments: "\u0645\u0633\u062A\u0646\u062F\u0627\u062A\u064A",
      myServices: "\u062E\u062F\u0645\u0627\u062A\u064A"
    },
    aria: {
      cta: "\u0646\u062F\u0627\u0621 \u0625\u0644\u0649 \u0627\u0644\u0639\u0645\u0644",
      suggestion: "\u0627\u0642\u062A\u0631\u0627\u062D"
    },
    contextualPrompt: {
      delayQuestion: "\u0623\u0631\u0649 \u0623\u0646 \u0647\u0630\u0627 \u0645\u062A\u0623\u062E\u0631\u060C \u0645\u0627 \u0633\u0628\u0628 \u0627\u0644\u062A\u0623\u062E\u064A\u0631\u061F"
    }
  },
  chip: {
    iconAction: "\u0625\u062C\u0631\u0627\u0621 \u0627\u0644\u0623\u064A\u0642\u0648\u0646\u0629"
  },
  notification: {
    defaultTitle: "\u0625\u0634\u0639\u0627\u0631"
  },
  accordion: {
    ariaLabel: "\u0642\u0627\u0626\u0645\u0629 \u0637\u064A"
  },
  banner: {
    defaultTitle: '\u0645\u0631\u062D\u0628\u0627\u064B \u0628\u0643 \u0641\u064A <span class="text-brand-gold">\u062A\u0633\u0627\u0645\u0649</span>'
  },
  dateInput: {
    chooseDate: "\u0627\u062E\u062A\u0631 \u0627\u0644\u062A\u0627\u0631\u064A\u062E",
    placeholder: "\u064A\u0648\u0645/\u0634\u0647\u0631/\u0633\u0646\u0629"
  },
  timeInput: {
    chooseTime: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0648\u0642\u062A",
    placeholder: "\u0633:\u062F \u0635/\u0645",
    done: "\u062A\u0645",
    hoursLabel: "\u0627\u0644\u0633\u0627\u0639\u0627\u062A",
    minutesLabel: "\u0627\u0644\u062F\u0642\u0627\u0626\u0642"
  },
  dateTimeInput: {
    chooseDateTime: "\u0627\u062E\u062A\u0631 \u0627\u0644\u062A\u0627\u0631\u064A\u062E \u0648\u0627\u0644\u0648\u0642\u062A",
    placeholder: "\u064A\u0648\u0645/\u0634\u0647\u0631/\u0633\u0646\u0629 \u0633:\u062F",
    selectDateTab: "\u0627\u062E\u062A\u0631 \u0627\u0644\u062A\u0627\u0631\u064A\u062E",
    selectTimeTab: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0648\u0642\u062A"
  },
  dateRangePicker: {
    chooseDateRange: "\u0627\u062E\u062A\u0631 \u0646\u0637\u0627\u0642 \u0627\u0644\u062A\u0627\u0631\u064A\u062E",
    placeholder: "\u064A\u0648\u0645/\u0634\u0647\u0631/\u0633\u0646\u0629 - \u064A\u0648\u0645/\u0634\u0647\u0631/\u0633\u0646\u0629",
    startDate: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0628\u062F\u0621",
    endDate: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0627\u0646\u062A\u0647\u0627\u0621",
    clearStartDateAriaLabel: "\u0645\u0633\u062D \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0628\u062F\u0621",
    clearEndDateAriaLabel: "\u0645\u0633\u062D \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0627\u0646\u062A\u0647\u0627\u0621",
    mobileTitle: "\u0646\u0637\u0627\u0642 \u062A\u0627\u0631\u064A\u062E \u0645\u062E\u0635\u0635",
    presets: {
      custom: "\u0645\u062E\u0635\u0635",
      last7Days: "\u0622\u062E\u0631 7 \u0623\u064A\u0627\u0645",
      last30Days: "\u0622\u062E\u0631 30 \u064A\u0648\u0645\u064B\u0627",
      last90Days: "\u0622\u062E\u0631 90 \u064A\u0648\u0645\u064B\u0627",
      last1Year: "\u0622\u062E\u0631 \u0633\u0646\u0629",
      all: "\u0627\u0644\u0643\u0644"
    }
  },
  languageSwitcher: {
    switchTo: "ENGLISH",
    currentLanguage: "\u0627\u0644\u0639\u0631\u0628\u064A\u0629"
  },
  personForm: {
    firstName: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644",
    lastName: "\u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629",
    prefix: "\u0627\u0644\u0644\u0642\u0628",
    selectPrefix: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0644\u0642\u0628",
    prefixes: {
      mr: "\u0627\u0644\u0633\u064A\u062F",
      ms: "\u0627\u0644\u0622\u0646\u0633\u0629",
      mrs: "\u0627\u0644\u0633\u064A\u062F\u0629",
      miss: "\u0627\u0644\u0622\u0646\u0633\u0629",
      dr: "\u062F\u0643\u062A\u0648\u0631"
    },
    jobTitle: "\u0627\u0644\u0645\u0633\u0645\u0649 \u0627\u0644\u0648\u0638\u064A\u0641\u064A",
    email: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    mobilePhone: "\u0627\u0644\u0647\u0627\u062A\u0641 \u0627\u0644\u0645\u062D\u0645\u0648\u0644"
  },
  companyForm: {
    registeredCompanyName: "\u0627\u0633\u0645 \u0627\u0644\u0634\u0631\u0643\u0629 \u0627\u0644\u0645\u0633\u062C\u0644\u0629",
    companyRegistrationNumber: "\u0631\u0642\u0645 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
    registrationStartDate: "\u062A\u0627\u0631\u064A\u062E \u0628\u062F\u0621 \u0627\u0644\u062A\u0633\u062C\u064A\u0644",
    companyWebsite: "\u0645\u0648\u0642\u0639 \u0627\u0644\u0634\u0631\u0643\u0629 (\u0627\u062E\u062A\u064A\u0627\u0631\u064A)",
    enterWebsiteUrl: "\u0623\u062F\u062E\u0644 \u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    industrySector: "\u0627\u0644\u0642\u0637\u0627\u0639 \u0627\u0644\u0635\u0646\u0627\u0639\u064A",
    industrySectorPlaceholder: "\u0627\u062E\u062A\u0631 \u0645\u062C\u0627\u0644 \u0639\u0645\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
    countryOfRegistration: "\u0628\u0644\u062F \u0627\u0644\u062A\u0633\u062C\u064A\u0644",
    registeredCompanyAddress: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0634\u0631\u0643\u0629 \u0627\u0644\u0645\u0633\u062C\u0644",
    enterAddress: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0631\u0642\u0645\u060C \u0627\u0633\u0645 \u0627\u0644\u0634\u0627\u0631\u0639 \u0623\u0648 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0628\u0631\u064A\u062F",
    city: "\u0627\u0644\u0645\u062F\u064A\u0646\u0629",
    enterCity: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0645\u062F\u064A\u0646\u0629",
    postcode: "\u0627\u0644\u0631\u0645\u0632 \u0627\u0644\u0628\u0631\u064A\u062F\u064A",
    enterPostcode: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0631\u0645\u0632 \u0627\u0644\u0628\u0631\u064A\u062F\u064A",
    countryPlaceholder: "\u0627\u0628\u062D\u062B \u0648\u0627\u062E\u062A\u0631 \u0627\u0644\u062F\u0648\u0644\u0629",
    numberOfEmployees: "\u0639\u062F\u062F \u0627\u0644\u0645\u0648\u0638\u0641\u064A\u0646",
    numberOfEmployeesPlaceholder: "\u0623\u062F\u062E\u0644 \u0639\u062F\u062F \u0627\u0644\u0645\u0648\u0638\u0641\u064A\u0646",
    annualRevenue: "\u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A \u0627\u0644\u0633\u0646\u0648\u064A\u0629",
    annualRevenuePlaceholder: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A \u0627\u0644\u0633\u0646\u0648\u064A\u0629",
    totalInvestmentRaised: "\u0645\u0627 \u0647\u0648 \u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0633\u062A\u062B\u0645\u0627\u0631 \u0627\u0644\u0634\u0631\u0643\u0629 \u0627\u0644\u0630\u064A \u062A\u0645 \u062C\u0645\u0639\u0647 \u062D\u062A\u0649 \u0627\u0644\u0622\u0646\u061F",
    totalInvestmentRaisedPlaceholder: "\u0627\u062E\u062A\u0631 \u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631 \u062D\u062A\u0649 \u0627\u0644\u0622\u0646",
    companyNamePlaceholder: "\u0623\u062F\u062E\u0644 \u0627\u0633\u0645 \u0634\u0631\u0643\u062A\u0643",
    crNumberPlaceholder: "\u0623\u062F\u062E\u0644 \u0631\u0642\u0645 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
    saudiArabia: "\u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
    employeeCountOptions: {
      range_0_5: "0 - 5",
      range_6_25: "6 - 25",
      range_26_100: "26 - 100",
      range_101_200: "101 - 200",
      range_201_1000: "201 - 1,000",
      range_gt_1000: "\u0623\u0643\u062B\u0631 \u0645\u0646 1,000"
    },
    annualRevenueOptions: {
      range_0_1M: "0 - 1 \u0645\u0644\u064A\u0648\u0646 \u0631\u064A\u0627\u0644",
      range_1M_5M: "1 \u0645\u0644\u064A\u0648\u0646 - 5 \u0645\u0644\u0627\u064A\u064A\u0646 \u0631\u064A\u0627\u0644",
      range_5M_20M: "5 \u0645\u0644\u0627\u064A\u064A\u0646 - 20 \u0645\u0644\u064A\u0648\u0646 \u0631\u064A\u0627\u0644",
      range_gt_20M: "\u0623\u0643\u062B\u0631 \u0645\u0646 20 \u0645\u0644\u064A\u0648\u0646 \u0631\u064A\u0627\u0644"
    },
    investmentRaisedOptions: {
      range_0_5M: "0 - 5 \u0645\u0644\u0627\u064A\u064A\u0646 \u0631\u064A\u0627\u0644",
      range_5M_20M: "5 \u0645\u0644\u0627\u064A\u064A\u0646 - 20 \u0645\u0644\u064A\u0648\u0646 \u0631\u064A\u0627\u0644",
      range_20M_50M: "20 \u0645\u0644\u064A\u0648\u0646 - 50 \u0645\u0644\u064A\u0648\u0646 \u0631\u064A\u0627\u0644",
      range_50M_100M: "50 \u0645\u0644\u064A\u0648\u0646 - 100 \u0645\u0644\u064A\u0648\u0646 \u0631\u064A\u0627\u0644",
      range_gt_100M: "\u0623\u0643\u062B\u0631 \u0645\u0646 100 \u0645\u0644\u064A\u0648\u0646 \u0631\u064A\u0627\u0644"
    }
  },
  onboardingNav: {
    links: {
      howItWorks: "\u0643\u064A\u0641 \u064A\u0639\u0645\u0644",
      whyTasama: "\u0644\u0645\u0627\u0630\u0627 \u062A\u0633\u0627\u0645\u0649",
      resources: "\u0627\u0644\u0645\u0648\u0627\u0631\u062F",
      contactUs: "\u0627\u062A\u0635\u0644 \u0628\u0646\u0627"
    },
    aria: {
      mainNavigation: "\u0627\u0644\u062A\u0646\u0642\u0644 \u0627\u0644\u0631\u0626\u064A\u0633\u064A"
    },
    logo: {
      aria: {
        goToHomepage: "\u0627\u0644\u0630\u0647\u0627\u0628 \u0625\u0644\u0649 \u0627\u0644\u0635\u0641\u062D\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629"
      },
      title: "\u0634\u0639\u0627\u0631 \u062A\u0633\u0627\u0645\u0649"
    }
  },
  filters: {
    title: "\u0627\u0644\u0641\u0644\u0627\u062A\u0631",
    triggerLabel: "\u0627\u0644\u0641\u0644\u0627\u062A\u0631",
    clearFilters: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646",
    apply: "\u062A\u0637\u0628\u064A\u0642"
  },
  dynamicRowsTable: {
    removeAll: "\u0625\u0632\u0627\u0644\u0629 \u0627\u0644\u0643\u0644",
    requiredField: "\u0647\u0630\u0627 \u0627\u0644\u062D\u0642\u0644 \u0645\u0637\u0644\u0648\u0628",
    incompleteFields: "\u0627\u0644\u062D\u0642\u0648\u0644 \u0627\u0644\u062A\u0627\u0644\u064A\u0629 \u063A\u064A\u0631 \u0645\u0643\u062A\u0645\u0644\u0629: {fields}",
    confirmationTitle: "\u062A\u0623\u0643\u064A\u062F",
    confirmLabel: "\u0625\u0632\u0627\u0644\u0629",
    deleteRow: {
      description: "\u0647\u0644 \u0623\u0646\u062A \u0645\u062A\u0623\u0643\u062F \u0623\u0646\u0643 \u062A\u0631\u064A\u062F \u062D\u0630\u0641 \u0647\u0630\u0627 \u0627\u0644\u0635\u0641\u061F"
    },
    removeAllConfirm: {
      description: "\u0647\u0644 \u0623\u0646\u062A \u0645\u062A\u0623\u0643\u062F \u0623\u0646\u0643 \u062A\u0631\u064A\u062F \u0625\u0632\u0627\u0644\u0629 \u062C\u0645\u064A\u0639 \u0627\u0644\u0635\u0641\u0648\u0641\u061F"
    }
  },
  charts: {
    noData: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0628\u064A\u0627\u0646\u0627\u062A \u0645\u062A\u0627\u062D\u0629"
  }
};

// src/locales/ar/auth.json
var auth_default2 = {
  customerLogin: "\u062A\u0633\u062C\u064A\u0644 \u062F\u062E\u0648\u0644 \u0627\u0644\u0639\u0645\u064A\u0644",
  emailLabel: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
  emailPlaceholder: "\u0623\u062F\u062E\u0644 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
  passwordLabel: "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
  passwordPlaceholder: "\u0623\u062F\u062E\u0644 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643",
  passwordHint: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0645\u0646 8 \u0623\u062D\u0631\u0641 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
  loginButton: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644",
  forgotPassword: "\u0646\u0633\u064A\u062A \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631\u061F",
  resetPassword: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
  accountNotFound: "\u0628\u064A\u0627\u0646\u0627\u062A \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0627\u0644\u062A\u064A \u0623\u062F\u062E\u0644\u062A\u0647\u0627 \u063A\u064A\u0631 \u0635\u062D\u064A\u062D\u0629. \u064A\u064F\u0631\u062C\u0649 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0633\u0645 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0648\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
  forgotPasswordTitle: "\u0646\u0633\u064A\u062A \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
  forgotPasswordSubtitle: "\u0633\u064A\u062A\u0645 \u0625\u0631\u0633\u0627\u0644 \u0631\u0627\u0628\u0637 \u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0625\u0644\u0649 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0627\u0644\u0645\u0633\u062C\u0644.",
  resetPasswordButton: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
  returnTo: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649",
  loginLink: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644",
  checkEmailTitle: "\u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
  checkEmailDescription: "\u0644\u0642\u062F \u0623\u0631\u0633\u0644\u0646\u0627 \u0631\u0627\u0628\u0637\u0627\u064B \u0625\u0644\u0649 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0627\u0644\u0645\u0633\u062C\u0644. \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0648\u0627\u0631\u062F \u0648\u0627\u062A\u0628\u0627\u0639 \u0627\u0644\u062A\u0639\u0644\u064A\u0645\u0627\u062A.",
  didNotReceiveEmail: "\u0644\u0645 \u062A\u0633\u062A\u0644\u0645 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A\u061F",
  tryAgain: "\u062D\u0627\u0648\u0644 \u0645\u062C\u062F\u062F\u0627\u064B",
  activateAccountTitle: "\u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628\u0643",
  activateAccountDescription: "\u0642\u0645 \u0628\u0625\u0639\u062F\u0627\u062F \u062D\u0633\u0627\u0628\u0643 \u0644\u0628\u062F\u0621 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062E\u062F\u0645\u0627\u062A\u0646\u0627",
  reactivateAccountTitle: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628\u0643",
  reactivateAccountDescription: "\u0627\u062D\u0635\u0644 \u0639\u0644\u0649 \u0631\u0627\u0628\u0637 \u062A\u0641\u0639\u064A\u0644 \u062C\u062F\u064A\u062F \u0648\u0627\u0633\u062A\u0645\u0631",
  resetPasswordTitle: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
  resetPasswordDescription: "\u0644\u0642\u062F \u0637\u0644\u0628\u062A \u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643",
  newPasswordLabel: "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062C\u062F\u064A\u062F\u0629",
  newPasswordPlaceholder: "\u0623\u062F\u062E\u0644 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062C\u062F\u064A\u062F\u0629",
  confirmPasswordLabel: "\u062A\u0623\u0643\u064A\u062F \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
  confirmPasswordPlaceholder: "\u0623\u0643\u0651\u062F \u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631\u0643 \u0627\u0644\u062C\u062F\u064A\u062F\u0629",
  confirmPasswordHint: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u062A\u0637\u0627\u0628\u0642 \u0645\u0639 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0623\u0639\u0644\u0627\u0647.",
  activateAccountButton: "\u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628",
  reactivateAccountButton: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628",
  alreadyActivated: "\u0644\u062F\u064A\u0643 \u062D\u0633\u0627\u0628 \u0645\u0641\u0639\u0644\u061F",
  resetPasswordFormDescription: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0648\u0625\u0646\u0634\u0627\u0621 \u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631 \u062C\u062F\u064A\u062F\u0629 \u0644\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u062D\u0633\u0627\u0628\u0643.",
  emailLinkExpired: "\u0631\u0627\u0628\u0637 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0642\u062F \u0627\u0646\u062A\u0647\u062A \u0635\u0644\u0627\u062D\u064A\u062A\u0647",
  expiredLinkDescription: "\u0627\u0646\u0642\u0631 \u0639\u0644\u0649 {action} \u0644\u062A\u0644\u0642\u064A \u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u062C\u062F\u064A\u062F \u0644\u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628\u0643. \u0633\u064A\u0643\u0648\u0646 \u0627\u0644\u0631\u0627\u0628\u0637 \u0627\u0644\u062C\u062F\u064A\u062F \u0635\u0627\u0644\u062D\u064B\u0627 \u0644\u0645\u062F\u0629 48 \u0633\u0627\u0639\u0629.",
  reactivateAction: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628\u0643",
  resetAction: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
  tooManyRequests: "\u0637\u0644\u0628\u0627\u062A \u0643\u062B\u064A\u0631\u0629 \u062C\u062F\u0627\u064B. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0627\u062D\u0642\u0627\u064B.",
  tooManyPasswordResets: "\u0645\u062D\u0627\u0648\u0644\u0627\u062A \u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0643\u062B\u064A\u0631\u0629 \u062C\u062F\u0627\u064B. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u062E\u0644\u0627\u0644 15 \u062F\u0642\u064A\u0642\u0629.",
  anErrorOccurred: "\u062D\u062F\u062B \u062E\u0637\u0623. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
  emailNotFound: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
  failedToSendResetLink: "\u0641\u0634\u0644 \u0641\u064A \u0625\u0631\u0633\u0627\u0644 \u0631\u0627\u0628\u0637 \u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0639\u064A\u064A\u0646",
  failedToSendReactivationLink: "\u0641\u0634\u0644 \u0641\u064A \u0625\u0631\u0633\u0627\u0644 \u0631\u0627\u0628\u0637 \u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0641\u0639\u064A\u0644",
  invalidRequest: "\u0637\u0644\u0628 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0628\u064A\u0627\u0646\u0627\u062A\u0643.",
  sessionConflict: {
    title: "\u0647\u0644 \u0623\u0646\u062A \u0645\u062A\u0623\u0643\u062F \u0623\u0646\u0643 \u062A\u0631\u064A\u062F \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062E\u0631\u0648\u062C\u061F",
    activateDescription: "\u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628 \u062C\u062F\u064A\u062F \u0633\u064A\u0624\u062F\u064A \u0625\u0644\u0649 \u062A\u0633\u062C\u064A\u0644 \u062E\u0631\u0648\u062C\u0643 \u0645\u0646 \u062C\u0644\u0633\u062A\u0643 \u0627\u0644\u062D\u0627\u0644\u064A\u0629.",
    resetPasswordDescription: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0633\u062A\u0624\u062F\u064A \u0625\u0644\u0649 \u062A\u0633\u062C\u064A\u0644 \u062E\u0631\u0648\u062C\u0643 \u0645\u0646 \u062C\u0644\u0633\u062A\u0643 \u0627\u0644\u062D\u0627\u0644\u064A\u0629.",
    backToDashboard: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0644\u0648\u062D\u0629 \u0627\u0644\u062A\u062D\u0643\u0645",
    logOut: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062E\u0631\u0648\u062C"
  },
  failedToActivateAccount: "\u0641\u0634\u0644 \u0641\u064A \u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628",
  failedToResetPassword: "\u0641\u0634\u0644 \u0641\u064A \u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
  validation: {
    email: {
      required: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0637\u0644\u0648\u0628!",
      invalid: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0635\u0627\u0644\u062D!",
      corporate: "\u064A\u0631\u062C\u0649 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0627\u0644\u0645\u0624\u0633\u0633\u064A. \u0639\u0646\u0627\u0648\u064A\u0646 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0627\u0644\u0634\u062E\u0635\u064A\u0629 \u063A\u064A\u0631 \u0645\u0633\u0645\u0648\u062D \u0628\u0647\u0627."
    },
    password: {
      required: "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0645\u0637\u0644\u0648\u0628\u0629!",
      minLength: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 8 \u0623\u062D\u0631\u0641 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644!",
      maxLength: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u062A\u062A\u062C\u0627\u0648\u0632 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 20 \u062D\u0631\u0641\u0627\u064B",
      charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u062A\u062D\u062A\u0648\u064A \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0639\u0644\u0649 \u0623\u062D\u0631\u0641 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648\u0623\u062D\u0631\u0641 \u062E\u0627\u0635\u0629 \u0641\u0642\u0637 (@$!%*?&)",
      lowercase: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u062D\u062A\u0648\u064A \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0639\u0644\u0649 \u062D\u0631\u0641 \u0635\u063A\u064A\u0631 \u0648\u0627\u062D\u062F \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
      uppercase: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u062D\u062A\u0648\u064A \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0639\u0644\u0649 \u062D\u0631\u0641 \u0643\u0628\u064A\u0631 \u0648\u0627\u062D\u062F \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
      number: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u062D\u062A\u0648\u064A \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0639\u0644\u0649 \u0631\u0642\u0645 \u0648\u0627\u062D\u062F \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
      specialChar: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u062D\u062A\u0648\u064A \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0639\u0644\u0649 \u062D\u0631\u0641 \u062E\u0627\u0635 \u0648\u0627\u062D\u062F \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644 (@$!%*?&)"
    },
    resetToken: {
      required: "\u0631\u0645\u0632 \u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0639\u064A\u064A\u0646 \u0645\u0637\u0644\u0648\u0628!"
    },
    confirmPassword: {
      required: "\u064A\u0631\u062C\u0649 \u062A\u0623\u0643\u064A\u062F \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631!",
      mismatch: "\u0643\u0644\u0645\u0627\u062A \u0627\u0644\u0645\u0631\u0648\u0631 \u063A\u064A\u0631 \u0645\u062A\u0637\u0627\u0628\u0642\u0629!"
    }
  },
  errors: {
    invalidCredentials: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0623\u0648 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u063A\u064A\u0631 \u0635\u062D\u064A\u062D\u0629.",
    accountLocked: "\u062A\u0645 \u0642\u0641\u0644 \u062D\u0633\u0627\u0628\u0643. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645 \u0627\u0644\u0641\u0646\u064A.",
    sessionExpired: "\u0627\u0646\u062A\u0647\u062A \u0635\u0644\u0627\u062D\u064A\u0629 \u062C\u0644\u0633\u062A\u0643. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    loginFailed: "\u0641\u0634\u0644 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644",
    fingerprintMismatch: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642 \u0627\u0644\u0623\u0645\u0646\u064A. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0633\u0644\u0627\u0645\u062A\u0643.",
    rateLimitExceeded: "\u0637\u0644\u0628\u0627\u062A \u0643\u062B\u064A\u0631\u0629 \u062C\u062F\u0627\u064B. \u064A\u0631\u062C\u0649 \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631 \u0644\u062D\u0638\u0629 \u0642\u0628\u0644 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    networkError: "\u062E\u0637\u0623 \u0641\u064A \u0627\u0644\u0634\u0628\u0643\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u062A\u0635\u0627\u0644\u0643 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    authenticationError: "\u062E\u0637\u0623 \u0641\u064A \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629: {{message}}",
    genericError: "\u062E\u0637\u0623: {{message}}",
    unexpectedError: "\u062D\u062F\u062B \u062E\u0637\u0623 \u063A\u064A\u0631 \u0645\u062A\u0648\u0642\u0639",
    missingRefreshToken: "\u0631\u0645\u0632 \u0627\u0644\u062A\u062D\u062F\u064A\u062B \u0645\u0641\u0642\u0648\u062F",
    sessionFingerprintRequired: "\u0645\u0637\u0644\u0648\u0628 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0628\u0635\u0645\u0629 \u0627\u0644\u062C\u0644\u0633\u0629",
    sessionFingerprintInvalid: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642 \u0627\u0644\u0623\u0645\u0646\u064A. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    tokenRefreshFailed: "\u0641\u0634\u0644 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u062C\u0644\u0633\u0629. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    invalidRefreshToken: "\u0627\u0646\u062A\u0647\u062A \u0635\u0644\u0627\u062D\u064A\u0629 \u062C\u0644\u0633\u062A\u0643. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    tokenExpired: "\u0627\u0646\u062A\u0647\u062A \u0635\u0644\u0627\u062D\u064A\u0629 \u062C\u0644\u0633\u062A\u0643. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    invalidToken: "\u0631\u0645\u0632 \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    invalidOrMissingToken: "\u0631\u0645\u0632 \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u0645\u0641\u0642\u0648\u062F \u0623\u0648 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    missingAuthHeader: "\u0631\u0623\u0633 \u0627\u0644\u062A\u0641\u0648\u064A\u0636 \u0645\u0641\u0642\u0648\u062F",
    invalidAuthHeaderFormat: "\u062A\u0646\u0633\u064A\u0642 \u0631\u0623\u0633 \u0627\u0644\u062A\u0641\u0648\u064A\u0636 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
    authTypeMustBeBearer: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0646\u0648\u0639 \u0627\u0644\u062A\u0641\u0648\u064A\u0636 Bearer",
    tokenMissing: "\u0631\u0645\u0632 \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u0645\u0641\u0642\u0648\u062F",
    tokenEmpty: "\u0631\u0645\u0632 \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u0641\u0627\u0631\u063A",
    tokenAlgorithmNotPermitted: "\u062E\u0648\u0627\u0631\u0632\u0645\u064A\u0629 \u0631\u0645\u0632 \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u063A\u064A\u0631 \u0645\u0633\u0645\u0648\u062D \u0628\u0647\u0627",
    invalidTokenFormat: "\u062A\u0646\u0633\u064A\u0642 \u0631\u0645\u0632 \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
    signingKeyUnreachable: "\u062A\u0639\u0630\u0631 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    signingKeyNotFound: "\u062E\u0637\u0623 \u0641\u064A \u0625\u0639\u062F\u0627\u062F \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    userGroupsNotFound: "\u062E\u0637\u0623 \u0641\u064A \u0625\u0639\u062F\u0627\u062F \u062D\u0633\u0627\u0628 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    noGroupsAssigned: "\u0644\u0645 \u064A\u062A\u0645 \u062A\u0639\u064A\u064A\u0646 \u0635\u0644\u0627\u062D\u064A\u0627\u062A \u0644\u062D\u0633\u0627\u0628\u0643. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    portalAccountIdNotFound: "\u062E\u0637\u0623 \u0641\u064A \u0625\u0639\u062F\u0627\u062F \u0627\u0644\u062D\u0633\u0627\u0628. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    portalUserIdNotFound: "\u062E\u0637\u0623 \u0641\u064A \u0625\u0639\u062F\u0627\u062F \u0627\u0644\u062D\u0633\u0627\u0628. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    companyNameNotFound: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    token: {
      invalidPayload: "\u0631\u0645\u0632 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      linkExpired: "\u0627\u0646\u062A\u0647\u062A \u0635\u0644\u0627\u062D\u064A\u0629 \u0647\u0630\u0627 \u0627\u0644\u0631\u0627\u0628\u0637. \u064A\u0631\u062C\u0649 \u0637\u0644\u0628 \u0631\u0627\u0628\u0637 \u062C\u062F\u064A\u062F.",
      invalidLink: "\u0647\u0630\u0627 \u0627\u0644\u0631\u0627\u0628\u0637 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D. \u064A\u0631\u062C\u0649 \u0637\u0644\u0628 \u0631\u0627\u0628\u0637 \u062C\u062F\u064A\u062F.",
      invalidType: "\u0646\u0648\u0639 \u0627\u0644\u0631\u0645\u0632 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D"
    }
  }
};

// src/locales/ar/portal.json
var portal_default2 = {
  userProfile: {
    errors: {
      notAuthenticated: "\u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u063A\u064A\u0631 \u0645\u0635\u0631\u062D \u0644\u0647.",
      failedToFetch: "\u0641\u0634\u0644 \u0641\u064A \u062C\u0644\u0628 \u0645\u0644\u0641 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645."
    }
  },
  app: {
    errors: {
      rootElementNotFound: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0639\u0646\u0635\u0631 \u0627\u0644\u062C\u0630\u0631."
    }
  },
  backToTop: {
    label: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u0623\u0639\u0644\u0649"
  },
  navigation: {
    home: "\u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    serviceCatalogue: "\u062F\u0644\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0627\u062A",
    dashboards: "\u0644\u0648\u062D\u0627\u062A \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A",
    myServices: "\u062E\u062F\u0645\u0627\u062A\u064A",
    supportTickets: "\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u062F\u0639\u0645",
    myTasks: "\u0645\u0647\u0627\u0645\u064A",
    openMenu: "\u0641\u062A\u062D \u0642\u0627\u0626\u0645\u0629 \u0627\u0644\u062A\u0646\u0642\u0644",
    closeMenu: "\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u0642\u0627\u0626\u0645\u0629",
    superAdmin: "\u0627\u0644\u0645\u0634\u0631\u0641 \u0627\u0644\u0639\u0627\u0645",
    catalogSyncPublish: "\u0645\u0632\u0627\u0645\u0646\u0629 \u0648\u0646\u0634\u0631 \u0627\u0644\u0643\u062A\u0627\u0644\u0648\u062C"
  },
  myTasks: {
    title: "\u0645\u0647\u0627\u0645\u064A",
    emptyHeading: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0645\u0647\u0627\u0645",
    emptyText: "\u0644\u0645 \u062A\u062A\u0644\u0642\u064E\u0651 \u0623\u064A \u0645\u0647\u0627\u0645 \u0628\u0639\u062F. \u0628\u0645\u062C\u0631\u062F \u062A\u0644\u0642\u064A\u0643 \u0645\u0647\u0645\u0629\u060C \u0633\u062A\u0638\u0647\u0631 \u0647\u0646\u0627.",
    loading: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0647\u0627\u0645...",
    scrollForMore: "\u0645\u0631\u0631 \u0644\u0645\u0632\u064A\u062F...",
    errorHeading: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627",
    errorLoading: "\u062E\u0637\u0623 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0647\u0627\u0645:",
    today: "\u0627\u0644\u064A\u0648\u0645",
    yesterday: "\u0623\u0645\u0633"
  },
  header: {
    myAccount: "\u062D\u0633\u0627\u0628\u064A",
    logout: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062E\u0631\u0648\u062C",
    notifications: "\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A",
    navigateHome: "\u0627\u0646\u062A\u0642\u0644 \u0625\u0644\u0649 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    userProfile: "\u0645\u0644\u0641 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645"
  },
  notifications: {
    title: "\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A",
    loading: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A...",
    empty: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0625\u0634\u0639\u0627\u0631\u0627\u062A",
    unreadAriaLabel: "\u0625\u0634\u0639\u0627\u0631 \u063A\u064A\u0631 \u0645\u0642\u0631\u0648\u0621",
    unreadNotificationsAriaLabel: "\u0625\u0634\u0639\u0627\u0631\u0627\u062A \u063A\u064A\u0631 \u0645\u0642\u0631\u0648\u0621\u0629",
    viewAllAriaLabel: "\u0639\u0631\u0636 \u062C\u0645\u064A\u0639 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A",
    sortNewestFirstAria: "\u0627\u0644\u062A\u0631\u062A\u064A\u0628: \u0627\u0644\u0623\u062D\u062F\u062B \u0623\u0648\u0644\u0627\u064B. \u0627\u0646\u0642\u0631 \u0644\u0644\u062A\u0628\u062F\u064A\u0644 \u0625\u0644\u0649 \u0627\u0644\u0623\u0642\u062F\u0645 \u0623\u0648\u0644\u0627\u064B.",
    sortOldestFirstAria: "\u0627\u0644\u062A\u0631\u062A\u064A\u0628: \u0627\u0644\u0623\u0642\u062F\u0645 \u0623\u0648\u0644\u0627\u064B. \u0627\u0646\u0642\u0631 \u0644\u0644\u062A\u0628\u062F\u064A\u0644 \u0625\u0644\u0649 \u0627\u0644\u0623\u062D\u062F\u062B \u0623\u0648\u0644\u0627\u064B.",
    markAllAsReadButton: "\u062A\u062D\u062F\u064A\u062F \u0627\u0644\u0643\u0644 \u0643\u0645\u0642\u0631\u0648\u0621",
    thirtyDayInfo: "\u062A\u0639\u0631\u0636 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A \u0644\u0622\u062E\u0631 30 \u064A\u0648\u0645\u0627\u064B \u0641\u0642\u0637",
    noNotificationsAvailable: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0625\u0634\u0639\u0627\u0631\u0627\u062A \u0645\u062A\u0627\u062D\u0629!",
    scrollForMore: "\u0645\u0631\u0631 \u0644\u0645\u0632\u064A\u062F...",
    errorLoading: "\u062E\u0637\u0623 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A:",
    today: "\u0627\u0644\u064A\u0648\u0645",
    yesterday: "\u0623\u0645\u0633",
    sortAriaLabel: "\u062A\u0631\u062A\u064A\u0628 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A"
  },
  websocketTestPanel: {
    title: "\u0644\u0648\u062D\u0629 \u0627\u062E\u062A\u0628\u0627\u0631 WebSocket",
    testMessage: "\u0645\u0631\u062D\u0628\u0627\u064B WebSocket!",
    notificationTitle: "\u0625\u0634\u0639\u0627\u0631 \u0627\u062E\u062A\u0628\u0627\u0631",
    notificationMessage: "\u0647\u0630\u0627 \u0625\u0634\u0639\u0627\u0631 \u0627\u062E\u062A\u0628\u0627\u0631 \u0645\u0646 \u0644\u0648\u062D\u0629 \u0627\u0644\u062A\u062D\u0643\u0645.",
    connecting: "\u062D\u0627\u0644\u0629 \u0627\u0644\u0627\u062A\u0635\u0627\u0644: \u062C\u0627\u0631\u064D \u0627\u0644\u0627\u062A\u0635\u0627\u0644 \u0628\u062E\u0627\u062F\u0645 WebSocket...",
    connected: "\u2705 \u062D\u0627\u0644\u0629 \u0627\u0644\u0627\u062A\u0635\u0627\u0644: \u062A\u0645 \u0627\u0644\u0627\u062A\u0635\u0627\u0644 \u0628\u0646\u062C\u0627\u062D \u0628\u062E\u0627\u062F\u0645 WebSocket",
    error: "\u274C \u062D\u0627\u0644\u0629 \u0627\u0644\u0627\u062A\u0635\u0627\u0644: \u062E\u0637\u0623 - {error}",
    disconnected: "\u062D\u0627\u0644\u0629 \u0627\u0644\u0627\u062A\u0635\u0627\u0644: \u062A\u0645 \u0642\u0637\u0639 \u0627\u0644\u0627\u062A\u0635\u0627\u0644 \u0639\u0646 \u062E\u0627\u062F\u0645 WebSocket",
    attemptingConnect: "\u062C\u0627\u0631\u064D \u0645\u062D\u0627\u0648\u0644\u0629 \u0627\u0644\u0627\u062A\u0635\u0627\u0644 \u0628\u0640 WebSocket...",
    disconnecting: "\u062C\u0627\u0631\u064D \u0642\u0637\u0639 \u0627\u0644\u0627\u062A\u0635\u0627\u0644 \u0639\u0646 WebSocket...",
    sendTestMessage: "\u0625\u0631\u0633\u0627\u0644 \u0631\u0633\u0627\u0644\u0629 \u0627\u062E\u062A\u0628\u0627\u0631",
    sendNotification: "\u0625\u0631\u0633\u0627\u0644 \u0625\u0634\u0639\u0627\u0631",
    connect: "\u0627\u062A\u0635\u0627\u0644",
    disconnect: "\u0642\u0637\u0639 \u0627\u0644\u0627\u062A\u0635\u0627\u0644",
    clearAllNotifications: "\u0645\u0633\u062D \u062C\u0645\u064A\u0639 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A",
    markAllAsRead: "\u062A\u062D\u062F\u064A\u062F \u0627\u0644\u0643\u0644 \u0643\u0645\u0642\u0631\u0648\u0621",
    subscribeToAll: "\u0627\u0644\u0627\u0634\u062A\u0631\u0627\u0643 \u0641\u064A \u0627\u0644\u0643\u0644",
    unsubscribeFromAll: "\u0625\u0644\u063A\u0627\u0621 \u0627\u0644\u0627\u0634\u062A\u0631\u0627\u0643 \u0645\u0646 \u0627\u0644\u0643\u0644",
    testResults: "\u0646\u062A\u0627\u0626\u062C \u0627\u0644\u0627\u062E\u062A\u0628\u0627\u0631",
    notifications: "\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A",
    unreadCount: "\u0639\u062F\u062F \u063A\u064A\u0631 \u0627\u0644\u0645\u0642\u0631\u0648\u0621",
    connectionStatus: "\u062D\u0627\u0644\u0629 \u0627\u0644\u0627\u062A\u0635\u0627\u0644",
    lastMessage: "\u0622\u062E\u0631 \u0631\u0633\u0627\u0644\u0629",
    stats: "\u0627\u0644\u0625\u062D\u0635\u0627\u0626\u064A\u0627\u062A",
    clients: "\u0627\u0644\u0639\u0645\u0644\u0627\u0621",
    testMessageLabel: "\u0631\u0633\u0627\u0644\u0629 \u0627\u0644\u0627\u062E\u062A\u0628\u0627\u0631",
    notificationTitleLabel: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0625\u0634\u0639\u0627\u0631",
    notificationMessageLabel: "\u0631\u0633\u0627\u0644\u0629 \u0627\u0644\u0625\u0634\u0639\u0627\u0631",
    notificationType: "\u0646\u0648\u0639 \u0627\u0644\u0625\u0634\u0639\u0627\u0631",
    notificationPriority: "\u0623\u0648\u0644\u0648\u064A\u0629 \u0627\u0644\u0625\u0634\u0639\u0627\u0631",
    targetUserId: "\u0645\u0639\u0631\u0641 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0627\u0644\u0645\u0633\u062A\u0647\u062F\u0641",
    userSpecific: "\u062E\u0627\u0635 \u0628\u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645",
    medium: "\u0645\u062A\u0648\u0633\u0637",
    send: "\u0625\u0631\u0633\u0627\u0644",
    subscribe: "\u0627\u0634\u062A\u0631\u0627\u0643",
    unsubscribe: "\u0625\u0644\u063A\u0627\u0621 \u0627\u0634\u062A\u0631\u0627\u0643",
    markAsRead: "\u062A\u062D\u062F\u064A\u062F \u0643\u0645\u0642\u0631\u0648\u0621",
    clearAll: "\u0645\u0633\u062D \u0627\u0644\u0643\u0644",
    testWebSocketConnection: "\u0627\u062E\u062A\u0628\u0627\u0631 \u0627\u062A\u0635\u0627\u0644 WebSocket",
    testConnection: "\u0627\u062E\u062A\u0628\u0627\u0631 \u0627\u0644\u0627\u062A\u0635\u0627\u0644"
  },
  session: {
    inactivityWarning: "\u062A\u062D\u0630\u064A\u0631 \u0639\u062F\u0645 \u0627\u0644\u0646\u0634\u0627\u0637",
    inactivityDescription: '\u0644\u0642\u062F \u0643\u0646\u062A \u063A\u064A\u0631 \u0646\u0634\u0637 \u0644\u0641\u062A\u0631\u0629 \u0645\u0646 \u0627\u0644\u0648\u0642\u062A. \u0633\u064A\u062A\u0645 \u062A\u0633\u062C\u064A\u0644 \u062E\u0631\u0648\u062C\u0643 \u062E\u0644\u0627\u0644 {time} \u0628\u0633\u0628\u0628 \u0639\u062F\u0645 \u0627\u0644\u0646\u0634\u0627\u0637. \u0627\u0646\u0642\u0631 \u0639\u0644\u0649 "\u0627\u0644\u0628\u0642\u0627\u0621 \u0645\u0633\u062C\u0644\u0627\u064B" \u0644\u0645\u062A\u0627\u0628\u0639\u0629 \u062C\u0644\u0633\u062A\u0643.',
    logout: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062E\u0631\u0648\u062C",
    stayLoggedIn: "\u0627\u0644\u0628\u0642\u0627\u0621 \u0645\u0633\u062C\u0644\u0627\u064B"
  },
  home: {
    logoAlt: "\u0627\u0644\u0634\u0639\u0627\u0631",
    myTasks: "\u0645\u0647\u0627\u0645\u064A",
    tasksSubtitle: "\u0623\u062D\u062F\u062B 3 \u0645\u0647\u0627\u0645 \u0645\u0639\u0631\u0648\u0636\u0629 \u0647\u0646\u0627",
    myServices: "\u062E\u062F\u0645\u0627\u062A\u064A",
    servicesSubtitle: "\u0623\u062D\u062F\u062B 3 \u062E\u062F\u0645\u0627\u062A \u0645\u0639\u0631\u0648\u0636\u0629 \u0647\u0646\u0627.",
    viewAll: "\u0639\u0631\u0636 \u0627\u0644\u0643\u0644",
    tilesLoadError: "\u0641\u0634\u0644 \u062A\u062D\u0645\u064A\u0644 \u0628\u0644\u0627\u0637\u0627\u062A \u0627\u0644\u0635\u0641\u062D\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629.",
    loadingServices: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u062E\u062F\u0645\u0627\u062A..."
  },
  status: {
    inProgress: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
    active: "\u0627\u0634\u062A\u0631\u0627\u0643 \u0646\u0634\u0637",
    pending: "\u0645\u0639\u0644\u0642",
    moreInfoRequired: "\u064A\u0644\u0632\u0645 \u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A",
    new: "\u062C\u062F\u064A\u062F",
    closed: "\u0645\u063A\u0644\u0642",
    resolved: "\u062A\u0645 \u062D\u0644\u0647\u0627",
    open: "\u0645\u0641\u062A\u0648\u062D",
    awaitingCustomer: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0639\u0645\u064A\u0644",
    unknown: "\u063A\u064A\u0631 \u0645\u0639\u0631\u0648\u0641"
  },
  activityStatus: {
    requested: "\u0645\u0637\u0644\u0648\u0628",
    inProgress: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
    awaiting: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631",
    closed: "\u0645\u063A\u0644\u0642",
    closedIncomplete: "\u0645\u063A\u0644\u0642 \u063A\u064A\u0631 \u0645\u0643\u062A\u0645\u0644"
  },
  ticketStatus: {
    new: "\u062C\u062F\u064A\u062F",
    open: "\u0645\u0641\u062A\u0648\u062D",
    awaitingCustomer: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0639\u0645\u064A\u0644",
    onHold: "\u0645\u0639\u0644\u0642",
    awaiting3rdPartyInfo: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0645\u0646 \u0637\u0631\u0641 \u062B\u0627\u0644\u062B",
    resolved: "\u062A\u0645 \u0627\u0644\u062D\u0644",
    closed: "\u0645\u063A\u0644\u0642",
    canceled: "\u0645\u0644\u063A\u0649",
    unknown: "\u063A\u064A\u0631 \u0645\u0639\u0631\u0648\u0641"
  },
  ticketAge: {
    SAME_DAY: "\u0646\u0641\u0633 \u0627\u0644\u064A\u0648\u0645",
    TWO_DAYS: "\u064A\u0648\u0645\u0627\u0646",
    TWO_TO_FIVE: "2-5 \u0623\u064A\u0627\u0645",
    FIVE_TO_SEVEN: "5-7 \u0623\u064A\u0627\u0645",
    ONE_TO_TWO_W: "1-2 \u0623\u0633\u0628\u0648\u0639",
    TWO_TO_FOUR_W: "2-4 \u0623\u0633\u0627\u0628\u064A\u0639",
    ONE_TO_TWO_M: "1-2 \u0634\u0647\u0631",
    OVER_TWO_M: "\u0623\u0643\u062B\u0631 \u0645\u0646 \u0634\u0647\u0631\u064A\u0646"
  },
  myAccount: {
    title: "\u062D\u0633\u0627\u0628\u064A",
    failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628",
    retry: "\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629",
    tabs: {
      myDetails: "\u0628\u064A\u0627\u0646\u0627\u062A\u064A",
      companyDetails: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
      myTeam: "\u0641\u0631\u064A\u0642\u064A",
      documents: "\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A"
    },
    myDetails: {
      failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645",
      loadingDetails: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645...",
      loadingForm: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0646\u0645\u0648\u0630\u062C...",
      edit: "\u062A\u0639\u062F\u064A\u0644",
      cancel: "\u0625\u0644\u063A\u0627\u0621",
      save: "\u062D\u0641\u0638",
      saving: "\u062C\u0627\u0631\u064D \u0627\u0644\u062D\u0641\u0638...",
      personalInformation: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0634\u062E\u0635\u064A\u0629",
      activeSince: "\u0646\u0634\u0637 \u0645\u0646\u0630 {date}"
    },
    companyDetails: {
      editButton: "\u062A\u0639\u062F\u064A\u0644",
      loadingDetails: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629...",
      failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      logoUploadError: "\u0641\u0634\u0644 \u0641\u064A \u0631\u0641\u0639 \u0627\u0644\u0634\u0639\u0627\u0631. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      contentNotAvailable: "\u0627\u0644\u0645\u062D\u062A\u0648\u0649 \u063A\u064A\u0631 \u0645\u062A\u0627\u062D \u0644\u0644\u0634\u0631\u0643\u0627\u062A \u0642\u064A\u062F \u0627\u0644\u062A\u0637\u0648\u064A\u0631.",
      changesUnderReview: "\u0627\u0644\u062A\u0639\u062F\u064A\u0644\u0627\u062A \u0627\u0644\u062A\u064A \u0642\u0645\u062A \u0628\u0647\u0627 \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629\u060C \u0648\u0633\u064A\u062A\u0645 \u062A\u062D\u062F\u064A\u062B\u0647\u0627 \u0628\u0645\u062C\u0631\u062F \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u064A\u0647\u0627.",
      companyTypes: {
        international: "\u0634\u0631\u0643\u0629 \u062F\u0648\u0644\u064A\u0629",
        ksaPrivate: "\u0634\u0631\u0643\u0629 \u0633\u0639\u0648\u062F\u064A\u0629 \u062E\u0627\u0635\u0629",
        pifEstablished: "\u0634\u0631\u0643\u0629 \u0645\u0624\u0633\u0633\u0629 \u0645\u0646 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629",
        pifUnderDevelopment: "\u0634\u0631\u0643\u0629 \u0642\u064A\u062F \u0627\u0644\u062A\u0637\u0648\u064A\u0631 \u0645\u0646 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629"
      },
      gccCountries: {
        "Saudi Arabia": "\u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
        "United Arab Emirates": "\u0627\u0644\u0625\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0645\u062A\u062D\u062F\u0629",
        Kuwait: "\u0627\u0644\u0643\u0648\u064A\u062A",
        Qatar: "\u0642\u0637\u0631",
        Bahrain: "\u0627\u0644\u0628\u062D\u0631\u064A\u0646",
        Oman: "\u0633\u0644\u0637\u0646\u0629 \u0639\u064F\u0645\u0627\u0646"
      },
      countries: {
        Afghanistan: "\u0623\u0641\u063A\u0627\u0646\u0633\u062A\u0627\u0646",
        Albania: "\u0623\u0644\u0628\u0627\u0646\u064A\u0627",
        Algeria: "\u0627\u0644\u062C\u0632\u0627\u0626\u0631",
        Argentina: "\u0627\u0644\u0623\u0631\u062C\u0646\u062A\u064A\u0646",
        Australia: "\u0623\u0633\u062A\u0631\u0627\u0644\u064A\u0627",
        Austria: "\u0627\u0644\u0646\u0645\u0633\u0627",
        Bahrain: "\u0627\u0644\u0628\u062D\u0631\u064A\u0646",
        Bangladesh: "\u0628\u0646\u063A\u0644\u0627\u062F\u064A\u0634",
        Belgium: "\u0628\u0644\u062C\u064A\u0643\u0627",
        Brazil: "\u0627\u0644\u0628\u0631\u0627\u0632\u064A\u0644",
        Canada: "\u0643\u0646\u062F\u0627",
        China: "\u0627\u0644\u0635\u064A\u0646",
        Egypt: "\u0645\u0635\u0631",
        France: "\u0641\u0631\u0646\u0633\u0627",
        Germany: "\u0623\u0644\u0645\u0627\u0646\u064A\u0627",
        India: "\u0627\u0644\u0647\u0646\u062F",
        Indonesia: "\u0625\u0646\u062F\u0648\u0646\u064A\u0633\u064A\u0627",
        Iran: "\u0625\u064A\u0631\u0627\u0646",
        Iraq: "\u0627\u0644\u0639\u0631\u0627\u0642",
        Italy: "\u0625\u064A\u0637\u0627\u0644\u064A\u0627",
        Japan: "\u0627\u0644\u064A\u0627\u0628\u0627\u0646",
        Jordan: "\u0627\u0644\u0623\u0631\u062F\u0646",
        Kuwait: "\u0627\u0644\u0643\u0648\u064A\u062A",
        Lebanon: "\u0644\u0628\u0646\u0627\u0646",
        Malaysia: "\u0645\u0627\u0644\u064A\u0632\u064A\u0627",
        Morocco: "\u0627\u0644\u0645\u063A\u0631\u0628",
        Netherlands: "\u0647\u0648\u0644\u0646\u062F\u0627",
        Nigeria: "\u0646\u064A\u062C\u064A\u0631\u064A\u0627",
        Oman: "\u0633\u0644\u0637\u0646\u0629 \u0639\u064F\u0645\u0627\u0646",
        Pakistan: "\u0628\u0627\u0643\u0633\u062A\u0627\u0646",
        Qatar: "\u0642\u0637\u0631",
        "Saudi Arabia": "\u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
        Singapore: "\u0633\u0646\u063A\u0627\u0641\u0648\u0631\u0629",
        "South Africa": "\u062C\u0646\u0648\u0628 \u0623\u0641\u0631\u064A\u0642\u064A\u0627",
        "South Korea": "\u0643\u0648\u0631\u064A\u0627 \u0627\u0644\u062C\u0646\u0648\u0628\u064A\u0629",
        Spain: "\u0625\u0633\u0628\u0627\u0646\u064A\u0627",
        Switzerland: "\u0633\u0648\u064A\u0633\u0631\u0627",
        Turkey: "\u062A\u0631\u0643\u064A\u0627",
        "United Arab Emirates": "\u0627\u0644\u0625\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0645\u062A\u062D\u062F\u0629",
        "United Kingdom": "\u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0645\u062A\u062D\u062F\u0629",
        "United States": "\u0627\u0644\u0648\u0644\u0627\u064A\u0627\u062A \u0627\u0644\u0645\u062A\u062D\u062F\u0629 \u0627\u0644\u0623\u0645\u0631\u064A\u0643\u064A\u0629",
        Yemen: "\u0627\u0644\u064A\u0645\u0646"
      }
    },
    myTeam: {
      add: "\u0625\u0636\u0627\u0641\u0629",
      cancel: "\u0625\u0644\u063A\u0627\u0621",
      save: "\u062D\u0641\u0638",
      saving: "\u062C\u0627\u0631\u064D \u0627\u0644\u062D\u0641\u0638...",
      failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0623\u0639\u0636\u0627\u0621 \u0627\u0644\u0641\u0631\u064A\u0642",
      loadingMembers: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0623\u0639\u0636\u0627\u0621 \u0627\u0644\u0641\u0631\u064A\u0642...",
      loadingTable: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062C\u062F\u0648\u0644...",
      updatingMembers: "\u062C\u0627\u0631\u064D \u0627\u0644\u062A\u062D\u062F\u064A\u062B...",
      loadingMore: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0641\u0631\u0642...",
      noTeamMembers: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0623\u0639\u0636\u0627\u0621 \u0641\u064A \u0627\u0644\u0641\u0631\u064A\u0642",
      noTeamMembersDescription: "\u0627\u0628\u062F\u0623 \u0628\u0625\u0636\u0627\u0641\u0629 \u0623\u0648\u0644 \u0639\u0636\u0648 \u0641\u064A \u0627\u0644\u0641\u0631\u064A\u0642 \u0644\u0644\u062A\u0639\u0627\u0648\u0646 \u0641\u064A \u0627\u0644\u0645\u0634\u0627\u0631\u064A\u0639.",
      noMatchingTeamMembers: "\u0644\u0627 \u064A\u0648\u062C\u062F \u0623\u0639\u0636\u0627\u0621 \u0641\u0631\u064A\u0642 \u064A\u0637\u0627\u0628\u0642\u0648\u0646 \u0628\u062D\u062B\u0643 \u0623\u0648 \u0627\u0644\u0641\u0644\u0627\u062A\u0631",
      noMatchingTeamMembersDescription: "\u062D\u0627\u0648\u0644 \u062A\u0639\u062F\u064A\u0644 \u0628\u062D\u062B\u0643 \u0623\u0648 \u0627\u0644\u0641\u0644\u0627\u062A\u0631 \u062B\u0645 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      emailAlreadyExists: "\u064A\u0648\u062C\u062F \u0628\u0627\u0644\u0641\u0639\u0644 \u0639\u0636\u0648 \u0641\u0631\u064A\u0642 \u0628\u0647\u0630\u0627 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
      addedSuccessfully: "\u062A\u0645\u062A \u0625\u0636\u0627\u0641\u0629 \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642 \u0628\u0646\u062C\u0627\u062D",
      failedToUpdate: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u062F\u064A\u062B \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      failedToCreate: "\u0641\u0634\u0644 \u0641\u064A \u0625\u0646\u0634\u0627\u0621 \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      cannotPerformAction: "\u0644\u0627 \u064A\u0645\u0643\u0646 \u062A\u0646\u0641\u064A\u0630 \u0647\u0630\u0627 \u0627\u0644\u0625\u062C\u0631\u0627\u0621. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u062D\u0627\u0644\u0629 \u0627\u0644\u0639\u0645\u064A\u0644.",
      failedToUpdateStatus: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u062F\u064A\u062B \u062D\u0627\u0644\u0629 \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      columns: {
        name: "\u0627\u0644\u0627\u0633\u0645",
        email: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
        functions: "\u0627\u0644\u0648\u0638\u0627\u0626\u0641",
        status: "\u0627\u0644\u062D\u0627\u0644\u0629",
        admin: "\u0645\u0633\u0624\u0648\u0644"
      },
      filters: {
        searchPlaceholder: "\u0627\u0644\u0628\u062D\u062B \u0628\u0627\u0644\u0627\u0633\u0645 \u0623\u0648 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0623\u0648 \u0627\u0644\u0648\u0638\u064A\u0641\u0629",
        statusPlaceholder: "\u0627\u0644\u062A\u0635\u0641\u064A\u0629 \u062D\u0633\u0628 \u0627\u0644\u062D\u0627\u0644\u0629",
        functionPlaceholder: "\u0627\u0644\u062A\u0635\u0641\u064A\u0629 \u062D\u0633\u0628 \u0627\u0644\u0648\u0638\u064A\u0641\u0629"
      },
      permissions: {
        function: "\u0627\u0644\u0648\u0638\u064A\u0641\u0629",
        requester: "\u0645\u0642\u062F\u0645 \u0627\u0644\u0637\u0644\u0628",
        reviewer: "\u0627\u0644\u0645\u062F\u0642\u0642",
        approver: "\u0627\u0644\u0645\u0639\u062A\u0645\u062F",
        functions: "\u0627\u0644\u0648\u0638\u0627\u0626\u0641",
        role: "\u0627\u0644\u062F\u0648\u0631",
        adminRole: "\u0645\u062F\u064A\u0631",
        adminRoleDesc: "\u0648\u0635\u0648\u0644 \u0643\u0627\u0645\u0644 \u0644\u062C\u0645\u064A\u0639 \u0627\u0644\u062E\u062F\u0645\u0627\u062A",
        operationTeamRole: "\u0641\u0631\u064A\u0642 \u0627\u0644\u0639\u0645\u0644\u064A\u0627\u062A",
        operationTeamRoleDesc: "\u062D\u062F\u062F \u0627\u0644\u0635\u0644\u0627\u062D\u064A\u0627\u062A \u0644\u0643\u0644 \u062E\u062F\u0645\u0629",
        access: "\u0627\u0644\u0648\u0635\u0648\u0644"
      },
      actions: {
        edit: "\u062A\u0639\u062F\u064A\u0644",
        deactivate: "\u062A\u0639\u0637\u064A\u0644",
        reactivate: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0641\u0639\u064A\u0644"
      },
      notAvailable: "\u063A\u064A\u0631 \u0645\u062A\u0627\u062D",
      deactivateModal: {
        deactivateTitle: "\u0625\u0644\u063A\u0627\u0621 \u062A\u0641\u0639\u064A\u0644 \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642",
        reactivateTitle: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0646\u0634\u064A\u0637 \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642",
        deactivateDescription: "\u0633\u064A\u0624\u062F\u064A \u062A\u0639\u0637\u064A\u0644 \u062D\u0633\u0627\u0628 \u0647\u0630\u0627 \u0627\u0644\u0639\u0636\u0648 \u0641\u064A \u0627\u0644\u0641\u0631\u064A\u0642 \u0625\u0644\u0649 \u0641\u0642\u062F\u0627\u0646\u0647 \u0635\u0644\u0627\u062D\u064A\u0629 \u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0628\u0648\u0627\u0628\u0629 \u0623\u0639\u0645\u0627\u0644 \u062A\u0633\u0627\u0645\u0649\u060C \u0648\u0623\u064A \u0648\u0638\u0627\u0626\u0641 \u0643\u0627\u0646 \u064A\u062A\u0645\u062A\u0639 \u0628\u0647\u0627 \u0633\u0627\u0628\u0642\u0627. \u0647\u0644 \u0623\u0646\u062A \u0645\u062A\u0623\u0643\u062F \u0645\u0646 \u0631\u063A\u0628\u062A\u0643 \u0641\u064A \u062A\u0639\u0637\u064A\u0644 \u062D\u0633\u0627\u0628 \u0647\u0630\u0627 \u0627\u0644\u0639\u0636\u0648\u061F",
        reactivateDescription: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628 \u0647\u0630\u0627 \u0627\u0644\u0639\u0636\u0648 \u0641\u064A \u0627\u0644\u0641\u0631\u064A\u0642 \u0633\u064A\u0645\u0646\u062D\u0647 \u0625\u0645\u0643\u0627\u0646\u064A\u0629 \u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0628\u0648\u0627\u0628\u0629 \u0623\u0639\u0645\u0627\u0644 \u062A\u0633\u0627\u0645\u0649\u060C \u0628\u0627\u0644\u0625\u0636\u0627\u0641\u0629 \u0625\u0644\u0649 \u062C\u0645\u064A\u0639 \u0627\u0644\u0648\u0638\u0627\u0626\u0641 \u0627\u0644\u062A\u064A \u0643\u0627\u0646 \u064A\u062A\u0645\u062A\u0639 \u0628\u0647\u0627 \u0633\u0627\u0628\u0642\u0627\u064B. \u0647\u0644 \u0623\u0646\u062A \u0645\u062A\u0623\u0643\u062F \u0645\u0646 \u0631\u063A\u0628\u062A\u0643 \u0641\u064A \u0625\u0639\u0627\u062F\u0629 \u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628 \u0647\u0630\u0627 \u0627\u0644\u0639\u0636\u0648\u061F",
        noCancel: "\u0644\u0627\u060C \u0625\u0644\u063A\u0627\u0621",
        yesDeactivate: "\u0646\u0639\u0645\u060C \u0625\u0644\u063A\u0627\u0621 \u0627\u0644\u062A\u0641\u0639\u064A\u0644",
        yesReactivate: "\u0646\u0639\u0645\u060C \u0623\u0639\u062F \u0627\u0644\u062A\u0646\u0634\u064A\u0637"
      },
      cancelModal: {
        title: "\u0644\u0646 \u064A\u062A\u0645 \u062D\u0641\u0638 \u0628\u064A\u0627\u0646\u0627\u062A\u0643",
        description: "\u0647\u0644 \u0623\u0646\u062A \u0645\u062A\u0623\u0643\u062F \u0645\u0646 \u0631\u063A\u0628\u062A\u0643 \u0641\u064A \u0627\u0644\u0625\u0644\u063A\u0627\u0621\u061F \u0633\u062A\u064F\u0641\u0642\u062F \u0623\u064A \u062A\u063A\u064A\u064A\u0631\u0627\u062A \u063A\u064A\u0631 \u0645\u062D\u0641\u0648\u0638\u0629.",
        no: "\u0644\u0627",
        yesCancel: "\u0646\u0639\u0645\u060C \u0625\u0644\u063A\u0627\u0621"
      },
      reactivate: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0641\u0639\u064A\u0644",
      deactivate: "\u0625\u0644\u063A\u0627\u0621 \u062A\u0641\u0639\u064A\u0644"
    },
    keyAccountManager: {
      title: "\u0645\u062F\u064A\u0631 \u062D\u0633\u0627\u0628\u0643 \u0627\u0644\u0631\u0626\u064A\u0633\u064A",
      name: "\u0627\u0644\u0627\u0633\u0645:",
      phoneNumber: "\u0631\u0642\u0645 \u0627\u0644\u0647\u0627\u062A\u0641:",
      email: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A:"
    }
  },
  serviceCatalogue: {
    title: "\u0643\u062A\u0627\u0644\u0648\u062C \u0627\u0644\u062E\u062F\u0645\u0627\u062A",
    bannerTitle: '<span class="text-fg-brand-gold">\u062E\u0628\u0631\u0627\u062A\u0646\u0627</span>',
    bannerDescription: "\u062F\u0639\u0645 \u0627\u0644\u0645\u0646\u0638\u0645\u0627\u062A \u0645\u0646 \u0645\u0631\u062D\u0644\u0629 \u0627\u0644\u062A\u0623\u0633\u064A\u0633 \u0648\u062D\u062A\u0649 \u0627\u0644\u0646\u0636\u062C",
    loading: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0643\u062A\u0627\u0644\u0648\u062C \u0627\u0644\u062E\u062F\u0645\u0627\u062A...",
    loadingServices: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0627\u062A...",
    failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0643\u062A\u0627\u0644\u0648\u062C \u0627\u0644\u062E\u062F\u0645\u0627\u062A",
    promoText: "\u0628\u062E\u0628\u0631\u0627\u062A\u0646\u0627 \u0627\u0644\u0639\u0645\u064A\u0642\u0629\u060C \u0635\u064F\u0645\u0645\u062A \u062D\u0644\u0648\u0644\u0646\u0627 \u0644\u062A\u0643\u0648\u0646 \u0645\u0631\u0646\u0629\u060C \u0631\u0642\u0645\u064A\u0629 \u0628\u0627\u0644\u0643\u0627\u0645\u0644\u060C \u0648\u0645\u062E\u0635\u0635\u0629 \u0644\u062A\u0644\u0628\u064A\u0629 \u0627\u0644\u0627\u062D\u062A\u064A\u0627\u062C\u0627\u062A \u0627\u0644\u0645\u062A\u063A\u064A\u0631\u0629 \u0644\u0644\u0645\u0646\u0638\u0645\u0627\u062A. \u0627\u062E\u062A\u0631 \u0641\u0626\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0644\u0627\u0633\u062A\u0639\u0631\u0627\u0636 \u0627\u0644\u062D\u0644\u0648\u0644 \u0627\u0644\u0645\u062A\u0627\u062D\u0629.",
    learnMore: "\u0627\u0639\u0631\u0641 \u0627\u0644\u0645\u0632\u064A\u062F",
    goBack: "\u0627\u0644\u0639\u0648\u062F\u0629",
    scrollPrevious: "\u0627\u0644\u062A\u0645\u0631\u064A\u0631 \u0625\u0644\u0649 \u0627\u0644\u0633\u0627\u0628\u0642",
    scrollNext: "\u0627\u0644\u062A\u0645\u0631\u064A\u0631 \u0625\u0644\u0649 \u0627\u0644\u062A\u0627\u0644\u064A",
    noServicesAvailable: "\u0644\u0627 \u062A\u0648\u062C\u062F \u062E\u062F\u0645\u0627\u062A \u0645\u062A\u0627\u062D\u0629 \u0644\u0647\u0630\u0647 \u0627\u0644\u0641\u0626\u0629.",
    noServicesFound: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u062E\u062F\u0645\u0627\u062A",
    searchNoResults: '\u0644\u0645 \u064A\u0637\u0627\u0628\u0642 \u0628\u062D\u062B\u0643 "{searchTerm}" \u0623\u064A \u062E\u062F\u0645\u0627\u062A. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.',
    allCategories: "\u062C\u0645\u064A\u0639 \u0627\u0644\u0641\u0626\u0627\u062A",
    requestService: "\u0637\u0644\u0628 \u0627\u0644\u062E\u062F\u0645\u0629",
    enquireNow: "\u0627\u0633\u062A\u0641\u0633\u0631 \u0627\u0644\u0622\u0646",
    valueToBusiness: "\u0627\u0644\u0642\u064A\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629 \u0644\u0644\u0623\u0639\u0645\u0627\u0644",
    prerequisites: "\u0627\u0644\u0645\u062A\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u0645\u0633\u0628\u0642\u0629",
    bannerAriaLabel: "\u0634\u0639\u0627\u0631 \u0643\u062A\u0627\u0644\u0648\u062C \u0627\u0644\u062E\u062F\u0645\u0627\u062A",
    timeToProposalSla_label: "\u0645\u062F\u0629 \u062A\u0642\u062F\u064A\u0645 \u0627\u0644\u0639\u0631\u0636:",
    timeToProposalSla_value: "{value} \u0623\u064A\u0627\u0645 \u0639\u0645\u0644",
    serviceTierTable_serviceTier: "\u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629",
    serviceTierTable_description: "\u0627\u0644\u0648\u0635\u0641",
    serviceTierTable_benefits: "\u0627\u0644\u0645\u0632\u0627\u064A\u0627",
    availableWithBundles: "\u0645\u062A\u0648\u0641\u0631\u0629 \u0636\u0645\u0646 \u0627\u0644\u0628\u0627\u0642\u0627\u062A \u0627\u0644\u062A\u0627\u0644\u064A\u0629:",
    requestBundle: "\u0637\u0644\u0628 \u0627\u0644\u0628\u0627\u0642\u0629",
    viewOptions: "\u0639\u0631\u0636 \u0627\u0644\u062E\u064A\u0627\u0631\u0627\u062A",
    serviceOptions: {
      title: "\u062E\u064A\u0627\u0631\u0627\u062A \u0627\u0644\u062E\u062F\u0645\u0629",
      description: "\u064A\u0645\u0643\u0646 \u0637\u0644\u0628 \u0647\u0630\u0647 \u0627\u0644\u062E\u062F\u0645\u0629 \u0628\u0634\u0643\u0644 \u0641\u0631\u062F\u064A \u0623\u0648 \u0643\u062C\u0632\u0621 \u0645\u0646 \u0628\u0627\u0642\u0629.",
      viewDetails: "\u0639\u0631\u0636 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644"
    },
    badges: {
      service: "\u062E\u062F\u0645\u0629",
      bundle: "\u0628\u0627\u0642\u0629",
      addOn: "\u0625\u0636\u0627\u0641\u0629"
    },
    groupHeadings: {
      bundles: "\u0627\u0644\u0628\u0627\u0642\u0627\u062A",
      services: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A",
      addOns: "\u0627\u0644\u0625\u0636\u0627\u0641\u0627\u062A"
    },
    tierNames: {
      Advanced: "\u0645\u062A\u0642\u062F\u0645",
      Bronze: "\u0628\u0631\u0648\u0646\u0632\u064A",
      Campaign: "\u062D\u0645\u0644\u0629",
      Elite: "\u0645\u062A\u0645\u064A\u0632",
      Enterprise: "\u0645\u0624\u0633\u0633\u064A",
      Essential: "\u0623\u0633\u0627\u0633\u064A",
      Gold: "\u0630\u0647\u0628\u064A",
      Platinum: "\u0628\u0644\u0627\u062A\u064A\u0646\u064A",
      Foundation: "\u062A\u0623\u0633\u064A\u0633\u064A",
      Silver: "\u0641\u0636\u064A",
      Standard: "\u0642\u064A\u0627\u0633\u064A",
      Strategic: "\u0627\u0633\u062A\u0631\u0627\u062A\u064A\u062C\u064A"
    },
    sowBundle: {
      title: "\u0646\u0645\u0648\u0630\u062C \u0637\u0644\u0628 \u0627\u0644\u062D\u0632\u0645\u0629",
      description: "\u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u0628\u0639\u0636 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644 \u0644\u0645\u0633\u0627\u0639\u062F\u0629 \u062A\u0633\u0627\u0645\u0647 \u0641\u064A \u0625\u0639\u062F\u0627\u062F \u0639\u0631\u0636 \u0627\u0644\u062D\u0632\u0645\u0629 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0648\u0627\u0644\u0628\u062F\u0621 \u0641\u064A \u0627\u0644\u062A\u0639\u0627\u0642\u062F.",
      stepLabel: "\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
      validation: {
        timeframeRequired: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0627\u0644\u0625\u0637\u0627\u0631 \u0627\u0644\u0632\u0645\u0646\u064A \u0644\u0644\u0645\u0634\u0627\u0631\u0643\u0629",
        tooLarge: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0647\u0648 5 \u0645\u064A\u062C\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0633\u062A\u0646\u062F",
        invalidType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645. \u0627\u0644\u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0645\u0633\u0645\u0648\u062D \u0628\u0647\u0627: PDF\u060C PNG\u060C JPG"
      },
      fields: {
        bundleName: "\u0627\u0633\u0645 \u0627\u0644\u062D\u0632\u0645\u0629",
        addonsLabel: "\u0647\u0644 \u062A\u0631\u063A\u0628 \u0641\u064A \u062A\u0636\u0645\u064A\u0646 \u0625\u0636\u0627\u0641\u0627\u062A\u061F",
        timeframeLabel: "\u0645\u062A\u0649 \u062A\u062A\u0637\u0644\u0639 \u0625\u0644\u0649 \u0628\u062F\u0621 \u062E\u062F\u0645\u062A\u0643 \u0627\u0644\u0623\u0648\u0644\u0649\u061F",
        timeframePlaceholder: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0625\u0637\u0627\u0631 \u0627\u0644\u0632\u0645\u0646\u064A \u0644\u0644\u0645\u0634\u0627\u0631\u0643\u0629",
        requirementsLabel: "\u0648\u0635\u0641 \u0645\u062A\u0637\u0644\u0628\u0627\u062A\u0643 (\u0627\u062E\u062A\u064A\u0627\u0631\u064A)",
        requirementsPlaceholder: "\u0623\u0636\u0641 \u0623\u064A \u062A\u0648\u0642\u0639\u0627\u062A \u0644\u0644\u0645\u0634\u0627\u0631\u0643\u0629 \u0623\u0648 \u0646\u0637\u0627\u0642 \u0639\u0645\u0644 \u0645\u0648\u062C\u0632 \u062A\u0631\u063A\u0628 \u0641\u064A \u0625\u0639\u0644\u0627\u0645\u0646\u0627 \u0628\u0647. \u0628\u062F\u0644\u0627\u064B \u0645\u0646 \u0630\u0644\u0643\u060C \u064A\u0645\u0643\u0646\u0643 \u062A\u062D\u0645\u064A\u0644 \u0645\u0633\u062A\u0646\u062F \u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644.",
        fileSupportingText: "PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u062C\u0627\u0628\u0627\u064A\u062A)"
      }
    }
  },
  resultScreen: {
    successTitle: "\u0634\u0643\u0631\u0627\u064B \u0644\u0643",
    successDescription: "\u0633\u064A\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0643 \u0623\u062D\u062F \u0623\u0639\u0636\u0627\u0621 \u0641\u0631\u064A\u0642\u0646\u0627 \u0644\u0627\u0637\u0644\u0627\u0639\u0643 \u0639\u0644\u0649 \u0627\u0644\u062E\u0637\u0648\u0627\u062A \u0627\u0644\u062A\u0627\u0644\u064A\u0629.",
    errorTitle: "\u062D\u062F\u062B \u062E\u0637\u0623",
    errorDescription: "\u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0627\u062D\u0642\u0627\u064B.",
    proposal: {
      rejectedDescription: "\u0633\u064A\u0642\u0648\u0645 \u0645\u062F\u064A\u0631 \u062D\u0633\u0627\u0628\u0643 \u0628\u0645\u0631\u0627\u062C\u0639\u0629 \u0633\u0628\u0628 \u0627\u0644\u0631\u0641\u0636 \u0648\u0625\u0631\u0633\u0627\u0644 \u0639\u0631\u0636 \u062C\u062F\u064A\u062F \u0625\u0644\u064A\u0643 \u0644\u0645\u0631\u0627\u062C\u0639\u062A\u0647"
    }
  },
  serviceCard: {
    step: "\u0627\u0644\u062E\u0637\u0648\u0629 {currentStep} \u0645\u0646 {totalSteps}:",
    ariaLabel: "\u0628\u0637\u0627\u0642\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 {title}",
    serviceId: "\u0631\u0642\u0645 \u0627\u0644\u062E\u062F\u0645\u0629:",
    select: "\u062A\u062D\u062F\u064A\u062F",
    moreDetails: "\u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644"
  },
  dashboards: {
    title: "\u0644\u0648\u062D\u0627\u062A \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A",
    lastUpdated: "\u0622\u062E\u0631 \u062A\u062D\u062F\u064A\u062B: {value}",
    exportData: "\u062A\u0635\u062F\u064A\u0631 \u0627\u0644\u0628\u064A\u0627\u0646\u0627\u062A",
    summaryFailedToLoad: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0644\u0648\u062D\u0629 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A. \u062D\u0627\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0627\u062D\u0642\u064B\u0627.",
    tableFailedToLoad: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062C\u062F\u0648\u0644. \u062D\u0627\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0627\u062D\u0642\u064B\u0627.",
    tabs: {
      activities: "\u0627\u0644\u0623\u0646\u0634\u0637\u0629",
      supportTickets: "\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u062F\u0639\u0645",
      servicesBundles: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0648\u0627\u0644\u0628\u0627\u0642\u0627\u062A"
    },
    filters: {
      l1Category: "\u0627\u0644\u0641\u0626\u0629 \u0627\u0644\u0623\u0648\u0644\u0649",
      l2Subcategory: "\u0627\u0644\u0641\u0626\u0629 \u0627\u0644\u0641\u0631\u0639\u064A\u0629",
      l3Service: "\u0627\u0644\u062E\u062F\u0645\u0629",
      topic: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
      status: "\u0627\u0644\u062D\u0627\u0644\u0629",
      dateRange: "\u0627\u0644\u0646\u0637\u0627\u0642 \u0627\u0644\u0632\u0645\u0646\u064A",
      dateRangePlaceholder: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0646\u0637\u0627\u0642 \u0627\u0644\u0632\u0645\u0646\u064A",
      statusOptions: {
        new: "\u062C\u062F\u064A\u062F",
        open: "\u0645\u0641\u062A\u0648\u062D",
        awaitingCustomer: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0639\u0645\u064A\u0644",
        onHold: "\u0645\u0639\u0644\u0642",
        awaiting3rdPartyInfo: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0645\u0646 \u0637\u0631\u0641 \u062B\u0627\u0644\u062B",
        resolved: "\u062A\u0645 \u0627\u0644\u062D\u0644",
        closed: "\u0645\u063A\u0644\u0642",
        canceled: "\u0645\u0644\u063A\u0649",
        unknown: "\u063A\u064A\u0631 \u0645\u0639\u0631\u0648\u0641"
      },
      serviceType: "\u0646\u0648\u0639 \u0627\u0644\u062E\u062F\u0645\u0629",
      bundles: "\u0627\u0644\u0628\u0627\u0642\u0627\u062A"
    },
    activities: {
      donut: {
        title: "\u0627\u0644\u0623\u0646\u0634\u0637\u0629 \u062D\u0633\u0628 \u0627\u0644\u062D\u0627\u0644\u0629",
        totalLabel: "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0637\u0644\u0628\u0627\u062A"
      },
      bar: {
        title: "\u0627\u0644\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u0646\u0634\u0637\u0629"
      },
      slaTrend: {
        title: "\u0627\u062A\u062C\u0627\u0647 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629 %"
      },
      turnaroundTrend: {
        title: "\u0645\u062A\u0648\u0633\u0637 \u0648\u0642\u062A \u0627\u0644\u0625\u0646\u062C\u0627\u0632"
      },
      openAges: {
        title: "\u0623\u0639\u0645\u0627\u0631 \u0627\u0644\u0623\u0646\u0634\u0637\u0629 \u0627\u0644\u0645\u0641\u062A\u0648\u062D\u0629"
      },
      closedAges: {
        title: "\u0623\u0639\u0645\u0627\u0631 \u0627\u0644\u0623\u0646\u0634\u0637\u0629 \u0627\u0644\u0645\u063A\u0644\u0642\u0629"
      },
      table: {
        sortBy: "\u062A\u0631\u062A\u064A\u0628 \u062D\u0633\u0628",
        columns: {
          activity: "\u0627\u0644\u0646\u0634\u0627\u0637",
          total: "\u0627\u0644\u0625\u062C\u0645\u0627\u0644\u064A",
          avgSlaPercent: "\u0645\u062A\u0648\u0633\u0637 \u0646\u0633\u0628\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629 %",
          avgTurnaroundDays: "\u0645\u062A\u0648\u0633\u0637 \u0648\u0642\u062A \u0627\u0644\u0625\u0646\u062C\u0627\u0632",
          avgAgeDays: "\u0645\u062A\u0648\u0633\u0637 \u0627\u0644\u0639\u0645\u0631"
        }
      }
    },
    supportTickets: {
      donut: {
        title: "\u0627\u0644\u062A\u0630\u0627\u0643\u0631 \u062D\u0633\u0628 \u0627\u0644\u062D\u0627\u0644\u0629",
        totalLabel: "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0637\u0644\u0628\u0627\u062A"
      },
      bar: {
        title: "\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u062A\u0630\u0627\u0643\u0631"
      },
      slaTrend: {
        title: "\u0627\u062A\u062C\u0627\u0647 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629 %"
      },
      turnaroundTrend: {
        title: "\u0645\u062A\u0648\u0633\u0637 \u0648\u0642\u062A \u0627\u0644\u0625\u0646\u062C\u0627\u0632"
      },
      openAges: {
        title: "\u0623\u0639\u0645\u0627\u0631 \u0627\u0644\u062A\u0630\u0627\u0643\u0631 \u0627\u0644\u0645\u0641\u062A\u0648\u062D\u0629"
      },
      closedAges: {
        title: "\u0623\u0639\u0645\u0627\u0631 \u0627\u0644\u062A\u0630\u0627\u0643\u0631 \u0627\u0644\u0645\u063A\u0644\u0642\u0629"
      },
      table: {
        sortBy: "\u062A\u0631\u062A\u064A\u0628 \u062D\u0633\u0628",
        columns: {
          topic: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
          total: "\u0627\u0644\u0625\u062C\u0645\u0627\u0644\u064A",
          avgSlaPercent: "\u0645\u062A\u0648\u0633\u0637 \u0646\u0633\u0628\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629 %",
          avgTurnaroundDays: "\u0645\u062A\u0648\u0633\u0637 \u0648\u0642\u062A \u0627\u0644\u0625\u0646\u062C\u0627\u0632",
          avgAgeDays: "\u0645\u062A\u0648\u0633\u0637 \u0627\u0644\u0639\u0645\u0631"
        }
      }
    },
    servicesBundles: {
      donut: {
        title: "\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u062E\u062F\u0645\u0629",
        totalLabel: "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0637\u0644\u0628\u0627\u062A"
      },
      bar: {
        title: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0646\u0634\u0637\u0629"
      },
      table: {
        sortBy: "\u062A\u0631\u062A\u064A\u0628 \u062D\u0633\u0628",
        columns: {
          requestId: "\u0631\u0642\u0645 \u0627\u0644\u0637\u0644\u0628",
          name: "\u0627\u0644\u0627\u0633\u0645",
          type: "\u0627\u0644\u0646\u0648\u0639",
          bundle: "\u0627\u0644\u0628\u0627\u0642\u0629",
          status: "\u0627\u0644\u062D\u0627\u0644\u0629",
          stage: "\u0627\u0644\u0645\u0631\u062D\u0644\u0629",
          requestedAt: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0637\u0644\u0628",
          activeSince: "\u0646\u0634\u0637 \u0645\u0646\u0630",
          lastCompletedAt: "\u0622\u062E\u0631 \u0625\u062A\u0645\u0627\u0645"
        },
        type: {
          SERVICE: "\u062E\u062F\u0645\u0629",
          ADD_ON: "\u0625\u0636\u0627\u0641\u0629",
          BUNDLE: "\u0628\u0627\u0642\u0629"
        }
      }
    }
  },
  myServices: {
    title: "\u062E\u062F\u0645\u0627\u062A\u064A",
    errorLoadingServices: "\u062E\u0637\u0623 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0627\u062A: {error}",
    loadingMoreServices: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u062E\u062F\u0645\u0627\u062A...",
    emptyState: {
      requestedHeading: "\u0644\u0627 \u062A\u0648\u062C\u062F \u062E\u062F\u0645\u0627\u062A \u0645\u0637\u0644\u0648\u0628\u0629",
      requestedText: "\u0644\u0645 \u062A\u0637\u0644\u0628 \u0623\u064A \u062E\u062F\u0645\u0627\u062A \u062D\u062A\u0649 \u0627\u0644\u0622\u0646. \u0628\u0645\u062C\u0631\u062F \u0637\u0644\u0628 \u062E\u062F\u0645\u0629\u060C \u0633\u062A\u0638\u0647\u0631 \u0647\u0646\u0627.",
      activeHeading: "\u0644\u0627 \u062A\u0648\u062C\u062F \u062E\u062F\u0645\u0627\u062A \u0641\u0639\u0627\u0644\u0629",
      activeText: "\u0644\u064A\u0633 \u0644\u062F\u064A\u0643 \u0623\u064A \u062E\u062F\u0645\u0627\u062A \u0646\u0634\u0637\u0629. \u0628\u0645\u062C\u0631\u062F \u062A\u0641\u0639\u064A\u0644 \u062E\u062F\u0645\u0629 \u0645\u0637\u0644\u0648\u0628\u0629\u060C \u0633\u062A\u0638\u0647\u0631 \u0647\u0646\u0627.",
      completedHeading: "\u0644\u0645 \u064A\u062A\u0645 \u0625\u0643\u0645\u0627\u0644 \u0623\u064A \u062E\u062F\u0645\u0627\u062A",
      completedText: "\u0633\u062A\u0638\u0647\u0631 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0647\u0646\u0627 \u0628\u0645\u062C\u0631\u062F \u0627\u0643\u062A\u0645\u0627\u0644\u0647\u0627 \u0623\u0648 \u0627\u0646\u062A\u0647\u0627\u0621 \u0635\u0644\u0627\u062D\u064A\u062A\u0647\u0627. \u0633\u062A\u062A\u0645\u0643\u0646 \u0645\u0646 \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644 \u0648\u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0630\u0627\u062A \u0627\u0644\u0635\u0644\u0629 \u0641\u064A \u0623\u064A \u0648\u0642\u062A.",
      noResultsHeading: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u062E\u062F\u0645\u0627\u062A",
      noResultsText: "\u0627\u0644\u0641\u0644\u0627\u062A\u0631 \u0627\u0644\u0645\u062D\u062F\u062F\u0629 \u0644\u0645 \u062A\u0637\u0627\u0628\u0642 \u0623\u064A \u062E\u062F\u0645\u0627\u062A. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
    },
    tabs_detail: {
      activities: "\u0627\u0644\u0623\u0646\u0634\u0637\u0629",
      supportTickets: "\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u062F\u0639\u0645",
      documents: "\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A",
      invoices: "\u0627\u0644\u0641\u0648\u0627\u062A\u064A\u0631",
      taskBadgeSingular: "\u0645\u0647\u0645\u0629",
      taskBadgePlural: "\u0645\u0647\u0627\u0645",
      taskBadgeManyPlus: "+9 \u0645\u0647\u0627\u0645"
    },
    filters: {
      l1Placeholder: "\u0627\u0644\u0641\u0626\u0629",
      l2Placeholder: "\u0627\u0644\u0641\u0626\u0629 \u0627\u0644\u0641\u0631\u0639\u064A\u0629",
      l3Placeholder: "\u0627\u0644\u062E\u062F\u0645\u0629"
    },
    bundleCard: {
      preTitle: "\u0628\u0627\u0642\u0629"
    },
    bundleDetail: {
      overviewTab: "\u0646\u0638\u0631\u0629 \u0639\u0627\u0645\u0629",
      servicesLabel: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A",
      addonsLabel: "\u0627\u0644\u0625\u0636\u0627\u0641\u0627\u062A",
      rejectionReasonLabel: "\u0633\u0628\u0628 \u0627\u0644\u0631\u0641\u0636",
      errorLoadingBundle: "\u062E\u0637\u0623 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0628\u0627\u0642\u0629: {error}"
    }
  },
  supportTickets: {
    title: "\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u062F\u0639\u0645",
    validation: {
      required: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0645\u0631\u0641\u0642 \u0648\u0627\u062D\u062F \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
      mustComplete: "\u064A\u0631\u062C\u0649 \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631 \u062D\u062A\u0649 \u064A\u0646\u062A\u0647\u064A \u0631\u0641\u0639 \u062C\u0645\u064A\u0639 \u0627\u0644\u0645\u0644\u0641\u0627\u062A",
      tooLarge: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0647\u0648 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0644\u0641",
      invalidType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645. \u0627\u0644\u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0645\u0633\u0645\u0648\u062D \u0628\u0647\u0627: PDF\u060C PNG\u060C JPG\u060C JPEG\u060C DOC\u060C DOCX\u060C TXT"
    },
    newTicket: "\u062A\u0630\u0643\u0631\u0629 \u062C\u062F\u064A\u062F\u0629",
    openTickets: "\u062A\u0630\u0627\u0643\u0631 \u0645\u0641\u062A\u0648\u062D\u0629",
    closedTickets: "\u062A\u0630\u0627\u0643\u0631 \u0645\u063A\u0644\u0642\u0629",
    searchPlaceholderOpen: "\u0627\u0628\u062D\u062B \u0628\u0631\u0642\u0645 \u0627\u0644\u062A\u0630\u0643\u0631\u0629 \u0623\u0648 \u0627\u0633\u0645 \u0627\u0644\u0648\u0638\u064A\u0641\u0629 \u0623\u0648 \u0627\u0644\u0645\u0648\u0636\u0648\u0639",
    searchPlaceholderClosed: "\u0627\u0628\u062D\u062B \u0628\u0631\u0642\u0645 \u0627\u0644\u062A\u0630\u0643\u0631\u0629 \u0623\u0648 \u0627\u0633\u0645 \u0627\u0644\u0648\u0638\u064A\u0641\u0629 \u0623\u0648 \u0627\u0644\u0645\u0648\u0636\u0648\u0639",
    placeholder: "\u0627\u0643\u062A\u0628 \u0631\u062F\u0643 \u0647\u0646\u0627",
    failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062A\u0630\u0627\u0643\u0631",
    unableToLoad: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062A\u0630\u0627\u0643\u0631",
    view: "\u0639\u0631\u0636",
    response: "\u0627\u0644\u0631\u062F",
    noResultsText: '\u0628\u062D\u062B\u0643 \u0639\u0646 "{query}" \u0644\u0645 \u064A\u062A\u0637\u0627\u0628\u0642 \u0645\u0639 \u0623\u064A \u062A\u0630\u0627\u0643\u0631. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.',
    noMatchingTicketsFound: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u062A\u0630\u0627\u0643\u0631 \u0645\u0637\u0627\u0628\u0642\u0629",
    noMatchingTicketsText: "\u0627\u0644\u0641\u0644\u0627\u062A\u0631 \u0627\u0644\u062A\u064A \u062D\u062F\u062F\u062A\u0647\u0627 \u0644\u0645 \u062A\u062A\u0637\u0627\u0628\u0642 \u0645\u0639 \u0623\u064A \u062A\u0630\u0627\u0643\u0631. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    noTicketsFound: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u062A\u0630\u0627\u0643\u0631",
    noTicketsText: "\u0644\u0627 \u062A\u0648\u062C\u062F \u062A\u0630\u0627\u0643\u0631 \u062F\u0639\u0645 \u0644\u0639\u0631\u0636\u0647\u0627 \u0641\u064A \u0627\u0644\u0648\u0642\u062A \u0627\u0644\u062D\u0627\u0644\u064A.",
    noSupportTickets: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0637\u0644\u0628\u0627\u062A \u062F\u0639\u0645",
    noSupportTicketsText: "\u0644\u0645 \u062A\u0642\u0645 \u0628\u0625\u0646\u0634\u0627\u0621 \u0623\u064A \u0637\u0644\u0628\u0627\u062A \u062F\u0639\u0645. \u0625\u0630\u0627 \u0643\u0646\u062A \u0628\u062D\u0627\u062C\u0629 \u0625\u0644\u0649 \u0645\u0633\u0627\u0639\u062F\u0629\u060C \u064A\u0645\u0643\u0646\u0643 \u0625\u0646\u0634\u0627\u0621 \u0637\u0644\u0628 \u0648\u0645\u062A\u0627\u0628\u0639\u0629 \u0627\u0644\u062A\u062D\u062F\u064A\u062B\u0627\u062A \u0647\u0646\u0627.",
    unreadTicket: "\u062A\u0630\u0643\u0631\u0629 \u063A\u064A\u0631 \u0645\u0642\u0631\u0648\u0621\u0629",
    columns: {
      ticketNumber: "\u0631\u0642\u0645 \u0627\u0644\u062A\u0630\u0643\u0631\u0629",
      topic: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
      serviceSubTopic: "\u0627\u0644\u062E\u062F\u0645\u0629/\u0627\u0644\u0645\u0648\u0636\u0648\u0639 \u0627\u0644\u0641\u0631\u0639\u064A",
      subject: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
      status: "\u0627\u0644\u062D\u0627\u0644\u0629",
      creationDate: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u0646\u0634\u0627\u0621",
      closureDate: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u063A\u0644\u0627\u0642",
      createdBy: "\u062A\u0645 \u0625\u0646\u0634\u0627\u0624\u0647 \u0628\u0648\u0627\u0633\u0637\u0629"
    },
    drawer: {
      title: "\u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062F \u0627\u0644\u0645\u062C\u0627\u0644 \u0627\u0644\u0645\u0631\u062A\u0628\u0637 \u0628\u0627\u0644\u0637\u0644\u0628",
      topic: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
      general: "\u0639\u0627\u0645",
      serviceSpecific: "\u062E\u062F\u0645\u0629 \u0645\u062D\u062F\u062F\u0629",
      activeServices: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0646\u0634\u0637\u0629",
      activeServiceLabel: "\u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0646\u0634\u0637\u0629",
      activeServicePlaceholder: "\u0627\u062E\u062A\u0631 \u062E\u062F\u0645\u0629 \u0646\u0634\u0637\u0629",
      requestedServices: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629",
      requestedServiceLabel: "\u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629",
      requestedServicePlaceholder: "\u0627\u062E\u062A\u0631 \u062E\u062F\u0645\u0629 \u0645\u0637\u0644\u0648\u0628\u0629",
      itServices: "\u062E\u062F\u0645\u0627\u062A \u062A\u0643\u0646\u0648\u0644\u0648\u062C\u064A\u0627 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A",
      oracleErpIssues: "\u0645\u0634\u0643\u0644\u0627\u062A \u0623\u0648\u0631\u0627\u0643\u0644 ERP",
      subTopic: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639 \u0627\u0644\u0641\u0631\u0639\u064A",
      selectSubTopic: "\u0627\u062E\u062A\u0631 \u0645\u0648\u0636\u0648\u0639\u0627\u064B \u0641\u0631\u0639\u064A\u0627\u064B",
      issue: "\u0627\u0644\u0645\u0634\u0643\u0644\u0629",
      selectIssue: "\u0627\u062E\u062A\u0631 \u0645\u0634\u0643\u0644\u0629",
      functionL1: "\u0627\u0644\u0648\u0638\u064A\u0641\u0629",
      selectFunction: "\u0627\u062E\u062A\u0631 \u0648\u0638\u064A\u0641\u0629",
      typeL2: "\u0627\u0644\u0646\u0648\u0639",
      selectType: "\u0627\u062E\u062A\u0631 \u0646\u0648\u0639\u0627\u064B",
      serviceL3: "\u0627\u0644\u062E\u062F\u0645\u0629",
      selectService: "\u0627\u062E\u062A\u0631 \u062E\u062F\u0645\u0629",
      subject: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
      subjectPlaceholder: "\u0627\u0643\u062A\u0628 \u0645\u0648\u0636\u0648\u0639 \u0627\u0644\u062A\u0630\u0643\u0631\u0629",
      describeNeeds: "\u0635\u0650\u0641 \u0627\u062D\u062A\u064A\u0627\u062C\u0627\u062A\u0643 \u0645\u0646 \u0627\u0644\u062F\u0639\u0645",
      describePlaceholder: "\u0643\u064A\u0641 \u064A\u0645\u0643\u0646\u0646\u0627 \u0645\u0633\u0627\u0639\u062F\u062A\u0643\u061F",
      prefilledDescription: "\u0623\u0648\u062F \u0627\u0644\u0627\u0633\u062A\u0641\u0633\u0627\u0631 \u0639\u0646 \u062E\u062F\u0645\u0629 {serviceName}",
      prefilledSubject: "\u0627\u0633\u062A\u0641\u0633\u0627\u0631 \u0628\u0634\u0623\u0646 {serviceName}",
      uploadSupportingText: "PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A)",
      somethingWentWrong: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627",
      ticketAlreadyExists: "\u064A\u0648\u062C\u062F \u062A\u0630\u0643\u0631\u0629 \u0628\u0646\u0641\u0633 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644 \u0628\u0627\u0644\u0641\u0639\u0644",
      requiredSummary: {
        title: "\u0645\u0637\u0644\u0648\u0628 \u0642\u0628\u0644 \u0627\u0644\u0625\u0631\u0633\u0627\u0644",
        schemaError: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u0646\u0645\u0648\u0630\u062C \u0647\u0630\u0647 \u0627\u0644\u0645\u0634\u0643\u0644\u0629. \u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0627\u0644\u0645\u0634\u0643\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0646 \u062C\u062F\u064A\u062F."
      }
    },
    priorityLabel: "\u0623\u0648\u0644\u0648\u064A\u0629 {level}",
    category: "\u0627\u0644\u0641\u0626\u0629",
    created: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u0646\u0634\u0627\u0621",
    lastUpdated: "\u0622\u062E\u0631 \u062A\u062D\u062F\u064A\u062B",
    assignedTo: "\u0645\u064F\u0633\u0646\u062F \u0625\u0644\u0649",
    closedDate: "\u0645\u063A\u0644\u0642\u0629",
    success: {
      title: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0627\u0644\u062A\u0630\u0643\u0631\u0629 \u0628\u0646\u062C\u0627\u062D",
      heading: "\u062A\u0645 \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u062A\u0630\u0643\u0631\u0629 \u0628\u0646\u062C\u0627\u062D",
      description: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0637\u0644\u0628 \u0627\u0644\u062F\u0639\u0645 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0648\u0633\u064A\u0642\u0648\u0645 \u0641\u0631\u064A\u0642\u0646\u0627 \u0628\u0645\u0631\u0627\u062C\u0639\u062A\u0647 \u0642\u0631\u064A\u0628\u0627\u064B.",
      ticketNumber: "\u0631\u0642\u0645 \u0627\u0644\u062A\u0630\u0643\u0631\u0629",
      topic: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
      function: "\u0627\u0644\u0648\u0638\u064A\u0641\u0629",
      type: "\u0627\u0644\u0646\u0648\u0639",
      service: "\u0627\u0644\u062E\u062F\u0645\u0629",
      subject: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646",
      created: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u0646\u0634\u0627\u0621",
      descriptionLabel: "\u0627\u0644\u0648\u0635\u0641",
      byAuthor: "\u0628\u0648\u0627\u0633\u0637\u0629 {name}",
      attachments: "\u0627\u0644\u0645\u0631\u0641\u0642\u0627\u062A"
    },
    submissionSuccessful: "\u062A\u0645 \u0627\u0644\u062A\u0642\u062F\u064A\u0645 \u0628\u0646\u062C\u0627\u062D",
    submissionFailed: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627",
    ticketClosed: "\u062A\u0645 \u0625\u063A\u0644\u0627\u0642 \u0647\u0630\u0647 \u0627\u0644\u062A\u0630\u0643\u0631\u0629.",
    respond: "\u0631\u062F",
    responding: "\u062C\u0627\u0631\u064D \u0627\u0644\u0631\u062F...",
    loadingDetails: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062A\u0630\u0643\u0631\u0629...",
    ticketHash: "\u0627\u0644\u062A\u0630\u0643\u0631\u0629 #{number}",
    loadingConversation: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629...",
    startConversation: "\u0627\u0628\u062F\u0623 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629",
    sendMessageToBegin: "\u0623\u0631\u0633\u0644 \u0631\u0633\u0627\u0644\u0629 \u0644\u0628\u062F\u0621 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629 \u0645\u0639 \u0641\u0631\u064A\u0642 \u0627\u0644\u062F\u0639\u0645 \u0644\u062F\u064A\u0646\u0627.",
    chatUploadSupportingText: "PDF\u060C PNG\u060C JPG\u060C DOC\u060C TXT (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0644\u0641)",
    typeResponseAriaLabel: "\u0627\u0643\u062A\u0628 \u0631\u062F\u0643",
    submitResponseAriaLabel: "\u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0631\u062F",
    hideUpload: "\u0625\u062E\u0641\u0627\u0621 \u0627\u0644\u0631\u0641\u0639",
    uploadFiles: "\u0631\u0641\u0639 \u0645\u0644\u0641\u0627\u062A",
    status: {
      open: "\u0645\u0641\u062A\u0648\u062D",
      pending: "\u0645\u0639\u0644\u0642",
      solved: "\u0645\u062D\u0644\u0648\u0644\u0629",
      closed: "\u0645\u063A\u0644\u0642\u0629",
      new: "\u062C\u062F\u064A\u062F"
    },
    priority: {
      low: "\u0645\u0646\u062E\u0641\u0636\u0629",
      normal: "\u0639\u0627\u062F\u064A\u0629",
      high: "\u0639\u0627\u0644\u064A\u0629",
      urgent: "\u0639\u0627\u062C\u0644\u0629"
    },
    topic: {
      general: "\u0639\u0627\u0645",
      service_specific: "\u062E\u0627\u0635 \u0628\u0627\u0644\u062E\u062F\u0645\u0629",
      active_services: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0646\u0634\u0637\u0629",
      requested_service: "\u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629",
      it_services: "\u062E\u062F\u0645\u0627\u062A \u062A\u0643\u0646\u0648\u0644\u0648\u062C\u064A\u0627 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A",
      oracle_erp_issues: "\u0645\u0634\u0643\u0644\u0627\u062A \u0623\u0648\u0631\u0627\u0643\u0644 ERP"
    },
    filters: {
      topicPlaceholder: "\u0627\u0644\u062A\u0635\u0641\u064A\u0629 \u062D\u0633\u0628 \u0627\u0644\u0645\u0648\u0636\u0648\u0639",
      servicePlaceholder: "\u0627\u0644\u062A\u0635\u0641\u064A\u0629 \u062D\u0633\u0628 \u0627\u0644\u062E\u062F\u0645\u0629",
      statusPlaceholder: "\u0627\u0644\u062A\u0635\u0641\u064A\u0629 \u062D\u0633\u0628 \u0627\u0644\u062D\u0627\u0644\u0629"
    }
  },
  nda: {
    downloadStep: "\u062A\u062D\u0645\u064A\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D",
    uploadStep: "\u0631\u0641\u0639 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0627\u0644\u0645\u0648\u0642\u0639\u0629",
    downloadTitle: "\u062A\u0646\u0632\u064A\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D",
    rejectionFallbackTitle: "\u0627\u0644\u062A\u062D\u062F\u064A\u062B\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629 \u0644\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643",
    downloadDescription: "\u0646\u0633\u062E\u0629 \u0645\u0646 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u062A\u0633\u0627\u0645\u0649 \u0645\u062A\u0627\u062D\u0629 \u0644\u0645\u0631\u0627\u062C\u0639\u062A\u0643 \u0648\u062A\u0648\u0642\u064A\u0639\u0643",
    uploadTitle: "\u0631\u0641\u0639 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D",
    uploadDescription: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F (\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A) \u0627\u0644\u062A\u0627\u0644\u064A\u0629.",
    uploadError: "\u062E\u0637\u0623 \u0641\u064A \u0627\u0644\u0631\u0641\u0639",
    readyForDownload: "\u062C\u0627\u0647\u0632 \u0644\u0644\u062A\u0646\u0632\u064A\u0644",
    supportingText: "\u0641\u0642\u0637 (\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A) JPG \u0623\u0648 PNG \u0623\u0648 PDF",
    successTitle: "\u0634\u0643\u0631\u0627\u064B \u0644\u0643.",
    successDescription: "\u0633\u064A\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0643 \u0645\u062F\u064A\u0631 \u062D\u0633\u0627\u0628\u0643 \u0642\u0631\u064A\u0628\u0627\u064B \u0644\u0645\u0634\u0627\u0631\u0643\u0629 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0629 \u0648\u0627\u0644\u0639\u0631\u0636",
    errorTitle: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627.",
    errorDescription: "\u062D\u062F\u062B\u062A \u0645\u0634\u0643\u0644\u0629 \u0641\u064A \u0645\u0639\u0627\u0644\u062C\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0623\u0648 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    tile: {
      title: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D"
    },
    task: {
      review: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D",
      modify: "\u062A\u0639\u062F\u064A\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D"
    },
    action: {
      sign: "\u062A\u062D\u0645\u064A\u0644 \u0646\u0645\u0648\u0630\u062C \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0648\u062A\u0648\u0642\u064A\u0639\u0647 \u0648\u0631\u0641\u0639\u0647 \u0648\u062A\u0642\u062F\u064A\u0645\u0647 \u0644\u0644\u0645\u0631\u0627\u062C\u0639\u0629",
      under_review: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A\u0629. \u064A\u0631\u062C\u0649 \u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629.",
      approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D. \u0644\u0627 \u064A\u0644\u0632\u0645 \u0627\u062A\u062E\u0627\u0630 \u0623\u064A \u0625\u062C\u0631\u0627\u0621.",
      rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D. \u064A\u0631\u062C\u0649 \u0645\u0639\u0627\u0644\u062C\u0629 \u0627\u0644\u0645\u0644\u0627\u062D\u0638\u0627\u062A \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
      none: "\u0644\u0627 \u064A\u0644\u0632\u0645 \u0627\u062A\u062E\u0627\u0630 \u0623\u064A \u0625\u062C\u0631\u0627\u0621."
    },
    validation: {
      required: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0645\u0644\u0641 \u0644\u0644\u0631\u0641\u0639",
      tooLarge: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0647\u0648 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0633\u062A\u0646\u062F",
      invalidType: "\u0646\u0648\u0639 \u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645. \u0627\u0644\u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0645\u0633\u0645\u0648\u062D\u0629: PDF\u060C PNG\u060C JPG"
    }
  },
  msa: {
    initiate: {
      title: "\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
      timeframeLabel: "\u0645\u062A\u0649 \u062A\u0631\u063A\u0628 \u0641\u064A \u0628\u062F\u0621 \u0623\u0648\u0644 \u062E\u062F\u0645\u0629 \u0644\u0643\u061F",
      timeframePlaceholder: "\u062D\u062F\u062F \u0627\u0644\u0625\u0637\u0627\u0631 \u0627\u0644\u0632\u0645\u0646\u064A \u0644\u0644\u062A\u0639\u0627\u0642\u062F",
      descriptionPlaceholder: "\u0623\u0636\u0641 \u0623\u064A \u062A\u0648\u0642\u0639\u0627\u062A \u0644\u0644\u062A\u0639\u0627\u0642\u062F\u060C \u0623\u0648 \u0648\u0635\u0641\u0627\u064B \u0645\u062E\u062A\u0635\u0631\u0627\u064B \u0644\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644 \u0627\u0644\u0630\u064A \u062A\u0648\u062F \u0645\u0634\u0627\u0631\u0643\u062A\u0647 \u0645\u0639\u0646\u0627. \u0623\u0648 \u064A\u0645\u0643\u0646\u0643 \u0631\u0641\u0639 \u0645\u0633\u062A\u0646\u062F \u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644.",
      additionalRequirementsLabel: "\u0648\u0635\u0641 \u0645\u062A\u0637\u0644\u0628\u0627\u062A\u0643",
      uploadSupportingDocumentsLabel: "\u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062F\u0627\u0639\u0645\u0629 (\u0627\u062E\u062A\u064A\u0627\u0631\u064A)",
      sidebarTitle: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      sidebarDescription: "\u062D\u062F\u062F \u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644 \u0644\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643 \u0644\u062A\u062D\u062F\u064A\u062F \u0627\u0644\u062A\u0633\u0644\u064A\u0645\u0627\u062A \u0648\u0627\u0644\u062A\u0648\u0642\u0639\u0627\u062A \u0628\u0648\u0636\u0648\u062D.",
      rejectionTitle: "\u0627\u0644\u062A\u062D\u062F\u064A\u062B\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629 \u0644\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643",
      validationError: "\u064A\u0631\u062C\u0649 \u0625\u0643\u0645\u0627\u0644 \u062C\u0645\u064A\u0639 \u0627\u0644\u062D\u0642\u0648\u0644 \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629",
      submissionFailed: "\u0641\u0634\u0644 \u0627\u0644\u0625\u0631\u0633\u0627\u0644",
      fetchStatusError: "\u0641\u0634\u0644 \u0641\u064A \u062C\u0644\u0628 \u062D\u0627\u0644\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      scopeOfWorkLabel: "\u0627\u062E\u062A\u0631 \u0648\u0638\u064A\u0641\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u062A\u064A \u062A\u0628\u062D\u062B \u0639\u0646 \u0645\u0633\u0627\u0639\u062F\u0629 \u0641\u064A\u0647\u0627:",
      uploadSupportingText: "PDF\u060C DOC\u060C DOCX\u060C PNG\u060C JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0644\u0641)",
      timeframe: {
        this_month: "\u0647\u0630\u0627 \u0627\u0644\u0634\u0647\u0631",
        next_month: "\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u0642\u0627\u062F\u0645",
        "3_months": "\u062E\u0644\u0627\u0644 3 \u0623\u0634\u0647\u0631",
        "6_months": "\u062E\u0644\u0627\u0644 6 \u0623\u0634\u0647\u0631",
        "12_months": "\u062E\u0644\u0627\u0644 12 \u0634\u0647\u0631\u0627\u064B"
      }
    },
    reviewSidebarTitle: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    reviewSidebarDescription: "\u0623\u0643\u0645\u0644 \u0639\u0645\u0644\u064A\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0644\u062A\u062D\u062F\u064A\u062F \u0634\u0631\u0648\u0637 \u0648\u0623\u062D\u0643\u0627\u0645 \u0639\u0644\u0627\u0642\u062A\u0646\u0627 \u0627\u0644\u062A\u062C\u0627\u0631\u064A\u0629.",
    reviewTitle: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    reviewDescription: "\u062A\u062A\u0648\u0641\u0631 \u0646\u0633\u062E\u0629 \u0645\u0646 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643 \u0644\u0645\u0631\u0627\u062C\u0639\u062A\u0647\u0627 \u0648\u062A\u0648\u0642\u064A\u0639\u0647\u0627.",
    readyForDownloadReview: "\u062C\u0627\u0647\u0632 \u0644\u0644\u062A\u0646\u0632\u064A\u0644 \u0648\u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629",
    failedToLoadDocument: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0633\u062A\u0646\u062F",
    successTitle: "\u0634\u0643\u0631\u0627\u064B \u0644\u0643.",
    successDescription: "\u0633\u064A\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0643 \u0623\u062D\u062F \u0623\u0639\u0636\u0627\u0621 \u0641\u0631\u064A\u0642\u0646\u0627 \u0644\u0627\u0637\u0644\u0627\u0639\u0643 \u0639\u0644\u0649 \u0627\u0644\u062E\u0637\u0648\u0627\u062A \u0627\u0644\u062A\u0627\u0644\u064A\u0629.",
    errorTitle: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627.",
    errorDescription: "\u062D\u062F\u062B\u062A \u0645\u0634\u0643\u0644\u0629 \u0641\u064A \u0645\u0639\u0627\u0644\u062C\u0629 \u0637\u0644\u0628 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0623\u0648 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
    uploadTitle: "\u0631\u0641\u0639 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u0645\u0648\u0642\u0639\u0629",
    uploadDescription: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0646\u0633\u062E\u062A\u0643 \u0645\u0646 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u0645\u0648\u0642\u0639\u0629 \u0647\u0646\u0627.",
    uploadSupportingText: "PDF\u060C DOC\u060C DOCX \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0644\u0641)",
    steps: {
      reviewMSA: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      signMSA: "\u062A\u0648\u0642\u064A\u0639 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      tasamaSignsMSA: "\u062A\u0648\u0642\u064A\u0639 \u062A\u0633\u0627\u0645\u0649 \u0639\u0644\u0649 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      msaActive: "\u062A\u0641\u0639\u064A\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629"
    },
    tile: {
      title: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629"
    },
    task: {
      request: "\u0637\u0644\u0628 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      sign: "\u062A\u0648\u0642\u064A\u0639 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      modify: "\u062A\u0639\u062F\u064A\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629"
    },
    helper: {
      draft_received: "\u0648\u062B\u064A\u0642\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u062C\u0627\u0647\u0632\u0629 \u0644\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0648\u0627\u0644\u062A\u0648\u0642\u064A\u0639.",
      pending_signature: "\u0633\u062A\u062A\u0648\u0641\u0631 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u0645\u0648\u0642\u0639\u0629 \u0642\u0631\u064A\u0628\u0627\u064B\u060C \u0628\u0639\u062F \u0627\u0639\u062A\u0645\u0627\u062F\u0647\u0627 \u062F\u0627\u062E\u0644\u064A\u0627\u064B."
    },
    action: {
      submit_sow: "\u062A\u0642\u062F\u064A\u0645 \u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
      draft_pending: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u062A\u0633\u0627\u0645\u0649 \u0644\u0625\u0639\u062F\u0627\u062F \u0645\u0633\u0648\u062F\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      review_sign: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0645\u0633\u0648\u062F\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0648\u0627\u0644\u062A\u0648\u0642\u064A\u0639 \u0639\u0644\u064A\u0647\u0627",
      counter_signature: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u062A\u0648\u0642\u064A\u0639 \u0627\u0644\u0645\u0642\u0627\u0628\u0644 \u0645\u0646 \u062A\u0633\u0627\u0645\u0649",
      active: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0633\u0627\u0631\u064A\u0629. \u0644\u0627 \u064A\u0644\u0632\u0645 \u0627\u062A\u062E\u0627\u0630 \u0623\u064A \u0625\u062C\u0631\u0627\u0621.",
      rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629. \u064A\u0631\u062C\u0649 \u0645\u0639\u0627\u0644\u062C\u0629 \u0627\u0644\u0645\u0644\u0627\u062D\u0638\u0627\u062A \u0648\u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0641\u0631\u064A\u0642 \u0627\u0644\u062F\u0639\u0645.",
      addendum: "\u064A\u062C\u0631\u064A \u0627\u0633\u062A\u0628\u062F\u0627\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0628\u0645\u0644\u062D\u0642 \u0637\u0644\u0628 \u062E\u062F\u0645\u0629.",
      none: "\u0644\u0627 \u064A\u0644\u0632\u0645 \u0627\u062A\u062E\u0627\u0630 \u0623\u064A \u0625\u062C\u0631\u0627\u0621."
    },
    validation: {
      scopeRequired: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0648\u0638\u064A\u0641\u0629 \u062E\u062F\u0645\u0629 \u0648\u0627\u062D\u062F\u0629 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
      scopeMinItems: "\u064A\u062C\u0628 \u0627\u062E\u062A\u064A\u0627\u0631 \u0648\u0638\u064A\u0641\u0629 \u062E\u062F\u0645\u0629 \u0648\u0627\u062D\u062F\u0629 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
      timeframeRequired: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0625\u0637\u0627\u0631 \u0632\u0645\u0646\u064A \u0644\u0644\u062E\u062F\u0645\u0629",
      descriptionMaxLength: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u0648\u0635\u0641 1,000 \u062D\u0631\u0641",
      uploadRequired: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0645\u0633\u062A\u0646\u062F \u0648\u0627\u062D\u062F \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644 \u0645\u0646 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u0645\u0648\u0642\u0639\u0629",
      uploadMustComplete: "\u064A\u0631\u062C\u0649 \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631 \u062D\u062A\u0649 \u064A\u0646\u062A\u0647\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0633\u062A\u0646\u062F",
      tooLarge: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0647\u0648 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0633\u062A\u0646\u062F",
      invalidType: "\u0646\u0648\u0639 \u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645. \u0627\u0644\u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0645\u0633\u0645\u0648\u062D\u0629: PDF\u060C DOC\u060C DOCX"
    }
  },
  proposal: {
    reviewTitle: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0639\u0631\u0636",
    reviewDescription: "\u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0623\u0633\u0639\u0627\u0631 \u0627\u0644\u0639\u0631\u0636 \u0648\u0645\u062F\u062A\u0647 \u0648 \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0623\u0648 \u0627\u0644\u0631\u0641\u0636 \u0644\u0644\u0645\u062A\u0627\u0628\u0639\u0629",
    rejectionReasonLabel: "\u0633\u0628\u0628 \u0627\u0644\u0631\u0641\u0636",
    rejectionReasonPlaceholder: "\u0627\u0643\u062A\u0628 \u0633\u0628\u0628\u0627\u064B \u0645\u062E\u062A\u0635\u0631\u0627\u064B \u0644\u0644\u0631\u0641\u0636.",
    sidebarTitle: "\u0639\u0631\u0636 \u062A\u0633\u0627\u0645\u0649",
    sidebarDescription: "\u0639\u0631\u0636 \u0645\u062E\u0635\u0635 \u0644\u0645\u0631\u0627\u062C\u0639\u062A\u0643",
    documentIdNotAvailable: "\u0645\u0639\u0631\u0651\u0641 \u0645\u0633\u062A\u0646\u062F \u0627\u0644\u0645\u0642\u062A\u0631\u062D \u063A\u064A\u0631 \u0645\u062A\u0627\u062D \u0628\u0639\u062F",
    downloadUrlError: "\u0641\u0634\u0644 \u0641\u064A \u0627\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u0631\u0627\u0628\u0637 \u0627\u0644\u062A\u0646\u0632\u064A\u0644 \u0645\u0646 \u0627\u0644\u062E\u0627\u062F\u0645",
    fallbackFilename: "Tasama_Proposal.pdf",
    columns: {
      serviceItem: "\u0628\u0646\u062F \u0627\u0644\u062E\u062F\u0645\u0629",
      frequency: "\u0627\u0644\u062A\u0643\u0631\u0627\u0631",
      unit: "\u0627\u0644\u0648\u062D\u062F\u0629",
      unitPrice: "\u0633\u0639\u0631 \u0627\u0644\u0648\u062D\u062F\u0629",
      total: "\u0627\u0644\u0625\u062C\u0645\u0627\u0644\u064A"
    },
    defaultServiceName: "\u0627\u0644\u062E\u062F\u0645\u0629",
    acceptQuestion: "\u0647\u0644 \u062A\u0648\u0627\u0641\u0642 \u0639\u0644\u0649 \u0647\u0630\u0627 \u0627\u0644\u0639\u0631\u0636\u061F"
  },
  serviceContract: {
    signTitle: "\u062A\u0648\u0642\u064A\u0639 \u0627\u0644\u0639\u0642\u062F",
    signDescription: "\u064A\u064F\u0631\u062C\u0649 \u0631\u0641\u0639 \u0646\u0633\u062E\u062A\u0643 \u0645\u0646 \u0639\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0648\u0642\u0639 \u0647\u0646\u0627."
  },
  purchaseOrder: {
    uploadTitle: "\u0631\u0641\u0639 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
    uploadDescription: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 \u0627\u0644\u0645\u0648\u0642\u0639 \u0644\u0627\u0633\u062A\u0643\u0645\u0627\u0644 \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0627\u0644\u0645\u0627\u0644\u064A\u0629.",
    uploadPOLabel: "\u0631\u0641\u0639 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
    sidebarTitle: "\u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
    sidebarDescription: "\u0642\u0645 \u0628\u0631\u0641\u0639 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 \u0639\u0646\u062F \u0631\u063A\u0628\u062A\u0643 \u0641\u064A \u062A\u0641\u0639\u064A\u0644 \u062E\u062F\u0645\u0629 \u0645\u062D\u062F\u062F\u0629",
    fileTypeError: "\u064A\u064F\u0633\u0645\u062D \u0641\u0642\u0637 \u0628\u0645\u0644\u0641\u0627\u062A PDF \u0623\u0648 PNG \u0623\u0648 JPG (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A)",
    fileSizeError: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A",
    uploadErrorTitle: "\u062E\u0637\u0623 \u0641\u064A \u0627\u0644\u0631\u0641\u0639",
    invalidFileType: "\u064A\u064F\u0633\u0645\u062D \u0641\u0642\u0637 \u0628\u0645\u0644\u0641\u0627\u062A PDF \u0623\u0648 PNG \u0623\u0648 JPG (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A)",
    fileTooLarge: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A",
    noFileSelected: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0645\u0644\u0641 \u0644\u0644\u0631\u0641\u0639"
  },
  companyDocuments: {
    uploadTitle: "\u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A",
    uploadError: "\u062E\u0637\u0623 \u0641\u064A \u0627\u0644\u0631\u0641\u0639",
    descriptionInternational: "\u0627\u0631\u0641\u0639 \u0634\u0647\u0627\u062F\u0629 \u062A\u0633\u062C\u064A\u0644 \u0634\u0631\u0643\u062A\u0643. \u0628\u0627\u0644\u0646\u0633\u0628\u0629 \u0644\u0644\u0634\u0631\u0643\u0627\u062A \u0627\u0644\u062F\u0648\u0644\u064A\u0629\u060C \u0645\u0633\u062A\u0646\u062F \u0648\u0627\u062D\u062F \u0641\u0642\u0637 \u0645\u0637\u0644\u0648\u0628.",
    descriptionKsaPrivate: "\u0627\u0631\u0641\u0639 \u0645\u0633\u062A\u0646\u062F\u0627\u062A\u0643 \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629. \u062C\u0645\u064A\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062B\u0644\u0627\u062B\u0629 \u0645\u0637\u0644\u0648\u0628\u0629 \u0644\u0644\u0634\u0631\u0643\u0627\u062A \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629 \u0627\u0644\u062E\u0627\u0635\u0629 \u0648\u0634\u0631\u0643\u0627\u062A \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629 \u0627\u0644\u0645\u0624\u0633\u0633\u0629.",
    descriptionDefault: "\u0627\u0631\u0641\u0639 \u0623\u064A \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0645\u062D\u062F\u0651\u062B\u0629 \u0647\u0646\u0627. \u0628\u0645\u062C\u0631\u062F \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629\u060C \u0633\u064A\u062A\u0645 \u0627\u0633\u062A\u0628\u062F\u0627\u0644 \u0627\u0644\u0646\u0633\u062E\u0629 \u0627\u0644\u062D\u0627\u0644\u064A\u0629.",
    supportingText: "\u0641\u0642\u0637 (\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A) JPG \u0623\u0648 PNG \u0623\u0648 PDF",
    lastReceived: "(\u0622\u062E\u0631 \u0627\u0633\u062A\u0644\u0627\u0645 {date})",
    invalidFileType: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0645\u0644\u0641\u0627\u062A PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0641\u0642\u0637.",
    fileTooLarge: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0623\u0642\u0644 \u0645\u0646 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A.",
    missingDocuments: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629 \u0627\u0644\u062A\u0627\u0644\u064A\u0629: {documents}",
    uploadFailedFor: "\u0641\u0634\u0644 \u0627\u0644\u0631\u0641\u0639 \u0644\u0640: {files}. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    someFilesFailed: "\u0641\u0634\u0644 \u0631\u0641\u0639 \u0628\u0639\u0636 \u0627\u0644\u0645\u0644\u0641\u0627\u062A. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    uploadFailed: "\u0641\u0634\u0644 \u0627\u0644\u0631\u0641\u0639. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
  },
  companyEdit: {
    companyDetailsStep: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
    uploadDocumentsStep: "\u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A",
    businessProfileTitle: "\u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u062A\u0639\u0631\u064A\u0641\u064A \u0644\u0644\u0634\u0631\u0643\u0629",
    businessProfileDescription: "\u0623\u0643\u0645\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u062A\u0639\u0631\u064A\u0641\u064A \u0644\u0634\u0631\u0643\u062A\u0643",
    termsAndConditions: "\u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645",
    submissionFailed: "\u0641\u0634\u0644 \u0627\u0644\u0625\u0631\u0633\u0627\u0644",
    submissionFailedDescription: "\u064A\u0631\u062C\u0649 \u062D\u0644 \u0627\u0644\u0645\u0634\u0643\u0644\u0627\u062A \u0627\u0644\u0645\u0630\u0643\u0648\u0631\u0629 \u0623\u0639\u0644\u0627\u0647 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    updatesRequired: "\u064A\u0644\u0632\u0645 \u062A\u062D\u062F\u064A\u062B \u0628\u064A\u0627\u0646\u0627\u062A \u0634\u0631\u0643\u062A\u0643"
  },
  rejectionModal: {
    updatesRequired: "\u062A\u062D\u062F\u064A\u062B\u0627\u062A \u0645\u0637\u0644\u0648\u0628\u0629",
    review: "\u0645\u0631\u0627\u062C\u0639\u0629"
  },
  confirmation: {
    thankYou: "\u062A\u0645 \u0627\u0633\u062A\u0644\u0627\u0645 \u0628\u064A\u0627\u0646\u0627\u062A\u0643 \u0628\u0646\u062C\u0627\u062D. \u0634\u0643\u0631\u0627\u064B \u0644\u0643"
  },
  errors: {
    failedToLoadTickets: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062A\u0630\u0627\u0643\u0631",
    unableToLoad: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062A\u0630\u0627\u0643\u0631",
    failedToFetchKam: "\u0641\u0634\u0644 \u0641\u064A \u062C\u0644\u0628 \u0628\u064A\u0627\u0646\u0627\u062A \u0645\u062F\u064A\u0631 \u0627\u0644\u062D\u0633\u0627\u0628 \u0627\u0644\u0631\u0626\u064A\u0633\u064A",
    validationFailed: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642",
    noFileSelected: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u062E\u062A\u064A\u0627\u0631 \u0645\u0644\u0641 \u0644\u0644\u0631\u0641\u0639",
    uploadFailed: "\u0641\u0634\u0644 \u0627\u0644\u0631\u0641\u0639. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    submissionUnexpected: "\u062D\u062F\u062B \u062E\u0637\u0623 \u063A\u064A\u0631 \u0645\u062A\u0648\u0642\u0639 \u0623\u062B\u0646\u0627\u0621 \u0627\u0644\u0625\u0631\u0633\u0627\u0644",
    accountManagement: {
      reactivationFailed: "\u0641\u0634\u0644 \u0645\u0639\u0627\u0644\u062C\u0629 \u0637\u0644\u0628 \u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0641\u0639\u064A\u0644. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      passwordResetFailed: "\u0641\u0634\u0644 \u0645\u0639\u0627\u0644\u062C\u0629 \u0637\u0644\u0628 \u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      forgotPasswordFailed: "\u0641\u0634\u0644 \u0645\u0639\u0627\u0644\u062C\u0629 \u0637\u0644\u0628 \u0646\u0633\u064A\u0627\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      linkAlreadyUsed: "\u0647\u0630\u0627 \u0627\u0644\u0631\u0627\u0628\u0637 \u0645\u0633\u062A\u062E\u062F\u0645 \u0645\u0633\u0628\u0642\u0627\u064B.",
      invalidTokenTypeActivation: "\u0631\u0627\u0628\u0637 \u0627\u0644\u062A\u0641\u0639\u064A\u0644 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D. \u064A\u0631\u062C\u0649 \u0637\u0644\u0628 \u0631\u0627\u0628\u0637 \u062C\u062F\u064A\u062F.",
      userNotFoundByEmail: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u062D\u0633\u0627\u0628 \u0628\u0647\u0630\u0627 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A.",
      activationFailed: "\u0641\u0634\u0644 \u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628\u0643. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0623\u0648 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
      invalidTokenTypePasswordReset: "\u0631\u0627\u0628\u0637 \u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D. \u064A\u0631\u062C\u0649 \u0637\u0644\u0628 \u0631\u0627\u0628\u0637 \u062C\u062F\u064A\u062F.",
      passwordResetSubmitFailed: "\u0641\u0634\u0644 \u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0623\u0648 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
      accountNotActivated: "\u0644\u0645 \u064A\u062A\u0645 \u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628\u0643 \u0628\u0639\u062F. \u064A\u064F\u0631\u062C\u0649 \u062A\u0641\u0639\u064A\u0644 \u062D\u0633\u0627\u0628\u0643 \u0642\u0628\u0644 \u0625\u0639\u0627\u062F\u0629 \u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631.",
      contactDetailsFailed: "\u0641\u0634\u0644 \u062C\u0644\u0628 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u062A\u0648\u0627\u0635\u0644."
    },
    cms: {
      contentNotFound: "\u0627\u0644\u0645\u062D\u062A\u0648\u0649 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      invalidFilePath: "\u0645\u0633\u0627\u0631 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      invalidFilePathCharacters: "\u0645\u0633\u0627\u0631 \u0627\u0644\u0645\u0644\u0641 \u064A\u062D\u062A\u0648\u064A \u0639\u0644\u0649 \u0623\u062D\u0631\u0641 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629",
      operationFailed: "\u0641\u0634\u0644 \u0625\u062A\u0645\u0627\u0645 \u0627\u0644\u0639\u0645\u0644\u064A\u0629",
      invalidContentType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u062D\u062A\u0648\u0649 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D"
    },
    keycloakAdmin: {
      userNotFound: "\u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      emailOrUsernameMissing: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0623\u0648 \u0627\u0633\u0645 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0645\u0637\u0644\u0648\u0628",
      emailRequired: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0637\u0644\u0648\u0628",
      userNotFoundByEmail: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0645\u0633\u062A\u062E\u062F\u0645 \u0628\u0647\u0630\u0627 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
      authFailed: "\u0641\u0634\u0644\u062A \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629",
      initFailed: "\u0641\u0634\u0644 \u062A\u0647\u064A\u0626\u0629 \u0627\u0644\u062E\u062F\u0645\u0629",
      resourceNotFound: "\u0627\u0644\u0645\u0648\u0631\u062F \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      resourceConflict: "\u0627\u0644\u0645\u0648\u0631\u062F \u0645\u0648\u062C\u0648\u062F \u0645\u0633\u0628\u0642\u0627\u064B",
      operationFailed: "\u0641\u0634\u0644\u062A \u0627\u0644\u0639\u0645\u0644\u064A\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      adminAuthRequired: "\u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u0645\u0637\u0644\u0648\u0628\u0629 \u0644\u0644\u0648\u0635\u0648\u0644 \u0627\u0644\u0625\u062F\u0627\u0631\u064A",
      insufficientPermissions: "\u0635\u0644\u0627\u062D\u064A\u0627\u062A \u063A\u064A\u0631 \u0643\u0627\u0641\u064A\u0629",
      roleNotAuthorized: "\u0647\u0630\u0627 \u0627\u0644\u062F\u0648\u0631 \u063A\u064A\u0631 \u0645\u062E\u0648\u0651\u0644",
      tokenValidationFailed: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0631\u0645\u0632",
      accessTokenValidationFailed: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0631\u0645\u0632 \u0627\u0644\u0648\u0635\u0648\u0644",
      accessDenied: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u0644\u0648\u0635\u0648\u0644",
      notConfigured: "\u062E\u0637\u0623 \u0641\u064A \u0625\u0639\u062F\u0627\u062F \u0627\u0644\u062E\u062F\u0645\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
      serviceUnavailable: "\u0627\u0644\u062E\u062F\u0645\u0629 \u063A\u064A\u0631 \u0645\u062A\u0627\u062D\u0629 \u062D\u0627\u0644\u064A\u0627\u064B. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0627\u062D\u0642\u0627\u064B."
    },
    msa: {
      signedUploadNotAllowed: "\u0644\u0627 \u064A\u0645\u0643\u0646 \u0631\u0641\u0639 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u0645\u0648\u0642\u0639\u0629 \u0625\u0644\u0627 \u0628\u0639\u062F \u0627\u0633\u062A\u0644\u0627\u0645 \u0627\u0644\u0645\u0633\u0648\u062F\u0629",
      signedAttachmentUploadFailed: "\u0641\u0634\u0644 \u0631\u0641\u0639 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u0645\u0648\u0642\u0639\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      signedSubmitFailed: "\u062A\u0645 \u0631\u0641\u0639 \u0627\u0644\u0645\u0644\u0641 \u0644\u0643\u0646 \u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u0645\u0648\u0642\u0639\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      submitNotAllowedForStatus: "\u0644\u0627 \u064A\u0645\u0643\u0646 \u0625\u0631\u0633\u0627\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0641\u064A \u0627\u0644\u0645\u0631\u062D\u0644\u0629 \u0627\u0644\u062D\u0627\u0644\u064A\u0629",
      attachmentUploadFailed: "\u0641\u0634\u0644 \u0631\u0641\u0639 \u0627\u0644\u0645\u0631\u0641\u0642\u0627\u062A. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      creationFailed: "\u0641\u0634\u0644 \u0625\u0646\u0634\u0627\u0621 \u0633\u062C\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      fileIdRequiredForDownload: "\u0645\u0639\u0631\u0651\u0641 \u0627\u0644\u0645\u0644\u0641 \u0645\u0637\u0644\u0648\u0628 \u0644\u0644\u062A\u0646\u0632\u064A\u0644",
      fileNotFound: "\u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      downloadForbidden: "\u0635\u0644\u0627\u062D\u064A\u0627\u062A \u063A\u064A\u0631 \u0643\u0627\u0641\u064A\u0629 \u0644\u062A\u0646\u0632\u064A\u0644 \u0647\u0630\u0627 \u0627\u0644\u0645\u0644\u0641",
      downloadFailed: "\u0641\u0634\u0644 \u062A\u0646\u0632\u064A\u0644 \u0627\u0644\u0645\u0644\u0641. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
    },
    myAccount: {
      authRequired: "\u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u0645\u0637\u0644\u0648\u0628\u0629",
      detailsNotFound: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629",
      invalidDetailsRequest: "\u0637\u0644\u0628 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      unauthorized: "\u0648\u0635\u0648\u0644 \u063A\u064A\u0631 \u0645\u0635\u0631\u062D \u0628\u0647 \u0625\u0644\u0649 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628",
      forbidden: "\u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628 \u0645\u062D\u0638\u0648\u0631",
      detailsFailed: "\u0641\u0634\u0644 \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628",
      noUpdateData: "\u0644\u0645 \u064A\u062A\u0645 \u062A\u0648\u0641\u064A\u0631 \u0628\u064A\u0627\u0646\u0627\u062A \u0644\u0644\u062A\u062D\u062F\u064A\u062B",
      accountDetailsNotFound: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629",
      invalidUpdateData: "\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u062A\u062D\u062F\u064A\u062B \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629",
      updateFailed: "\u0641\u0634\u0644 \u062A\u062D\u062F\u064A\u062B \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      companyDetailsNotFound: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629",
      invalidCompanyDetailsRequest: "\u0637\u0644\u0628 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      companyDetailsFailed: "\u0641\u0634\u0644 \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
      companyUpdateFailed: "\u0641\u0634\u0644 \u062A\u062D\u062F\u064A\u062B \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      teamsAdminOnly: "\u064A\u0645\u0643\u0646 \u0644\u0644\u0645\u062F\u064A\u0631\u064A\u0646 \u0641\u0642\u0637 \u0639\u0631\u0636 \u0623\u0639\u0636\u0627\u0621 \u0627\u0644\u0641\u0631\u064A\u0642",
      teamMemberConflict: "\u064A\u0648\u062C\u062F \u0639\u0636\u0648 \u0641\u0631\u064A\u0642 \u0628\u0647\u0630\u0627 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0633\u0628\u0642\u0627\u064B",
      teamMemberUpdateConflict: "\u0641\u0634\u0644 \u062A\u062D\u062F\u064A\u062B \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      teamMemberActivationPending: "\u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0623\u0648 \u0625\u0644\u063A\u0627\u0621 \u062A\u0641\u0639\u064A\u0644\u0647 \u0644\u0627 \u064A\u0632\u0627\u0644 \u0642\u064A\u062F \u0627\u0644\u0645\u0639\u0627\u0644\u062C\u0629",
      invalidTeamMemberData: "\u0628\u064A\u0627\u0646\u0627\u062A \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629",
      kybSubmitValidationFailed: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 KYB",
      kybSubmitFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 KYB"
    },
    notifications: {
      authRequired: "\u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u0645\u0637\u0644\u0648\u0628\u0629",
      notFound: "\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629",
      invalidParams: "\u0645\u0639\u0627\u0645\u0644\u0627\u062A \u0627\u0644\u0637\u0644\u0628 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629",
      fetchFailed: "\u0641\u0634\u0644 \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A",
      unreadCountNotFound: "\u0639\u062F\u062F \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A \u063A\u064A\u0631 \u0627\u0644\u0645\u0642\u0631\u0648\u0621\u0629 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      unreadCountFailed: "\u0641\u0634\u0644 \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u0639\u062F\u062F \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A \u063A\u064A\u0631 \u0627\u0644\u0645\u0642\u0631\u0648\u0621\u0629",
      singleNotFound: "\u0627\u0644\u0625\u0634\u0639\u0627\u0631 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      invalidData: "\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0625\u0634\u0639\u0627\u0631 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629",
      markReadFailed: "\u0641\u0634\u0644 \u062A\u062D\u062F\u064A\u062F \u0627\u0644\u0625\u0634\u0639\u0627\u0631 \u0643\u0645\u0642\u0631\u0648\u0621",
      markAllReadFailed: "\u0641\u0634\u0644 \u062A\u062D\u062F\u064A\u062F \u062C\u0645\u064A\u0639 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A \u0643\u0645\u0642\u0631\u0648\u0621\u0629"
    },
    serviceCatalogue: {
      invalidRequest: "\u0637\u0644\u0628 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      notFound: "\u0643\u062A\u0627\u0644\u0648\u062C \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      categoryNotFound: "\u0627\u0644\u0641\u0626\u0629 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629"
    },
    serviceRequest: {
      authRequired: "\u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629 \u0645\u0637\u0644\u0648\u0628\u0629",
      serviceIdRequired: "\u0645\u0639\u0631\u0651\u0641 \u0627\u0644\u062E\u062F\u0645\u0629 \u0645\u0637\u0644\u0648\u0628",
      eligibilityCheckFailed: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0623\u0647\u0644\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629",
      documentIdNotFound: "\u0641\u0634\u0644 \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u0645\u0639\u0631\u0651\u0641 \u0627\u0644\u0645\u0633\u062A\u0646\u062F",
      submitFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0627\u0644\u062E\u062F\u0645\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      fetchFailed: "\u0641\u0634\u0644 \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u0637\u0644\u0628\u0627\u062A \u0627\u0644\u062E\u062F\u0645\u0629",
      detailsFetchFailed: "\u0641\u0634\u0644 \u062C\u0644\u0628 \u062A\u0641\u0627\u0635\u064A\u0644 \u0637\u0644\u0628 \u0627\u0644\u062E\u062F\u0645\u0629",
      poDocumentRequired: "\u0645\u0633\u062A\u0646\u062F \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 \u0645\u0637\u0644\u0648\u0628",
      poSubmitFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      signedUrlNotFound: "\u0631\u0627\u0628\u0637 \u0627\u0644\u062A\u0646\u0632\u064A\u0644 \u0644\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F",
      signedUrlFetchFailed: "\u0641\u0634\u0644 \u0627\u0633\u062A\u0631\u062F\u0627\u062F \u0631\u0627\u0628\u0637 \u062A\u0646\u0632\u064A\u0644 \u0627\u0644\u0645\u0644\u0641",
      signedContractSubmitFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0639\u0642\u062F \u0627\u0644\u0645\u0648\u0642\u0639. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      proposalReviewFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0639\u0631\u0636. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      documentsFetchFailed: "\u0641\u0634\u0644 \u062C\u0644\u0628 \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062E\u062F\u0645\u0629"
    },
    home: {
      tasksFetchFailed: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u0645\u0647\u0627\u0645\u0643. \u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0635\u0641\u062D\u0629.",
      servicesFetchFailed: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u062E\u062F\u0645\u0627\u062A\u0643. \u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0635\u0641\u062D\u0629.",
      companyNotFound: "\u062A\u0639\u0630\u0631 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0627\u0644\u062F\u0639\u0645.",
      rateLimitExceeded: "\u0637\u0644\u0628\u0627\u062A \u0643\u062B\u064A\u0631\u0629 \u062C\u062F\u0627\u064B. \u064A\u0631\u062C\u0649 \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631 \u0644\u062D\u0638\u0629 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u062C\u062F\u062F\u0627\u064B."
    }
  },
  validation: {
    myDetails: {
      firstName: {
        required: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0645\u0637\u0644\u0648\u0628",
        min: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u062D\u0631\u0641\u064A\u0646 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 255 \u062D\u0631\u0641\u0627\u064B",
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0639\u0644\u0649 \u0623\u062D\u0631\u0641 \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
      },
      lastName: {
        required: "\u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0645\u0637\u0644\u0648\u0628",
        min: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u062D\u0631\u0641\u064A\u0646 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 255 \u062D\u0631\u0641\u0627\u064B",
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0639\u0644\u0649 \u0623\u062D\u0631\u0641 \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
      },
      salutation: {
        required: "\u0627\u0644\u0628\u0627\u062F\u0626\u0629 \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      jobTitle: {
        required: "\u0627\u0644\u0645\u0633\u0645\u0649 \u0627\u0644\u0648\u0638\u064A\u0641\u064A \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u0645\u0633\u0645\u0649 \u0627\u0644\u0648\u0638\u064A\u0641\u064A 255 \u062D\u0631\u0641\u0627\u064B"
      },
      email: {
        required: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0637\u0644\u0648\u0628",
        invalid: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0635\u0627\u0644\u062D"
      },
      country: {
        required: "\u0631\u0645\u0632 \u0627\u0644\u062F\u0648\u0644\u0629 \u0645\u0637\u0644\u0648\u0628"
      },
      mobile: {
        required: "\u0631\u0642\u0645 \u0627\u0644\u0647\u0627\u062A\u0641 \u0627\u0644\u0645\u062D\u0645\u0648\u0644 \u0645\u0637\u0644\u0648\u0628",
        invalid: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0631\u0642\u0645 \u0647\u0627\u062A\u0641 \u0645\u062D\u0645\u0648\u0644 \u0635\u0627\u0644\u062D \u0644\u0644\u062F\u0648\u0644\u0629 \u0627\u0644\u0645\u062D\u062F\u062F\u0629"
      }
    },
    companyDetails: {
      companyName: {
        required: "\u0627\u0633\u0645 \u0627\u0644\u0634\u0631\u0643\u0629 \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0633\u0645 \u0627\u0644\u0634\u0631\u0643\u0629 200 \u062D\u0631\u0641",
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0627\u0633\u0645 \u0627\u0644\u0634\u0631\u0643\u0629 \u062D\u0631\u0648\u0641\u0627\u064B \u0648\u0623\u0631\u0642\u0627\u0645\u0627\u064B \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0627\u0644\u0631\u0645\u0648\u0632 \u0627\u0644\u0634\u0627\u0626\u0639\u0629 (&\u060C -\u060C ' \u060C .\u060C (\u060C )\u060C /\u060C ,)"
      },
      crNumber: {
        required: "\u0631\u0642\u0645 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0631\u0642\u0645 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 100 \u062D\u0631\u0641",
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0631\u0642\u0645 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0623\u0631\u0642\u0627\u0645\u0627\u064B \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
      },
      registrationStartDate: {
        required: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0645\u0637\u0644\u0648\u0628",
        format: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0627\u0631\u064A\u062E \u0635\u062D\u064A\u062D \u0628\u0635\u064A\u063A\u0629 DD/MM/YYYY",
        future: "\u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0641\u064A \u0627\u0644\u0645\u0633\u062A\u0642\u0628\u0644"
      },
      website: {
        invalid: "\u0623\u062F\u062E\u0644 \u0631\u0627\u0628\u0637 \u0645\u0648\u0642\u0639 \u0635\u062D\u064A\u062D (\u0645\u062B\u0627\u0644: https://example.com)"
      },
      country: {
        required: "\u062F\u0648\u0644\u0629 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      countryOfHeadquarter: {
        required: "\u062F\u0648\u0644\u0629 \u0627\u0644\u0645\u0642\u0631 \u0627\u0644\u0631\u0626\u064A\u0633\u064A \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      city: {
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u062A\u062A\u0636\u0645\u0646 \u0627\u0644\u0645\u062F\u064A\u0646\u0629 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
      },
      postalCode: {
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0627\u0644\u0631\u0645\u0632 \u0627\u0644\u0628\u0631\u064A\u062F\u064A \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0623\u0631\u0642\u0627\u0645\u0627\u064B \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A (3-12 \u062D\u0631\u0641\u0627\u064B)"
      },
      industrySector: {
        required: "\u0642\u0637\u0627\u0639 \u0627\u0644\u0635\u0646\u0627\u0639\u0629 \u0645\u0637\u0644\u0648\u0628"
      },
      address: {
        required: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0634\u0631\u0643\u0629 \u0627\u0644\u0645\u0633\u062C\u0644\u0629 \u0645\u0637\u0644\u0648\u0628"
      },
      numberOfEmployees: {
        required: "\u0639\u062F\u062F \u0627\u0644\u0645\u0648\u0638\u0641\u064A\u0646 \u0645\u0637\u0644\u0648\u0628"
      },
      annualRevenue: {
        required: "\u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A \u0627\u0644\u0633\u0646\u0648\u064A\u0629 \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      totalInvestmentRaised: {
        required: "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631 \u0627\u0644\u0645\u062C\u0645\u0648\u0639 \u0645\u0637\u0644\u0648\u0628"
      },
      general: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642"
    },
    projectDetails: {
      projectCodeName: {
        required: "\u0627\u0633\u0645 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 \u0645\u0637\u0644\u0648\u0628",
        minLength: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u062A\u0643\u0648\u0646 \u0627\u0633\u0645 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 \u0645\u0646 3 \u0623\u062D\u0631\u0641 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
        maxLength: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0633\u0645 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 100 \u062D\u0631\u0641"
      },
      projectDetails: {
        maxLength: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0648\u0635\u0641 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 1000 \u062D\u0631\u0641"
      },
      expectedLaunchDate: {
        invalidFuture: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0627\u0631\u064A\u062E \u0645\u0633\u062A\u0642\u0628\u0644\u064A \u0635\u0627\u0644\u062D \u0628\u0635\u064A\u063A\u0629 \u064A\u0648\u0645/\u0634\u0647\u0631/\u0633\u0646\u0629",
        invalidFormat: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0627\u0631\u064A\u062E \u0635\u0627\u0644\u062D \u0628\u0635\u064A\u063A\u0629 \u064A\u0648\u0645/\u0634\u0647\u0631/\u0633\u0646\u0629",
        mustBeFuture: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u0637\u0644\u0627\u0642 \u0627\u0644\u0645\u062A\u0648\u0642\u0639 \u0641\u064A \u0627\u0644\u0645\u0633\u062A\u0642\u0628\u0644"
      }
    }
  },
  banner: {
    welcomeTitle: '\u0623\u0647\u0644\u0627\u064B \u0628\u0643\u0645 \u0641\u064A <span class="text-fg-brand-gold">\u062A\u0633\u0627\u0645\u0649</span>',
    welcomeDescription: "\u062A\u0633\u0627\u0639\u062F \u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u0627\u0644\u0634\u0631\u0643\u0627\u062A \u0639\u0644\u0649 \u0627\u0643\u062A\u0633\u0627\u0628 \u062B\u0642\u0629 \u0623\u0643\u0628\u0631 \u0641\u064A \u0627\u0644\u0633\u0648\u0642 \u0627\u0644\u0633\u0639\u0648\u062F\u064A",
    softLandingTitle: '\u0637\u0644\u0628 \u062E\u062F\u0645\u0629 \u0648\u0627\u062C\u0647\u0629 <span class="text-fg-brand-gold">\u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644</span> \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629',
    softLandingDescription: "\u0645\u0631\u062D\u0628\u064B\u0627 \u0628\u0643\u0645 \u0641\u064A \u062A\u0633\u0627\u0645\u0649\u060C \u0634\u0631\u064A\u0643\u0643\u0645 \u0627\u0644\u0645\u0648\u062B\u0648\u0642 \u0644\u062A\u0645\u0643\u064A\u0646 \u062F\u062E\u0648\u0644 \u0623\u0639\u0645\u0627\u0644\u0643\u0645 \u0625\u0644\u0649 \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629 \u0628\u0633\u0644\u0627\u0633\u0629 \u0648\u062B\u0642\u0629. \u0646\u0631\u0634\u062F\u0643\u0645 \u062E\u0644\u0627\u0644 \u0645\u0631\u0627\u062D\u0644 \u0627\u0644\u062A\u0623\u0633\u064A\u0633 \u0648\u0627\u0644\u0627\u0645\u062A\u062B\u0627\u0644 \u0648\u062F\u062E\u0648\u0644 \u0627\u0644\u0633\u0648\u0642\u060C \u0644\u062A\u062A\u0645\u0643\u0646\u0648\u0627 \u0645\u0646 \u0627\u0644\u062A\u0634\u063A\u064A\u0644 \u0648\u0627\u0644\u0646\u0645\u0648 \u0628\u0643\u0641\u0627\u0621\u0629.",
    msa: {
      title: '\u0628\u062F\u0621 <span class="text-fg-brand-gold">\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629</span>',
      description: "\u062D\u062F\u062F \u0627\u0644\u0641\u0626\u0627\u062A \u0627\u0644\u062A\u064A \u062A\u0647\u0645\u0643\u060C \u0648\u0633\u0646\u0633\u0627\u0639\u062F\u0643 \u0641\u064A \u0635\u064A\u0627\u063A\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u062E\u062F\u0645\u0627\u062A \u0645\u062E\u0635\u0635\u0629\u061B \u0628\u062F\u0621\u0627\u064B \u0645\u0646 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0648\u0635\u0648\u0644\u0627\u064B \u0625\u0644\u0649 \u0628\u0627\u0642\u0627\u062A \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u062A\u0639\u062F\u062F\u0629",
      buttonText: "\u0637\u0644\u0628 \u062E\u062F\u0645\u0629"
    },
    newService: {
      title: '\u062E\u062F\u0645\u0629 \u062C\u062F\u064A\u062F\u0629 <span class="text-fg-brand-gold">\u0623\u064F\u0636\u064A\u0641\u062A</span>',
      description: "\u0628\u0631\u0646\u0627\u0645\u062C \u062A\u062F\u0631\u064A\u0628\u064A \u062A\u0646\u0641\u064A\u0630\u064A \u0645\u0643\u062B\u0641 \u0644\u062A\u0639\u0632\u064A\u0632 \u0642\u062F\u0631\u0627\u062A\u0643 \u0627\u0644\u0642\u064A\u0627\u062F\u064A\u0629 \u0648\u0627\u0644\u0627\u0628\u062A\u0643\u0627\u0631 \u0644\u0645\u0633\u062A\u0642\u0628\u0644 \u0627\u0644\u0623\u0639\u0645\u0627\u0644.",
      buttonText: "\u0637\u0644\u0628 \u062E\u062F\u0645\u0629"
    }
  },
  actions: {
    retry: "\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629",
    markAllAsRead: "\u062A\u062D\u062F\u064A\u062F \u0627\u0644\u0643\u0644 \u0643\u0645\u0642\u0631\u0648\u0621",
    backToTickets: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u062A\u0630\u0627\u0643\u0631",
    createAnotherTicket: "\u0625\u0646\u0634\u0627\u0621 \u062A\u0630\u0643\u0631\u0629 \u0623\u062E\u0631\u0649",
    acceptProposal: "\u0646\u0639\u0645\u060C \u0623\u0648\u0627\u0641\u0642 \u0639\u0644\u0649 \u0647\u0630\u0627 \u0627\u0644\u0639\u0631\u0636",
    rejectProposal: "\u0644\u0627\u060C \u0623\u062D\u062A\u0627\u062C \u0625\u0644\u0649 \u0645\u0631\u0627\u062C\u0639\u0629"
  },
  companyForm: {
    title: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
    countryOfHeadquarters: "\u062F\u0648\u0644\u0629 \u0627\u0644\u0645\u0642\u0631 \u0627\u0644\u0631\u0626\u064A\u0633\u064A",
    registeredAddressHeadquarters: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0633\u062C\u0644 \u0644\u0644\u0645\u0642\u0631 \u0627\u0644\u0631\u0626\u064A\u0633\u064A"
  },
  documents: {
    companyRegistrationCertificate: "\u0634\u0647\u0627\u062F\u0629 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
    vatCertificate: "\u0634\u0647\u0627\u062F\u0629 \u0636\u0631\u064A\u0628\u0629 \u0627\u0644\u0642\u064A\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629 \u0633\u0627\u0631\u064A\u0629 \u0627\u0644\u0645\u0641\u0639\u0648\u0644",
    nationalAddressId: "\u0645\u0639\u0631\u0641 \u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0648\u0637\u0646\u064A",
    lastReceived: "(\u0622\u062E\u0631 \u0627\u0633\u062A\u0644\u0627\u0645 {date})",
    serviceDocumentsInfo: '\u0644\u0644\u0627\u0637\u0644\u0627\u0639 \u0639\u0644\u0649 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0645\u062A\u0639\u0644\u0642\u0629 \u0628\u0627\u0644\u062E\u062F\u0645\u0629\u060C \u0627\u0628\u062D\u062B \u0636\u0645\u0646 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u062D\u062F\u062F\u0629 \u0641\u064A \u0642\u0633\u0645 "\u062E\u062F\u0645\u0627\u062A\u064A".'
  },
  documentsTable: {
    documentName: "\u0627\u0633\u0645 \u0627\u0644\u0645\u0633\u062A\u0646\u062F",
    type: "\u0627\u0644\u0646\u0648\u0639",
    uploadDate: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0631\u0641\u0639",
    uploadedBy: "\u062A\u0645 \u0627\u0644\u0631\u0641\u0639 \u0628\u0648\u0627\u0633\u0637\u0629",
    action: "\u0627\u0644\u0625\u062C\u0631\u0627\u0621",
    download: "\u062A\u062D\u0645\u064A\u0644",
    failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    loadingDocuments: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A...",
    noDocuments: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0645\u0631\u0641\u0648\u0639\u0629",
    noDocumentsText: "\u0633\u062A\u0638\u0647\u0631 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0645\u062A\u0639\u0644\u0642\u0629 \u0628\u0647\u0630\u0647 \u0627\u0644\u062E\u062F\u0645\u0629 \u0647\u0646\u0627 \u0628\u0645\u062C\u0631\u062F \u0631\u0641\u0639\u0647\u0627 \u0623\u0648 \u0645\u0634\u0627\u0631\u0643\u062A\u0647\u0627."
  },
  serviceDetail: {
    stepOf: "\u0627\u0644\u062E\u0637\u0648\u0629 {currentStep} \u0645\u0646 {totalSteps}",
    currentStep: "\u0627\u0644\u062E\u0637\u0648\u0629 \u0627\u0644\u062D\u0627\u0644\u064A\u0629",
    closeStepper: "\u0625\u063A\u0644\u0627\u0642 \u0645\u062D\u062F\u062F \u0627\u0644\u0645\u0631\u0627\u062D\u0644",
    myTasks: "\u0645\u0647\u0627\u0645\u064A",
    tasamaActivities: "\u0623\u0646\u0634\u0637\u0629 \u062A\u0633\u0627\u0645\u0649",
    noActionRequired: "\u0644\u0627 \u064A\u0644\u0632\u0645\u0643 \u0627\u062A\u062E\u0627\u0630 \u0623\u064A \u0625\u062C\u0631\u0627\u0621.",
    requestSubmitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0627\u0644\u0637\u0644\u0628",
    requestSubmittedDescription: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0637\u0644\u0628\u0643\u0645 \u0628\u0646\u062C\u0627\u062D. \u0646\u0642\u0648\u0645 \u062D\u0627\u0644\u064A\u0627\u064B \u0628\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u062E\u0637\u0648\u0627\u062A \u0627\u0644\u062A\u0627\u0644\u064A\u0629 \u0648\u0633\u0646\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0643\u0645 \u0642\u0631\u064A\u0628\u0627\u064B.",
    errorLoading: "\u062E\u0637\u0623 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0629: {error}",
    loadingServiceDetails: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0629...",
    failedToLoadActivities: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0623\u0646\u0634\u0637\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    stepCountDetails: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0639\u062F\u062F \u0627\u0644\u062E\u0637\u0648\u0627\u062A"
  },
  addTravelDateModal: {
    title: "\u0625\u0636\u0627\u0641\u0629 \u062A\u0648\u0627\u0631\u064A\u062E \u0627\u0644\u0633\u0641\u0631",
    description: "\u0627\u0644\u0631\u062C\u0627\u0621 \u062A\u062D\u062F\u064A\u062F \u0627\u0644\u062A\u0648\u0627\u0631\u064A\u062E \u0627\u0644\u062A\u064A \u0633\u062A\u0642\u064A\u0645 \u062E\u0644\u0627\u0644\u0647\u0627 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629. \u064A\u062C\u0628 \u0623\u0646 \u062A\u0634\u0645\u0644 \u0627\u0644\u0625\u0642\u0627\u0645\u0629 \u0645\u0627 \u0644\u0627 \u064A\u0642\u0644 \u0639\u0646 10 \u0623\u064A\u0627\u0645 \u0639\u0645\u0644 \u0644\u062A\u062A\u0645\u0643\u0646 \u062A\u0633\u0627\u0645\u0649 \u0645\u0646 \u0627\u0644\u0645\u0636\u064A \u0642\u062F\u0645\u064B\u0627 \u0641\u064A \u0625\u062A\u0645\u0627\u0645 \u0623\u0646\u0634\u0637\u0629 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629.",
    note: "\u0645\u0644\u0627\u062D\u0638\u0629: \u0642\u062F \u062A\u0624\u062B\u0631 \u0627\u0644\u0639\u0637\u0644\u0627\u062A \u0627\u0644\u0631\u0633\u0645\u064A\u0629 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629 \u0639\u0644\u0649 \u0645\u062F\u0629 \u0627\u0644\u0625\u0642\u0627\u0645\u0629 \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629. \u062E\u0644\u0627\u0644 \u0634\u0647\u0631 \u0631\u0645\u0636\u0627\u0646 \u0627\u0644\u0645\u0628\u0627\u0631\u0643\u060C \u0646\u0648\u0635\u064A \u0628\u0627\u0644\u062A\u062E\u0637\u064A\u0637 \u0644\u0625\u0642\u0627\u0645\u0629 \u062A\u0628\u0644\u063A \u062D\u0648\u0627\u0644\u064A 15 \u064A\u0648\u0645 \u0639\u0645\u0644\u060C \u0646\u0638\u0631\u064B\u0627 \u0644\u062A\u0642\u0644\u064A\u0635 \u0633\u0627\u0639\u0627\u062A \u0627\u0644\u0639\u0645\u0644 \u0627\u0644\u0630\u064A \u0642\u062F \u064A\u0624\u062B\u0631 \u0639\u0644\u0649 \u0645\u0648\u0627\u0639\u064A\u062F \u0627\u0644\u0645\u0639\u0627\u0644\u062C\u0629 \u0644\u062F\u0649 \u0627\u0644\u062C\u0647\u0627\u062A \u0627\u0644\u0645\u0639\u0646\u064A\u0629.",
    weekendsLegend: "\u0639\u0637\u0644 \u0646\u0647\u0627\u064A\u0629 \u0627\u0644\u0623\u0633\u0628\u0648\u0639 (\u0645\u0639\u0637\u0651\u0644)",
    publicHolidaysLegend: "\u0627\u0644\u0639\u0637\u0644\u0627\u062A \u0627\u0644\u0631\u0633\u0645\u064A\u0629 (\u0645\u0639\u0637\u0651\u0644)",
    fromLabel: "\u0645\u0646",
    toLabel: "\u0625\u0644\u0649",
    fromTitle: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0628\u062F\u0627\u064A\u0629",
    toTitle: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0646\u0647\u0627\u064A\u0629",
    validation: {
      fromRequired: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0628\u062F\u0627\u064A\u0629 \u0645\u0637\u0644\u0648\u0628",
      toRequired: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0646\u0647\u0627\u064A\u0629 \u0645\u0637\u0644\u0648\u0628",
      fromFuture: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0628\u062F\u0627\u064A\u0629 \u0627\u0644\u064A\u0648\u0645 \u0623\u0648 \u0641\u064A \u0627\u0644\u0645\u0633\u062A\u0642\u0628\u0644",
      toAfterFrom: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0646\u0647\u0627\u064A\u0629 \u0628\u0639\u062F \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0628\u062F\u0627\u064A\u0629",
      minStayDays: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u062A\u0642\u0644 \u0645\u062F\u0629 \u0627\u0644\u0625\u0642\u0627\u0645\u0629 \u0639\u0646 {days} \u064A\u0648\u0645\u0627\u064B (\u0623\u0633\u0628\u0648\u0639\u0627\u0646)"
    }
  },
  company: {
    type: {
      pif_established: "\u062A\u0623\u0633\u0633\u062A \u0645\u0646 \u0642\u0650\u0628\u0644 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629",
      pif_under_dev: "\u0642\u064A\u062F \u0627\u0644\u062A\u0637\u0648\u064A\u0631 \u0645\u0646 \u0642\u0650\u0628\u0644 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629",
      ksa_private: "\u0634\u0631\u0643\u0629 \u0633\u0639\u0648\u062F\u064A\u0629 \u062E\u0627\u0635\u0629",
      international: "\u062F\u0648\u0644\u064A\u0629"
    }
  },
  onboarding: {
    action: {
      start: "\u0628\u062F\u0621 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 / \u0627\u0644\u0627\u0646\u0636\u0645\u0627\u0645",
      contact_details: "\u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u062A\u0648\u0627\u0635\u0644",
      company_details: "\u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629",
      project_details: "\u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u0634\u0631\u0648\u0639",
      upload_docs: "\u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629",
      service_needs: "\u062A\u062D\u062F\u064A\u062F \u0627\u062D\u062A\u064A\u0627\u062C\u0627\u062A \u0627\u0644\u062E\u062F\u0645\u0629",
      terms: "\u0642\u0628\u0648\u0644 \u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645",
      submit: "\u062A\u0642\u062F\u064A\u0645 \u0644\u0644\u0645\u0648\u0627\u0641\u0642\u0629",
      awaiting_approval: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629",
      awaiting_sales: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0645\u0631\u0627\u062C\u0639\u0629 \u0641\u0631\u064A\u0642 \u0627\u0644\u0645\u0628\u064A\u0639\u0627\u062A",
      awaiting_crm: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0625\u0646\u0634\u0627\u0621 \u062D\u0633\u0627\u0628 \u0641\u064A \u0646\u0638\u0627\u0645 \u0625\u062F\u0627\u0631\u0629 \u0639\u0644\u0627\u0642\u0627\u062A \u0627\u0644\u0639\u0645\u0644\u0627\u0621",
      awaiting_auth: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0625\u0639\u062F\u0627\u062F \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629",
      set_password: "\u062A\u0639\u064A\u064A\u0646 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
      activation_pending: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u2014 \u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u062D\u0633\u0627\u0628 \u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      complete: "\u0627\u0643\u062A\u0645\u0644 \u0627\u0644\u062A\u0633\u062C\u064A\u0644",
      rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u0644\u0637\u0644\u0628",
      cancelled: "\u062A\u0645 \u0625\u0644\u063A\u0627\u0621 \u0627\u0644\u062A\u0633\u062C\u064A\u0644",
      contact_support: "\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0641\u0631\u064A\u0642 \u0627\u0644\u062F\u0639\u0645"
    }
  },
  ar: {
    task: {
      more_info_required: "\u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0625\u0636\u0627\u0641\u064A\u0629 \u062D\u0648\u0644 {activityName}",
      awaiting_customer: "\u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0625\u0636\u0627\u0641\u064A\u0629 \u062D\u0648\u0644 {activityName}"
    }
  },
  kyb: {
    tile: {
      title: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629"
    },
    task: {
      complete_details: "\u0623\u0643\u0645\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0634\u0631\u0643\u062A\u0643",
      modify_details: "\u062A\u0639\u062F\u064A\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0634\u0631\u0643\u062A\u0643"
    },
    action: {
      upload: "\u0631\u0641\u0639 \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0634\u0631\u0643\u0629 \u0648\u062A\u0642\u062F\u064A\u0645\u0647\u0627 \u0644\u0644\u0645\u0631\u0627\u062C\u0639\u0629",
      under_review: "\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0634\u0631\u0643\u0629 \u0642\u064A\u062F \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0627\u0645\u062A\u062B\u0627\u0644. \u064A\u0631\u062C\u0649 \u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629.",
      approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0634\u0631\u0643\u0629. \u0644\u0627 \u064A\u0644\u0632\u0645 \u0627\u062A\u062E\u0627\u0630 \u0623\u064A \u0625\u062C\u0631\u0627\u0621.",
      rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0634\u0631\u0643\u0629. \u064A\u0631\u062C\u0649 \u0645\u0639\u0627\u0644\u062C\u0629 \u0627\u0644\u0645\u0644\u0627\u062D\u0638\u0627\u062A \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
      none: "\u0644\u0627 \u064A\u0644\u0632\u0645 \u0627\u062A\u062E\u0627\u0630 \u0623\u064A \u0625\u062C\u0631\u0627\u0621."
    }
  },
  common: {
    helper: {
      under_review: "\u0637\u0644\u0628\u0643 \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0645\u0646 \u0642\u0650\u0628\u0644 \u0641\u0631\u064A\u0642\u0646\u0627.",
      updates_required: "\u064A\u0633\u062A\u0644\u0632\u0645 \u0637\u0644\u0628\u0643 \u062A\u062D\u062F\u064A\u062B\u0627\u062A."
    }
  },
  event: {
    source: {
      system: "\u0627\u0644\u0646\u0638\u0627\u0645",
      customer: "\u0627\u0644\u0639\u0645\u064A\u0644",
      itsm: "\u062E\u062F\u0645\u0629 \u0625\u062F\u0627\u0631\u0629 \u062A\u0642\u0646\u064A\u0629 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0648\u0627\u0644\u062E\u062F\u0645\u0627\u062A",
      snow: "ServiceNow"
    }
  },
  bundle: {
    step: {
      scope_of_work: "\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
      proposal: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0639\u0631\u0636 \u062A\u0633\u0627\u0645\u0649",
      contract: "\u062A\u0648\u0642\u064A\u0639 \u0639\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629",
      purchase_order: "\u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0627\u0644\u0634\u0631\u0627\u0621",
      activation: "\u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0629",
      error: "\u062E\u0637\u0623"
    },
    state: {
      submitted: "\u062A\u0645 \u0627\u0644\u0625\u0631\u0633\u0627\u0644",
      proposal_route_decided: "\u062C\u0627\u0631\u064D \u0645\u0639\u0627\u0644\u062C\u0629 \u0627\u0644\u0639\u0631\u0636",
      proposal_received: "\u062A\u0645 \u0627\u0633\u062A\u0644\u0627\u0645 \u0627\u0644\u0639\u0631\u0636",
      proposal_accepted: "\u062A\u0645 \u0642\u0628\u0648\u0644 \u0627\u0644\u0639\u0631\u0636",
      proposal_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u0644\u0639\u0631\u0636",
      contract_route_decided: "\u062C\u0627\u0631\u064D \u0645\u0639\u0627\u0644\u062C\u0629 \u0627\u0644\u0639\u0642\u062F",
      contract_received: "\u062A\u0645 \u0627\u0633\u062A\u0644\u0627\u0645 \u0627\u0644\u0639\u0642\u062F",
      contract_signed: "\u062A\u0645 \u062A\u0648\u0642\u064A\u0639 \u0627\u0644\u0639\u0642\u062F",
      contract_under_review: "\u0627\u0644\u0639\u0642\u062F \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629",
      msa_addendum_in_progress: "\u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0642\u064A\u062F \u0627\u0644\u0625\u0639\u062F\u0627\u062F",
      msa_addendum_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      msa_addendum_approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      msa_addendum_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      contract_approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0627\u0644\u0639\u0642\u062F",
      contract_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u0644\u0639\u0642\u062F",
      po_route_decided: "\u062C\u0627\u0631\u064D \u0645\u0639\u0627\u0644\u062C\u0629 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
      po_required: "\u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 \u0645\u0637\u0644\u0648\u0628",
      po_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
      po_approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
      po_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
      pending_activation: "\u0627\u0644\u062A\u0641\u0639\u064A\u0644 \u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      split_complete: "\u0627\u0643\u062A\u0645\u0644 \u0627\u0644\u062A\u0642\u0633\u064A\u0645",
      error: "\u062E\u0637\u0623"
    }
  },
  sr: {
    state: {
      pending: "\u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      sow_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
      proposal_received: "\u062A\u0645 \u0627\u0633\u062A\u0644\u0627\u0645 \u0627\u0644\u0639\u0631\u0636",
      proposal_accepted: "\u062A\u0645 \u0642\u0628\u0648\u0644 \u0627\u0644\u0639\u0631\u0636",
      proposal_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u0644\u0639\u0631\u0636",
      contract_received: "\u062A\u0645 \u0627\u0633\u062A\u0644\u0627\u0645 \u0627\u0644\u0639\u0642\u062F",
      contract_signed: "\u062A\u0645 \u062A\u0648\u0642\u064A\u0639 \u0627\u0644\u0639\u0642\u062F",
      contract_under_review: "\u0627\u0644\u0639\u0642\u062F \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629",
      contract_approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0627\u0644\u0639\u0642\u062F",
      contract_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u0644\u0639\u0642\u062F",
      po_required: "\u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 \u0645\u0637\u0644\u0648\u0628",
      po_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
      po_approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
      po_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
      msa_addendum_in_progress: "\u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0642\u064A\u062F \u0627\u0644\u0625\u0639\u062F\u0627\u062F",
      msa_addendum_approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      msa_addendum_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      nda_pending: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      pending_activation: "\u0627\u0644\u062A\u0641\u0639\u064A\u0644 \u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      active: "\u0646\u0634\u0637",
      completed: "\u0645\u0643\u062A\u0645\u0644",
      rejected: "\u0645\u0631\u0641\u0648\u0636"
    },
    step: {
      scope_of_work: "\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
      review_proposal: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0639\u0631\u0636 \u062A\u0633\u0627\u0645\u0649",
      sign_contract: "\u062A\u0648\u0642\u064A\u0639 \u0639\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629",
      sign_addendum: "\u062A\u0648\u0642\u064A\u0639 \u0627\u0644\u0645\u0644\u062D\u0642",
      review_msa: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      submit_po: "\u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0627\u0644\u0634\u0631\u0627\u0621",
      pending_activation: "\u0627\u0644\u062A\u0641\u0639\u064A\u0644 \u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      active: "\u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0629"
    },
    route: {
      service_contract: "\u0639\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629",
      addendum: "\u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      covered_in_msa: "\u0645\u0634\u0645\u0648\u0644 \u0641\u064A \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629"
    },
    helper: {
      sow_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0646\u0637\u0627\u0642 \u0639\u0645\u0644\u0643. \u062A\u0633\u0627\u0645\u0649 \u062A\u0631\u0627\u062C\u0639\u0647 \u0627\u0644\u0622\u0646.",
      proposal_received: "\u0623\u0631\u0633\u0644\u062A \u0625\u0644\u064A\u0643 \u062A\u0633\u0627\u0645\u0649 \u0639\u0631\u0636\u0627\u064B. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0648\u0627\u0644\u0631\u062F.",
      proposal_accepted: "\u062A\u0645 \u0642\u0628\u0648\u0644 \u0639\u0631\u0636\u0643. \u064A\u0631\u062C\u0649 \u0627\u0646\u062A\u0638\u0627\u0631 \u0639\u0642\u062F\u0643.",
      proposal_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0639\u0631\u0636\u0643. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0645\u0633\u062A\u0634\u0627\u0631\u0643.",
      contract_received: "\u0639\u0642\u062F \u062E\u062F\u0645\u062A\u0643 \u062C\u0627\u0647\u0632. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0648\u0627\u0644\u062A\u0648\u0642\u064A\u0639.",
      contract_signed: "\u062A\u0645 \u062A\u0648\u0642\u064A\u0639 \u0639\u0642\u062F\u0643 \u0648\u0625\u0631\u0633\u0627\u0644\u0647 \u0644\u0644\u0645\u0631\u0627\u062C\u0639\u0629.",
      contract_under_review: "\u0639\u0642\u062F\u0643 \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u062D\u0627\u0644\u064A\u0627\u064B.",
      contract_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0639\u0642\u062F\u0643. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0645\u0633\u062A\u0634\u0627\u0631\u0643.",
      msa_addendum_in_progress: "\u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0642\u064A\u062F \u0627\u0644\u0625\u0639\u062F\u0627\u062F.",
      msa_addendum_approved: "\u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0645\u0646 \u0642\u0628\u0644 \u062A\u0633\u0627\u0645\u0649.",
      msa_addendum_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0645\u0633\u062A\u0634\u0627\u0631\u0643.",
      po_required: "\u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 \u0645\u0637\u0644\u0648\u0628 \u0644\u0644\u0645\u062A\u0627\u0628\u0639\u0629.",
      po_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621.",
      po_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621.",
      nda_pending: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u062A\u0648\u0642\u064A\u0639.",
      pending_activation: "\u062E\u062F\u0645\u062A\u0643 \u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u062A\u0641\u0639\u064A\u0644.",
      pending_activation_sales_off: "\u062C\u0627\u0631\u064D \u062A\u0641\u0639\u064A\u0644 \u062E\u062F\u0645\u062A\u0643. \u064A\u0633\u062A\u063A\u0631\u0642 \u0630\u0644\u0643 \u0639\u0627\u062F\u0629\u064B \u0628\u0636\u0639\u0629 \u0623\u064A\u0627\u0645. \u0644\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u062A\u062D\u062F\u064A\u062B\u060C \u064A\u064F\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0645\u062F\u064A\u0631 \u062D\u0633\u0627\u0628\u0643 \u0627\u0644\u0631\u0626\u064A\u0633\u064A \u0623\u0648 \u0625\u0646\u0634\u0627\u0621 \u062A\u0630\u0643\u0631\u0629 \u062F\u0639\u0645."
    },
    pricing: {
      frequency: {
        monthly: "\u0634\u0647\u0631\u064A\u0627\u064B",
        one_time: "\u0645\u0631\u0629 \u0648\u0627\u062D\u062F\u0629"
      }
    },
    cta: {
      upload: "\u0631\u0641\u0639",
      review: "\u0645\u0631\u0627\u062C\u0639\u0629",
      submit: "\u062A\u0642\u062F\u064A\u0645"
    }
  },
  tbl: {
    status: {
      not_started: "\u0644\u0645 \u062A\u0628\u062F\u0623",
      in_progress: "\u062C\u0627\u0631\u064D \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
      more_info_required: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A",
      completed: "\u0645\u0643\u062A\u0645\u0644",
      on_hold: "\u0645\u0639\u0644\u0642",
      payment_required: "\u0645\u0637\u0644\u0648\u0628 \u0627\u0644\u062F\u0641\u0639",
      bypassed: "\u0645\u062A\u062E\u0637\u0649",
      waiting_for_tasama: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u062A\u0633\u0627\u0645\u0649",
      pending: "\u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631"
    },
    stage: {
      not_started: "\u0644\u0645 \u062A\u0628\u062F\u0623",
      in_progress: "\u062C\u0627\u0631\u064A\u0629 \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
      completed: "\u0645\u0643\u062A\u0645\u0644\u0629"
    },
    overall: {
      in_progress: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
      completed: "\u0645\u0643\u062A\u0645\u0644"
    },
    payment: {
      pending: "\u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      invoiced: "\u0635\u062F\u0631\u062A \u0641\u0627\u062A\u0648\u0631\u0629",
      proof_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
      confirmed: "\u0645\u0624\u0643\u062F"
    },
    milestone: {
      service_initiation: "\u0628\u062F\u0627\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629",
      doc_legal: "\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0648\u0627\u0644\u0634\u0624\u0648\u0646 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A\u0629",
      company_formation: "\u062A\u0623\u0633\u064A\u0633 \u0627\u0644\u0634\u0631\u0643\u0629",
      gm_visa: "\u062A\u0623\u0634\u064A\u0631\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 / \u0627\u0644\u0645\u0646\u0635\u0629 \u0627\u0644\u062D\u0643\u0648\u0645\u064A\u0629",
      company_activated: "\u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629"
    },
    action: {
      kickoff_required: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0625\u0637\u0644\u0627\u0642 \u0645\u0637\u0644\u0648\u0628\u0629",
      courier_required: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u062D\u0646 \u0645\u0637\u0644\u0648\u0628\u0629",
      general_manager_required: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0645\u0637\u0644\u0648\u0628\u0629",
      travel_dates_required: "\u062A\u0648\u0627\u0631\u064A\u062E \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0645\u0637\u0644\u0648\u0628\u0629",
      payment_service_initiation: "\u062F\u0641\u0639\u0629 \u0628\u062F\u0621 \u0627\u0644\u062E\u062F\u0645\u0629",
      payment_doc_legal: "\u062F\u0641\u0639\u0629 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0648\u0627\u0644\u0634\u0624\u0648\u0646 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A\u0629",
      payment_company_formation: "\u062F\u0641\u0639\u0629 \u062A\u0623\u0633\u064A\u0633 \u0627\u0644\u0634\u0631\u0643\u0629",
      payment_gm_visa: "\u062F\u0641\u0639\u0629 \u062A\u0623\u0634\u064A\u0631\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 / \u0627\u0644\u0645\u0646\u0635\u0629 \u0627\u0644\u062D\u0643\u0648\u0645\u064A\u0629",
      payment_company_activated: "\u062F\u0641\u0639\u0629 \u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629"
    },
    attestation: {
      apostille: "\u0627\u0644\u0623\u0628\u0648\u0633\u062A\u064A\u0644",
      embassy: "\u0627\u0644\u0633\u0641\u0627\u0631\u0629"
    },
    gm: {
      nationality: {
        saudi: "\u0633\u0639\u0648\u062F\u064A",
        non_saudi: "\u063A\u064A\u0631 \u0633\u0639\u0648\u062F\u064A"
      }
    },
    doc: {
      gm_passport: "\u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      ownership_structure: "\u0647\u064A\u0643\u0644 \u0627\u0644\u0645\u0644\u0643\u064A\u0629",
      ubo_passport: "\u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u0627\u0644\u0643 \u0627\u0644\u0641\u0639\u0644\u064A",
      courier_soft_copy: "\u0646\u0633\u062E\u0629 \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A\u0629 \u0644\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0633\u0631\u064A\u0639",
      payment_proof: "\u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
      proforma_invoice: "\u0641\u0627\u062A\u0648\u0631\u0629 \u0623\u0648\u0644\u064A\u0629",
      aoa_draft: "\u0645\u0633\u0648\u062F\u0629 \u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      aoa: "\u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633 \u0627\u0644\u0646\u0647\u0627\u0626\u064A",
      cr: "\u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A \u0627\u0644\u0646\u0647\u0627\u0626\u064A",
      misa_license: "\u062A\u0631\u062E\u064A\u0635 \u0648\u0632\u0627\u0631\u0629 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631",
      iqama: "\u0648\u062B\u064A\u0642\u0629 \u0627\u0644\u0625\u0642\u0627\u0645\u0629",
      final_doc_package: "\u062D\u0632\u0645\u0629 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0646\u0647\u0627\u0626\u064A\u0629",
      gm_visa: "\u0648\u062B\u064A\u0642\u0629 \u062A\u0623\u0634\u064A\u0631\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      trade_name_cert: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
      bank_account_certification: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062D\u0633\u0627\u0628 \u0627\u0644\u0628\u0646\u0643\u064A",
      tax_invoice: "\u0641\u0627\u062A\u0648\u0631\u0629 \u0636\u0631\u064A\u0628\u064A\u0629",
      receipt: "\u0625\u064A\u0635\u0627\u0644 \u0631\u0633\u0645\u064A",
      commercial_registration: "\u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
      articles_of_association: "\u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      memorandum_of_association: "\u0627\u0644\u0646\u0638\u0627\u0645 \u0627\u0644\u0623\u0633\u0627\u0633\u064A",
      financial_statement: "\u0627\u0644\u0628\u064A\u0627\u0646 \u0627\u0644\u0645\u0627\u0644\u064A",
      power_of_attorney: "\u062A\u0648\u0643\u064A\u0644 \u0631\u0633\u0645\u064A",
      board_resolution: "\u0642\u0631\u0627\u0631 \u0645\u062C\u0644\u0633 \u0627\u0644\u0625\u062F\u0627\u0631\u0629",
      cert_incorporation: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      cert_good_standing: "\u0634\u0647\u0627\u062F\u0629 \u062D\u0633\u0646 \u0627\u0644\u0633\u064A\u0631\u0629 \u0648\u0627\u0644\u0633\u0645\u0639\u0629",
      cert_incumbency: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062A\u0641\u0648\u064A\u0636",
      authorization_letter: "\u062E\u0637\u0627\u0628 \u062A\u0641\u0648\u064A\u0636",
      ubo_declaration: "\u0625\u0642\u0631\u0627\u0631 \u0627\u0644\u0645\u0644\u0643\u064A\u0629 \u0627\u0644\u0641\u0639\u0644\u064A\u0629",
      shareholder_register: "\u0633\u062C\u0644 \u0627\u0644\u0645\u0633\u0627\u0647\u0645\u064A\u0646",
      bylaws: "\u0627\u0644\u0644\u0648\u0627\u0626\u062D \u0627\u0644\u062F\u0627\u062E\u0644\u064A\u0629",
      apostille_cert: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u0623\u0628\u0648\u0633\u062A\u064A\u0644",
      passport_copy: "\u0646\u0633\u062E\u0629 \u0645\u0646 \u062C\u0648\u0627\u0632 \u0627\u0644\u0633\u0641\u0631",
      national_id: "\u0627\u0644\u0647\u0648\u064A\u0629 \u0627\u0644\u0648\u0637\u0646\u064A\u0629"
    },
    attachment: {
      gm_passport: "\u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      ownership_structure: "\u0647\u064A\u0643\u0644 \u0627\u0644\u0645\u0644\u0643\u064A\u0629",
      ubo_passport: "\u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u0627\u0644\u0643 \u0627\u0644\u0641\u0639\u0644\u064A",
      commercial_registration: "\u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
      aoa: "\u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633 (AoA)",
      moa: "\u0627\u0644\u0646\u0638\u0627\u0645 \u0627\u0644\u0623\u0633\u0627\u0633\u064A",
      financial_statement: "\u0627\u0644\u0628\u064A\u0627\u0646 \u0627\u0644\u0645\u0627\u0644\u064A",
      poa: "\u062A\u0648\u0643\u064A\u0644 \u0631\u0633\u0645\u064A (PoA)",
      board_resolution: "\u0642\u0631\u0627\u0631 \u0645\u062C\u0644\u0633 \u0627\u0644\u0625\u062F\u0627\u0631\u0629",
      cert_incorporation: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      cert_good_standing: "\u0634\u0647\u0627\u062F\u0629 \u062D\u0633\u0646 \u0627\u0644\u0633\u064A\u0631\u0629 \u0648\u0627\u0644\u0633\u0645\u0639\u0629",
      cert_incumbency: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062A\u0641\u0648\u064A\u0636",
      authorization_letter: "\u062E\u0637\u0627\u0628 \u062A\u0641\u0648\u064A\u0636",
      ubo_declaration: "\u0625\u0642\u0631\u0627\u0631 \u0627\u0644\u0645\u0644\u0643\u064A\u0629 \u0627\u0644\u0641\u0639\u0644\u064A\u0629",
      shareholder_register: "\u0633\u062C\u0644 \u0627\u0644\u0645\u0633\u0627\u0647\u0645\u064A\u0646",
      bylaws: "\u0627\u0644\u0644\u0648\u0627\u0626\u062D \u0627\u0644\u062F\u0627\u062E\u0644\u064A\u0629",
      apostille_cert: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u0623\u0628\u0648\u0633\u062A\u064A\u0644",
      passport_copy: "\u0646\u0633\u062E\u0629 \u0645\u0646 \u062C\u0648\u0627\u0632 \u0627\u0644\u0633\u0641\u0631",
      national_id: "\u0627\u0644\u0647\u0648\u064A\u0629 \u0627\u0644\u0648\u0637\u0646\u064A\u0629",
      proof_of_payment: "\u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
      other: "\u0623\u062E\u0631\u0649"
    },
    task: {
      pending: "\u0637\u0644\u0628 \u062E\u062F\u0645\u0629 \u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644",
      action_required: "\u0645\u0637\u0644\u0648\u0628 \u0625\u062C\u0631\u0627\u0621 \u0644\u062E\u062F\u0645\u0629 \u0648\u0627\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644"
    },
    helper: {
      in_progress: "\u062E\u062F\u0645\u0629 \u0627\u0644\u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u062A\u062F\u0631\u064A\u062C\u064A \u0642\u064A\u062F \u0627\u0644\u0625\u0639\u062F\u0627\u062F.",
      action_required: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0646\u0643 \u0627\u062A\u062E\u0627\u0630 \u0625\u062C\u0631\u0627\u0621 \u0644\u0644\u0645\u062A\u0627\u0628\u0639\u0629.",
      completed: "\u062A\u0645 \u062A\u0641\u0639\u064A\u0644 \u0634\u0631\u0643\u062A\u0643 \u0628\u0646\u062C\u0627\u062D \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629."
    }
  },
  service: {
    status: {
      requested: "\u0645\u0637\u0644\u0648\u0628\u0629",
      active: "\u0641\u0639\u0627\u0644",
      completed: "\u0645\u0643\u062A\u0645\u0644\u0629",
      rejected: "\u0645\u0631\u0641\u0648\u0636\u0629"
    }
  },
  workflow: {
    type: {
      onboarding: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0639\u0645\u064A\u0644",
      kyb: "\u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0634\u0631\u0643\u0629",
      nda: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D",
      msa: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      tbl: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644",
      service_activation: "\u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u062E\u062F\u0645\u0629",
      service_request: "\u0637\u0644\u0628 \u0627\u0644\u062E\u062F\u0645\u0629",
      hr_request: "\u0637\u0644\u0628 \u0627\u0644\u0645\u0648\u0627\u0631\u062F \u0627\u0644\u0628\u0634\u0631\u064A\u0629",
      payroll: "\u0643\u0634\u0641 \u0627\u0644\u0631\u0648\u0627\u062A\u0628",
      invoice: "\u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629",
      legal_compliance: "\u0627\u0644\u0627\u0645\u062A\u062B\u0627\u0644 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A"
    },
    status: {
      in_progress: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
      completed: "\u0645\u0643\u062A\u0645\u0644",
      failed: "\u0641\u0634\u0644",
      cancelled: "\u0645\u0644\u063A\u0649",
      expired: "\u0645\u0646\u062A\u0647\u064A \u0627\u0644\u0635\u0644\u0627\u062D\u064A\u0629",
      rejected: "\u0645\u0631\u0641\u0648\u0636"
    }
  },
  notification: {
    title: {
      company_status: "\u062A\u062D\u062F\u064A\u062B \u062D\u0627\u0644\u0629 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629",
      new_document: "\u0645\u0633\u062A\u0646\u062F \u062C\u062F\u064A\u062F \u0645\u062A\u0627\u062D",
      nda_status: "\u062A\u062D\u062F\u064A\u062B \u062D\u0627\u0644\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D",
      msa_status: "\u062A\u062D\u062F\u064A\u062B \u062D\u0627\u0644\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      kyb_approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629 - \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0634\u0631\u0643\u0629",
      kyb_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u062A\u062D\u062F\u064A\u062B \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629 - \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0634\u0631\u0643\u0629",
      kyb: {
        approved: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629",
        rejected: "\u062A\u0645 \u0631\u0641\u0636 \u062A\u062D\u062F\u064A\u062B \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629",
        document_available: "\u0648\u062B\u064A\u0642\u0629 \u062C\u062F\u064A\u062F\u0629 \u0645\u062A\u0627\u062D\u0629"
      },
      nda: {
        document_available: "\u0648\u062B\u064A\u0642\u0629 \u062C\u062F\u064A\u062F\u0629 \u0645\u062A\u0627\u062D\u0629",
        rejected: "\u062A\u062D\u062F\u064A\u062B \u062D\u0627\u0644\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D"
      },
      msa: {
        draft_ready: "\u0648\u062B\u064A\u0642\u0629 \u062C\u062F\u064A\u062F\u0629 \u0645\u062A\u0627\u062D\u0629",
        document_available: "\u0648\u062B\u064A\u0642\u0629 \u062C\u062F\u064A\u062F\u0629 \u0645\u062A\u0627\u062D\u0629",
        rejected: "\u062A\u062D\u062F\u064A\u062B \u062D\u0627\u0644\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629"
      },
      ticket: {
        comment: "\u062A\u0639\u0644\u064A\u0642 \u062C\u062F\u064A\u062F \u0639\u0644\u0649 \u062A\u0630\u0643\u0631\u0629 \u0627\u0644\u062F\u0639\u0645",
        status_changed: "\u062A\u063A\u064A\u0631\u062A \u062D\u0627\u0644\u0629 \u062A\u0630\u0643\u0631\u0629 \u0627\u0644\u062F\u0639\u0645"
      },
      team: {
        member_added: "\u062A\u0645\u062A \u0625\u0636\u0627\u0641\u0629 \u0639\u0636\u0648 \u0641\u0631\u064A\u0642 \u062C\u062F\u064A\u062F",
        member_updated: "\u062A\u0645 \u062A\u062D\u062F\u064A\u062B \u0628\u064A\u0627\u0646\u0627\u062A \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642",
        member_activated: "\u062A\u0645 \u062A\u0641\u0639\u064A\u0644 \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642",
        member_deactivated: "\u062A\u0645 \u0625\u0644\u063A\u0627\u0621 \u062A\u0641\u0639\u064A\u0644 \u0639\u0636\u0648 \u0627\u0644\u0641\u0631\u064A\u0642"
      },
      sr: {
        requested: "{serviceName}: \u062A\u063A\u064A\u0631\u062A \u0627\u0644\u062D\u0627\u0644\u0629 \u0625\u0644\u0649 \u0645\u0637\u0644\u0648\u0628",
        proposal_ready: "{serviceName}: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0645\u0647\u0645\u0629 '\u0645\u0631\u0627\u062C\u0639\u0629 \u0639\u0631\u0636 \u062A\u0633\u0627\u0645\u0649'.",
        proposal_accepted: "{serviceName}: \u0645\u0631\u0627\u062C\u0639\u0629 \u0639\u0631\u0636 \u062A\u0633\u0627\u0645\u0649 \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
        proposal_rejected: "{serviceName}: \u0645\u0631\u0627\u062C\u0639\u0629 \u0639\u0631\u0636 \u062A\u0633\u0627\u0645\u0649 \u0645\u0631\u0641\u0648\u0636\u0629",
        contract_ready: "{serviceName}: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0645\u0647\u0645\u0629 '\u062A\u0648\u0642\u064A\u0639 \u0639\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629'.",
        contract_signed: "{serviceName}: \u062A\u0648\u0642\u064A\u0639 \u0639\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629 \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
        po_required: "{serviceName}: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0645\u0647\u0645\u0629 '\u062A\u0642\u062F\u064A\u0645 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621'.",
        po_submitted: "{serviceName}: \u062A\u0642\u062F\u064A\u0645 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
        task_rejected: "{serviceName}: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0645\u0647\u0645\u0629 ''{rejectedTask}''.",
        active: "{serviceName}: \u062A\u063A\u064A\u0631\u062A \u0627\u0644\u062D\u0627\u0644\u0629 \u0625\u0644\u0649 \u0646\u0634\u0637",
        new_document: "{serviceName}: {documentName} \u0645\u062A\u0627\u062D \u0627\u0644\u0622\u0646",
        invoice: "{serviceName}: \u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629 \u0645\u062A\u0627\u062D\u0629 \u0627\u0644\u0622\u0646"
      },
      tbl: {
        requested: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u063A\u064A\u0651\u0631\u062A \u0627\u0644\u062D\u0627\u0644\u0629 \u0625\u0644\u0649 \u0645\u0637\u0644\u0648\u0628\u0629",
        kickoff_assigned: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0625\u0637\u0644\u0627\u0642",
        kickoff_rejected: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0625\u0639\u0627\u062F\u0629 \u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0625\u0637\u0644\u0627\u0642",
        courier_assigned: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u062D\u0646",
        courier_rejected: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0625\u0639\u0627\u062F\u0629 \u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u062D\u0646",
        travel_assigned: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u062A\u0642\u062F\u064A\u0645 \u062A\u0648\u0627\u0631\u064A\u062E \u0627\u0644\u0633\u0641\u0631",
        gm_assigned: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
        payment_assigned: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0631\u0641\u0639 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
        payment_rejected: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0625\u0639\u0627\u062F\u0629 \u0631\u0641\u0639 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
        new_document: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: {documentName} \u0645\u062A\u0627\u062D \u0627\u0644\u0622\u0646",
        payment_confirmed: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u062F\u0641\u0639 \u0644\u0640 {milestoneCode}",
        step_updated: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u063A\u064A\u0651\u0631\u062A \u062D\u0627\u0644\u0629 {stepName} \u0625\u0644\u0649 {status}",
        completed: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644: \u062A\u0645 \u062A\u0641\u0639\u064A\u0644 \u0634\u0631\u0643\u062A\u0643"
      },
      ar: {
        created: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0637\u0644\u0628 \u0646\u0634\u0627\u0637 \u062C\u062F\u064A\u062F",
        group_assigned: "\u062A\u0645 \u062A\u0639\u064A\u064A\u0646 \u0641\u0631\u064A\u0642 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637",
        member_assigned: "\u062A\u0645 \u062A\u0639\u064A\u064A\u0646 \u0639\u0636\u0648 \u0644\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637",
        in_progress: "\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
        more_info_required: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A",
        responded: "\u062A\u0645 \u0627\u0633\u062A\u0644\u0627\u0645 \u0631\u062F \u0627\u0644\u0639\u0645\u064A\u0644",
        confirm_completion: "\u064A\u064F\u0631\u062C\u0649 \u062A\u0623\u0643\u064A\u062F \u0627\u0644\u0625\u062A\u0645\u0627\u0645",
        rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637",
        completed: "\u062A\u0645 \u0625\u062A\u0645\u0627\u0645 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637",
        on_hold: "\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 \u0645\u0639\u0644\u0651\u0642",
        comment_added: {
          client: "\u062A\u0639\u0644\u064A\u0642 \u062C\u062F\u064A\u062F \u0645\u0646 \u0627\u0644\u0639\u0645\u064A\u0644",
          tasama: "\u062A\u062D\u062F\u064A\u062B \u062C\u062F\u064A\u062F \u0645\u0646 \u062A\u0633\u0627\u0645\u0649"
        },
        grace_period_expiring: "\u0641\u062A\u0631\u0629 \u0627\u0644\u0633\u0645\u0627\u062D \u0639\u0644\u0649 \u0648\u0634\u0643 \u0627\u0644\u0627\u0646\u062A\u0647\u0627\u0621",
        auto_closed: "\u062A\u0645 \u0625\u063A\u0644\u0627\u0642 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 \u062A\u0644\u0642\u0627\u0626\u064A\u064B\u0627",
        sla_approaching: "\u0627\u0642\u062A\u0631\u0627\u0628 \u0645\u0648\u0639\u062F \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629",
        sla_breached: "\u062A\u0645 \u062A\u062C\u0627\u0648\u0632 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629"
      }
    },
    msg: {
      kyb: {
        approved: "\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629: \u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u062A\u0639\u062F\u064A\u0644\u0627\u062A\u0643. \u062A\u0638\u0647\u0631 \u0627\u0644\u062A\u062D\u062F\u064A\u062B\u0627\u062A \u0627\u0644\u0622\u0646 \u0641\u064A \u062D\u0633\u0627\u0628\u0643.",
        rejected: "\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629: \u062A\u0645 \u0631\u0641\u0636 \u062A\u0639\u062F\u064A\u0644\u0627\u062A\u0643\u060C \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0625\u0634\u0639\u0627\u0631 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
        document_available: "\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0634\u0631\u0643\u0629: \u062A\u0645 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0648\u062B\u0627\u0626\u0642 \u0634\u0631\u0643\u062A\u0643."
      },
      nda: {
        rejected: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D: \u062A\u0645 \u0631\u0641\u0636 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643\u060C \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0625\u0634\u0639\u0627\u0631 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
        document_available: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D: \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0645\u062A\u0627\u062D\u0629."
      },
      msa: {
        rejected: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629: \u062A\u0645 \u0631\u0641\u0636 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643\u060C \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0625\u0634\u0639\u0627\u0631 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
        draft_ready: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629: \u0645\u0633\u0648\u062F\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u062C\u0627\u0647\u0632\u0629 \u0644\u0644\u0645\u0631\u0627\u062C\u0639\u0629.",
        document_available: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629: \u0639\u0642\u062F \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0645\u062A\u0627\u062D."
      },
      new_document: "{requestType}: {documentName}",
      sr: {
        requested: "\u062A\u0645 \u0627\u0633\u062A\u0644\u0627\u0645 \u0637\u0644\u0628 \u062E\u062F\u0645\u062A\u0643 \u0648\u0647\u0648 \u0627\u0644\u0622\u0646 \u0641\u064A \u062D\u0627\u0644\u0629 \u0645\u0637\u0644\u0648\u0628.",
        proposal_ready: "\u0639\u0631\u0636 \u062A\u0633\u0627\u0645\u0649 \u0627\u0644\u062C\u062F\u064A\u062F \u062C\u0627\u0647\u0632 \u0644\u0645\u0631\u0627\u062C\u0639\u062A\u0643.",
        proposal_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0639\u0631\u0636 \u0648\u0647\u064A \u0627\u0644\u0622\u0646 \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630.",
        proposal_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0639\u0631\u0636\u0643.",
        contract_ready: "\u0639\u0642\u062F \u062E\u062F\u0645\u062A\u0643 \u062C\u0627\u0647\u0632 \u0644\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0648\u0627\u0644\u062A\u0648\u0642\u064A\u0639.",
        contract_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0639\u0642\u062F\u0643 \u0627\u0644\u0645\u0648\u0642\u0639 \u0648\u0647\u0648 \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629.",
        po_ready: "\u062A\u0645\u062A \u0627\u0644\u0645\u0648\u0627\u0641\u0642\u0629 \u0639\u0644\u0649 \u0639\u0642\u062F\u0643. \u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621.",
        po_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621 \u0648\u0647\u0648 \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629.",
        submission_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u062A\u0642\u062F\u064A\u0645\u0643. \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0645\u0644\u0627\u062D\u0638\u0627\u062A \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
        active: "\u062A\u0647\u0627\u0646\u064A\u0646\u0627! \u062E\u062F\u0645\u062A\u0643 \u0627\u0644\u0622\u0646 \u0646\u0634\u0637\u0629.",
        new_document: "\u062A\u0645 \u0631\u0641\u0639 \u0645\u0633\u062A\u0646\u062F \u062C\u062F\u064A\u062F: {documentName}",
        invoice: "\u0641\u0627\u062A\u0648\u0631\u0629 \u062C\u062F\u064A\u062F\u0629 \u0645\u062A\u0627\u062D\u0629. \u064A\u0631\u062C\u0649 \u0625\u062A\u0645\u0627\u0645 \u0627\u0644\u062F\u0641\u0639."
      },
      tbl: {
        requested: "\u062A\u0645 \u0627\u0633\u062A\u0644\u0627\u0645 \u0637\u0644\u0628 \u062E\u062F\u0645\u0629 \u0648\u0627\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644 \u0648\u0647\u0648 \u0627\u0644\u0622\u0646 \u0641\u064A \u062D\u0627\u0644\u0629 \u0645\u0637\u0644\u0648\u0628.",
        kickoff_assigned: "\u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0645\u0647\u0645\u0629 \u062C\u062F\u064A\u062F\u0629 \u0644\u062E\u062F\u0645\u0629 \u0648\u0627\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644. \u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0625\u0637\u0644\u0627\u0642.",
        kickoff_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u062A\u0642\u062F\u064A\u0645 \u0627\u0644\u0625\u0637\u0644\u0627\u0642. \u0627\u0644\u0633\u0628\u0628: {rejectionReason}. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
        courier_assigned: "\u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0645\u0647\u0645\u0629 \u062C\u062F\u064A\u062F\u0629 \u0644\u062E\u062F\u0645\u0629 \u0648\u0627\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644. \u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0633\u0631\u064A\u0639.",
        courier_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u062A\u0642\u062F\u064A\u0645 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0633\u0631\u064A\u0639. \u0627\u0644\u0633\u0628\u0628: {rejectionReason}. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
        travel_assigned: "\u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0645\u0647\u0645\u0629 \u062C\u062F\u064A\u062F\u0629 \u0644\u062E\u062F\u0645\u0629 \u0648\u0627\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644. \u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u062A\u0648\u0627\u0631\u064A\u062E \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645.",
        gm_assigned: "\u062A\u0645 \u062A\u0643\u0644\u064A\u0641\u0643 \u0628\u0645\u0647\u0645\u0629 \u062C\u062F\u064A\u062F\u0629 \u0636\u0645\u0646 \u062E\u062F\u0645\u0629 \u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644. \u064A\u064F\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645.",
        payment_assigned: "\u0642\u0633\u0637 \u0627\u0644\u062F\u0641\u0639 \u0631\u0642\u0645 #{installmentNumber} \u0645\u0633\u062A\u062D\u0642. \u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639.",
        new_document: "\u062A\u0645 \u0631\u0641\u0639 \u0645\u0633\u062A\u0646\u062F \u062C\u062F\u064A\u062F: {documentName}",
        payment_confirmed: "\u062A\u0645 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u062F\u0641\u0639\u062A\u0643 \u0648\u062A\u0623\u0643\u064A\u062F\u0647\u0627.",
        payment_rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639 \u0644\u0644\u0642\u0633\u0637 \u0631\u0642\u0645 #{installmentNumber}. \u0627\u0644\u0633\u0628\u0628: {rejectionReason}. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0648\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062A\u0642\u062F\u064A\u0645.",
        step_updated: "\u062A\u0645 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u062E\u0637\u0648\u0629 {stepName} \u0625\u0644\u0649 {status}.",
        completed: "\u062A\u0647\u0627\u0646\u064A\u0646\u0627! \u062E\u062F\u0645\u0629 \u0648\u0627\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644 \u0645\u0643\u062A\u0645\u0644\u0629 \u0627\u0644\u0622\u0646. \u062A\u0645 \u062A\u0641\u0639\u064A\u0644 \u0634\u0631\u0643\u062A\u0643."
      },
      ticket: {
        comment: "{ticketSubject}: {commentText}",
        status_changed: "{ticketNumber}: \u062A\u063A\u064A\u0631\u062A \u0627\u0644\u062D\u0627\u0644\u0629 \u0625\u0644\u0649 {updatedFields}"
      },
      team: {
        member_added: "\u062A\u0645\u062A \u0625\u0636\u0627\u0641\u0629 \u0639\u0636\u0648 \u0641\u0631\u064A\u0642 \u062C\u062F\u064A\u062F ''{contactName}'' \u0625\u0644\u0649 \u062D\u0633\u0627\u0628\u0643",
        member_updated: "\u062A\u0645 \u062A\u062D\u062F\u064A\u062B \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0644\u0640 ''{contactName}'' ({updatedFields})",
        member_activated: "\u062A\u0645\u062A \u0625\u0636\u0627\u0641\u0629 \u0639\u0636\u0648 \u062C\u062F\u064A\u062F \u0625\u0644\u0649 \u062D\u0633\u0627\u0628\u0643 \u0623\u0648 \u062A\u062D\u062F\u064A\u062B \u0635\u0644\u0627\u062D\u064A\u0627\u062A\u0647: {contactName}",
        member_deactivated: "\u062A\u0645 \u0625\u0644\u063A\u0627\u0621 \u0635\u0644\u0627\u062D\u064A\u0629 \u0648\u0635\u0648\u0644 {contactName} \u0645\u0646 \u0641\u0631\u064A\u0642\u0643"
      },
      ar: {
        created: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u0648\u0647\u0648 \u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629.",
        group_assigned: "\u062A\u0645 \u062A\u0639\u064A\u064A\u0646 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u0625\u0644\u0649 \u0641\u0631\u064A\u0642.",
        member_assigned: "\u062A\u0645 \u062A\u0639\u064A\u064A\u0646 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u0625\u0644\u0649 {ownerName}.",
        in_progress: "\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630 \u0627\u0644\u0622\u0646.",
        more_info_required: "\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u064A\u062D\u062A\u0627\u062C \u0625\u0644\u0649 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0625\u0636\u0627\u0641\u064A\u0629 \u0645\u0646\u0643.",
        responded: "\u0642\u062F\u0651\u0645 \u0627\u0644\u0639\u0645\u064A\u0644 \u0631\u062F\u064B\u0627 \u0639\u0644\u0649 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber}.",
        confirm_completion: "\u064A\u064F\u0631\u062C\u0649 \u062A\u0623\u0643\u064A\u062F \u0625\u062A\u0645\u0627\u0645 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber}.",
        rejected: "\u062A\u0645 \u0631\u0641\u0636 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber}. \u0627\u0644\u0633\u0628\u0628: {reason}",
        completed: "\u062A\u0645 \u0625\u062A\u0645\u0627\u0645 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u0628\u0646\u062C\u0627\u062D.",
        on_hold: "\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u0645\u0639\u0644\u0651\u0642. \u0627\u0644\u0633\u0628\u0628: {reason}",
        comment_added: {
          client: "\u062A\u0645\u062A \u0625\u0636\u0627\u0641\u0629 \u062A\u0639\u0644\u064A\u0642 \u062C\u062F\u064A\u062F \u0625\u0644\u0649 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber}.",
          tasama: "\u062A\u062D\u062F\u064A\u062B \u062C\u062F\u064A\u062F \u0639\u0644\u0649 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u0645\u0646 \u062A\u0633\u0627\u0645\u0649."
        },
        grace_period_expiring: "\u0641\u062A\u0631\u0629 \u0627\u0644\u0633\u0645\u0627\u062D \u0644\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u0639\u0644\u0649 \u0648\u0634\u0643 \u0627\u0644\u0627\u0646\u062A\u0647\u0627\u0621.",
        auto_closed: "\u062A\u0645 \u0625\u063A\u0644\u0627\u0642 \u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber} \u062A\u0644\u0642\u0627\u0626\u064A\u064B\u0627.",
        sla_approaching: "\u064A\u0642\u062A\u0631\u0628 \u0645\u0648\u0639\u062F \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629 \u0644\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber}. \u0623\u064A\u0627\u0645 \u0627\u0644\u0639\u0645\u0644 \u0627\u0644\u0645\u062A\u0628\u0642\u064A\u0629: {remainingDays}.",
        sla_breached: "\u062A\u0645 \u062A\u062C\u0627\u0648\u0632 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629 \u0644\u0637\u0644\u0628 \u0627\u0644\u0646\u0634\u0627\u0637 {arNumber}."
      }
    },
    doc: {
      kyb_verified: "\u062A\u0645 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0634\u0631\u0643\u062A\u0643",
      nda_available: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0645\u062A\u0627\u062D\u0629.",
      msa_available: "\u0639\u0642\u062F \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0645\u062A\u0627\u062D.",
      msa_draft: "\u0645\u0633\u0648\u062F\u0629 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u062C\u0627\u0647\u0632\u0629 \u0644\u0644\u0645\u0631\u0627\u062C\u0639\u0629"
    }
  },
  services: {
    sow: {
      title: "\u0646\u0645\u0648\u0630\u062C \u0637\u0644\u0628 \u0627\u0644\u062E\u062F\u0645\u0629",
      description: "\u0627\u0644\u0631\u062C\u0627\u0621 \u062A\u0642\u062F\u064A\u0645 \u0628\u0639\u0636 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644 \u0644\u0645\u0633\u0627\u0639\u062F\u0629 \u062A\u0633\u0627\u0645\u0649 \u0641\u064A \u0625\u0639\u062F\u0627\u062F \u0639\u0631\u0636 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0648\u0628\u062F\u0621 \u0639\u0645\u0644\u064A\u0629 \u0627\u0644\u062A\u0639\u0627\u0642\u062F.",
      stepLabel: "\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
      reviewLabel: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0639\u0631\u0636",
      defaultFileName: "\u0627\u0642\u062A\u0631\u0627\u062D \u062A\u0633\u0627\u0645\u0627.pdf",
      uploadPOLabel: "\u0631\u0641\u0639 \u0623\u0645\u0631 \u0627\u0644\u0634\u0631\u0627\u0621",
      validation: {
        serviceTierRequired: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629",
        timeframeRequired: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0627\u0644\u0625\u0637\u0627\u0631 \u0627\u0644\u0632\u0645\u0646\u064A \u0644\u0644\u0645\u0634\u0627\u0631\u0643\u0629",
        tooLarge: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0647\u0648 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0633\u062A\u0646\u062F",
        invalidType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645. \u0627\u0644\u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0645\u0633\u0645\u0648\u062D \u0628\u0647\u0627: PDF\u060C PNG\u060C JPG"
      },
      fields: {
        serviceName: "\u0627\u0633\u0645 \u0627\u0644\u062E\u062F\u0645\u0629",
        serviceTier: "\u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629",
        serviceTierPlaceholder: "\u0627\u062E\u062A\u0631 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629",
        timeframeLabel: "\u0645\u062A\u0649 \u062A\u0631\u063A\u0628 \u0641\u064A \u0628\u062F\u0621 \u0623\u0648\u0644 \u062E\u062F\u0645\u0629 \u0644\u0643\u061F",
        timeframePlaceholder: "\u062D\u062F\u062F \u0627\u0644\u0625\u0637\u0627\u0631 \u0627\u0644\u0632\u0645\u0646\u064A \u0644\u0644\u062A\u0639\u0627\u0642\u062F",
        timeframeOptions: {
          thisMonth: "\u0647\u0630\u0627 \u0627\u0644\u0634\u0647\u0631",
          nextMonth: "\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u0642\u0627\u062F\u0645",
          within3Months: "\u062E\u0644\u0627\u0644 3 \u0623\u0634\u0647\u0631",
          within6Months: "\u062E\u0644\u0627\u0644 6 \u0623\u0634\u0647\u0631",
          within12Months: "\u062E\u0644\u0627\u0644 12 \u0634\u0647\u0631\u0627\u064B"
        },
        descriptionLabel: "\u0648\u0635\u0641 \u0645\u062A\u0637\u0644\u0628\u0627\u062A\u0643",
        descriptionPlaceholder: "\u0623\u0636\u0641 \u0623\u064A \u062A\u0648\u0642\u0639\u0627\u062A \u0644\u0644\u062A\u0639\u0627\u0642\u062F\u060C \u0623\u0648 \u0648\u0635\u0641\u0627\u064B \u0645\u062E\u062A\u0635\u0631\u0627\u064B \u0644\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644 \u0627\u0644\u0630\u064A \u062A\u0648\u062F \u0645\u0634\u0627\u0631\u0643\u062A\u0647 \u0645\u0639\u0646\u0627. \u0623\u0648 \u064A\u0645\u0643\u0646\u0643 \u0631\u0641\u0639 \u0645\u0633\u062A\u0646\u062F \u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644.",
        fileSupportingText: "PDF, PNG, JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0644\u0641)"
      }
    },
    activityRequest: {
      title: "\u0637\u0644\u0628 \u0646\u0634\u0627\u0637",
      description: "\u0627\u0644\u0631\u062C\u0627\u0621 \u062A\u0642\u062F\u064A\u0645 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0646\u0634\u0627\u0637 \u0623\u0648 \u0627\u0644\u0637\u0644\u0628 \u0627\u0644\u0630\u064A \u062A\u0631\u064A\u062F \u062A\u0642\u062F\u064A\u0645\u0647. \u0627\u062E\u062A\u0631 \u0646\u0648\u0639 \u0627\u0644\u0646\u0634\u0627\u0637 \u0627\u0644\u0645\u062D\u062F\u062F \u0648\u0623\u0631\u0641\u0642 \u0623\u064A \u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0645\u0637\u0644\u0648\u0628\u0629.",
      stepLabel: "\u0637\u0644\u0628 \u0646\u0634\u0627\u0637",
      validation: {
        titleRequired: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0646\u0634\u0627\u0637",
        activityRequired: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0646\u0634\u0627\u0637",
        tierRequired: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629",
        fieldRequired: "\u0647\u0630\u0627 \u0627\u0644\u062D\u0642\u0644 \u0645\u0637\u0644\u0648\u0628",
        tooLarge: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0647\u0648 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0633\u062A\u0646\u062F",
        invalidType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645. \u0627\u0644\u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0645\u0633\u0645\u0648\u062D \u0628\u0647\u0627: PDF\u060C PNG\u060C CSV\u060C Excel\u060C Word\u060C JPG",
        tooManyFiles: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0647\u0648 10 \u0645\u0644\u0641\u0627\u062A",
        invalidValue: "\u0642\u064A\u0645\u0629 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629"
      },
      requiredSummary: {
        title: "\u0645\u0637\u0644\u0648\u0628 \u0642\u0628\u0644 \u0627\u0644\u0625\u0631\u0633\u0627\u0644",
        schemaError: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u0646\u0645\u0648\u0630\u062C \u0647\u0630\u0627 \u0627\u0644\u0646\u0634\u0627\u0637. \u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u0627\u0644\u0646\u0634\u0627\u0637 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0644\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0646 \u062C\u062F\u064A\u062F."
      },
      fields: {
        title: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646",
        titlePlaceholder: "\u0623\u062F\u062E\u0644 \u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0646\u0634\u0627\u0637",
        activity: "\u0627\u0644\u0646\u0634\u0627\u0637",
        activityPlaceholder: "\u0627\u062E\u062A\u0631 \u0646\u0634\u0627\u0637\u0627\u064B",
        tier: "\u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629",
        tierPlaceholder: "\u0627\u062E\u062A\u0631 \u0645\u0633\u062A\u0648\u0649 \u0627\u0644\u062E\u062F\u0645\u0629",
        descriptionLabel: "\u0627\u0644\u0648\u0635\u0641:",
        slaLabel: "\u0645\u062F\u0629 \u0627\u0644\u062A\u0633\u0644\u064A\u0645:",
        slaBusinessDays: "{count} \u0623\u064A\u0627\u0645 \u0639\u0645\u0644",
        requiredDocuments: "\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629",
        requiredDocumentsList: "\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629:",
        templateForms: "\u0627\u0644\u0646\u0645\u0627\u0630\u062C \u0648\u0627\u0644\u0627\u0633\u062A\u0645\u0627\u0631\u0627\u062A:",
        templates: "\u0627\u0644\u0646\u0645\u0627\u0630\u062C",
        supportingDocuments: "\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062F\u0627\u0639\u0645\u0629",
        fileSupportingText: "PDF\u060C PNG\u060C CSV\u060C Excel\u060C Word\u060C JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0644\u0641\u060C \u062D\u062A\u0649 10 \u0645\u0644\u0641\u0627\u062A)"
      },
      successTitle: "\u0634\u0643\u0631\u0627\u064B \u0644\u0643.",
      successDescription: "\u0633\u062A\u062A\u0644\u0642\u0649 \u0625\u0634\u0639\u0627\u0631\u064B\u0627 \u0639\u0646\u062F \u062A\u0641\u0639\u064A\u0644 \u0646\u0634\u0627\u0637\u0643.",
      multiRow: {
        addButtonLabel: "\u0625\u0636\u0627\u0641\u0629 {label}",
        modalTitle: "\u0625\u0636\u0627\u0641\u0629 {label}",
        editModalTitle: "\u062A\u0639\u062F\u064A\u0644 {label}",
        emptyStateHeading: "\u0644\u0645 \u062A\u062A\u0645 \u0625\u0636\u0627\u0641\u0629 {label} \u0628\u0639\u062F"
      },
      tierNames: {
        AdvancedAnalytics: "\u062A\u062D\u0644\u064A\u0644\u0627\u062A \u0645\u062A\u0642\u062F\u0645\u0629",
        CampaignDelivery: "\u062A\u0633\u0644\u064A\u0645 \u0627\u0644\u062D\u0645\u0644\u0629",
        ComplexAdvisory: "\u0627\u0633\u062A\u0634\u0627\u0631\u0627\u062A \u0645\u062A\u062E\u0635\u0635\u0629",
        EliteOnboarding: "\u062A\u0623\u0647\u064A\u0644 \u0627\u0644\u0646\u062E\u0628\u0629",
        FastTrack: "\u0627\u0644\u0645\u0633\u0627\u0631 \u0627\u0644\u0633\u0631\u064A\u0639",
        FinanceOperations: "\u0627\u0644\u0639\u0645\u0644\u064A\u0627\u062A \u0627\u0644\u0645\u0627\u0644\u064A\u0629",
        HROperations: "\u0639\u0645\u0644\u064A\u0627\u062A \u0627\u0644\u0645\u0648\u0627\u0631\u062F \u0627\u0644\u0628\u0634\u0631\u064A\u0629",
        LegalReview: "\u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A\u0629",
        ProcurementDelivery: "\u062A\u0633\u0644\u064A\u0645 \u0627\u0644\u0645\u0634\u062A\u0631\u064A\u0627\u062A",
        StandardDelivery: "\u0627\u0644\u062A\u0633\u0644\u064A\u0645 \u0627\u0644\u0642\u064A\u0627\u0633\u064A",
        StandardOnboarding: "\u0627\u0644\u062A\u0623\u0647\u064A\u0644 \u0627\u0644\u0642\u064A\u0627\u0633\u064A"
      }
    },
    serviceContract: {
      sidebar: {
        title: "\u0639\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629",
        description: "\u064A\u062A\u0636\u0645\u0646 \u0639\u0642\u062F \u062E\u062F\u0645\u062A\u0643 \u0627\u0644\u0646\u0637\u0627\u0642 \u0648\u0627\u0644\u062C\u062F\u0627\u0648\u0644 \u0627\u0644\u0632\u0645\u0646\u064A\u0629 \u0648\u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A\u0629 \u0644\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u062A\u064A \u0637\u0644\u0628\u062A\u0647\u0627. \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0645\u0633\u062A\u0646\u062F \u0628\u0639\u0646\u0627\u064A\u0629 \u0642\u0628\u0644 \u0627\u0644\u062A\u0648\u0642\u064A\u0639."
      },
      steps: {
        review: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0639\u0642\u062F",
        sign: "\u062A\u0648\u0642\u064A\u0639 \u0627\u0644\u0639\u0642\u062F"
      },
      reviewScreen: {
        title: "\u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0639\u0642\u062F",
        description: "\u0627\u0644\u0631\u062C\u0627\u0621 \u0631\u0641\u0639 \u0646\u0633\u062E\u0629 \u0645\u0646 \u0639\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0645\u0648\u0642\u0639 \u0647\u0646\u0627.",
        readyForDownload: "\u062C\u0627\u0647\u0632 \u0644\u0644\u062A\u062D\u0645\u064A\u0644"
      }
    }
  },
  doc: {
    type: {
      sr: {
        po: "\u0623\u0645\u0631 \u0634\u0631\u0627\u0621",
        signed_contract: "\u0639\u0642\u062F \u0645\u0648\u0642\u0651\u0639",
        contract: "\u0639\u0642\u062F",
        proposal: "\u0639\u0631\u0636",
        sow: "\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
        invoice: "\u0641\u0627\u062A\u0648\u0631\u0629"
      },
      bundle: {
        sow: "\u0646\u0637\u0627\u0642 \u0627\u0644\u0639\u0645\u0644",
        proposal: "\u0639\u0631\u0636",
        contract: "\u0639\u0642\u062F",
        signed_contract: "\u0639\u0642\u062F \u0645\u0648\u0642\u0651\u0639",
        po: "\u0623\u0645\u0631 \u0634\u0631\u0627\u0621",
        msa_addendum_draft: "\u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 (\u0645\u0633\u0648\u062F\u0629)",
        msa_addendum_signed: "\u0645\u0644\u062D\u0642 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 (\u0645\u0648\u0642\u0651\u0639)"
      }
    },
    category: {
      kyb: "\u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0627\u0644\u0634\u0631\u0643\u0629",
      nda: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D",
      msa: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
      service_request: "\u0637\u0644\u0628 \u062E\u062F\u0645\u0629",
      other: "\u0623\u062E\u0631\u0649"
    }
  },
  context: {
    my_account: "\u062D\u0633\u0627\u0628\u064A",
    msa: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0627\u0644\u062E\u062F\u0645\u0629 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    tbl: "\u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644"
  },
  scopeOfWork: {
    finance_accounting: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u0627\u0644\u064A\u0629 \u0648\u0627\u0644\u0645\u062D\u0627\u0633\u0628\u0629",
    hr: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u0648\u0627\u0631\u062F \u0627\u0644\u0628\u0634\u0631\u064A\u0629",
    incubation: "\u062E\u062F\u0645\u0627\u062A \u062D\u0627\u0636\u0646\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644",
    innovation: "\u062E\u062F\u0645\u0627\u062A \u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0627\u0628\u062A\u0643\u0627\u0631",
    marketing: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u062A\u0633\u0648\u064A\u0642 \u0648\u0627\u0644\u0641\u0639\u0627\u0644\u064A\u0627\u062A",
    digital_tech: "\u062E\u062F\u0645\u0627\u062A \u062A\u0642\u0646\u064A\u0629 \u0648\u0631\u0642\u0645\u064A\u0629",
    corporate: "\u062E\u062F\u0645\u0627\u062A \u0625\u062F\u0627\u0631\u0629 \u0623\u0635\u0648\u0644 \u0627\u0644\u0634\u0631\u0643\u0627\u062A",
    soft_landing: "\u062E\u062F\u0645\u0627\u062A \u0648\u0627\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644",
    company_builder: "\u062E\u062F\u0645\u0627\u062A \u0628\u0646\u0627\u0621 \u0627\u0644\u0634\u0631\u0643\u0627\u062A",
    legal: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A\u0629",
    pmo_as_service: "\u0645\u0643\u062A\u0628 \u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0645\u0634\u0627\u0631\u064A\u0639 \u0643\u062E\u062F\u0645\u0629",
    technology: "\u0639\u0645\u0644\u064A\u0627\u062A \u062A\u0642\u0646\u064A\u0629",
    strategic_consulting: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0627\u0633\u062A\u0634\u0627\u0631\u0627\u062A \u0627\u0644\u0627\u0633\u062A\u0631\u0627\u062A\u064A\u062C\u064A\u0629",
    risk_management: "\u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0645\u062E\u0627\u0637\u0631",
    compliance: "\u0627\u0644\u0627\u0645\u062A\u062B\u0627\u0644 \u0648\u0627\u0644\u062A\u0646\u0638\u064A\u0645"
  },
  task: {
    status: {
      incomplete: "\u063A\u064A\u0631 \u0645\u0643\u062A\u0645\u0644\u0629"
    }
  },
  activityRequests: {
    requestButton: "\u0637\u0644\u0628 \u0646\u0634\u0627\u0637",
    emptyState: {
      title: "\u0633\u062A\u0638\u0647\u0631 \u0637\u0644\u0628\u0627\u062A\u0643 \u0647\u0646\u0627.",
      subtitle: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0637\u0644\u0628\u0627\u062A \u0623\u0646\u0634\u0637\u0629 \u0628\u0639\u062F. \u0642\u062F\u0651\u0645 \u0637\u0644\u0628\u0643 \u0627\u0644\u0623\u0648\u0644 \u0644\u0644\u0628\u062F\u0621.",
      noResultsTitle: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0623\u0646\u0634\u0637\u0629",
      noResultsSubtitle: "\u0628\u062D\u062B\u0643 \u0623\u0648 \u0627\u0644\u0641\u0644\u0627\u062A\u0631 \u0627\u0644\u0645\u062D\u062F\u062F\u0629 \u0644\u0645 \u062A\u0637\u0627\u0628\u0642 \u0623\u064A \u0623\u0646\u0634\u0637\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
    },
    filters: {
      searchPlaceholder: "\u0627\u0628\u062D\u062B \u0639\u0646 \u0627\u0644\u0623\u0646\u0634\u0637\u0629 \u0623\u0648 \u0627\u0644\u0639\u0646\u0627\u0648\u064A\u0646 \u0623\u0648 \u0623\u0631\u0642\u0627\u0645 \u0627\u0644\u0637\u0644\u0628\u0627\u062A",
      filterByStatus: "\u062A\u0635\u0641\u064A\u0629 \u062D\u0633\u0628 \u0627\u0644\u062D\u0627\u0644\u0629",
      filterByActivity: "\u062A\u0635\u0641\u064A\u0629 \u062D\u0633\u0628 \u0627\u0644\u0646\u0634\u0627\u0637",
      sortBy: "\u062A\u0631\u062A\u064A\u0628 \u062D\u0633\u0628",
      sortOptions: {
        number: "\u0627\u0644\u0637\u0644\u0628",
        title: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646",
        activityName: "\u0627\u0644\u0646\u0634\u0627\u0637",
        lastUpdatedAt: "\u0627\u0644\u062A\u0627\u0631\u064A\u062E",
        ownerName: "\u0627\u0644\u0645\u0633\u0624\u0648\u0644",
        status: "\u0627\u0644\u062D\u0627\u0644\u0629"
      },
      statuses: {
        OPEN: "\u0645\u0637\u0644\u0648\u0628\u0629",
        WORK_IN_PROGRESS: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
        AWAITING_CUSTOMER: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0639\u0645\u064A\u0644",
        AWAITING_VENDOR: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0648\u0631\u0651\u062F",
        AWAITING_CANDIDATE: "\u0628\u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0631\u0634\u062D",
        CLOSED_COMPLETE: "\u0645\u0643\u062A\u0645\u0644\u0629",
        CLOSED_INCOMPLETE: "\u0645\u063A\u0644\u0642\u0629 (\u063A\u064A\u0631 \u0645\u0643\u062A\u0645\u0644\u0629)"
      }
    },
    table: {
      columns: {
        request: "\u0627\u0644\u0637\u0644\u0628",
        title: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646",
        activity: "\u0627\u0644\u0646\u0634\u0627\u0637",
        date: "\u0627\u0644\u062A\u0627\u0631\u064A\u062E",
        owner: "\u0627\u0644\u0645\u0633\u0624\u0648\u0644",
        status: "\u0627\u0644\u062D\u0627\u0644\u0629"
      },
      viewAction: "\u0639\u0631\u0636",
      unassigned: "\u063A\u064A\u0631 \u0645\u0639\u064A\u0651\u0646"
    },
    drawer: {
      expectedCompletion: "\u0645\u062F\u0629 \u0627\u0644\u062A\u0633\u0644\u064A\u0645:",
      workingDays: "{count} \u0623\u064A\u0627\u0645 \u0639\u0645\u0644",
      overdue: "\u0645\u062A\u0623\u062E\u0631",
      requestLabel: "\u0627\u0644\u0637\u0644\u0628:",
      tierLabel: "\u0627\u0644\u0645\u0633\u062A\u0648\u0649:",
      activityLabel: "\u0627\u0644\u0646\u0634\u0627\u0637:",
      descriptionLabel: "\u0627\u0644\u0648\u0635\u0641:",
      documentRequirementsLabel: "\u0645\u062A\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u0648\u062B\u0627\u0626\u0642:",
      descriptionPlaceholder: "\u0633\u064A\u062A\u0648\u0641\u0631 \u0648\u0635\u0641 \u0627\u0644\u0646\u0634\u0627\u0637 \u0647\u0646\u0627.",
      responseLabel: "\u0627\u0644\u0631\u062F",
      responsePlaceholder: "\u0627\u0643\u062A\u0628 \u0631\u062F\u0643 \u0647\u0646\u0627",
      respondButton: "\u0625\u0631\u0633\u0627\u0644",
      activityInfoAriaLabel: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0646\u0634\u0627\u0637",
      expandPanel: "\u062A\u0648\u0633\u064A\u0639 \u0627\u0644\u0644\u0648\u062D\u0629",
      collapsePanel: "\u0637\u064A \u0627\u0644\u0644\u0648\u062D\u0629",
      closePanel: "\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u0644\u0648\u062D\u0629",
      slaPaused: "\u0645\u062A\u0648\u0642\u0641",
      uploadFiles: "\u0631\u0641\u0639 \u0645\u0644\u0641\u0627\u062A",
      uploadDropzoneText: "\u0627\u0646\u0642\u0631 \u0644\u0644\u062A\u062D\u0645\u064A\u0644 \u0623\u0648 \u0627\u0633\u062D\u0628 \u0648\u0623\u0641\u0644\u062A",
      uploadDropzoneInfo: "PDF\u060C PNG\u060C CSV\u060C Excel\u060C Word \u0623\u0648 JPG \u0641\u0642\u0637 (\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 5 \u0645\u064A\u062C\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0644\u0641. \u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 10 \u0645\u0644\u0641\u0627\u062A)",
      submitSuccess: "\u0646\u062C\u0627\u062D",
      submitError: "\u0641\u0634\u0644 \u0627\u0644\u0625\u0631\u0633\u0627\u0644.",
      noActivity: "\u0644\u0627 \u062A\u0648\u062C\u062F \u062A\u0639\u0644\u064A\u0642\u0627\u062A \u0623\u0648 \u0648\u062B\u0627\u0626\u0642 \u0628\u0639\u062F.",
      noActivitySubtitle: "\u0623\u0631\u0633\u0644 \u0631\u0633\u0627\u0644\u0629 \u0644\u0628\u062F\u0621 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629.",
      loadingConversation: "\u062C\u0627\u0631\u064D \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629\u2026",
      confirmButton: "\u062A\u0623\u0643\u064A\u062F",
      reopenButton: "\u0625\u0639\u0627\u062F\u0629 \u0641\u062A\u062D \u0627\u0644\u0637\u0644\u0628",
      submitReopenButton: "\u0625\u0631\u0633\u0627\u0644",
      completedNotice: "\u062A\u0645 \u0627\u0643\u062A\u0645\u0627\u0644 \u0647\u0630\u0627 \u0627\u0644\u0646\u0634\u0627\u0637.",
      rejectedNotice: "\u062A\u0645 \u0631\u0641\u0636 \u0647\u0630\u0627 \u0627\u0644\u0646\u0634\u0627\u0637 \u0648\u0625\u063A\u0644\u0627\u0642\u0647.",
      moreDetails: "\u0627\u0644\u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644",
      activityDetailsTitle: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0646\u0634\u0627\u0637",
      noneValue: "\u0644\u0627 \u064A\u0648\u062C\u062F.",
      yes: "\u0646\u0639\u0645",
      no: "\u0644\u0627"
    }
  },
  superAdmin: {
    catalogSync: {
      title: "\u0645\u0632\u0627\u0645\u0646\u0629 \u0648\u0646\u0634\u0631 \u0627\u0644\u0643\u062A\u0627\u0644\u0648\u062C",
      description: "\u0627\u0633\u062D\u0628 \u0623\u062D\u062F\u062B \u0643\u062A\u0627\u0644\u0648\u062C \u0644\u0644\u062E\u062F\u0645\u0627\u062A \u0645\u0646 ServiceNow \u0648\u0627\u0646\u0634\u0631\u0647 \u0641\u064A \u0627\u0644\u0628\u0648\u0627\u0628\u0629. \u0633\u064A\u0624\u062F\u064A \u0630\u0644\u0643 \u0625\u0644\u0649 \u0627\u0633\u062A\u0628\u062F\u0627\u0644 \u0627\u0644\u0643\u062A\u0627\u0644\u0648\u062C \u0628\u0627\u0644\u0643\u0627\u0645\u0644 \u0648\u0645\u0633\u062D \u0627\u0644\u0630\u0627\u0643\u0631\u0629 \u0627\u0644\u0645\u0624\u0642\u062A\u0629\u060C \u0644\u062A\u0638\u0647\u0631 \u0627\u0644\u0645\u062D\u062A\u0648\u064A\u0627\u062A \u0627\u0644\u062C\u062F\u064A\u062F\u0629 \u0641\u064A \u0627\u0644\u0628\u0648\u0627\u0628\u0629 \u0641\u0648\u0631\u0627\u064B.",
      publishButton: "\u0627\u0646\u0634\u0631 \u0627\u0644\u0622\u0646",
      publishingButton: "\u062C\u0627\u0631\u064A \u0627\u0644\u0646\u0634\u0631...",
      lastPublished: "\u0622\u062E\u0631 \u0646\u0634\u0631 {when} \u0628\u0648\u0627\u0633\u0637\u0629 {who}",
      neverPublished: "\u0644\u0645 \u064A\u062A\u0645 \u0646\u0634\u0631 \u0627\u0644\u0643\u062A\u0627\u0644\u0648\u062C \u0645\u0646 \u0627\u0644\u0628\u0648\u0627\u0628\u0629 \u0628\u0639\u062F.",
      inProgress: "\u062C\u0627\u0631\u064A \u062A\u0646\u0641\u064A\u0630 \u0639\u0645\u0644\u064A\u0629 \u0646\u0634\u0631. \u0642\u062F \u062A\u0633\u062A\u063A\u0631\u0642 \u0628\u0636\u0639 \u062F\u0642\u0627\u0626\u0642.",
      steps: {
        sync: "\u0627\u0644\u0645\u0632\u0627\u0645\u0646\u0629 \u0645\u0646 ServiceNow",
        serviceCatalogueCache: "\u062A\u062D\u062F\u064A\u062B \u0643\u062A\u0627\u0644\u0648\u062C \u0627\u0644\u062E\u062F\u0645\u0627\u062A",
        cmsCache: "\u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0645\u062D\u062A\u0648\u0649"
      },
      state: {
        PENDING: "\u0641\u064A \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
        RUNNING: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
        SUCCEEDED: "\u062A\u0645",
        FAILED: "\u0641\u0634\u0644",
        UNCONFIRMED: "\u0645\u0627 \u0632\u0627\u0644\u062A \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630"
      },
      succeeded: "\u062A\u0645 \u0646\u0634\u0631 \u0627\u0644\u0643\u062A\u0627\u0644\u0648\u062C \u0628\u0646\u062C\u0627\u062D.",
      failed: "\u0641\u0634\u0644 \u0627\u0644\u0646\u0634\u0631: {reason}",
      conflict: "\u0647\u0646\u0627\u0643 \u0639\u0645\u0644\u064A\u0629 \u0646\u0634\u0631 \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630 \u0628\u0627\u0644\u0641\u0639\u0644. \u0627\u0646\u062A\u0638\u0631 \u062D\u062A\u0649 \u062A\u0646\u062A\u0647\u064A \u0642\u0628\u0644 \u0628\u062F\u0621 \u0639\u0645\u0644\u064A\u0629 \u0623\u062E\u0631\u0649.",
      forbidden: "\u0644\u064A\u0633 \u0644\u062F\u064A\u0643 \u0635\u0644\u0627\u062D\u064A\u0629 \u0646\u0634\u0631 \u0627\u0644\u0643\u062A\u0627\u0644\u0648\u062C.",
      unconfirmed: "\u062A\u0633\u062A\u063A\u0631\u0642 \u0627\u0644\u0645\u0632\u0627\u0645\u0646\u0629 \u0648\u0642\u062A\u0627\u064B \u0623\u0637\u0648\u0644 \u0645\u0645\u0627 \u062A\u0633\u0645\u062D \u0628\u0647 \u0627\u0644\u0628\u0648\u0627\u0628\u0629 \u0648\u0645\u0627 \u0632\u0627\u0644\u062A \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630. \u0644\u0627 \u062A\u0642\u0645 \u0628\u0627\u0644\u0646\u0634\u0631 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 - \u062A\u062D\u0642\u0642 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649 \u0628\u0639\u062F \u0628\u0636\u0639 \u062F\u0642\u0627\u0626\u0642."
    },
    selectCompany: {
      title: "\u0645\u0631\u062D\u0628\u0627\u064B \u0623\u064A\u0647\u0627 \u0627\u0644\u0645\u0634\u0631\u0641 \u0627\u0644\u0639\u0627\u0645",
      label: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0634\u0631\u0643\u0629",
      searchPlaceholder: "\u0627\u0628\u062D\u062B \u0639\u0646 \u0627\u0644\u0634\u0631\u0643\u0627\u062A",
      selectAction: "\u0627\u062E\u062A\u064A\u0627\u0631",
      close: "\u0625\u063A\u0644\u0627\u0642",
      loading: "\u062C\u0627\u0631\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0627\u062A...",
      switchLabel: "\u0627\u0644\u0645\u0634\u0631\u0641 \u0627\u0644\u0639\u0627\u0645",
      switchAction: "\u062A\u063A\u064A\u064A\u0631 \u0627\u0644\u0634\u0631\u0643\u0629"
    }
  }
};

// src/locales/ar/onboarding.json
var onboarding_default2 = {
  navigation: {
    steps: {
      companyType: "\u0646\u0648\u0639 \u0627\u0644\u0634\u0631\u0643\u0629",
      companyDetails: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
      yourDetails: "\u0628\u064A\u0627\u0646\u0627\u062A\u0643",
      projectDetails: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0645\u0634\u0631\u0648\u0639",
      serviceNeeds: "\u0627\u062D\u062A\u064A\u0627\u062C\u0627\u062A \u0627\u0644\u062E\u062F\u0645\u0629",
      uploadDocuments: "\u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A",
      confirmation: "\u0627\u0644\u062A\u0623\u0643\u064A\u062F"
    },
    stepProgress: "\u0627\u0644\u062E\u0637\u0648\u0629 {current, number} \u0645\u0646 {total, number}"
  },
  companyType: {
    title: "\u0646\u0648\u0639 \u0627\u0644\u0634\u0631\u0643\u0629",
    description: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0645\u0646\u0638\u0645\u0629 \u0627\u0644\u062A\u064A \u062A\u0645\u062B\u0644\u0647\u0627. \u0633\u062A\u0633\u0627\u0639\u062F\u0646\u0627 \u0647\u0630\u0647 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0641\u064A \u0627\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u0627\u0644\u0641\u0631\u064A\u0642 \u0627\u0644\u0645\u0646\u0627\u0633\u0628 \u0644\u0645\u0633\u0627\u0639\u062F\u062A\u0643 \u0641\u064A \u0627\u062D\u062A\u064A\u0627\u062C\u0627\u062A\u0643",
    companyStatus: {
      title: "\u062D\u0627\u0644\u0629 \u0627\u0644\u0634\u0631\u0643\u0629",
      description: "\u064A\u0633\u0627\u0639\u062F\u0646\u0627 \u0630\u0644\u0643 \u0639\u0644\u0649 \u062A\u0648\u062C\u064A\u0647\u0643 \u0639\u0628\u0631 \u062E\u0637\u0648\u0627\u062A \u0627\u0644\u0627\u0646\u0636\u0645\u0627\u0645 \u0627\u0644\u0623\u0646\u0633\u0628 \u0644\u0643"
    },
    errorBoundary: "\u062D\u062F\u062B \u062E\u0637\u0623 \u0641\u064A \u0634\u0627\u0634\u0629 \u062D\u0627\u0644\u0629 \u0627\u0644\u0634\u0631\u0643\u0629. \u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0635\u0641\u062D\u0629 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    options: {
      international: {
        label: "\u0634\u0631\u0643\u0629 \u062F\u0648\u0644\u064A\u0629 \u062A\u0646\u062A\u0642\u0644 \u0625\u0644\u0649 \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
        description: "\u0644\u0644\u0634\u0631\u0643\u0627\u062A \u0627\u0644\u062A\u064A \u064A\u0642\u0639 \u0645\u0642\u0631\u0647\u0627 \u062E\u0627\u0631\u062C \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629"
      },
      ksaPrivate: {
        label: "\u0634\u0631\u0643\u0629 \u062E\u0627\u0635\u0629 \u0645\u0642\u0631\u0647\u0627 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
        description: "\u0644\u0644\u0634\u0631\u0643\u0627\u062A \u0627\u0644\u062E\u0627\u0635\u0629 \u0627\u0644\u0645\u0633\u062A\u0642\u0631\u0629 \u0641\u064A \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629"
      },
      pifOwned: {
        label: "\u0634\u0631\u0643\u0629 \u0645\u0645\u0644\u0648\u0643\u0629 \u0644\u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629",
        description: "\u0644\u0634\u0631\u0643\u0627\u062A \u0645\u062D\u0641\u0638\u0629 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629"
      },
      pifEstablished: {
        label: "\u0634\u0631\u0643\u062A\u0646\u0627 \u0645\u0643\u062A\u0645\u0644\u0629 \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
        description: "\u0644\u062F\u064A\u0646\u0627 \u0628\u0627\u0644\u0641\u0639\u0644 \u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A \u0623\u0648 \u0634\u0647\u0627\u062F\u0629 \u0636\u0631\u064A\u0628\u0629 \u0627\u0644\u0642\u064A\u0645\u0629 \u0627\u0644\u0645\u0636\u0627\u0641\u0629 \u0623\u0648 \u0648\u062B\u0627\u0626\u0642 \u062C\u0632\u0626\u064A\u0629"
      },
      pifUnderDevelopment: {
        label: "\u0646\u062D\u0646 \u0645\u0634\u0631\u0648\u0639 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629 \u0642\u064A\u062F \u0627\u0644\u062A\u0637\u0648\u064A\u0631",
        description: "\u0644\u0627 \u064A\u0648\u062C\u062F \u0644\u062F\u064A\u0646\u0627 \u0643\u064A\u0627\u0646 \u0645\u0633\u062C\u0644 \u0628\u0639\u062F \u0648\u0646\u062D\u062A\u0627\u062C \u0625\u0644\u0649 \u062F\u0639\u0645 \u062A\u0633\u0627\u0645\u0649"
      }
    },
    beforeYouBegin: {
      title: "\u0642\u0628\u0644 \u0623\u0646 \u062A\u0628\u062F\u0623",
      description: "\u0633\u062A\u062D\u062A\u0627\u062C \u0625\u0644\u0649 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0648\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062A\u0627\u0644\u064A\u0629 \u0644\u0625\u0643\u0645\u0627\u0644 \u0639\u0645\u0644\u064A\u0629 \u0627\u0644\u0627\u0646\u0636\u0645\u0627\u0645 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643",
      items: {
        companyRegistrationCertificate: "\u0634\u0647\u0627\u062F\u0629 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
        companyOperationDetailsHomeCountry: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0639\u0645\u0644\u064A\u0627\u062A \u0627\u0644\u0623\u0633\u0627\u0633\u064A\u0629 \u0644\u0644\u0634\u0631\u0643\u0629 \u0645\u062B\u0644 \u0627\u0644\u062D\u062C\u0645 \u0648\u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A \u0641\u064A \u0628\u0644\u062F\u0643 \u0627\u0644\u0623\u0635\u0644\u064A",
        otherCountriesRegistration: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0623\u064A \u062F\u0648\u0644 \u0623\u062E\u0631\u0649 \u0645\u0633\u062C\u0644\u0629 \u0641\u064A\u0647\u0627 \u0634\u0631\u0643\u062A\u0643",
        companyOperationDetails: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0639\u0645\u0644\u064A\u0627\u062A \u0627\u0644\u0623\u0633\u0627\u0633\u064A\u0629 \u0644\u0644\u0634\u0631\u0643\u0629 \u0645\u062B\u0644 \u0627\u0644\u062D\u062C\u0645 \u0648\u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A"
      }
    }
  },
  companyDetails: {
    gccCountries: {
      SA: "\u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
      AE: "\u0627\u0644\u0625\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0645\u062A\u062D\u062F\u0629",
      KW: "\u0627\u0644\u0643\u0648\u064A\u062A",
      QA: "\u0642\u0637\u0631",
      BH: "\u0627\u0644\u0628\u062D\u0631\u064A\u0646",
      OM: "\u0639\u064F\u0645\u0627\u0646"
    },
    title: "\u0623\u062E\u0628\u0631\u0646\u0627 \u0639\u0646 \u0634\u0631\u0643\u062A\u0643",
    subtitle: "\u0646\u0633\u062A\u062E\u062F\u0645 \u0647\u0630\u0647 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0644\u062A\u062E\u0635\u064A\u0635 \u062A\u062C\u0631\u0628\u062A\u0643.",
    description: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0645\u0646\u0638\u0645\u0629 \u0627\u0644\u062A\u064A \u062A\u0645\u062B\u0644\u0647\u0627. \u0633\u062A\u0633\u0627\u0639\u062F\u0646\u0627 \u0647\u0630\u0647 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0641\u064A \u0627\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u0627\u0644\u0641\u0631\u064A\u0642 \u0627\u0644\u0645\u0646\u0627\u0633\u0628 \u0644\u0645\u0633\u0627\u0639\u062F\u062A\u0643 \u0641\u064A \u0627\u062D\u062A\u064A\u0627\u062C\u0627\u062A\u0643",
    fields: {
      industrySector: {
        options: {
          agriculture: "\u0627\u0644\u0632\u0631\u0627\u0639\u0629",
          consulting: "\u0627\u0644\u0627\u0633\u062A\u0634\u0627\u0631\u0627\u062A",
          construction: "\u0627\u0644\u0628\u0646\u0627\u0621",
          education: "\u0627\u0644\u062A\u0639\u0644\u064A\u0645",
          energy: "\u0627\u0644\u0637\u0627\u0642\u0629",
          entertainment: "\u0627\u0644\u062A\u0631\u0641\u064A\u0647",
          finance: "\u0627\u0644\u0645\u0627\u0644\u064A\u0629",
          healthcare: "\u0627\u0644\u0631\u0639\u0627\u064A\u0629 \u0627\u0644\u0635\u062D\u064A\u0629",
          hospitality: "\u0627\u0644\u0636\u064A\u0627\u0641\u0629",
          manufacturing: "\u0627\u0644\u062A\u0635\u0646\u064A\u0639",
          realEstate: "\u0627\u0644\u0639\u0642\u0627\u0631\u0627\u062A",
          retail: "\u0627\u0644\u062A\u062C\u0632\u0626\u0629",
          technology: "\u0627\u0644\u062A\u0643\u0646\u0648\u0644\u0648\u062C\u064A\u0627",
          telecommunications: "\u0627\u0644\u0627\u062A\u0635\u0627\u0644\u0627\u062A",
          transportation: "\u0627\u0644\u0646\u0642\u0644",
          other: "\u0623\u062E\u0631\u0649"
        }
      },
      countryOfHeadquarter: {
        label: "\u062F\u0648\u0644\u0629 \u0627\u0644\u0645\u0642\u0631 \u0627\u0644\u0631\u0626\u064A\u0633\u064A"
      },
      addressLine1: {
        labelInternational: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0633\u062C\u0644 \u0644\u0644\u0645\u0642\u0631 \u0627\u0644\u0631\u0626\u064A\u0633\u064A"
      }
    }
  },
  yourDetails: {
    title: "\u0623\u062E\u0628\u0631\u0646\u0627 \u0639\u0646 \u0646\u0641\u0633\u0643",
    subtitle: "\u0646\u062D\u062A\u0627\u062C \u0625\u0644\u0649 \u0628\u0639\u0636 \u0627\u0644\u062A\u0641\u0627\u0635\u064A\u0644 \u0644\u0625\u0639\u062F\u0627\u062F \u062D\u0633\u0627\u0628\u0643.",
    description: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u062C\u0647\u0629 \u0627\u0644\u0627\u062A\u0635\u0627\u0644 \u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629 \u0641\u064A \u0645\u0646\u0634\u0623\u062A\u0643\u0645. \u0633\u062A\u064F\u0633\u062A\u062E\u062F\u0645 \u0647\u0630\u0647 \u0627\u0644\u0628\u064A\u0627\u0646\u0627\u062A \u0645\u0646 \u0642\u0628\u0644 \u0645\u0645\u062B\u0644\u064A \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0643\u0645",
    fields: {
      firstName: {
        placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0633\u0645\u0643 \u0627\u0644\u0623\u0648\u0644"
      },
      lastName: {
        placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643"
      },
      salutation: {
        placeholder: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0644\u0642\u0628"
      },
      jobTitle: {
        placeholder: "\u0623\u062F\u062E\u0644 \u0645\u0633\u0645\u0649 \u0648\u0638\u064A\u0641\u062A\u0643"
      },
      email: {
        placeholder: "\u0623\u062F\u062E\u0644 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A"
      },
      mobile: {
        placeholder: "\u0623\u062F\u062E\u0644 \u0631\u0642\u0645 \u0647\u0627\u062A\u0641\u0643 \u0627\u0644\u0645\u062D\u0645\u0648\u0644"
      }
    }
  },
  projectDetails: {
    title: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0645\u0634\u0631\u0648\u0639",
    subtitle: "\u0633\u0627\u0639\u062F\u0646\u0627 \u0639\u0644\u0649 \u0641\u0647\u0645 \u0645\u062A\u0637\u0644\u0628\u0627\u062A \u0645\u0634\u0631\u0648\u0639\u0643.",
    description: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 \u0627\u0644\u0630\u064A \u062A\u0645\u062B\u0644\u0647. \u0633\u062A\u0633\u0627\u0639\u062F\u0646\u0627 \u0647\u0630\u0647 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0641\u064A \u0627\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u0627\u0644\u0641\u0631\u064A\u0642 \u0627\u0644\u0645\u0646\u0627\u0633\u0628 \u0644\u0645\u0633\u0627\u0639\u062F\u062A\u0643.",
    fields: {
      projectCodeName: {
        label: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0631\u0645\u0632\u064A \u0644\u0644\u0645\u0634\u0631\u0648\u0639",
        placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0633\u0645 \u0645\u0634\u0631\u0648\u0639\u0643"
      },
      country: {
        label: "\u062F\u0648\u0644\u0629 \u0627\u0644\u062A\u0633\u062C\u064A\u0644"
      },
      projectDetails: {
        label: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0645\u0634\u0631\u0648\u0639",
        placeholder: "\u0627\u0643\u062A\u0628 \u0648\u0635\u0641\u0627\u064B \u0645\u0648\u062C\u0632\u0627\u064B \u0644\u0645\u0634\u0631\u0648\u0639\u0643"
      },
      expectedLaunchDate: {
        label: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u0637\u0644\u0627\u0642 \u0627\u0644\u0645\u062A\u0648\u0642\u0639"
      }
    }
  },
  serviceNeeds: {
    title: "\u0645\u0627 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u062A\u064A \u062A\u062D\u062A\u0627\u062C\u0647\u0627 \u061F",
    subtitle: "\u062D\u062F\u062F \u062C\u0645\u064A\u0639 \u0645\u0627 \u064A\u0646\u0637\u0628\u0642.",
    description: "\u0623\u062E\u0628\u0631\u0646\u0627 \u0628\u0645\u0627 \u062A\u062D\u062A\u0627\u062C\u0647 \u0644\u0646\u062A\u0645\u0643\u0646 \u0645\u0646 \u062A\u0632\u0648\u064A\u062F\u0643 \u0628\u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u0646\u0627\u0633\u0628\u0629 \u0639\u0646\u062F \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0643",
    servicesLabel: "\u0627\u062E\u062A\u0631 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u062A\u064A \u062A\u062D\u062A\u0627\u062C \u0627\u0644\u0645\u0633\u0627\u0639\u062F\u0629 \u0641\u064A\u0647\u0627:",
    establishmentTimeframe: {
      label: "\u0645\u062A\u0649 \u062A\u062E\u0637\u0637 \u0644\u0644\u062A\u0623\u0633\u064A\u0633 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629\u061F",
      placeholder: "\u062D\u062F\u062F \u0627\u0644\u0625\u0637\u0627\u0631 \u0627\u0644\u0632\u0645\u0646\u064A \u0627\u0644\u062A\u0639\u0627\u0642\u062F"
    },
    goalsExpectations: {
      label: "\u0645\u0627 \u0647\u064A \u0623\u0647\u062F\u0627\u0641\u0643 \u0623\u0648 \u062A\u0648\u0642\u0639\u0627\u062A\u0643 \u0644\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629\u061F",
      placeholder: "\u0623\u0636\u0641 \u0623\u064A \u062A\u0648\u0642\u0639\u0627\u062A \u0627\u0644\u062A\u0639\u0627\u0642\u062F"
    },
    engagementTimeframe: {
      label: "\u0645\u062A\u0649 \u062A\u062A\u0637\u0644\u0639 \u0625\u0644\u0649 \u0627\u0644\u0628\u062F\u0621 \u0641\u064A \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062E\u062F\u0645\u0627\u062A\u0646\u0627\u061F",
      placeholder: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0625\u0637\u0627\u0631 \u0627\u0644\u0632\u0645\u0646\u064A \u0644\u0644\u062A\u0639\u0627\u0648\u0646"
    },
    budget: {
      label: "\u0647\u0644 \u064A\u0648\u062C\u062F \u0645\u064A\u0632\u0627\u0646\u064A\u0629 \u0645\u062E\u0635\u0635\u0629 \u0644\u0630\u0644\u0643\u061F",
      yes: "\u0646\u0639\u0645",
      no: "\u0644\u0627"
    },
    terms: {
      text: "\u0623\u0648\u0627\u0641\u0642 \u0639\u0644\u0649",
      link: "\u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645."
    },
    services: {
      finance_accounting: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u0627\u0644\u064A\u0629 \u0648\u0627\u0644\u0645\u062D\u0627\u0633\u0628\u0629",
      hr: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u0648\u0627\u0631\u062F \u0627\u0644\u0628\u0634\u0631\u064A\u0629",
      incubation: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u062D\u0627\u0636\u0646\u0627\u062A",
      innovation: "\u062E\u062F\u0645\u0627\u062A \u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0627\u0628\u062A\u0643\u0627\u0631",
      marketing: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u062A\u0633\u0648\u064A\u0642 \u0648\u0627\u0644\u0641\u0639\u0627\u0644\u064A\u0627\u062A",
      digital_tech: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0631\u0642\u0645\u0646\u0629 \u0648\u0627\u0644\u062A\u0643\u0646\u0648\u0644\u0648\u062C\u064A\u0627",
      corporate: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u0645\u062A\u0644\u0643\u0627\u062A \u0627\u0644\u0645\u0624\u0633\u0633\u064A\u0629",
      soft_landing: "\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0625\u0642\u0627\u0645\u0629 \u0627\u0644\u062A\u062F\u0631\u064A\u062C\u064A\u0629",
      company_builder: "\u062E\u062F\u0645\u0627\u062A \u0628\u0646\u0627\u0621 \u0627\u0644\u0634\u0631\u0643\u0627\u062A",
      legal: "\u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A\u0629",
      pmo_as_service: "\u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0645\u0634\u0627\u0631\u064A\u0639 \u0643\u062E\u062F\u0645\u0629",
      technology: "\u0639\u0645\u0644\u064A\u0627\u062A \u0627\u0644\u062A\u0643\u0646\u0648\u0644\u0648\u062C\u064A\u0627"
    },
    engagementOptions: {
      "3_months": "\u062E\u0644\u0627\u0644 3 \u0623\u0634\u0647\u0631",
      "6_12_months": "\u0645\u0646 6 \u0625\u0644\u0649 12 \u0634\u0647\u0631\u0627\u064B",
      gt_12_months: "\u0623\u0643\u062B\u0631 \u0645\u0646 12 \u0634\u0647\u0631\u0627\u064B",
      not_sure: "\u063A\u064A\u0631 \u0645\u062A\u0623\u0643\u062F"
    },
    establishmentOptions: {
      "3_months": "\u062E\u0644\u0627\u0644 3 \u0623\u0634\u0647\u0631",
      "6_12_months": "\u0645\u0646 6 \u0625\u0644\u0649 12 \u0634\u0647\u0631\u0627\u064B",
      gt_12_months: "\u0623\u0643\u062B\u0631 \u0645\u0646 12 \u0634\u0647\u0631\u0627\u064B",
      not_sure: "\u063A\u064A\u0631 \u0645\u062A\u0623\u0643\u062F"
    }
  },
  uploadDocuments: {
    title: "\u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A",
    subtitle: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062A\u0627\u0644\u064A\u0629",
    validation: {
      tooLarge: "\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0647\u0648 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A \u0644\u0643\u0644 \u0645\u0633\u062A\u0646\u062F",
      invalidType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0644\u0641 \u063A\u064A\u0631 \u0645\u062F\u0639\u0648\u0645. \u0627\u0644\u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0645\u0633\u0645\u0648\u062D \u0628\u0647\u0627: PDF\u060C PNG\u060C JPG"
    },
    dropzone: "\u0627\u0633\u062D\u0628 \u0627\u0644\u0645\u0644\u0641\u0627\u062A \u0648\u0623\u0641\u0644\u062A\u0647\u0627 \u0647\u0646\u0627\u060C \u0623\u0648 \u0627\u0646\u0642\u0631 \u0644\u0644\u0627\u0633\u062A\u0639\u0631\u0627\u0636",
    maxSize: "\u0627\u0644\u062D\u062C\u0645 \u0627\u0644\u0623\u0642\u0635\u0649 \u0644\u0644\u0645\u0644\u0641: {size}",
    allowedTypes: "\u0627\u0644\u0635\u064A\u063A \u0627\u0644\u0645\u0642\u0628\u0648\u0644\u0629: {types}",
    actionText: "\u0627\u0646\u0642\u0631 \u0644\u0644\u0631\u0641\u0639",
    dragHintText: "\u0623\u0648 \u0627\u0633\u062D\u0628 \u0648\u0623\u0641\u0644\u062A",
    fields: {
      registrationCertificate: {
        label: "\u0634\u0647\u0627\u062F\u0629 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
        supportingText: "\u0641\u0642\u0637 (\u0627\u0644\u062D\u062F \u0627\u0644\u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A) JPG \u0623\u0648 PNG \u0623\u0648 PDF"
      }
    }
  },
  confirmation: {
    title: "\u062A\u0645 \u0628\u0646\u062C\u0627\u062D!",
    subtitle: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0637\u0644\u0628\u0643. \u0633\u0646\u0646\u062A\u0638\u0631 \u062D\u062A\u0649 \u0646\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0643 \u0642\u0631\u064A\u0628\u0627\u064B.",
    referenceLabel: "\u0627\u0644\u0631\u0642\u0645 \u0627\u0644\u0645\u0631\u062C\u0639\u064A",
    nextStepsTitle: "\u0645\u0627 \u0627\u0644\u0630\u064A \u064A\u062D\u062F\u062B \u0628\u0639\u062F \u0630\u0644\u0643\u061F",
    followUpMessage: "\u0633\u064A\u062A\u0648\u0627\u0635\u0644 \u0645\u0639\u0643 \u0623\u062D\u062F \u0623\u0639\u0636\u0627\u0621 \u0641\u0631\u064A\u0642\u0646\u0627 \u062E\u0644\u0627\u0644 5 \u0623\u064A\u0627\u0645 \u0639\u0645\u0644.",
    supportPrefix: "\u0644\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u062F\u0639\u0645 \u0641\u0648\u0631\u064A\u060C \u064A\u0645\u0643\u0646\u0643 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0641\u0631\u064A\u0642\u0646\u0627",
    supportLinkText: "\u0647\u0646\u0627"
  },
  errors: {
    submitFailed: "\u0641\u0634\u0644 \u0627\u0644\u0625\u0631\u0633\u0627\u0644. \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0628\u064A\u0627\u0646\u0627\u062A\u0643 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    uploadFailed: "\u0641\u0634\u0644 \u0631\u0641\u0639 \u0627\u0644\u0645\u0644\u0641. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    crNumberAlreadyRegistered: "\u0647\u0630\u0627 \u0627\u0644\u0631\u0642\u0645 \u0645\u0633\u062C\u0644 \u0628\u0627\u0644\u0641\u0639\u0644\u060C \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0648\u0627\u0635\u0644 \u0645\u0639 \u0641\u0631\u064A\u0642 \u0627\u0644\u062F\u0639\u0645 \u0644\u0644\u0645\u0633\u0627\u0639\u062F\u0629",
    emailAlreadyRegistered: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0633\u062C\u0644 \u0628\u0627\u0644\u0641\u0639\u0644",
    projectAlreadyExists: "\u0627\u0644\u0645\u0634\u0631\u0648\u0639 \u0645\u0648\u062C\u0648\u062F \u0628\u0627\u0644\u0641\u0639\u0644",
    stepNotFound: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u062E\u0637\u0648\u0629 \u0627\u0644\u062D\u0627\u0644\u064A\u0629 \u063A\u064A\u0631 \u0645\u062A\u0648\u0641\u0631\u0629",
    failedToLoadCountries: "\u0641\u0634\u0644 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062F\u0648\u0644",
    failedToLoadGccCountries: "\u0641\u0634\u0644 \u062A\u062D\u0645\u064A\u0644 \u062F\u0648\u0644 \u0645\u062C\u0644\u0633 \u0627\u0644\u062A\u0639\u0627\u0648\u0646 \u0627\u0644\u062E\u0644\u064A\u062C\u064A",
    invalidFileSelected: "\u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u0645\u062D\u062F\u062F \u063A\u064A\u0631 \u0635\u0627\u0644\u062D.",
    fileRequired: "\u064A\u062C\u0628 \u062A\u0648\u0641\u064A\u0631 \u0645\u0644\u0641",
    unknownStep: "\u062A\u0645\u062A \u0645\u0648\u0627\u062C\u0647\u0629 \u062E\u0637\u0648\u0629 \u063A\u064A\u0631 \u0645\u0639\u0631\u0648\u0641\u0629",
    validationFailed: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642. \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0645\u062F\u062E\u0644\u0627\u062A.",
    validationUnexpectedError: "\u062D\u062F\u062B \u062E\u0637\u0623 \u063A\u064A\u0631 \u0645\u062A\u0648\u0642\u0639 \u0623\u062B\u0646\u0627\u0621 \u0627\u0644\u062A\u062D\u0642\u0642",
    ndaFileRequired: "\u0645\u0644\u0641 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0645\u0637\u0644\u0648\u0628",
    detailsFetchFailed: "\u0641\u0634\u0644 \u062C\u0644\u0628 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062A\u0633\u062C\u064A\u0644",
    portalAccountIdMissing: "\u0627\u0644\u062D\u0633\u0627\u0628 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F. \u064A\u0631\u062C\u0649 \u0627\u0644\u062A\u0623\u0643\u062F \u0645\u0646 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644.",
    invalidDocumentType: "\u0646\u0648\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F \u0627\u0644\u0645\u062D\u062F\u062F \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
    submissionFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0627\u0644\u062A\u0633\u062C\u064A\u0644. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    stepProcessingFailed: "\u0641\u0634\u0644 \u0645\u0639\u0627\u0644\u062C\u0629 \u0647\u0630\u0647 \u0627\u0644\u062E\u0637\u0648\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    nda: {
      submissionPending: "\u0637\u0644\u0628 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0642\u064A\u062F \u0627\u0644\u0645\u0631\u0627\u062C\u0639\u0629 \u0628\u0627\u0644\u0641\u0639\u0644",
      validationFailed: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642 \u0645\u0646 \u0625\u0631\u0633\u0627\u0644 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D",
      submissionFailed: "\u062A\u0645 \u0631\u0641\u0639 \u0645\u0644\u0641 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0644\u0643\u0646 \u062A\u0639\u0630\u0651\u0631 \u0625\u0631\u0633\u0627\u0644\u0647"
    },
    conflictError: "\u062D\u062F\u062B \u062A\u0639\u0627\u0631\u0636 \u0645\u0639 \u0637\u0644\u0628 \u0645\u0648\u062C\u0648\u062F \u0645\u0633\u0628\u0642\u0627\u064B",
    validation: {
      required: "\u0647\u0630\u0627 \u0627\u0644\u062D\u0642\u0644 \u0645\u0637\u0644\u0648\u0628",
      companyName: {
        required: "\u0627\u0633\u0645 \u0627\u0644\u0634\u0631\u0643\u0629 \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0633\u0645 \u0627\u0644\u0634\u0631\u0643\u0629 \u0628\u062D\u062F \u0623\u0642\u0635\u0649 200 \u062D\u0631\u0641",
        charset: "\u064A\u0645\u0643\u0646 \u0644\u0627\u0633\u0645 \u0627\u0644\u0634\u0631\u0643\u0629 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0623\u0631\u0642\u0627\u0645\u0627\u064B \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0631\u0645\u0648\u0632 \u0639\u0627\u0645\u0629 (&\u060C -\u060C '\u060C .\u060C (\u060C )\u060C /\u060C \u060C)"
      },
      registrationNumber: {
        required: "\u0631\u0642\u0645 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0631\u0642\u0645 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0628\u062D\u062F \u0623\u0642\u0635\u0649 100 \u062D\u0631\u0641",
        charset: "\u0631\u0642\u0645 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0623\u0631\u0642\u0627\u0645\u0627\u064B \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637",
        saudiFormat: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0631\u0642\u0645 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0628\u0627\u0644\u0636\u0628\u0637 10 \u0623\u0631\u0642\u0627\u0645",
        internationalFormat: "\u0631\u0642\u0645 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0623\u0631\u0642\u0627\u0645\u0627\u064B \u0648\u0634\u0631\u0637\u0627\u062A \u0648\u0645\u0633\u0627\u0641\u0627\u062A (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 50 \u062D\u0631\u0641\u0627\u064B)"
      },
      registrationDate: {
        required: "\u062A\u0627\u0631\u064A\u062E \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0645\u0637\u0644\u0648\u0628",
        format: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0627\u0631\u064A\u062E \u0635\u062D\u064A\u062D \u0628\u0635\u064A\u063A\u0629 DD/MM/YYYY",
        future: "\u0644\u0627 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0641\u064A \u0627\u0644\u0645\u0633\u062A\u0642\u0628\u0644"
      },
      website: {
        invalid: "\u0623\u062F\u062E\u0644 \u0639\u0646\u0648\u0627\u0646 \u0645\u0648\u0642\u0639 \u0648\u064A\u0628 \u0635\u062D\u064A\u062D (\u0645\u062B\u0644 https://example.com)"
      },
      country: {
        required: "\u062F\u0648\u0644\u0629 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      countryOfHeadquarter: {
        required: "\u062F\u0648\u0644\u0629 \u0627\u0644\u0645\u0642\u0631 \u0627\u0644\u0631\u0626\u064A\u0633\u064A \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      city: {
        charset: "\u0627\u0644\u0645\u062F\u064A\u0646\u0629 \u064A\u0645\u0643\u0646 \u0623\u0646 \u062A\u062A\u0636\u0645\u0646 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637",
        required: "\u0627\u0644\u0645\u062F\u064A\u0646\u0629 \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      address: {
        required: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0634\u0631\u0643\u0629 \u0627\u0644\u0645\u0633\u062C\u0644\u0629 \u0645\u0637\u0644\u0648\u0628"
      },
      postcode: {
        charset: "\u0627\u0644\u0631\u0645\u0632 \u0627\u0644\u0628\u0631\u064A\u062F\u064A \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0623\u0631\u0642\u0627\u0645\u0627\u064B \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A (3-12 \u0623\u062D\u0631\u0641)"
      },
      industrySector: {
        required: "\u0627\u0644\u0642\u0637\u0627\u0639 \u0627\u0644\u0635\u0646\u0627\u0639\u064A \u0645\u0637\u0644\u0648\u0628"
      },
      numberOfEmployees: {
        required: "\u0639\u062F\u062F \u0627\u0644\u0645\u0648\u0638\u0641\u064A\u0646 \u0645\u0637\u0644\u0648\u0628"
      },
      annualRevenue: {
        required: "\u0627\u0644\u0625\u064A\u0631\u0627\u062F\u0627\u062A \u0627\u0644\u0633\u0646\u0648\u064A\u0629 \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      totalInvestmentRaised: {
        required: "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631 \u0627\u0644\u0645\u062C\u0645\u0639 \u0645\u0637\u0644\u0648\u0628"
      },
      selectedServices: {
        required: "\u064A\u0631\u062C\u0649 \u0627\u062E\u062A\u064A\u0627\u0631 \u062E\u062F\u0645\u0629 \u0648\u0627\u062D\u062F\u0629 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644 \u062A\u062D\u062A\u0627\u062C \u0627\u0644\u0645\u0633\u0627\u0639\u062F\u0629 \u0641\u064A\u0647\u0627"
      },
      engagementTimeframe: {
        required: "\u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062F \u0645\u062A\u0649 \u062A\u062A\u0637\u0644\u0639 \u0625\u0644\u0649 \u0627\u0644\u0628\u062F\u0621 \u0641\u064A \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062E\u062F\u0645\u0627\u062A\u0646\u0627"
      },
      budgetAllocated: {
        required: "\u064A\u0631\u062C\u0649 \u0627\u0644\u0625\u0634\u0627\u0631\u0629 \u0625\u0644\u0649 \u0645\u0627 \u0625\u0630\u0627 \u0643\u0627\u0646\u062A \u0647\u0646\u0627\u0643 \u0645\u064A\u0632\u0627\u0646\u064A\u0629 \u0645\u062E\u0635\u0635\u0629"
      },
      acceptTerms: {
        required: "\u064A\u062C\u0628 \u0639\u0644\u064A\u0643 \u0642\u0628\u0648\u0644 \u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645 \u0644\u0644\u0645\u062A\u0627\u0628\u0639\u0629"
      },
      establishmentTimeframe: {
        required: "\u064A\u0631\u062C\u0649 \u062A\u062D\u062F\u064A\u062F \u0645\u062A\u0649 \u062A\u062E\u0637\u0637 \u0644\u0644\u062A\u0623\u0633\u064A\u0633 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629"
      },
      firstName: {
        required: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0645\u0637\u0644\u0648\u0628",
        min: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0628\u064A\u0646 2 \u0648 100 \u062D\u0631\u0641",
        max: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0628\u064A\u0646 2 \u0648 100 \u062D\u0631\u0641",
        charset: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
      },
      lastName: {
        required: "\u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0645\u0637\u0644\u0648\u0628",
        min: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0628\u064A\u0646 2 \u0648 100 \u062D\u0631\u0641",
        max: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0628\u064A\u0646 2 \u0648 100 \u062D\u0631\u0641",
        charset: "\u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062A\u0636\u0645\u0646 \u0623\u062D\u0631\u0641\u0627\u064B \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
      },
      salutation: {
        required: "\u0627\u0644\u0644\u0642\u0628 \u0645\u0637\u0644\u0648\u0628"
      },
      jobTitle: {
        required: "\u0627\u0644\u0645\u0633\u0645\u0649 \u0627\u0644\u0648\u0638\u064A\u0641\u064A \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0644\u0645\u0633\u0645\u0649 \u0627\u0644\u0648\u0638\u064A\u0641\u064A \u0628\u062D\u062F \u0623\u0642\u0635\u0649 255 \u062D\u0631\u0641"
      },
      email: {
        required: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0637\u0644\u0648\u0628",
        invalid: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0635\u062D\u064A\u062D",
        business: "\u064A\u0633\u0645\u062D \u0641\u0642\u0637 \u0628\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0627\u0644\u0645\u0631\u062A\u0628\u0637 \u0628\u0646\u0637\u0627\u0642 \u0627\u0644\u0645\u0646\u0634\u0623\u0629",
        pif: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0635\u062D\u064A\u062D \u0644\u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629"
      },
      mobile: {
        required: "\u0631\u0642\u0645 \u0627\u0644\u0647\u0627\u062A\u0641 \u0627\u0644\u0645\u062D\u0645\u0648\u0644 \u0645\u0637\u0644\u0648\u0628",
        invalid: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0631\u0642\u0645 \u0647\u0627\u062A\u0641 \u0645\u062D\u0645\u0648\u0644 \u0635\u062D\u064A\u062D \u0644\u0644\u062F\u0648\u0644\u0629 \u0627\u0644\u0645\u062E\u062A\u0627\u0631\u0629"
      },
      projectCodeName: {
        required: "\u0627\u0633\u0645 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 \u0645\u0637\u0644\u0648\u0628",
        min: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u062A\u0643\u0648\u0646 \u0627\u0633\u0645 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 \u0645\u0646 3 \u0623\u062D\u0631\u0641 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0633\u0645 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 100 \u062D\u0631\u0641"
      },
      projectDetails: {
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0648\u0635\u0641 \u0627\u0644\u0645\u0634\u0631\u0648\u0639 1000 \u062D\u0631\u0641"
      },
      expectedLaunchDate: {
        format: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0627\u0631\u064A\u062E \u0635\u062D\u064A\u062D \u0628\u0635\u064A\u063A\u0629 DD/MM/YYYY",
        future: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062A\u0627\u0631\u064A\u062E \u0645\u0633\u062A\u0642\u0628\u0644\u064A \u0635\u062D\u064A\u062D \u0628\u0635\u064A\u063A\u0629 DD/MM/YYYY",
        mustBeFuture: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u0637\u0644\u0627\u0642 \u0627\u0644\u0645\u062A\u0648\u0642\u0639 \u0641\u064A \u0627\u0644\u0645\u0633\u062A\u0642\u0628\u0644"
      }
    }
  },
  termsAndConditions: {
    title: "\u0634\u0631\u0648\u0637 \u0648\u0623\u062D\u0643\u0627\u0645 \u062A\u0633\u0627\u0645\u0649",
    lastUpdated: "\u0622\u062E\u0631 \u062A\u062D\u062F\u064A\u062B: {date}",
    sections: {
      acceptanceOfTerms: {
        title: "1. \u0642\u0628\u0648\u0644 \u0627\u0644\u0634\u0631\u0648\u0637",
        content: "\u0645\u0646 \u062E\u0644\u0627\u0644 \u0627\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0647\u0630\u0627 \u0627\u0644\u0645\u0648\u0642\u0639 \u0623\u0648 \u0627\u0633\u062A\u062E\u062F\u0627\u0645\u0647 \u0648\u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0641\u064A \u0627\u0644\u062E\u062F\u0645\u0627\u062A\u060C \u0641\u0625\u0646\u0643 \u062A\u0648\u0627\u0641\u0642 \u0639\u0644\u0649 \u0627\u0644\u0627\u0644\u062A\u0632\u0627\u0645 \u0628\u0647\u0630\u0647 \u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645. \u0625\u0630\u0627 \u0643\u0646\u062A \u0644\u0627 \u062A\u0648\u0627\u0641\u0642 \u0639\u0644\u0649 \u0647\u0630\u0647 \u0627\u0644\u0634\u0631\u0648\u0637\u060C \u064A\u062C\u0628 \u0639\u0644\u064A\u0643 \u0627\u0644\u0627\u0645\u062A\u0646\u0627\u0639 \u0639\u0646 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0627\u0644\u0645\u0648\u0642\u0639.",
        summary: "\u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0627\u0644\u0645\u0648\u0642\u0639 \u0641\u0625\u0646\u0643 \u062A\u0648\u0627\u0641\u0642 \u0639\u0644\u0649 \u0634\u0631\u0648\u0637\u0646\u0627"
      },
      websiteUse: {
        title: "2. \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0627\u0644\u0645\u0648\u0642\u0639",
        content: "\u064A\u0647\u062F\u0641 \u0647\u0630\u0627 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0625\u0644\u0649 \u062A\u0648\u0641\u064A\u0631 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u062D\u0648\u0644 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u062A\u064A \u062A\u0642\u062F\u0645\u0647\u0627 \u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0648\u0644\u062A\u0645\u0643\u064A\u0646 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u064A\u0646 \u0645\u0646 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0648\u0637\u0644\u0628 \u0627\u0644\u062E\u062F\u0645\u0627\u062A.\n\n\u064A\u0648\u0627\u0641\u0642 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u0648\u0646 \u0639\u0644\u0649 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0644\u0623\u063A\u0631\u0627\u0636 \u0642\u0627\u0646\u0648\u0646\u064A\u0629 \u0641\u0642\u0637 \u0648\u0628\u0637\u0631\u064A\u0642\u0629 \u0644\u0627 \u062A\u0646\u062A\u0647\u0643 \u0623\u064A \u0642\u0648\u0627\u0646\u064A\u0646 \u0623\u0648 \u0644\u0648\u0627\u0626\u062D \u0645\u0639\u0645\u0648\u0644 \u0628\u0647\u0627 \u0623\u0648 \u062A\u0646\u062A\u0647\u0643 \u062D\u0642\u0648\u0642 \u0627\u0644\u0622\u062E\u0631\u064A\u0646.",
        guidelines: {
          permitted: "\u0627\u0644\u0625\u062C\u0631\u0627\u0621\u0627\u062A \u0627\u0644\u0645\u0633\u0645\u0648\u062D \u0628\u0647\u0627",
          prohibited: "\u0627\u0644\u0625\u062C\u0631\u0627\u0621\u0627\u062A \u0627\u0644\u0645\u062D\u0638\u0648\u0631\u0629"
        }
      },
      userRegistration: {
        title: "3. \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645",
        content: "\u0644\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0628\u0639\u0636 \u0627\u0644\u062E\u062F\u0645\u0627\u062A\u060C \u0642\u062F \u064A\u064F\u0637\u0644\u0628 \u0645\u0646 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u064A\u0646 \u0625\u0646\u0634\u0627\u0621 \u062D\u0633\u0627\u0628 \u0648\u062A\u0642\u062F\u064A\u0645 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u062F\u0642\u064A\u0642\u0629 \u0648\u0643\u0627\u0645\u0644\u0629.\n\n\u0645\u0646 \u062E\u0644\u0627\u0644 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0639\u0644\u0649 \u0627\u0644\u0645\u0648\u0642\u0639\u060C \u064A\u0648\u0627\u0641\u0642 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0639\u0644\u0649 \u0645\u0627 \u064A\u0644\u064A:",
        items: [
          "\u0623\u0646 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u0642\u062F\u0645\u0629 \u062F\u0642\u064A\u0642\u0629 \u0648\u0645\u062D\u062F\u062B\u0629.",
          "\u0623\u0646 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0645\u0633\u0624\u0648\u0644 \u0639\u0646 \u0627\u0644\u062D\u0641\u0627\u0638 \u0639\u0644\u0649 \u0633\u0631\u064A\u0629 \u0628\u064A\u0627\u0646\u0627\u062A \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644.",
          "\u0623\u0646 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0645\u0633\u0624\u0648\u0644 \u0639\u0646 \u062C\u0645\u064A\u0639 \u0627\u0644\u0623\u0646\u0634\u0637\u0629 \u0627\u0644\u062A\u064A \u062A\u062A\u0645 \u062A\u062D\u062A \u062D\u0633\u0627\u0628\u0647."
        ],
        additionalContent: "\u062A\u062D\u062A\u0641\u0638 \u0627\u0644\u0634\u0631\u0643\u0629 \u0628\u0627\u0644\u062D\u0642 \u0641\u064A \u062A\u0639\u0644\u064A\u0642 \u0623\u0648 \u0625\u0646\u0647\u0627\u0621 \u0623\u064A \u062D\u0633\u0627\u0628 \u064A\u0642\u062F\u0645 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u063A\u064A\u0631 \u062F\u0642\u064A\u0642\u0629 \u0623\u0648 \u064A\u0646\u062A\u0647\u0643 \u0647\u0630\u0647 \u0627\u0644\u0634\u0631\u0648\u0637."
      },
      confidentiality: {
        title: "4. \u0627\u0644\u0633\u0631\u064A\u0629",
        content: "\u0623\u064A \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u064A\u0634\u0627\u0631\u0643\u0647\u0627 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u0648\u0646 \u0639\u0628\u0631 \u0627\u0644\u0645\u0648\u0642\u0639 \u0642\u062F \u062A\u064F\u0639\u0627\u0645\u0644 \u0639\u0644\u0649 \u0623\u0646\u0647\u0627 \u0633\u0631\u064A\u0629 \u0648\u062A\u064F\u0633\u062A\u062E\u062F\u0645 \u0641\u0642\u0637 \u0644\u063A\u0631\u0636 \u062A\u0642\u064A\u064A\u0645 \u0623\u0648 \u062A\u0642\u062F\u064A\u0645 \u0623\u0648 \u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629.\n\n\u0633\u062A\u062A\u062E\u0630 \u0627\u0644\u0634\u0631\u0643\u0629 \u062A\u062F\u0627\u0628\u064A\u0631 \u0645\u0639\u0642\u0648\u0644\u0629 \u0644\u062D\u0645\u0627\u064A\u0629 \u0647\u0630\u0647 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0645\u0646 \u0627\u0644\u0648\u0635\u0648\u0644 \u0623\u0648 \u0627\u0644\u0643\u0634\u0641 \u0623\u0648 \u0627\u0644\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u063A\u064A\u0631 \u0627\u0644\u0645\u0635\u0631\u062D \u0628\u0647\u060C \u0628\u0627\u0633\u062A\u062B\u0646\u0627\u0621 \u0627\u0644\u062D\u0627\u0644\u0627\u062A \u0627\u0644\u062A\u064A \u064A\u0643\u0648\u0646 \u0641\u064A\u0647\u0627 \u0627\u0644\u0643\u0634\u0641 \u0645\u0637\u0644\u0648\u0628\u064B\u0627 \u0628\u0645\u0648\u062C\u0628 \u0627\u0644\u0642\u0627\u0646\u0648\u0646 \u0623\u0648 \u0627\u0644\u0633\u0644\u0637\u0627\u062A \u0627\u0644\u062A\u0646\u0638\u064A\u0645\u064A\u0629.",
        dataProtection: "\u0627\u0644\u062A\u0632\u0627\u0645 \u062D\u0645\u0627\u064A\u0629 \u0627\u0644\u0628\u064A\u0627\u0646\u0627\u062A"
      },
      cookiesAndWebsiteData: {
        title: "5. \u0645\u0644\u0641\u0627\u062A \u062A\u0639\u0631\u064A\u0641 \u0627\u0644\u0627\u0631\u062A\u0628\u0627\u0637 \u0648\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
        content: "\u0642\u062F \u064A\u0633\u062A\u062E\u062F\u0645 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0644\u0641\u0627\u062A \u062A\u0639\u0631\u064A\u0641 \u0627\u0644\u0627\u0631\u062A\u0628\u0627\u0637 \u0648\u0627\u0644\u062A\u0642\u0646\u064A\u0627\u062A \u0627\u0644\u0645\u0645\u0627\u062B\u0644\u0629 \u0644\u0640:",
        items: [
          "\u062A\u062D\u0633\u064A\u0646 \u0648\u0638\u0627\u0626\u0641 \u0627\u0644\u0645\u0648\u0642\u0639 \u0648\u062A\u062C\u0631\u0628\u0629 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645",
          "\u062A\u062D\u0644\u064A\u0644 \u062D\u0631\u0643\u0629 \u0627\u0644\u0645\u0631\u0648\u0631 \u0648\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0627\u0644\u0645\u0648\u0642\u0639",
          "\u0627\u0644\u062D\u0641\u0627\u0638 \u0639\u0644\u0649 \u062C\u0644\u0633\u0627\u062A \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0648\u062A\u0641\u0636\u064A\u0644\u0627\u062A\u0647\u0645"
        ],
        additionalContent: "\u0628\u0645\u0648\u0627\u0635\u0644\u0629 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A\u060C \u064A\u0648\u0627\u0641\u0642 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u0648\u0646 \u0639\u0644\u0649 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0645\u0644\u0641\u0627\u062A \u062A\u0639\u0631\u064A\u0641 \u0627\u0644\u0627\u0631\u062A\u0628\u0627\u0637 \u0648\u0641\u0642\u064B\u0627 \u0644\u0645\u0645\u0627\u0631\u0633\u0627\u062A \u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0627\u0644\u0645\u062A\u0639\u0644\u0642\u0629 \u0628\u0645\u0644\u0641\u0627\u062A \u062A\u0639\u0631\u064A\u0641 \u0627\u0644\u0627\u0631\u062A\u0628\u0627\u0637. \u064A\u0645\u0643\u0646 \u0644\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u064A\u0646 \u062A\u0639\u0637\u064A\u0644 \u0645\u0644\u0641\u0627\u062A \u062A\u0639\u0631\u064A\u0641 \u0627\u0644\u0627\u0631\u062A\u0628\u0627\u0637 \u0645\u0646 \u062E\u0644\u0627\u0644 \u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0627\u0644\u0645\u062A\u0635\u0641\u062D\u061B \u0648\u0645\u0639 \u0630\u0644\u0643\u060C \u0642\u062F \u0644\u0627 \u062A\u0639\u0645\u0644 \u0628\u0639\u0636 \u0645\u064A\u0632\u0627\u062A \u0627\u0644\u0645\u0648\u0642\u0639 \u0628\u0634\u0643\u0644 \u0635\u062D\u064A\u062D \u0646\u062A\u064A\u062C\u0629 \u0644\u0630\u0644\u0643.",
        cookieSettings: "\u0625\u0639\u062F\u0627\u062F\u0627\u062A \u0645\u0644\u0641\u0627\u062A \u062A\u0639\u0631\u064A\u0641 \u0627\u0644\u0627\u0631\u062A\u0628\u0627\u0637"
      },
      intellectualProperty: {
        title: "6. \u0627\u0644\u0645\u0644\u0643\u064A\u0629 \u0627\u0644\u0641\u0643\u0631\u064A\u0629",
        content: "\u062C\u0645\u064A\u0639 \u0627\u0644\u0645\u062D\u062A\u0648\u064A\u0627\u062A \u0627\u0644\u0645\u0648\u0642\u0639\u060C \u0628\u0645\u0627 \u0641\u064A \u0630\u0644\u0643 \u0627\u0644\u0646\u0635\u0648\u0635 \u0648\u0627\u0644\u0631\u0633\u0648\u0645\u0627\u062A \u0648\u0627\u0644\u0634\u0639\u0627\u0631\u0627\u062A \u0648\u0627\u0644\u0645\u0648\u0627\u062F\u060C \u0647\u064A \u0645\u0644\u0643 \u0644\u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0623\u0648 \u0644\u0644\u0645\u0631\u062E\u0650\u0651\u0635\u064A\u0646 \u0627\u0644\u062E\u0627\u0635\u064A\u0646 \u0628\u0647\u0627 \u0648\u0645\u062D\u0645\u064A\u0629 \u0628\u0645\u0648\u062C\u0628 \u0642\u0648\u0627\u0646\u064A\u0646 \u0627\u0644\u0645\u0644\u0643\u064A\u0629 \u0627\u0644\u0641\u0643\u0631\u064A\u0629 \u0627\u0644\u0645\u0639\u0645\u0648\u0644 \u0628\u0647\u0627.\n\n\u0644\u0627 \u064A\u062C\u0648\u0632 \u0644\u0644\u0645\u0633\u062A\u062E\u062F\u0645\u064A\u0646 \u0646\u0633\u062E \u0623\u0648 \u062A\u0648\u0632\u064A\u0639 \u0623\u0648 \u062A\u0639\u062F\u064A\u0644 \u0623\u0648 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0623\u064A \u0645\u062D\u062A\u0648\u0649 \u0645\u0646 \u0645\u062D\u062A\u0648\u064A\u0627\u062A \u0627\u0644\u0645\u0648\u0642\u0639 \u062F\u0648\u0646 \u0645\u0648\u0627\u0641\u0642\u0629 \u062E\u0637\u064A\u0629 \u0645\u0633\u0628\u0642\u0629 \u0645\u0646 \u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644.",
        rights: "\u062C\u0645\u064A\u0639 \u0627\u0644\u062D\u0642\u0648\u0642 \u0645\u062D\u0641\u0648\u0638\u0629"
      },
      limitationOfLiability: {
        title: "7. \u062A\u062D\u062F\u064A\u062F \u0627\u0644\u0645\u0633\u0624\u0648\u0644\u064A\u0629",
        content: "\u062A\u0628\u0630\u0644 \u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u062C\u0647\u0648\u062F\u0627\u064B \u0645\u0639\u0642\u0648\u0644\u0629 \u0644\u0636\u0645\u0627\u0646 \u062F\u0642\u0629 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0639\u0644\u0649 \u0647\u0630\u0627 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A. \u0648\u0645\u0639 \u0630\u0644\u0643\u060C \u064A\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0648\u0645\u062D\u062A\u0648\u0627\u0647 \u0643\u0645\u0627 \u0647\u0648 \u062F\u0648\u0646 \u0623\u064A \u0636\u0645\u0627\u0646\u0627\u062A \u0645\u0646 \u0623\u064A \u0646\u0648\u0639.\n\n\u0625\u0644\u0649 \u0623\u0642\u0635\u0649 \u062D\u062F \u064A\u0633\u0645\u062D \u0628\u0647 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u060C \u0644\u0646 \u062A\u062A\u062D\u0645\u0644 \u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0623\u064A \u0645\u0633\u0624\u0648\u0644\u064A\u0629 \u0639\u0646 \u0623\u064A \u0623\u0636\u0631\u0627\u0631 \u0645\u0628\u0627\u0634\u0631\u0629 \u0623\u0648 \u063A\u064A\u0631 \u0645\u0628\u0627\u0634\u0631\u0629 \u0623\u0648 \u0639\u0631\u0636\u064A\u0629 \u0623\u0648 \u062A\u0628\u0639\u064A\u0629 \u062A\u0646\u0634\u0623 \u0639\u0646 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A.",
        disclaimer: "\u0625\u062E\u0644\u0627\u0621 \u0627\u0644\u0645\u0633\u0624\u0648\u0644\u064A\u0629"
      },
      thirdPartyLinks: {
        title: "8. \u0627\u0644\u0631\u0648\u0627\u0628\u0637 \u0627\u0644\u062E\u0627\u0631\u062C\u064A\u0629",
        content: "\u0642\u062F \u064A\u062D\u062A\u0648\u064A \u0627\u0644\u0645\u0648\u0642\u0639 \u0639\u0644\u0649 \u0631\u0648\u0627\u0628\u0637 \u0644\u0645\u0648\u0627\u0642\u0639 \u0637\u0631\u0641 \u062B\u0627\u0644\u062B \u0644\u0623\u063A\u0631\u0627\u0636 \u0645\u0639\u0644\u0648\u0645\u0627\u062A\u064A\u0629. \u0644\u0627 \u062A\u062A\u062D\u0643\u0645 \u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0641\u064A \u0645\u062D\u062A\u0648\u0649 \u0623\u0648 \u0633\u064A\u0627\u0633\u0627\u062A \u0623\u0648 \u0645\u0645\u0627\u0631\u0633\u0627\u062A \u0647\u0630\u0647 \u0627\u0644\u0645\u0648\u0627\u0642\u0639 \u0627\u0644\u062E\u0627\u0631\u062C\u064A\u0629 \u0648\u0644\u0627 \u062A\u062A\u062D\u0645\u0644 \u0623\u064A \u0645\u0633\u0624\u0648\u0644\u064A\u0629 \u0639\u0646\u0647\u0627.",
        warning: "\u0625\u062E\u0644\u0627\u0621 \u0645\u0633\u0624\u0648\u0644\u064A\u0629 \u0627\u0644\u0631\u0648\u0627\u0628\u0637 \u0627\u0644\u062E\u0627\u0631\u062C\u064A\u0629"
      },
      governingLaw: {
        title: "9. \u0627\u0644\u0642\u0627\u0646\u0648\u0646 \u0627\u0644\u062D\u0627\u0643\u0645",
        content: "\u062A\u062E\u0636\u0639 \u0647\u0630\u0647 \u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645 \u0648\u062A\u064F\u0641\u0633\u0631 \u0648\u0641\u0642\u064B\u0627 \u0644\u0642\u0648\u0627\u0646\u064A\u0646 \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629. \u0623\u064A \u0646\u0632\u0627\u0639\u0627\u062A \u062A\u0646\u0634\u0623 \u0639\u0646 \u0623\u0648 \u062A\u062A\u0639\u0644\u0642 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0627\u0644\u0645\u0648\u0642\u0639 \u0633\u062A\u0643\u0648\u0646 \u062E\u0627\u0636\u0639\u0629 \u0644\u0627\u062E\u062A\u0635\u0627\u0635 \u0627\u0644\u0645\u062D\u0627\u0643\u0645 \u0627\u0644\u0645\u062E\u062A\u0635\u0629 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629.",
        jurisdiction: "\u0627\u0644\u0627\u062E\u062A\u0635\u0627\u0635 \u0627\u0644\u0642\u0636\u0627\u0626\u064A"
      },
      amendments: {
        title: "10. \u0627\u0644\u062A\u0639\u062F\u064A\u0644\u0627\u062A",
        content: "\u062A\u062D\u062A\u0641\u0638 \u0634\u0631\u0643\u0629 \u062A\u0633\u0627\u0645\u0649 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0628\u0627\u0644\u062D\u0642 \u0641\u064A \u062A\u0639\u062F\u064A\u0644 \u0623\u0648 \u062A\u062D\u062F\u064A\u062B \u0647\u0630\u0647 \u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645 \u0641\u064A \u0623\u064A \u0648\u0642\u062A. \u0633\u064A\u062A\u0645 \u0646\u0634\u0631 \u0627\u0644\u0646\u0633\u062E \u0627\u0644\u0645\u062D\u062F\u062B\u0629 \u0639\u0644\u0649 \u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0648\u0633\u062A\u0635\u0628\u062D \u0646\u0627\u0641\u0630\u0629 \u0627\u0644\u0645\u0641\u0639\u0648\u0644 \u0639\u0646\u062F \u0627\u0644\u0646\u0634\u0631.",
        notification: "\u0625\u0634\u0639\u0627\u0631 \u0627\u0644\u062A\u063A\u064A\u064A\u0631\u0627\u062A"
      }
    },
    actions: {
      accept: "\u0623\u0642\u0628\u0644 \u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645",
      decline: "\u0623\u0631\u0641\u0636",
      print: "\u0637\u0628\u0627\u0639\u0629 \u0627\u0644\u0634\u0631\u0648\u0637",
      download: "\u062A\u062D\u0645\u064A\u0644 PDF"
    },
    warnings: {
      required: "\u064A\u062C\u0628 \u0639\u0644\u064A\u0643 \u0642\u0628\u0648\u0644 \u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062D\u0643\u0627\u0645 \u0644\u0644\u0645\u062A\u0627\u0628\u0639\u0629",
      ageRequirement: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u0643\u0648\u0646 \u0639\u0645\u0631\u0643 18 \u0639\u0627\u0645\u0627\u064B \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644 \u0644\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0647\u0630\u0647 \u0627\u0644\u062E\u062F\u0645\u0629"
    }
  },
  stepper: {
    journey: {
      title: "\u0627\u0628\u062F\u0623 \u0631\u062D\u0644\u062A\u0643",
      subtitle: "\u0645\u0639\u0646\u0627 \u0627\u0644\u064A\u0648\u0645",
      description: "\u0627\u0643\u062A\u0634\u0641 \u0625\u0645\u0643\u0627\u0646\u0627\u062A \u0623\u0639\u0645\u0627\u0644\u0643 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629 \u0645\u0646 \u062E\u0644\u0627\u0644 \u0627\u0644\u0625\u062C\u0627\u0628\u0629 \u0639\u0644\u0649 \u0628\u0639\u0636 \u0627\u0644\u0623\u0633\u0626\u0644\u0629 \u0627\u0644\u0628\u0633\u064A\u0637\u0629",
      currentStep: "\u0627\u0644\u062E\u0637\u0648\u0629 \u0627\u0644\u062D\u0627\u0644\u064A\u0629",
      viewAllSteps: "\u0639\u0631\u0636 \u062C\u0645\u064A\u0639 \u0627\u0644\u062E\u0637\u0648\u0627\u062A",
      closeSteps: "\u0625\u063A\u0644\u0627\u0642 \u0627\u0644\u062E\u0637\u0648\u0627\u062A"
    },
    login: {
      welcome: "\u0627\u0647\u0644\u0627\u064B \u0628\u0643\u0645 \u0641\u064A",
      brand: "\u0645\u0631\u0643\u0632 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644",
      features: [
        "\u0633\u0631\u0651\u0639 \u0625\u0637\u0644\u0627\u0642 \u0623\u0639\u0645\u0627\u0644\u0643 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
        "\u0627\u0633\u062A\u0641\u062F \u0645\u0646 \u0645\u062C\u0645\u0648\u0639\u0629 \u0648\u0627\u0633\u0639\u0629 \u0645\u0646 \u062E\u062F\u0645\u0627\u062A \u0627\u0644\u0623\u0639\u0645\u0627\u0644",
        "\u062A\u062A\u0628\u0639 \u0648\u0623\u062F\u0631 \u0643\u0627\u0641\u0629 \u0637\u0644\u0628\u0627\u062A\u0643 \u0645\u0646 \u0645\u0643\u0627\u0646 \u0648\u0627\u062D\u062F"
      ],
      cta: {
        question: "\u0647\u0644 \u0623\u0646\u062A \u0639\u0645\u064A\u0644 \u062C\u062F\u064A\u062F\u061F",
        button: "\u0633\u062C\u0644 \u0627\u0644\u0622\u0646"
      }
    },
    nda: {
      title: "\u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645",
      subtitle: "\u0627\u0644\u0625\u0641\u0635\u0627\u062D",
      description: "\u0644\u0636\u0645\u0627\u0646 \u0633\u0631\u064A\u0629 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u062A\u062C\u0627\u0631\u064A\u0629 \u0627\u0644\u062D\u0633\u0627\u0633\u0629 \u0648\u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0628\u064A\u0646 \u0639\u0645\u0644\u0643 \u0648\u062A\u0633\u0627\u0645\u0649\u060C \u064A\u062C\u0628 \u062A\u0648\u0642\u064A\u0639 \u0627\u062A\u0641\u0627\u0642\u064A\u0629 \u0639\u062F\u0645 \u0627\u0644\u0625\u0641\u0635\u0627\u062D \u0642\u0628\u0644 \u0623\u0646 \u0646\u062A\u0645\u0643\u0646 \u0645\u0646 \u0627\u0644\u0645\u062A\u0627\u0628\u0639\u0629"
    }
  },
  appInfo: {
    backToOnboarding: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0625\u0644\u0649 \u0627\u0644\u062A\u0647\u064A\u0626\u0629"
  }
};

// src/locales/ar/business-landing.json
var business_landing_default2 = {
  kickoffDocumentationLayout: {
    sidebar: {
      title: "\u0648\u062B\u0627\u0626\u0642 \u0627\u0644\u0627\u0646\u0637\u0644\u0627\u0642",
      description: "\u0623\u0643\u0645\u0644 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629 \u0644\u0648\u062B\u0627\u0626\u0642 \u0627\u0644\u0627\u0646\u0637\u0644\u0627\u0642"
    },
    steps: {
      generalManagerInformation: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      companyDetails: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
      companySetup: "\u062A\u0623\u0633\u064A\u0633 \u0627\u0644\u0634\u0631\u0643\u0629",
      businessActivities: "\u0627\u0644\u0623\u0646\u0634\u0637\u0629 \u0627\u0644\u062A\u062C\u0627\u0631\u064A\u0629"
    }
  },
  companyDetails: {
    validation: {
      companyType: { required: "\u0646\u0648\u0639 \u0627\u0644\u0634\u0631\u0643\u0629 \u0645\u0637\u0644\u0648\u0628" },
      sharedCapital: {
        required: "\u0631\u0623\u0633 \u0627\u0644\u0645\u0627\u0644 \u0627\u0644\u0645\u0634\u062A\u0631\u0643 \u0645\u0637\u0644\u0648\u0628",
        invalid: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0645\u0628\u0644\u063A \u0635\u062D\u064A\u062D"
      },
      currency: { required: "\u0627\u0644\u0639\u0645\u0644\u0629 \u0645\u0637\u0644\u0648\u0628\u0629" },
      ownershipDocument: {
        required: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0648\u062B\u064A\u0642\u0629 \u0647\u064A\u0643\u0644 \u0627\u0644\u0645\u0644\u0643\u064A\u0629",
        empty: "\u0648\u062B\u064A\u0642\u0629 \u0647\u064A\u0643\u0644 \u0645\u0644\u0643\u064A\u0629 \u0627\u0644\u0634\u0631\u0643\u0629 \u0645\u0637\u0644\u0648\u0628\u0629"
      },
      beneficialOwner: {
        firstName: {
          required: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0644\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A \u0645\u0637\u0644\u0648\u0628",
          max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 255 \u062D\u0631\u0641\u0627\u064B",
          charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0639\u0644\u0649 \u0623\u062D\u0631\u0641 \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
        },
        lastName: {
          required: "\u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0644\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A \u0645\u0637\u0644\u0648\u0628",
          max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 255 \u062D\u0631\u0641\u0627\u064B",
          charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0639\u0644\u0649 \u0623\u062D\u0631\u0641 \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
        },
        passportCopy: {
          required: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0646\u0633\u062E\u0629 \u0645\u0646 \u062C\u0648\u0627\u0632 \u0627\u0644\u0633\u0641\u0631",
          empty: "\u0646\u0633\u062E\u0629 \u0645\u0646 \u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A \u0645\u0637\u0644\u0648\u0628\u0629"
        }
      }
    },
    header: {
      title: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
      description: "\u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u062A\u0641\u0627\u0635\u064A\u0644 \u0645\u0644\u0643\u064A\u0629 \u0627\u0644\u0634\u0631\u0643\u0629 \u0648\u0647\u064A\u0643\u0644\u0647\u0627 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629 \u0644\u0623\u063A\u0631\u0627\u0636 \u0627\u0644\u062A\u062D\u0642\u0642 \u0627\u0644\u062A\u0646\u0638\u064A\u0645\u064A \u0648\u0627\u0633\u062A\u0643\u0645\u0627\u0644 \u0625\u062C\u0631\u0627\u0621\u0627\u062A \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629."
    },
    companyType: {
      label: "\u0646\u0648\u0639 \u0627\u0644\u0634\u0631\u0643\u0629",
      placeholder: "\u0627\u062E\u062A\u0631 \u0646\u0648\u0639 \u0627\u0644\u0634\u0631\u0643\u0629",
      options: {
        llc: "\u0634\u0631\u0643\u0629 \u0630\u0627\u062A \u0645\u0633\u0624\u0648\u0644\u064A\u0629 \u0645\u062D\u062F\u0648\u062F\u0629 (LLC)",
        jsc: "\u0634\u0631\u0643\u0629 \u0645\u0633\u0627\u0647\u0645\u0629 (JSC)",
        sjsc: "\u0634\u0631\u0643\u0629 \u0645\u0633\u0627\u0647\u0645\u0629 \u0645\u0628\u0633\u0637\u0629 (SJSC)"
      }
    },
    sharedCapital: {
      label: "\u0631\u0623\u0633 \u0627\u0644\u0645\u0627\u0644 \u0641\u064A \u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
      currencyPlaceholder: "\u0627\u0644\u0639\u0645\u0644\u0629",
      amountPlaceholder: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0645\u0628\u0644\u063A",
      currencyOptions: {
        SAR: "\u0631\u064A\u0627\u0644 \u0633\u0639\u0648\u062F\u064A"
      }
    },
    ownershipDocument: {
      label: "\u0648\u062B\u064A\u0642\u0629 \u0647\u064A\u0643\u0644 \u0645\u0644\u0643\u064A\u0629 \u0627\u0644\u0634\u0631\u0643\u0629 (\u064A\u062C\u0628 \u0623\u0646 \u062A\u062A\u0636\u0645\u0646 \u0646\u0633\u0628 \u0627\u0644\u0645\u0644\u0643\u064A\u0629)",
      supportingText: "PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0623\u0648 JPEG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A)",
      errors: {
        invalidType: "\u064A\u064F\u0633\u0645\u062D \u0641\u0642\u0637 \u0628\u0645\u0644\u0641\u0627\u062A PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0623\u0648 JPEG",
        invalidSize: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0623\u0642\u0644 \u0645\u0646 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A"
      }
    },
    beneficialOwner: {
      sectionDescription: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A \u0644\u0644\u0634\u0631\u0643\u0629 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629\u060C \u0648\u0630\u0644\u0643 \u0644\u0644\u0627\u0645\u062A\u062B\u0627\u0644 \u0644\u0644\u0645\u062A\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u062A\u0646\u0638\u064A\u0645\u064A\u0629 \u0648\u0627\u0644\u0645\u0635\u0631\u0641\u064A\u0629.",
      firstName: {
        label: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0644\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A",
        placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0644\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A"
      },
      lastName: {
        label: "\u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0644\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A",
        placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0644\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A"
      },
      passportCopy: {
        label: "\u0646\u0633\u062E\u0629 \u0645\u0646 \u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A",
        supportingText: "PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A)",
        errors: {
          invalidType: "\u064A\u064F\u0633\u0645\u062D \u0641\u0642\u0637 \u0628\u0645\u0644\u0641\u0627\u062A PDF \u0623\u0648 PNG \u0623\u0648 JPG",
          invalidSize: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0623\u0642\u0644 \u0645\u0646 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A"
        }
      }
    }
  },
  generalManagerInformation: {
    validation: {
      firstName: {
        required: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0645\u0637\u0644\u0648\u0628",
        min: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u062D\u0631\u0641\u064A\u0646 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 100 \u062D\u0631\u0641",
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0639\u0644\u0649 \u0623\u062D\u0631\u0641 \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
      },
      lastName: {
        required: "\u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0645\u0637\u0644\u0648\u0628",
        min: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u062D\u0631\u0641\u064A\u0646 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 100 \u062D\u0631\u0641",
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0639\u0644\u0649 \u0623\u062D\u0631\u0641 \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0634\u0631\u0637\u0627\u062A \u0641\u0642\u0637"
      },
      email: {
        required: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0637\u0644\u0648\u0628",
        invalid: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0635\u0627\u0644\u062D"
      },
      country: { required: "\u0631\u0645\u0632 \u0627\u0644\u062F\u0648\u0644\u0629 \u0645\u0637\u0644\u0648\u0628" },
      mobile: {
        required: "\u0631\u0642\u0645 \u0627\u0644\u0647\u0627\u062A\u0641 \u0627\u0644\u0645\u062D\u0645\u0648\u0644 \u0645\u0637\u0644\u0648\u0628",
        invalid: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u0631\u0642\u0645 \u0647\u0627\u062A\u0641 \u0645\u062D\u0645\u0648\u0644 \u0635\u0627\u0644\u062D \u0644\u0644\u062F\u0648\u0644\u0629 \u0627\u0644\u0645\u062D\u062F\u062F\u0629"
      },
      homeAddress: {
        required: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0646\u0632\u0644\u064A \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0646\u0632\u0644\u064A 500 \u062D\u0631\u0641"
      },
      nationality: {
        required: "\u062C\u0646\u0633\u064A\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0645\u0637\u0644\u0648\u0628\u0629",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u062A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u062C\u0646\u0633\u064A\u0629 100 \u062D\u0631\u0641"
      },
      passportCopy: {
        required: "\u0646\u0633\u062E\u0629 \u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0645\u0637\u0644\u0648\u0628\u0629",
        size: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0623\u0642\u0644 \u0645\u0646 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A",
        type: "\u064A\u064F\u0633\u0645\u062D \u0641\u0642\u0637 \u0628\u0645\u0644\u0641\u0627\u062A PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0623\u0648 JPEG"
      }
    },
    header: {
      title: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      description: "\u062A\u0633\u0627\u0639\u062F\u0646\u0627 \u0647\u0630\u0647 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0641\u064A \u0627\u0644\u062A\u062D\u0642\u0651\u0642 \u0645\u0646 \u0647\u0648\u064A\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645\u060C \u0648\u062F\u0639\u0645 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629\u060C \u0648\u0625\u0639\u062F\u0627\u062F \u0627\u0644\u062D\u0633\u0627\u0628 \u0627\u0644\u0628\u0646\u0643\u064A\u060C \u0648\u0627\u0633\u062A\u0643\u0645\u0627\u0644 \u0627\u0644\u0625\u062C\u0631\u0627\u0621\u0627\u062A \u0627\u0644\u0646\u0638\u0627\u0645\u064A\u0629"
    },
    firstName: {
      label: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644",
      placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0623\u0648\u0644 \u0644\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645"
    },
    lastName: {
      label: "\u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629",
      placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0633\u0645 \u0627\u0644\u0639\u0627\u0626\u0644\u0629 \u0644\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645"
    },
    email: {
      label: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
      placeholder: "\u0623\u062F\u062E\u0644 \u0628\u0631\u064A\u062F \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A"
    },
    mobile: {
      label: "\u0631\u0642\u0645 \u0627\u0644\u062C\u0648\u0627\u0644",
      placeholder: "\u0623\u062F\u062E\u0644 \u0631\u0642\u0645 \u0627\u0644\u062C\u0648\u0627\u0644"
    },
    homeAddress: {
      label: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0646\u0632\u0644\u064A",
      placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0646\u0632\u0644\u064A \u0644\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645"
    },
    nationality: {
      label: "\u062C\u0646\u0633\u064A\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0643\u0645\u0627 \u062A\u0638\u0647\u0631 \u0641\u064A \u062C\u0648\u0627\u0632 \u0627\u0644\u0633\u0641\u0631",
      placeholder: "\u0627\u0628\u062D\u062B \u0648\u0627\u062E\u062A\u0631 \u0627\u0644\u062C\u0646\u0633\u064A\u0629"
    },
    passportCopy: {
      label: "\u0646\u0633\u062E\u0629 \u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      supportingText: "PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A)",
      errors: {
        invalidType: "\u064A\u064F\u0633\u0645\u062D \u0641\u0642\u0637 \u0628\u0645\u0644\u0641\u0627\u062A PDF \u0623\u0648 PNG \u0623\u0648 JPG",
        invalidSize: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062D\u062C\u0645 \u0627\u0644\u0645\u0644\u0641 \u0623\u0642\u0644 \u0645\u0646 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A"
      }
    }
  },
  businessActivitiesTable: {
    columns: {
      activities: "\u0627\u0644\u0623\u0646\u0634\u0637\u0629",
      licenseType: "\u0646\u0648\u0639 \u0627\u0644\u062A\u0631\u062E\u064A\u0635"
    },
    actions: {
      addActivity: "\u0625\u0636\u0627\u0641\u0629 {activity}",
      removeActivity: "\u0625\u0632\u0627\u0644\u0629 {activity}"
    }
  },
  businessActivities: {
    validation: {
      required: "\u064A\u062C\u0628 \u0627\u062E\u062A\u064A\u0627\u0631 \u0646\u0634\u0627\u0637 \u062A\u062C\u0627\u0631\u064A \u0648\u0627\u062D\u062F \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644"
    },
    milestoneStepperDescription: "\u0647\u0646\u0627\u0643 \u062E\u0645\u0633 \u0645\u0631\u0627\u062D\u0644 \u0631\u0626\u064A\u0633\u064A\u0629 \u0644\u0628\u062F\u0621 \u0627\u0644\u0623\u0639\u0645\u0627\u0644 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629 \u0645\u0639 \u0648\u062C\u0647\u0629 \u062A\u0633\u0627\u0645\u0649 \u0644\u0644\u0623\u0639\u0645\u0627\u0644\u060C \u0645\u0639 \u0648\u062C\u0648\u062F \u0639\u0645\u0644\u064A\u0627\u062A \u0645\u062A\u0639\u062F\u062F\u0629 \u0636\u0645\u0646 \u0643\u0644 \u0645\u0631\u062D\u0644\u0629.",
    description: "\u0627\u0644\u0631\u062C\u0627\u0621 \u0633\u0631\u062F \u0627\u0644\u0623\u0646\u0634\u0637\u0629 \u0627\u0644\u062A\u062C\u0627\u0631\u064A\u0629 \u0627\u0644\u062A\u064A \u062A\u0631\u064A\u062F \u0625\u062F\u0631\u0627\u062C\u0647\u0627 \u0636\u0645\u0646 \u0634\u0647\u0627\u062F\u0629 \u062A\u0633\u062C\u064A\u0644 \u0634\u0631\u0643\u062A\u0643 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
    selectedActivities: "\u0627\u0644\u0623\u0646\u0634\u0637\u0629 \u0627\u0644\u0645\u062D\u062F\u062F\u0629",
    searchPlaceholder: "\u0627\u0644\u0628\u062D\u062B \u0639\u0646 \u0627\u0644\u0623\u0646\u0634\u0637\u0629",
    licenseTypePlaceholder: "\u0646\u0648\u0639 \u0627\u0644\u062A\u0631\u062E\u064A\u0635",
    activitiesCount: "{count} \u0646\u0634\u0627\u0637",
    noActivitiesFound: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0623\u0646\u0634\u0637\u0629",
    tryAdjustingFilters: "\u062D\u0627\u0648\u0644 \u062A\u0639\u062F\u064A\u0644 \u0627\u0644\u0628\u062D\u062B \u0623\u0648 \u0627\u0644\u0641\u0644\u0627\u062A\u0631.",
    noActivitiesAvailable: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0623\u0646\u0634\u0637\u0629 \u0645\u062A\u0627\u062D\u0629.",
    clearFilters: "\u0645\u0633\u062D \u0627\u0644\u0628\u062D\u062B \u0648\u0627\u0644\u0641\u0644\u0627\u062A\u0631"
  },
  travel: {
    actions: {
      addGmDetails: "\u0625\u0636\u0627\u0641\u0629 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      editGmDetails: "\u062A\u0639\u062F\u064A\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645"
    }
  },
  tasamaActivities: {
    onHoldReason: "\u0627\u0644\u0633\u0628\u0628: {reason}",
    tradeName: {
      approvedPrefix: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u062A\u062C\u0627\u0631\u064A \u0627\u0644\u0645\u0639\u062A\u0645\u062F \u0628\u0646\u0627\u0621\u064B \u0639\u0644\u0649 \u062A\u0641\u0636\u064A\u0644\u0627\u062A\u0643 \u0648\u062A\u0648\u0641\u0631\u0647:"
    },
    downloadActions: {
      misaLicense: {
        title: "\u0631\u062E\u0635\u0629 \u0648\u0632\u0627\u0631\u0629 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631",
        description: "\u062A\u062D\u0645\u064A\u0644 \u0631\u062E\u0635\u0629 \u0648\u0632\u0627\u0631\u0629 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631"
      },
      articleOfAssociation: {
        title: "\u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
        description: "\u062A\u062D\u0645\u064A\u0644 \u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633"
      },
      commerceRegistration: {
        title: "\u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
        description: "\u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A"
      },
      generalManagerVisa: {
        title: "\u062A\u0623\u0634\u064A\u0631\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
        description: "\u062A\u062D\u0645\u064A\u0644 \u0648\u062B\u064A\u0642\u0629 \u062A\u0623\u0634\u064A\u0631\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645"
      },
      commercialRegistrationUpdate: {
        title: "\u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
        description: "\u062A\u062D\u0645\u064A\u0644 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A"
      },
      bankAccountCertification: {
        title: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062D\u0633\u0627\u0628 \u0627\u0644\u0628\u0646\u0643\u064A",
        description: "\u062A\u062D\u0645\u064A\u0644 \u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062D\u0633\u0627\u0628 \u0627\u0644\u0628\u0646\u0643\u064A"
      }
    }
  },
  projectKickoff: {
    actions: {
      addKickoffInformation: "\u0625\u0636\u0627\u0641\u0629 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0628\u062F\u0621 \u0627\u0644\u062A\u0634\u063A\u064A\u0644",
      editKickoffInformation: "\u062A\u0639\u062F\u064A\u0644 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0628\u062F\u0621 \u0627\u0644\u062A\u0634\u063A\u064A\u0644",
      gmInfoCompleteNow: "\u0623\u0643\u0645\u0644 \u0627\u0644\u0622\u0646",
      gmInfoUpdateNow: "\u062D\u062F\u0651\u062B \u0627\u0644\u0622\u0646"
    },
    rejectionModal: {
      title: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A"
    }
  },
  skipGmModal: {
    title: "\u062A\u062E\u0637\u064A \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645\u061F",
    body: "\u064A\u0645\u0643\u0646\u0643 \u0625\u062F\u062E\u0627\u0644 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0644\u0627\u062D\u0642\u064B\u0627. \u0648\u0644\u0643\u0646 \u064A\u062C\u0628 \u0627\u0633\u062A\u0643\u0645\u0627\u0644 \u0647\u0630\u0647 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0642\u0628\u0644 \u0623\u0646 \u062A\u062A\u0645\u0643\u0646 \u062A\u0633\u0627\u0645\u0649 \u0645\u0646 \u0645\u062A\u0627\u0628\u0639\u0629 \u0625\u062C\u0631\u0627\u0621\u0627\u062A \u062A\u0623\u0633\u064A\u0633 \u0627\u0644\u0634\u0631\u0643\u0629.",
    cancel: "\u0625\u0644\u063A\u0627\u0621",
    confirm: "\u062A\u0623\u0643\u064A\u062F"
  },
  docsAttestation: {
    requiredDocuments: {
      label: "\u064A\u0631\u062C\u0649 \u062A\u062C\u0647\u064A\u0632 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u062A\u0627\u0644\u064A\u0629:"
    },
    actions: {
      addSubmissionDetails: "\u0625\u0636\u0627\u0641\u0629 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062A\u0642\u062F\u064A\u0645",
      editSubmissionDetails: "\u062A\u0639\u062F\u064A\u0644 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062A\u0642\u062F\u064A\u0645",
      download: "\u062A\u062D\u0645\u064A\u0644"
    },
    rejectionModal: {
      title: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A"
    },
    powerOfAttorney: {
      title: "\u0646\u0645\u0648\u0630\u062C \u0627\u0644\u062A\u0648\u0643\u064A\u0644 \u0627\u0644\u0631\u0633\u0645\u064A",
      description: "\u0642\u0645 \u0628\u062A\u0646\u0632\u064A\u0644 \u0646\u0645\u0648\u0630\u062C \u0627\u0644\u062A\u0648\u0643\u064A\u0644 \u0627\u0644\u0631\u0633\u0645\u064A \u0648\u062A\u0648\u0642\u064A\u0639\u0647"
    },
    finalDocuments: {
      title: "\u062D\u0632\u0645\u0629 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0646\u0647\u0627\u0626\u064A\u0629",
      description: "\u062A\u062D\u0645\u064A\u0644 \u062D\u0632\u0645\u0629 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0627\u0644\u0646\u0647\u0627\u0626\u064A\u0629"
    }
  },
  proofOfPayment: {
    rejectionModal: {
      title: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A"
    },
    sidebar: {
      title: "\u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
      description: "\u064A\u0631\u062C\u0649 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643\u060C \u0648\u0628\u0645\u062C\u0631\u062F \u0625\u062A\u0645\u0627\u0645 \u0639\u0645\u0644\u064A\u0629 \u0627\u0644\u062F\u0641\u0639\u060C \u0642\u0645 \u0628\u0631\u0641\u0639 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639 \u0644\u062A\u0623\u0643\u064A\u062F \u0627\u0644\u062A\u0633\u0648\u064A\u0629 \u0648\u0627\u0644\u0633\u0645\u0627\u062D \u0644\u0644\u062E\u062F\u0645\u0629 \u0628\u0627\u0644\u0645\u062A\u0627\u0628\u0639\u0629."
    },
    steps: {
      downloadInvoice: "\u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629",
      uploadProofOfPayment: "\u0631\u0641\u0639 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639"
    },
    uploadProofOfPayment: {
      header: {
        title: "\u0631\u0641\u0639 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
        description: "\u064A\u0631\u062C\u0649 \u0631\u0641\u0639 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639 \u0647\u0646\u0627."
      },
      supportingText: "PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 50 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A)",
      errors: {
        submitFailed: "\u0641\u0634\u0644 \u0627\u0644\u0631\u0641\u0639. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
      }
    },
    header: {
      title: "\u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629",
      description: "\u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0627\u0644\u062E\u062F\u0645\u0629 \u062C\u0627\u0647\u0632\u0629 \u0644\u0644\u0645\u0631\u0627\u062C\u0639\u0629."
    },
    invoice: {
      fileName: "invoice.pdf",
      description: "\u0641\u0627\u062A\u0648\u0631\u0629 \u0627\u0644\u062E\u062F\u0645\u0629"
    },
    result: {
      title: "\u0634\u0643\u0631\u0627\u064B \u0644\u0643.",
      description: "\u0633\u064A\u062A\u0645 \u0625\u0634\u0639\u0627\u0631\u0643 \u0639\u0646\u062F \u062A\u0648\u0641\u0631 \u0641\u0627\u062A\u0648\u0631\u0629 \u0636\u0631\u064A\u0628\u064A\u0629 \u0648\u0625\u064A\u0635\u0627\u0644."
    }
  },
  documentAttestation: {
    layout: {
      sidebar: {
        title: "\u062A\u0635\u062F\u064A\u0642 \u0627\u0644\u0648\u062B\u0627\u0626\u0642 / \u0645\u062A\u0637\u0644\u0628\u0627\u062A \u0627\u0644\u0623\u0628\u0648\u0633\u062A\u064A\u0644"
      },
      steps: {
        submissionDetails: "\u0625\u0636\u0627\u0641\u0629 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062A\u0642\u062F\u064A\u0645"
      }
    },
    header: {
      title: "\u0625\u0636\u0627\u0641\u0629 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u062A\u0642\u062F\u064A\u0645",
      description: "\u0625\u0646 \u062A\u0642\u062F\u064A\u0645 \u062A\u0641\u0627\u0635\u064A\u0644 \u062A\u062A\u0628\u0639 \u0627\u0644\u0634\u062D\u0646\u0629 \u0633\u064A\u0636\u0645\u0646 \u062A\u0644\u0642\u0651\u064A\u0646\u0627 \u0625\u0634\u0639\u0627\u0631\u0627\u064B \u0641\u064A \u0627\u0644\u0648\u0642\u062A \u0627\u0644\u0645\u0646\u0627\u0633\u0628 \u0628\u0634\u0623\u0646 \u0645\u0633\u062A\u0646\u062F\u0627\u062A\u0643 \u0627\u0644\u0645\u0647\u0645\u0629"
    },
    validation: {
      courierCompany: {
        required: "\u0634\u0631\u0643\u0629 \u0627\u0644\u0634\u062D\u0646 \u0645\u0637\u0644\u0648\u0628\u0629",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0633\u0645 \u0634\u0631\u0643\u0629 \u0627\u0644\u0634\u062D\u0646 200 \u062D\u0631\u0641",
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0627\u0633\u0645 \u0634\u0631\u0643\u0629 \u0627\u0644\u0634\u062D\u0646 \u0639\u0644\u0649 \u062D\u0631\u0648\u0641 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0648\u0627\u0644\u0631\u0645\u0648\u0632 \u0627\u0644\u0634\u0627\u0626\u0639\u0629 (&\u060C -\u060C '\u060C .\u060C (\u060C )\u060C /\u060C ,)"
      },
      trackingNumber: {
        required: "\u0631\u0642\u0645 \u0627\u0644\u062A\u062A\u0628\u0639 \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0631\u0642\u0645 \u0627\u0644\u062A\u062A\u0628\u0639 100 \u062D\u0631\u0641",
        charset: "\u064A\u0645\u0643\u0646 \u0623\u0646 \u064A\u062D\u062A\u0648\u064A \u0631\u0642\u0645 \u0627\u0644\u062A\u062A\u0628\u0639 \u0639\u0644\u0649 \u062D\u0631\u0648\u0641 \u0648\u0623\u0631\u0642\u0627\u0645 \u0648\u0634\u0631\u0637\u0627\u062A \u0648\u0645\u0633\u0627\u0641\u0627\u062A \u0641\u0642\u0637"
      }
    },
    courierCompany: {
      label: "\u0634\u0631\u0643\u0629 \u0627\u0644\u0634\u062D\u0646",
      placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0633\u0645 \u0634\u0631\u0643\u0629 \u0627\u0644\u0634\u062D\u0646"
    },
    trackingNumber: {
      label: "\u0631\u0642\u0645 \u0627\u0644\u062A\u062A\u0628\u0639",
      placeholder: "\u0623\u062F\u062E\u0644 \u0631\u0642\u0645 \u0627\u0644\u062A\u062A\u0628\u0639"
    },
    documentUpload: {
      supportingText: "PDF \u0623\u0648 PNG \u0623\u0648 JPG \u0641\u0642\u0637 (\u0628\u062D\u062F \u0623\u0642\u0635\u0649 5 \u0645\u064A\u063A\u0627\u0628\u0627\u064A\u062A)"
    }
  },
  companyActivated: {
    welcomeMessage: "\u0623\u0647\u0644\u0627\u064B \u0628\u0643 \u0641\u064A \u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
    activationMessage: "\u0644\u0642\u062F \u062A\u0645 \u062A\u0641\u0639\u064A\u0644 \u0634\u0631\u0643\u062A\u0643 \u0631\u0633\u0645\u064A\u0627\u064B \u0648\u0647\u064A \u062C\u0627\u0647\u0632\u0629 \u0644\u0644\u0639\u0645\u0644.",
    browseServices: "\u062A\u0635\u0641\u062D \u0627\u0644\u062E\u062F\u0645\u0627\u062A",
    title: "\u062A\u0645 \u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
    inProgressTitle: "\u062C\u0627\u0631\u064A \u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629",
    inProgressDescription: "\u062A\u0641\u0639\u064A\u0644 \u0634\u0631\u0643\u062A\u0643 \u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630 \u062D\u0627\u0644\u064A\u0627\u064B\n\n\u0644\u062A\u062C\u0646\u0628 \u0623\u064A \u062A\u0623\u062E\u064A\u0631\u060C \u064A\u064F\u0631\u062C\u0649 \u0627\u0644\u062A\u0623\u0643\u062F \u0645\u0646 \u0625\u062A\u0645\u0627\u0645 \u062C\u0645\u064A\u0639 \u0627\u0644\u0645\u062F\u0641\u0648\u0639\u0627\u062A \u0648\u0645\u0631\u0627\u062C\u0639\u0629 \u0642\u0633\u0645 \u0627\u0644\u0641\u0648\u0627\u062A\u064A\u0631 \u0644\u0644\u0627\u0637\u0644\u0627\u0639 \u0639\u0644\u0649 \u0623\u064A \u0641\u0648\u0627\u062A\u064A\u0631 \u0645\u0639\u0644\u0642\u0629. \u0633\u0646\u064F\u0639\u0644\u0645\u0643 \u0641\u0648\u0631 \u0627\u0644\u0627\u0646\u062A\u0647\u0627\u0621."
  },
  invoices: {
    emptyHeading: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0641\u0648\u0627\u062A\u064A\u0631 \u062D\u062A\u0649 \u0627\u0644\u0622\u0646",
    emptyText: "\u0633\u062A\u0638\u0647\u0631 \u0627\u0644\u0641\u0648\u0627\u062A\u064A\u0631 \u0647\u0646\u0627 \u0628\u0645\u062C\u0631\u062F \u0625\u0635\u062F\u0627\u0631\u0647\u0627.",
    columnInvoiceNumber: "\u0631\u0642\u0645 \u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629",
    columnAmount: "\u0627\u0644\u0645\u0628\u0644\u063A",
    columnDate: "\u0627\u0644\u062A\u0627\u0631\u064A\u062E",
    columnStatus: "\u0627\u0644\u062D\u0627\u0644\u0629",
    payButton: "\u0627\u062F\u0641\u0639",
    failedToLoad: "\u0641\u0634\u0644 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0641\u0648\u0627\u062A\u064A\u0631. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
  },
  tbl: {
    status: {
      not_started: "\u0644\u0645 \u062A\u0628\u062F\u0623",
      in_progress: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
      completed: "\u0645\u0643\u062A\u0645\u0644",
      on_hold: "\u0645\u0639\u0644\u0651\u0642",
      payment_required: "\u0627\u0644\u062F\u0641\u0639 \u0645\u0637\u0644\u0648\u0628",
      bypassed: "\u062A\u0645 \u0627\u0644\u062A\u062C\u0627\u0648\u0632",
      waiting_for_tasama: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0641\u0631\u064A\u0642 \u062A\u0633\u0627\u0645\u0649",
      more_info_required: "\u0645\u0637\u0644\u0648\u0628 \u0645\u0632\u064A\u062F \u0645\u0646 \u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A"
    },
    stage: {
      not_started: "\u0644\u0645 \u062A\u0628\u062F\u0623",
      in_progress: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
      completed: "\u0645\u0643\u062A\u0645\u0644"
    },
    overall: {
      in_progress: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
      completed: "\u0645\u0643\u062A\u0645\u0644"
    },
    payment: {
      pending: "\u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      invoiced: "\u0635\u062F\u0631\u062A \u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629",
      proof_submitted: "\u062A\u0645 \u062A\u0642\u062F\u064A\u0645 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
      confirmed: "\u0645\u0624\u0643\u062F",
      display: {
        paid: "\u0645\u062F\u0641\u0648\u0639",
        not_paid: "\u063A\u064A\u0631 \u0645\u062F\u0641\u0648\u0639",
        delayed: "\u0645\u062A\u0623\u062E\u0631"
      }
    },
    milestone: {
      service_initiation: "\u0628\u062F\u0621 \u0627\u0644\u062E\u062F\u0645\u0629",
      doc_legal: "\u0627\u0644\u062A\u0648\u062B\u064A\u0642 \u0648\u0627\u0644\u0625\u0639\u062F\u0627\u062F \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A",
      company_formation: "\u062A\u0623\u0633\u064A\u0633 \u0627\u0644\u0634\u0631\u0643\u0629",
      gm_visa: "\u062A\u0623\u0634\u064A\u0631\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 / \u0627\u0644\u0645\u0646\u0635\u0627\u062A \u0627\u0644\u062D\u0643\u0648\u0645\u064A\u0629",
      company_activated: "\u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629"
    },
    action: {
      kickoff_required: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0625\u0637\u0644\u0627\u0642 \u0645\u0637\u0644\u0648\u0628\u0629",
      courier_required: "\u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0634\u062D\u0646 \u0645\u0637\u0644\u0648\u0628\u0629",
      general_manager_required: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0645\u0637\u0644\u0648\u0628\u0629",
      travel_dates_required: "\u062A\u0648\u0627\u0631\u064A\u062E \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 \u0645\u0637\u0644\u0648\u0628\u0629",
      payment_service_initiation: "\u062F\u0641\u0639\u0629 \u0628\u062F\u0621 \u0627\u0644\u062E\u062F\u0645\u0629",
      payment_doc_legal: "\u062F\u0641\u0639\u0629 \u0627\u0644\u062A\u0648\u062B\u064A\u0642 \u0648\u0627\u0644\u0625\u0639\u062F\u0627\u062F \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064A",
      payment_company_formation: "\u062F\u0641\u0639\u0629 \u062A\u0623\u0633\u064A\u0633 \u0627\u0644\u0634\u0631\u0643\u0629",
      payment_gm_visa: "\u062F\u0641\u0639\u0629 \u062A\u0623\u0634\u064A\u0631\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645 / \u0627\u0644\u0645\u0646\u0635\u0627\u062A \u0627\u0644\u062D\u0643\u0648\u0645\u064A\u0629",
      payment_company_activated: "\u062F\u0641\u0639\u0629 \u062A\u0641\u0639\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629"
    },
    task: {
      pending: "\u0642\u064A\u062F \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631",
      action_required: "\u0625\u062C\u0631\u0627\u0621 \u0645\u0637\u0644\u0648\u0628"
    },
    helper: {
      in_progress: "\u0642\u064A\u062F \u0627\u0644\u062A\u0646\u0641\u064A\u0630",
      action_required: "\u0625\u062C\u0631\u0627\u0621 \u0645\u0637\u0644\u0648\u0628",
      completed: "\u0645\u0643\u062A\u0645\u0644"
    },
    attestation: {
      apostille: "\u0623\u0628\u0648\u0633\u062A\u064A\u0644",
      embassy: "\u0633\u0641\u0627\u0631\u0629"
    },
    gm: {
      nationality: {
        saudi: "\u0633\u0639\u0648\u062F\u064A",
        non_saudi: "\u063A\u064A\u0631 \u0633\u0639\u0648\u062F\u064A"
      }
    },
    doc: {
      gm_passport: "\u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      ownership_structure: "\u0647\u064A\u0643\u0644 \u0627\u0644\u0645\u0644\u0643\u064A\u0629",
      ubo_passport: "\u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A",
      courier_soft_copy: "\u0646\u0633\u062E\u0629 \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A\u0629 \u0645\u0646 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0633\u0631\u064A\u0639",
      payment_proof: "\u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
      proforma_invoice: "\u0641\u0627\u062A\u0648\u0631\u0629 \u0623\u0648\u0644\u064A\u0629",
      aoa_draft: "\u0645\u0633\u0648\u062F\u0629 \u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      aoa: "\u0648\u062B\u064A\u0642\u0629 \u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      cr: "\u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
      misa_license: "\u0631\u062E\u0635\u0629 \u0648\u0632\u0627\u0631\u0629 \u0627\u0644\u0627\u0633\u062A\u062B\u0645\u0627\u0631",
      iqama: "\u0648\u062B\u064A\u0642\u0629 \u0627\u0644\u0625\u0642\u0627\u0645\u0629",
      final_doc_package: "\u062D\u0632\u0645\u0629 \u0627\u0644\u0648\u062B\u0627\u0626\u0642 \u0627\u0644\u0646\u0647\u0627\u0626\u064A\u0629",
      gm_visa: "\u0648\u062B\u064A\u0642\u0629 \u062A\u0623\u0634\u064A\u0631\u0629 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      trade_name_cert: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
      bank_account_certification: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062D\u0633\u0627\u0628 \u0627\u0644\u0628\u0646\u0643\u064A",
      tax_invoice: "\u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629 \u0627\u0644\u0636\u0631\u064A\u0628\u064A\u0629",
      receipt: "\u0627\u0644\u0625\u064A\u0635\u0627\u0644 \u0627\u0644\u0631\u0633\u0645\u064A"
    },
    attachment: {
      gm_passport: "\u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u062F\u064A\u0631 \u0627\u0644\u0639\u0627\u0645",
      ownership_structure: "\u0647\u064A\u0643\u0644 \u0627\u0644\u0645\u0644\u0643\u064A\u0629",
      ubo_passport: "\u062C\u0648\u0627\u0632 \u0633\u0641\u0631 \u0627\u0644\u0645\u0633\u062A\u0641\u064A\u062F \u0627\u0644\u0646\u0647\u0627\u0626\u064A",
      commercial_registration: "\u0627\u0644\u0633\u062C\u0644 \u0627\u0644\u062A\u062C\u0627\u0631\u064A",
      aoa: "\u0639\u0642\u062F \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      moa: "\u0645\u0630\u0643\u0631\u0629 \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      financial_statement: "\u0627\u0644\u0642\u0648\u0627\u0626\u0645 \u0627\u0644\u0645\u0627\u0644\u064A\u0629",
      poa: "\u062A\u0648\u0643\u064A\u0644 \u0631\u0633\u0645\u064A",
      board_resolution: "\u0642\u0631\u0627\u0631 \u0645\u062C\u0644\u0633 \u0627\u0644\u0625\u062F\u0627\u0631\u0629",
      cert_incorporation: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u062A\u0623\u0633\u064A\u0633",
      cert_good_standing: "\u0634\u0647\u0627\u062F\u0629 \u062D\u0633\u0646 \u0627\u0644\u0633\u064A\u0631\u0629 \u0648\u0627\u0644\u0633\u0644\u0648\u0643",
      cert_incumbency: "\u0634\u0647\u0627\u062F\u0629 \u062A\u0648\u0644\u064A \u0645\u0633\u0624\u0648\u0644\u064A\u0629",
      authorization_letter: "\u062E\u0637\u0627\u0628 \u062A\u0641\u0648\u064A\u0636",
      ubo_declaration: "\u0625\u0642\u0631\u0627\u0631 \u0627\u0644\u0645\u0644\u0643\u064A\u0629 \u0627\u0644\u0645\u0633\u062A\u0641\u064A\u062F\u0629",
      shareholder_register: "\u0633\u062C\u0644 \u0627\u0644\u0645\u0633\u0627\u0647\u0645\u064A\u0646",
      bylaws: "\u0627\u0644\u0644\u0648\u0627\u0626\u062D \u0627\u0644\u062F\u0627\u062E\u0644\u064A\u0629",
      apostille_cert: "\u0634\u0647\u0627\u062F\u0629 \u0627\u0644\u0623\u0628\u0648\u0633\u062A\u064A\u0644",
      passport_copy: "\u0646\u0633\u062E\u0629 \u0645\u0646 \u062C\u0648\u0627\u0632 \u0627\u0644\u0633\u0641\u0631",
      national_id: "\u0627\u0644\u0647\u0648\u064A\u0629 \u0627\u0644\u0648\u0637\u0646\u064A\u0629",
      proof_of_payment: "\u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639",
      other: "\u0623\u062E\u0631\u0649"
    }
  },
  errors: {
    kickoff: {
      passportFileRequired: "\u0645\u0644\u0641 \u062C\u0648\u0627\u0632 \u0627\u0644\u0633\u0641\u0631 \u0645\u0637\u0644\u0648\u0628",
      ownershipStructureFileRequired: "\u0645\u0644\u0641 \u0647\u064A\u0643\u0644 \u0627\u0644\u0645\u0644\u0643\u064A\u0629 \u0645\u0637\u0644\u0648\u0628",
      invalidStepId: "\u0627\u0644\u062E\u0637\u0648\u0629 \u0627\u0644\u0645\u062D\u062F\u062F\u0629 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629",
      unknownLicenseId: "\u0646\u0648\u0639 \u0627\u0644\u062A\u0631\u062E\u064A\u0635 \u0627\u0644\u0645\u062D\u062F\u062F \u063A\u064A\u0631 \u0645\u0639\u0631\u0648\u0641",
      validationFailed: "\u0641\u0634\u0644 \u0627\u0644\u062A\u062D\u0642\u0642. \u064A\u0631\u062C\u0649 \u0645\u0631\u0627\u062C\u0639\u0629 \u0627\u0644\u0645\u062F\u062E\u0644\u0627\u062A.",
      accessDenied: "\u062A\u0645 \u0631\u0641\u0636 \u0627\u0644\u0648\u0635\u0648\u0644",
      tblServiceNotFound: "\u062E\u062F\u0645\u0629 \u0627\u0644\u0647\u0628\u0648\u0637 \u0627\u0644\u062A\u062C\u0627\u0631\u064A \u0644\u062A\u0633\u0627\u0645\u0627 \u063A\u064A\u0631 \u0645\u0648\u062C\u0648\u062F\u0629",
      submissionFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0627\u0646\u0637\u0644\u0627\u0642. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
      useFileEndpoint: "\u0631\u0641\u0639 \u0627\u0644\u0645\u0644\u0641 \u0645\u0637\u0644\u0648\u0628 \u0644\u0647\u0630\u0647 \u0627\u0644\u062E\u0637\u0648\u0629"
    },
    gmTravel: {
      minDateRange: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u0643\u0648\u0646 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0639\u0648\u062F\u0629 \u0628\u0639\u062F \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0645\u063A\u0627\u062F\u0631\u0629 \u0628\u0640 14 \u064A\u0648\u0645\u0627\u064B \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
      datesLocked: "\u062A\u0648\u0627\u0631\u064A\u062E \u0627\u0644\u0633\u0641\u0631 \u0645\u0642\u0641\u0644\u0629 \u0644\u0623\u0646 \u062E\u0637\u0648\u0629 \u0627\u0644\u0625\u0642\u0627\u0645\u0629 \u0642\u062F \u0627\u0643\u062A\u0645\u0644\u062A \u0645\u0633\u0628\u0642\u0627\u064B"
    },
    invoices: {
      fileRequired: "\u064A\u062C\u0628 \u062A\u0648\u0641\u064A\u0631 \u0645\u0644\u0641",
      invalidProofOfPayment: "\u0637\u0644\u0628 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D",
      submitFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0625\u062B\u0628\u0627\u062A \u0627\u0644\u062F\u0641\u0639. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
    },
    documentsAttestation: {
      fileRequired: "\u064A\u062C\u0628 \u062A\u0648\u0641\u064A\u0631 \u0645\u0644\u0641",
      softCopyRequired: "\u064A\u062C\u0628 \u062A\u0648\u0641\u064A\u0631 \u0646\u0633\u062E\u0629 \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A\u0629 \u0648\u0627\u062D\u062F\u0629 \u0639\u0644\u0649 \u0627\u0644\u0623\u0642\u0644",
      documentTypeCountMismatch: "\u064A\u062C\u0628 \u0623\u0646 \u064A\u062A\u0637\u0627\u0628\u0642 \u0639\u062F\u062F \u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A \u0645\u0639 \u0639\u062F\u062F \u0627\u0644\u0645\u0644\u0641\u0627\u062A \u0627\u0644\u0645\u0631\u0641\u0648\u0639\u0629",
      invalidCourierDetails: "\u0628\u064A\u0627\u0646\u0627\u062A \u0634\u0631\u0643\u0629 \u0627\u0644\u0634\u062D\u0646 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629",
      submitFailed: "\u0641\u0634\u0644 \u0625\u0631\u0633\u0627\u0644 \u0645\u0635\u0627\u062F\u0642\u0629 \u0627\u0644\u0645\u0633\u062A\u0646\u062F\u0627\u062A. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649."
    }
  },
  companySetup: {
    validation: {
      tradeName: {
        required: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u062A\u062C\u0627\u0631\u064A \u0627\u0644\u0645\u0641\u0636\u0644 \u0644\u0644\u062A\u0633\u062C\u064A\u0644 #{number} \u0645\u0637\u0644\u0648\u0628",
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u062A\u062C\u0627\u0631\u064A 255 \u062D\u0631\u0641\u0627\u064B",
        duplicate: "\u0647\u0630\u0627 \u0627\u0644\u0627\u0633\u0645 \u0645\u064F\u062F\u062E\u0644 \u0628\u0627\u0644\u0641\u0639\u0644. \u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u0627\u0633\u0645 \u062A\u062C\u0627\u0631\u064A \u0641\u0631\u064A\u062F."
      },
      preferredBank: { required: "\u0627\u0644\u0628\u0646\u0643 \u0627\u0644\u0645\u0641\u0636\u0644 \u0645\u0637\u0644\u0648\u0628" },
      preferredBankOther: {
        required: '\u0627\u0633\u0645 \u0627\u0644\u0628\u0646\u0643 \u0627\u0644\u0645\u0641\u0636\u0644 \u0645\u0637\u0644\u0648\u0628 \u0639\u0646\u062F \u0627\u062E\u062A\u064A\u0627\u0631 "\u0623\u062E\u0631\u0649"',
        max: "\u064A\u062C\u0628 \u0623\u0644\u0627 \u064A\u062A\u062C\u0627\u0648\u0632 \u0627\u0633\u0645 \u0627\u0644\u0628\u0646\u0643 255 \u062D\u0631\u0641\u0627\u064B"
      }
    },
    header: {
      title: "\u062A\u0623\u0633\u064A\u0633 \u0627\u0644\u0634\u0631\u0643\u0629",
      description: "\u064A\u0631\u062C\u0649 \u0625\u062F\u062E\u0627\u0644 \u062E\u064A\u0627\u0631\u0627\u062A \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u062A\u062C\u0627\u0631\u064A \u0627\u0644\u0645\u0641\u0636\u0644 \u0644\u062F\u064A\u0643\u060C \u0628\u0627\u0644\u0625\u0636\u0627\u0641\u0629 \u0625\u0644\u0649 \u0627\u0644\u0628\u0646\u0643 \u0627\u0644\u0645\u0641\u0636\u0644 \u0644\u062F\u0639\u0645 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0634\u0631\u0643\u0629 \u0648\u0628\u062F\u0621 \u062A\u0634\u063A\u064A\u0644\u0647\u0627."
    },
    tradeName: {
      label: "\u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u062A\u062C\u0627\u0631\u064A \u0627\u0644\u0645\u0641\u0636\u0644 \u0644\u0644\u062A\u0633\u062C\u064A\u0644 #{number}",
      placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0627\u0633\u0645 \u0627\u0644\u0645\u0641\u0636\u0644"
    },
    preferredBank: {
      label: "\u0627\u0644\u0628\u0646\u0643 \u0627\u0644\u0645\u0641\u0636\u0644",
      placeholder: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0628\u0646\u0643"
    },
    preferredBankOther: {
      label: '\u0641\u064A \u062D\u0627\u0644 \u0627\u062E\u062A\u064A\u0627\u0631 "\u0622\u062E\u0631"',
      placeholder: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0628\u0646\u0643 \u0627\u0644\u0645\u0641\u0636\u0644"
    },
    bankOptions: {
      "jp-morgan": "\u062C\u0649 \u0628\u0649 \u0645\u0648\u0631\u063A\u0627\u0646 \u0627\u0644\u0639\u0631\u0628\u064A\u0629 \u0627\u0644\u0633\u0639\u0648\u062F\u064A\u0629",
      sab: "\u0627\u0644\u0628\u0646\u0643 \u0627\u0644\u0633\u0639\u0648\u062F\u064A \u0627\u0644\u0623\u0648\u0644",
      snb: "\u0627\u0644\u0628\u0646\u0643 \u0627\u0644\u0623\u0647\u0644\u0649 \u0627\u0644\u0633\u0639\u0648\u062F\u064A",
      enbd: "\u0628\u0646\u0643 \u0627\u0644\u0625\u0645\u0627\u0631\u0627\u062A \u062F\u0628\u0649 \u0627\u0644\u0648\u0637\u0646\u0649",
      other: "\u0623\u062E\u0631\u0649"
    }
  }
};

// src/config.ts
var defaultNS = "common";
var supportedLanguages = ["en", "ar"];
var RTL_LANGUAGES = ["ar"];
var resources = {
  en: {
    common: common_default,
    auth: auth_default,
    portal: portal_default,
    onboarding: onboarding_default,
    "business-landing": business_landing_default
  },
  ar: {
    common: common_default2,
    auth: auth_default2,
    portal: portal_default2,
    onboarding: onboarding_default2,
    "business-landing": business_landing_default2
  }
};
function initI18n(cookieDomain) {
  void i18n.use(ICU).use(LanguageDetector).use(initReactI18next).init({
    resources,
    fallbackLng: "en",
    defaultNS,
    fallbackNS: "common",
    supportedLngs: supportedLanguages,
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ["cookie", "navigator"],
      caches: ["cookie"],
      lookupCookie: "tasama-language",
      cookieOptions: {
        path: "/",
        sameSite: "lax",
        ...cookieDomain ? { domain: cookieDomain } : {}
      }
    }
  });
}
var config_default = i18n;

// src/provider.tsx
import { jsx } from "react/jsx-runtime";
function I18nProvider({ children }) {
  useEffect(() => {
    function applyDirection(lng) {
      const dir = RTL_LANGUAGES.includes(lng) ? "rtl" : "ltr";
      document.documentElement.dir = dir;
      document.documentElement.lang = lng;
    }
    applyDirection(config_default.language);
    config_default.on("languageChanged", applyDirection);
    return () => {
      config_default.off("languageChanged", applyDirection);
    };
  }, []);
  return /* @__PURE__ */ jsx(I18nextProvider, { i18n: config_default, children });
}

// src/hooks/useTranslation.ts
import { useTranslation } from "react-i18next";

// src/hooks/useDirection.ts
import { useTranslation as useTranslation2 } from "react-i18next";
function useDirection() {
  const { i18n: i18n2 } = useTranslation2();
  return RTL_LANGUAGES.includes(i18n2.language) ? "rtl" : "ltr";
}

// src/utils.ts
function normalizeLanguage(lang) {
  return supportedLanguages.includes(lang) ? lang : "en";
}
export {
  I18nProvider,
  RTL_LANGUAGES,
  defaultNS,
  i18n,
  initI18n,
  normalizeLanguage,
  supportedLanguages,
  useDirection,
  useTranslation
};
//# sourceMappingURL=index.js.map